﻿// ── State ────────────────────────────────────────────────────────────────────
const state = {
  currentStep: 1,
  foremanName: '',
  date: '',
  workersOnSite: '',
  project: '',
  submissionType: '',
  fields: {},
  photos: [],
  signature: null,
  allowedTypes: null,
  fromDashboard: false,  // true when type + project were pre-selected on dashboard
  sendToSafety: true,    // toggle on step 4 — send PDF to CSO
  _csoContact: null,     // { cso_email, cso_name } loaded for current project
  // Production Report only (admin-only): the SUBCONTRACT CREW whose production
  // this is. It changes production_reports.foreman_id — the field Job Costs
  // reads to price sub work — and nothing else. The form itself is always
  // filed under the account that submitted it: no one signs in another
  // person's name. Null = own crew.
  productionCrewId: null,
  productionCrewName: '',
  // Has the admin ANSWERED "whose production is this?" — own crew is an answer
  // (id stays null), and so is a crew. False = not asked yet: the Production
  // Report will not file. See js/core/production-crew.js.
  productionCrewPicked: false,
};

// CSS's blanket prefers-reduced-motion rule (styles.css) covers CSS
// animations/transitions, but NOT a `behavior:'smooth'` passed explicitly to
// scrollTo/scrollIntoView from JS — that overrides the CSS scroll-behavior
// property per spec. Every smooth-scroll call site below gates on this.
function _prefersReducedMotion() {
  return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
}

// ── Sub-projects: field pickers hide a mother that has buildings ──────────────
// A "mother" project with buildings under it is never itself a filable job —
// crews always file on the building. auth.js's loadUserProjects() (and its
// offline-cache loader) publish window.projectRows: {id, name, parent_id}
// per project, same order/length as window.userProjects (plain names), for
// every load path (fresh fetch, cache, offline fallback).
//
// Pure over the fetched rows: given the same list, always the same output,
// so a picker built from the cached payload renders identically to one built
// online. Applied at RENDER time (not baked into the cache) so nothing about
// the offline-sync path changes — see syncTimesheetToDb/syncProductionReport
// ToDb/syncExtraWorkOrderToDb/openDeliveryModal's submit path, all of which
// resolve the project by name directly against the DB, never against this
// filtered list (a draft on a project that later became a mother still
// syncs).
//
// Degrade case: a cache written before this feature carries no parent_id at
// all — auth.js synthesizes parent_id: null rows for it, so motherIds below
// is always empty and nothing gets dropped ("no buildings").
// Zero-building orgs: every parent_id is null, motherIds is empty, no-op.
function filterFilableProjects(list) {
  const rows = Array.isArray(list) ? list : [];
  const motherIds = new Set(rows.map(r => r && r.parent_id).filter(Boolean));
  return rows.filter(r => r && !motherIds.has(r.id));
}

// ── Project-first nav (Task 4): the app opens on the job list, pick once ──────
// window.fieldProject = { id, name } | null — the one piece of state every
// screen will read once Task 5 scopes them (Today board, New Form, Timesheet,
// Deliveries, My Submissions). This task lands the plumbing + the list +
// auto-enter + a minimal Back; the Today board still renders with its own
// project selector visible, unchanged, exactly as it does today.
//
// Persisted per user (shared-device safe, same idiom as auth.js's
// ordinex_profile_<uid>/ordinex_projects_<uid> cache) so the choice survives
// a reload — and so it works with NO network: this reads localStorage only,
// same as the cached projects payload it's checked against.
function _fieldProjectKey() {
  return 'ordinexFieldProject:' + ((window.currentUser && window.currentUser.id) || 'default');
}
function _saveFieldProjectContext(p) {
  try { localStorage.setItem(_fieldProjectKey(), JSON.stringify({ id: p.id, name: p.name || '' })); } catch { /* private mode */ }
}
function _loadFieldProjectContext() {
  try {
    const v = JSON.parse(localStorage.getItem(_fieldProjectKey()) || 'null');
    return (v && v.id) ? v : null;
  } catch { return null; }
}
function _clearFieldProjectContext() {
  try { localStorage.removeItem(_fieldProjectKey()); } catch { /* private mode */ }
}

// app.js has no HTML-escaping helper of its own (every existing list here is
// built with textContent, not innerHTML) — this list is the first to need
// one, so it gets a small local one, same pattern as equipment.js's escEq.
function _fieldEsc(str) {
  return String(str ?? '').replace(/[&<>"']/g, c =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

// Sets the job context, persists it, and enters that job's Today board.
// `p` is a row from window.projectRows: { id, name, parent_id }.
function openFieldProject(p) {
  if (!p || !p.id) return;
  window.fieldProject = { id: p.id, name: p.name || '' };
  _saveFieldProjectContext(window.fieldProject);
  state.project = window.fieldProject.name;
  // Best-effort: pre-set the (already-populated, from a prior job) selector
  // so showHomeDashboard()'s own "keep whatever was already selected" restore
  // logic reads the NEW job, not whatever was left over from the last one —
  // see that function's own comment on why fieldProject now leads the
  // restore priority. A silent no-op the first time this project isn't in
  // the select's (stale) option list yet; state.project covers that case.
  const projSel = document.getElementById('dashProjectSelect');
  if (projSel) projSel.value = window.fieldProject.name;
  showHomeDashboard();
}

// Clears the job context and returns to the list — the Back skeleton this
// task ships (full workspace-style header is Task 5).
function closeFieldProject() {
  window.fieldProject = null;
  _clearFieldProjectContext();
  showFieldJobListScreen();
}

// Home for whichever state we're actually in: a job if one's picked, the
// list otherwise. Used everywhere the app used to just call
// showHomeDashboard() directly to mean "go home" (goHome(), the sidebar's
// Today button, the hardware-back handler, the post-submission reset) — none
// of those call sites need to know whether a job is picked; this decides it
// once, in one place.
function _fieldHome() {
  if (window.fieldProject && window.fieldProject.id) { showHomeDashboard(); return; }
  // Nothing to pick — a worker has jobs only through clock_context() when the
  // time clock is on (auth.js loadUserProjects), a new foreman may have none yet — lands on
  // the Today board, the same answer boot gives (resolveFieldBootContext mode
  // 'home'). The list used to land here and read "No jobs assigned yet. Ask
  // your admin to add you to a project" with nothing to tap but Sign out.
  if (!filterFilableProjects(window.projectRows || []).length) { showHomeDashboard(); return; }
  showFieldJobListScreen();
}

// The last list actually painted — _fieldRowTap looks a tapped row up here
// rather than round-tripping the id/name through an inline onclick string
// (a project name is free text; embedding it unescaped in an attribute is
// the wrong place to rely on _fieldEsc doing double duty as both HTML- and
// JS-string-escaping).
let _fieldListRows = [];

function renderFieldJobList() {
  const filable = filterFilableProjects(window.projectRows || []);
  _fieldListRows = filable;
  const wrap = document.getElementById('fieldJobListItems');
  if (!wrap) return;
  if (!filable.length) {
    wrap.innerHTML = '<div class="field-joblist-empty">No projects assigned yet. Ask your admin to add you to one.</div>';
    return;
  }
  // data-id + delegated wiring, not an inline onclick with the id spliced
  // into a JS string literal — id is NOT guaranteed to be a safe UUID here.
  // On the old-format offline cache degrade path (auth.js's
  // _loadCachedProjects, "old format: names only"), window.projectRows is
  // synthesized as { id: name, name, parent_id: null } — id IS the free-text
  // project name on that path, apostrophe and all (e.g. "O'Brien & Sons").
  // An inline onclick="_fieldRowTap('${p.id}')" breaks the literal on that
  // input; data-id (HTML-attribute-escaped via _fieldEsc, same as the name
  // right next to it) never has this problem. Same pattern the repo already
  // uses for exactly this reason — see loadCompanyResources()'s
  // data-doc-id + querySelectorAll(...).onclick wiring.
  wrap.innerHTML = filable.map(p => `
    <button type="button" class="fx-row field-joblist-row" data-id="${_fieldEsc(p.id)}">
      <span class="fx-row-lead" aria-hidden="true"><i data-lucide="building-2"></i></span>
      <span class="fx-row-name">${_fieldEsc(p.name)}</span>
      <span class="fx-row-chev" aria-hidden="true"><i data-lucide="chevron-right"></i></span>
    </button>`).join('');
  wrap.querySelectorAll('button[data-id]').forEach(btn => {
    btn.onclick = () => _fieldRowTap(btn.dataset.id);
  });
  if (window.lucide?.createIcons) window.lucide.createIcons();
}

let _fieldRowBusy = false;   // a row tap still deciding: the list takes no other tap

// A worker on site on one job who taps another moves there: out of the old job and in on the
// new one at one instant (spec 6.1 "Move to Marpole?"). Asked here, on a tap in the list, and
// never at boot: openFieldProject is also the boot path (_fieldBootEnter). Cancel keeps the
// list on screen and changes nothing. The job opens first, so the PIPA notice of the Move's
// check-in (its GPS read) shows in a card that is on screen. A punch on its way goes first (never
// dropped, never overtaken): meanwhile the list says it is saving and takes no other tap, so a
// worker tapping again and again does not stack up Move questions.
async function _fieldRowTap(id) {
  const row = _fieldListRows.find(p => p.id === id);
  if (!row || _fieldRowBusy) return;
  const worker = window.currentProfile?.role === 'worker';
  const list = document.getElementById('fieldJobListItems');
  const sub = document.querySelector('#fieldJobListScreen .field-joblist-sub');
  const said = sub?.textContent;
  let open = null;
  _fieldRowBusy = true;
  try {
    if (worker && _clockRunning) {
      list?.setAttribute('aria-busy', 'true');
      if (sub) sub.textContent = 'Saving your punch…';
      while (_clockRunning) await _clockRunning;
    }
    if (worker) open = await _clockOptimisticOpen();
  } finally {
    _fieldRowBusy = false;
    list?.removeAttribute('aria-busy');
    if (sub) sub.textContent = said;
  }
  if (!open || row.id === open.project_id) { openFieldProject(row); return; }
  if (!confirm('Move to ' + row.name + '?')) return;
  openFieldProject(row);
  await _clockRun('move', () => punchMany(Ordinex.clockCorrection.movePair(
    { workerId: window.clockCtx.worker_id, projectId: open.project_id, workDate: open.work_date }, row.id, new Date().toISOString(), 'self'),
  { pair: 'move', gps: true }));
}

// Screen-toggle idiom, matching showHomeDashboard()/showInnerPage() exactly —
// hide every other top-level screen, show this one.
function showFieldJobListScreen() {
  // Wrong-job draft reassignment fix (final review, project-first nav): this
  // is one of the 3 functions that actually hide the wizard's #mainApp .app
  // (the other two are showHomeDashboard and showInnerPage) — every path out
  // of the wizard funnels through one of them (Back → _handleBack →
  // _fieldHome → here-or-showHomeDashboard; the device Back button via the
  // popstate handler → same; the desktop sidebar's "Today" → _fieldHome →
  // same; "‹ All jobs" → closeFieldProject → here directly). The 30s draft
  // heartbeat (_startAutoSaveDraft, armed by renderStep3 for every draftable
  // form) was previously stopped only by resetApp/startAnotherForm, so it
  // kept running across a Back-out + job switch and re-saved the OLD job's
  // fields stamped with the NEW job's name (loadDraftData's DRAFT RULE then
  // made that new stamp authoritative on resume — a real submission could
  // file one job's hours under another). Stopping it here is a safe no-op
  // whenever no heartbeat was running (_stopAutoSaveDraft no-ops on a null
  // timer) — including first boot, which reaches this function with nothing
  // to stop.
  _stopAutoSaveDraft();
  if (typeof _flowNavArmed !== 'undefined') _flowNavArmed = false;   // top of the stack
  const homeScreen   = document.getElementById('homeScreen');
  const listScreen   = document.getElementById('fieldJobListScreen');
  const appDiv       = document.querySelector('#mainApp .app');
  const progressWrap = document.querySelector('.progress-wrap');
  const navBar       = document.querySelector('.nav-bar');
  if (homeScreen)   homeScreen.style.display   = 'none';
  if (listScreen)   listScreen.style.display   = 'block';
  if (appDiv)       appDiv.style.display       = 'none';
  if (progressWrap) progressWrap.style.display = 'none';
  if (navBar)       navBar.style.display       = 'none';
  document.querySelectorAll('.inner-page').forEach(p => p.style.display = 'none');
  renderFieldJobList();
  window.scrollTo({ top: 0, behavior: 'instant' });
  // The sidebar foot (name/role) and the Admin Panel item were only painted
  // by the job dashboard's render — a boot that lands on this list showed
  // "—" in the foot card and no way into the admin panel until a job was
  // entered. Fill here too; it is idempotent.
  _fdSideFill(window.currentProfile?.role === 'worker');
  if (typeof _fdSideSync === 'function') _fdSideSync();
}

// The boot-time decision — called once, at the end of initializeApp(), in
// place of the direct showHomeDashboard() call this task removes. Every boot
// path converges through initializeApp() (normal login, the offline-cached-
// profile fallback, and first-time profile setup all call it — see auth.js),
// so this single call site is the one choke point that needs to learn about
// the list; nothing else in the boot chain does.
//
// Pure decision via js/core/field-nav.js's resolveFieldBootContext (window.
// Ordinex.fieldNav) — this function only supplies the two DOM/localStorage-
// backed arguments (the filtered payload, the cached context) and carries
// out whichever mode comes back. No network call: window.projectRows is
// already populated by the time initializeApp() runs, by auth.js, from
// either a fresh fetch OR its own localStorage cache when offline — so this
// whole decision, including auto-enter and the list render, works with the
// radio off. The clock context too: boot reads its cached copy only (the
// session may be wedged in a token refresh, auth.js _bootFromCache) and asks
// the server in the background; the clock surfaces repaint when it answers.
// A worker on site lands on the job he is checked in on, without a question.
async function _fieldBootEnter() {
  await _loadClockContext({ network: false });
  _loadClockContext().then(() => _clockRefreshSurfaces());
  const filable = filterFilableProjects(window.projectRows || []);
  if (window.currentProfile?.role === 'worker') {
    const open = await _clockOptimisticOpen();
    const here = open && filable.find(p => p.id === open.project_id);
    if (here) { openFieldProject(here); return; }
  }
  const stored  = _loadFieldProjectContext();
  const result  = window.Ordinex?.fieldNav?.resolveFieldBootContext(filable, stored)
    // Defensive fallback if core.min.js somehow didn't load — never a dead
    // end. Same 3-mode shape as the real resolver, no auto-enter-on-stale
    // nuance (rare enough not to hand-roll twice).
    ?? (filable.length === 0 ? { mode: 'home', project: null, stale: false }
      : filable.length === 1 ? { mode: 'enter', project: filable[0], stale: false }
      : { mode: 'list', project: null, stale: false });

  if (result.stale) _clearFieldProjectContext();   // never auto-pick the wrong job

  if (result.mode === 'home')  { window.fieldProject = null; showHomeDashboard(); return; }
  if (result.mode === 'enter') { openFieldProject(result.project); return; }
  window.fieldProject = null;
  showFieldJobListScreen();
}

// ── Bootstrap ─────────────────────────────────────────────────────────────────
// DOMContentLoaded just hands off to auth — initializeApp() is called by
// auth.js after a successful login (or valid existing session).
document.addEventListener('DOMContentLoaded', () => initAuth());

// Returns today as YYYY-MM-DD in the foreman's LOCAL timezone (not UTC).
// Using toISOString() would shift the date forward in any time zone east of UTC
// (and backward west of it after ~5pm local) — bad for Vancouver crews filling
// out a form in the evening.
function _todayLocal() {
  const d = new Date();
  return d.getFullYear() + '-' +
         String(d.getMonth() + 1).padStart(2, '0') + '-' +
         String(d.getDate()).padStart(2, '0');
}

// ── Week Of / Week Ending follow the Date ────────────────────────────────────
// The database week (production_reports / time_entries) comes from state.date;
// the PDF header printed whatever was typed in these fields, and nothing
// compared the two (2026-10-17). They are prefilled with the Date's week
// (Monday / Sunday), bounded to it in the picker, and refused outside it in
// validateStep(3).
const WEEK_FIELD_IDS = { 'Production Report': 'week_of', 'Weekly Timesheet': 'week_ending' };
function _weekFieldId() { return WEEK_FIELD_IDS[state.submissionType] || null; }
function _prefillWeekField() {
  const id = _weekFieldId();
  if (!id || state.fields[id]) return;
  const wd = window.Ordinex.guards.weekDatesFor(state.date);
  state.fields[id] = id === 'week_of' ? wd[0] : wd[6];
}
function _syncWeekFieldBounds() {
  const id = _weekFieldId();
  const el = id && document.getElementById('field_' + id);
  if (!el) return;
  const wd = window.Ordinex.guards.weekDatesFor(state.date);
  el.min = wd[0]; el.max = wd[6];
}
// The Date moved to another week (a report filed late): the week field moves with
// it. It is often not on screen at all, so nobody could fix it by hand and the
// step refused with "week_of is not in the week of the Date" (2026-10-28).
// A value still inside the new week (a hand-typed one) is kept.
function _followDateWeek() {
  const id = _weekFieldId();
  if (!id) return;
  const wd = window.Ordinex.guards.weekDatesFor(state.date);
  const cur = state.fields[id];
  if (!cur || cur < wd[0] || cur > wd[6]) {
    state.fields[id] = id === 'week_of' ? wd[0] : wd[6];
    const el = document.getElementById('field_' + id);
    if (el) el.value = state.fields[id];
  }
  _syncWeekFieldBounds();
}

// ── initializeApp ─────────────────────────────────────────────────────────────
// Called by auth.js once the user is authenticated and their profile is loaded.
function initializeApp() {
  const today = _todayLocal();
  const dateEl = document.getElementById('foremanDate');
  dateEl.value = today;
  dateEl.max   = today;   // browser-enforced: picker can't scroll past today
  state.date = today;

  // Identity comes from the account, never from typing. Every form is filed in
  // the name of whoever is signed in — the field is read-only in the markup, so
  // there is no path (admin included) to file a form under someone else's name.
  _syncNameFromAccount();

  // The self-heal check gets its own trigger: app open, reconnect, and tab
  // brought back to the front. It used to hang off the tail of flushOutbox,
  // which returns early when the outbox is empty — the exact state of a phone
  // whose ONLINE filing lost only its second write, i.e. the case it exists
  // for. Idempotent: _startSyncWatchdog is a no-op if already armed.
  if (typeof _startSyncWatchdog === 'function' && !window.__syncWatchdogOn) {
    window.__syncWatchdogOn = true;
    _startSyncWatchdog();
  }

  checkBackendHealth().then(online => {
    const dot = document.getElementById('backendStatus');
    if (dot) {
      dot.className = online ? 'status-dot online' : 'status-dot offline';
      dot.title = online ? 'Online' : 'Offline — forms saved locally';
    }
  });

  attachNavListeners();

  document.getElementById('foremanDate').addEventListener('input', e => { state.date = e.target.value; _followDateWeek(); });

  // Load shared workers list for crew sign-in autofill (background, non-blocking)
  loadWorkersForAutofill().catch(err => console.warn('Workers autofill load failed:', err));

  // Admin-only: pre-warm the subcontract-crew cache so the Production Report
  // renders its crew picker instantly (see renderProductionCrewPicker).
  if (window.currentProfile?.role === 'admin') {
    _loadSubCrewsCached().catch(err => console.warn('Sub crew prewarm failed:', err));
  }

  // Project-first nav (Task 4): the app opens on the job list, not the Today
  // board — _fieldBootEnter() decides list vs. auto-enter vs. (0 jobs) home.
  // Home-screen shortcuts / share target (PWA item 5) — consumed after the
  // normal boot decided where to land, so a plain open is never affected.
  _fieldBootEnter().then(() => _handleLaunchParams());
}

// ── Launch params (PWA item 5) ────────────────────────────────────────────────
// Home-screen shortcuts and the OS share sheet land with a query string:
//   ?form=<type>   → jump into that form's flow (project must already be
//                    picked — a shortcut never guesses a job; wrong-project
//                    paperwork costs more than one extra tap)
//   ?goto=actions  → open My Actions
//   ?share=photos  → jump toward Site Photos; the shared files themselves are
//                    injected by _consumeSharedPhotos() when the screen opens,
//                    so they survive any detour through the project picker.
// Consumed once and stripped, so a reload never repeats the jump.
const _tF = (k, p) => window.Ordinex?.i18n?.t(k, p) ?? k;
// The screen on top right now, by id ('form' for the wizard), read like _fdSideSync reads it: to tell
// whether the person has moved on while something was awaited.
function _screenOnTop() {
  const shown = [...document.querySelectorAll('#homeScreen, #fieldJobListScreen, #mainApp .app, .inner-page')]
    .find(el => el.style.display !== 'none');
  return shown ? shown.id || 'form' : null;
}
async function _handleLaunchParams() {
  let params;
  try { params = new URLSearchParams(window.location.search); } catch (_) { return; }
  const form = params.get('form'), goTo = params.get('goto'), share = params.get('share'), denied = params.get('denied');
  if (!form && !goTo && !share && !denied) return;
  try { history.replaceState(null, '', window.location.pathname); } catch (_) {}
  // admin.html / calendar.html send a non-admin back here with ?denied=admin;
  // without this line the bounce was silent (stranger sweep 2026-10-17).
  if (denied) { showToast(_tF('nav.adminOnly'), 'warning'); return; }
  // The Check in shortcut and the "still checked in" push (contract 4.4): Today, where the card is
  // (a worker's own, a foreman's crew card), after a fresh read of the clock. With no job picked
  // _fieldHome opens the list first, as everywhere else. That read can take 15 s: Today is drawn only
  // if the screen on top is still the one the link found, never over what he opened meanwhile.
  if (goTo === 'clock') {
    const was = _screenOnTop();
    await _loadClockContext();
    if (_screenOnTop() === was) _fieldHome();
    if (window.currentProfile?.role !== 'worker') document.getElementById('crewClockCard')?.scrollIntoView({ block: 'start' });
    return;
  }
  if (goTo === 'actions') {
    if (typeof openMyActionsPage === 'function') openMyActionsPage();
    return;
  }
  const type = share === 'photos' ? 'Site Photos Only' : form;
  if (!type) return;
  // The dashboard project selector fills as the boot settles — give it a
  // moment; startSpecificFormFlow() itself handles "no project picked yet"
  // with the pulse + toast, which is exactly the right fallback here.
  const visible = () => (visibleSubmissionTypes?.() || []).some(t => t.id === type || t === type);
  for (let i = 0; i < 12; i++) {
    if (document.getElementById('dashProjectSelect')?.value) break;
    await new Promise(r => setTimeout(r, 250));
  }
  try {
    if (typeof visibleSubmissionTypes === 'function' && !visible()) return;  // hidden for this trade/role
    await startSpecificFormFlow(type);
  } catch (e) { console.warn('[launch] shortcut open failed:', e); }
}

// ── Who filed the form: the account, always ──────────────────────────────────
// One identity, no override. `foremanName` mirrors the signed-in profile and
// the Step 1 input is read-only, so a form can never be filed — or signed — in
// another person's name. Called on boot and after every reset.
function _syncNameFromAccount() {
  state.foremanName = window.currentProfile?.full_name
    || window.currentUser?.email
    || '';
  const nameEl = document.getElementById('foremanName');
  if (nameEl) nameEl.value = state.foremanName;
}

// Author of the row being written. Both are read at every DB write spot.
function _submitterId()   { return window.currentUser?.id || null; }
function _submitterName() { return state.foremanName || window.currentProfile?.full_name || ''; }

// ── Production Report: whose production is this? ──────────────────────────────
// A subcontract crew files no timesheet, so its production report is the only
// trace of the work — and Job Costs prices that work by reading
// production_reports.foreman_id (see js/admin/66-jobcosts.js). When the admin
// enters a sub's week, the units have to land on the SUB's row or the job reads
// as if that labour were free.
//
// So this picker moves ONE thing: which crew owns the production. The
// submissions row, the PDF author and the signature stay with the admin who
// typed it. Only profiles flagged is_subcontractor are listed — an in-house
// foreman is never selectable, because his cost already arrives by the hour
// through the timesheet (double-count guard).
//
// NO SILENT DEFAULT (owner's decision, 2026-10-02). The picker used to open on
// "Own crew", so a sub's week typed by the office and never changed filed as
// own crew — production that earned and consumed material with no labour cost
// at all. It now opens on nothing, and the form does not file until the admin
// says which it is: the company's own crew, or one of the sub crews. A foreman
// never sees it, and neither does an org with no sub crew to choose.
// The three states and their rules are in js/core/production-crew.js.
let _subCrewsCache = null;
// The last list this device saw, per account — what makes the question
// askable with no signal. Without it an offline form had no picker at all and
// filed as own crew, which is the exact hole this control exists to close.
const _subCrewsKey = () => 'ordinexSubCrews:' + (window.currentUser?.id || '');
function _storedSubCrews() {
  try {
    const c = JSON.parse(localStorage.getItem(_subCrewsKey()) || 'null');
    return Array.isArray(c) ? c : null;
  } catch (_) { return null; }
}
// What is known RIGHT NOW, with no network: the gates in validateStep and
// handleSubmit are synchronous and must never wait on a hanging request.
function _knownSubCrews() {
  return (_subCrewsCache && _subCrewsCache.length) ? _subCrewsCache : (_storedSubCrews() || []);
}
// "This org has no sub crew" and "I could not find out" both read as an empty
// list, and they are not the same answer: the first means there is nothing to
// ask, the second means a draft's crew must be left exactly as it was saved.
// Known = a list was obtained at least once — this boot, or stored by an
// earlier one (a stored [] counts: that org is known to have none).
let _subCrewsFetched = false;
function _subCrewsKnown() { return _subCrewsFetched || _storedSubCrews() !== null; }
// The request itself: the fresh list (stored for the next boot), or null when
// it could not be had.
async function _fetchSubCrews() {
  // Offline: don't even ask — a client whose token expired offline is wedged
  // in a refresh retry loop, and awaiting it would stall the form.
  if (navigator.onLine === false) return null;
  let fresh = null;
  try {
    const { data, error } = await sbClient
      .from('profiles')
      .select('id, full_name')
      .eq('is_subcontractor', true)
      .order('full_name');
    if (error) console.warn('Sub crew list load failed:', error.message);
    else fresh = data || [];
  } catch (e) { console.warn('Sub crew list load failed:', e); }
  if (fresh) {
    _subCrewsFetched = true;
    try { localStorage.setItem(_subCrewsKey(), JSON.stringify(fresh)); } catch (_) { /* private mode / quota */ }
  }
  return fresh;
}
async function _loadSubCrewsCached() {
  // An EMPTY result is never cached in memory: an org that flags its first sub
  // crew in the admin panel must see the picker on the next form, not after a
  // reload.
  if (_subCrewsCache && _subCrewsCache.length) return _subCrewsCache;
  // Paint from what the device already knows and refresh behind it. The gates
  // read the stored list synchronously, so waiting on the network here meant a
  // hung request (weak signal: onLine true, nothing answers) left the admin
  // refused at Next for a control that was not on screen.
  const stored = _storedSubCrews();
  if (stored && stored.length) {
    _subCrewsCache = stored;             // set first: one refresh per boot
    _fetchSubCrews().then(fresh => {
      if (!fresh) return;
      _subCrewsCache = fresh;
      if (JSON.stringify(fresh) === JSON.stringify(stored)) return;
      // The list on screen is a boot old and has changed. Redraw the picker
      // from the new one: an answer that is still valid is re-selected, a crew
      // un-flagged since is dropped to unanswered (the DB refuses that write).
      const wrap = document.getElementById('prodCrewPickerWrap');
      if (!wrap || !wrap.parentNode) return;
      const container = wrap.parentNode;
      const resumedDraft = !!wrap.querySelector('#prodCrewAsk');
      wrap.remove();
      renderProductionCrewPicker(container, { resumedDraft });
    }).catch(err => console.warn('Sub crew refresh failed:', err));
    return stored;
  }
  const fresh = await _fetchSubCrews();
  _subCrewsCache = fresh || stored || [];
  return _subCrewsCache;
}

function _productionCrewChoice() {
  return { picked: !!state.productionCrewPicked, id: state.productionCrewId || null,
           name: state.productionCrewName || '' };
}
function _setProductionCrew(choice) {
  state.productionCrewPicked = !!choice.picked;
  state.productionCrewId     = choice.id || null;
  state.productionCrewName   = choice.name || '';
}
// Must the form stop and ask? Admin + Production Report + a sub crew to choose
// from + no answer yet. Synchronous on purpose (see _knownSubCrews).
function _productionCrewMissing() {
  // Decided here first, before the shared module is touched: this runs on the
  // way out of EVERY form, for every role, and must cost a foreman filing a
  // tailgate nothing at all.
  if (window.currentProfile?.role !== 'admin' || state.submissionType !== 'Production Report') return false;
  return window.Ordinex.productionCrew.crewChoiceMissing({
    role: window.currentProfile?.role,
    formType: state.submissionType,
    crews: _knownSubCrews(),
    myId: window.currentUser?.id,
    picked: state.productionCrewPicked,
  });
}

async function renderProductionCrewPicker(container, opts = {}) {
  // Admins only, Production Report only — silent no-op everywhere else.
  if (window.currentProfile?.role !== 'admin') return;
  if (state.submissionType !== 'Production Report') return;
  if (container.querySelector('#prodCrewPickerWrap')) return;   // never twice

  // Nothing to choose from → no card at all. An org with no sub crews should
  // never see a control that does nothing. Yourself is dropped from the list:
  // "own crew" already means exactly that, and two options writing the same
  // foreman_id is just noise.
  const PC = window.Ordinex.productionCrew;
  const crews = PC.pickableCrews(await _loadSubCrewsCached(), window.currentUser?.id);
  if (!crews.length) {
    // UNKNOWN (no signal or a failed request, and nothing stored yet): touch
    // nothing. A crew a resumed draft carries stays the answer — nulling it
    // here turned "a sub crew" into "own crew, chosen" and the next autosave
    // wrote that over the draft, so it never asked again. Keeping it is safe:
    // the filing and the offline record carry the crew, and the DB refuses a
    // crew that is no longer flagged, which leaves the record queued.
    if (!_subCrewsKnown()) return;
    // KNOWN to be empty: nothing to choose between. The report files as the
    // company's own, and that is NOT stored as an answer — an org that flags
    // its first sub crew later must still be asked on this draft.
    const hadCrew = !!state.productionCrewId;
    _setProductionCrew({ picked: false });
    if (hadCrew) showToast('That sub crew is no longer flagged as a subcontractor — this report now files as your own crew.', 'warning');
    return;
  }
  if (container.querySelector('#prodCrewPickerWrap')) return;   // async re-entry

  const wrap = document.createElement('div');
  wrap.id = 'prodCrewPickerWrap';
  wrap.className = 'field-group prod-crew-wrap';
  wrap.innerHTML = `
    <label class="field-label" for="prodCrewSelect">
      <span class="prod-crew-label">
        <span>Whose production is this?</span>
        <span class="prod-crew-tag">ADMIN</span>
      </span>
    </label>
    <select class="field-input field-select" id="prodCrewSelect" aria-required="true">
      <option value="" disabled selected>Choose whose production this is…</option>
      <option value="${PC.OWN}">Our own crew — paid by the hour</option>
      <optgroup label="Subcontract crew — paid per unit" id="prodCrewSubs"></optgroup>
    </select>
    <div class="field-hint prod-crew-hint">
      Picks which crew the units and their cost belong to. The form itself stays
      filed under your name.
    </div>
  `;
  // Top of the step, right under the draft banner. Positioned explicitly rather
  // than appended: the crew list is fetched first (so an org with no sub crews
  // gets no card at all), and by the time it lands the rest of the step is
  // already built — an append would drop this at the very bottom of the form.
  const banner = container.querySelector('.draft-banner');
  if (banner && banner.nextSibling) container.insertBefore(wrap, banner.nextSibling);
  else if (banner)                  container.appendChild(wrap);
  else                              container.insertBefore(wrap, container.firstChild);

  const sel = wrap.querySelector('#prodCrewSelect');
  const subs = wrap.querySelector('#prodCrewSubs');
  crews.forEach(p => {
    const opt = document.createElement('option');
    opt.value = p.id;
    opt.dataset.name = p.full_name || '';
    opt.textContent = p.full_name || '(unnamed crew)';
    subs.appendChild(opt);
  });
  // Restore the answer — navigating back from review, or resuming a draft (see
  // the __crew key in saveDraft). A crew that is no longer flagged as a
  // subcontractor is dropped rather than left invisible on `state`: the DB
  // would refuse that write and the week's production would vanish. Dropped
  // means UNANSWERED — it asks again, it does not fall back to own crew.
  if (state.productionCrewId && !crews.some(p => p.id === state.productionCrewId)) {
    _setProductionCrew(PC.crewFromSelect(''));
    showToast('That sub crew is no longer flagged as a subcontractor — pick the crew again.', 'warning');
  }
  sel.value = PC.crewSelectValue(_productionCrewChoice());
  // A draft saved before this question existed carries no answer (it used to
  // mean "own crew" by default). It asks once: the answer is then saved with
  // the draft like any other.
  if (!state.productionCrewPicked && opts.resumedDraft) {
    const note = document.createElement('div');
    note.className = 'field-hint prod-crew-hint';
    note.id = 'prodCrewAsk';
    note.textContent = 'This draft has no answer here yet — choose once and it is saved with the draft.';
    wrap.appendChild(note);
  }

  sel.addEventListener('change', () => {
    const opt = sel.options[sel.selectedIndex];
    _setProductionCrew(PC.crewFromSelect(sel.value, opt?.dataset?.name));
    sel.classList.remove('field-error');
    wrap.querySelectorAll('.error-msg, #prodCrewAsk').forEach(el => el.remove());
    // Saved now, not at the next 30-second heartbeat: this answer decides
    // which crew is costed, and it must survive the app being closed.
    if (DRAFTABLE_FORMS.includes(state.submissionType)) {
      saveDraft({ silent: true }).catch(err => console.warn('draft save failed:', err));
    }
  });
}

// ── Load workers master list ──────────────────────────────────────────────────
// Full records (with id + trade) feed the select-only pickers: crew sign-in
// and the timesheet worker picker. Foremen can only choose real workers — no
// freehand names, no typos, no silently-lost rows.
// Also loads this account's worker_assignments (admin's "Assign Foremen"
// modal) so pickers can group "My crew" above "Other workers". Both lists are
// cached per-account in localStorage: a foreman with no signal still gets a
// working picker from the last list this device saw.
async function loadWorkersForAutofill() {
  let uid = window.currentUser?.id || null;
  if (!uid) {
    try { uid = (await sbClient.auth.getUser())?.data?.user?.id || null; } catch (_) {}
  }
  const cacheKey = uid ? `ox_workers_cache_${uid}` : null;

  const [wRes, aRes] = await Promise.all([
    sbClient.from('workers').select('id, name, trade').eq('active', true).order('name'),
    uid
      ? sbClient.from('worker_assignments').select('worker_id').eq('foreman_id', uid)
      : Promise.resolve({ data: [], error: null }),
  ]);

  if (wRes.error) {
    if (cacheKey) {
      try {
        const c = JSON.parse(localStorage.getItem(cacheKey) || 'null');
        if (c && Array.isArray(c.workers)) {
          window.workersList = c.workers.map(w => w.name);
          window.workersFull = c.workers;
          window.myCrewIds   = new Set((c.crewIds || []).map(String));
        }
      } catch (_) {}
    }
    return;
  }

  const workers = wRes.data || [];
  const crewIds = aRes.error ? [] : (aRes.data || []).map(r => String(r.worker_id));
  window.workersList = workers.map(w => w.name);
  window.workersFull = workers;
  window.myCrewIds   = new Set(crewIds);
  if (cacheKey) {
    try { localStorage.setItem(cacheKey, JSON.stringify({ workers, crewIds })); } catch (_) {}
  }
}

// Split the master list into this foreman's assigned workers vs the rest.
// An account with zero assignments gets everyone in "others" — the pickers
// then render the flat list exactly as before the feature existed.
function _workerGroups() {
  const all = (Array.isArray(window.workersFull) ? window.workersFull.slice() : [])
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  const ids = window.myCrewIds instanceof Set ? window.myCrewIds : new Set();
  if (!ids.size) return { mine: [], others: all };
  const mine = [], others = [];
  all.forEach(w => (ids.has(String(w.id)) ? mine : others).push(w));
  return { mine, others };
}

// Append worker <option>s to a <select>, grouped "My crew" / "Other workers"
// when this account has assignments, flat otherwise. buildOpt(w) returns the
// <option> element — each picker keeps its own option shape and restore logic.
function _appendGroupedWorkerOptions(sel, buildOpt) {
  const { mine, others } = _workerGroups();
  if (mine.length && others.length) {
    const gMine = document.createElement('optgroup');
    gMine.label = 'My crew';
    mine.forEach(w => gMine.appendChild(buildOpt(w)));
    const gOther = document.createElement('optgroup');
    gOther.label = 'Other workers';
    others.forEach(w => gOther.appendChild(buildOpt(w)));
    sel.appendChild(gMine);
    sel.appendChild(gOther);
  } else {
    (mine.length ? mine : others).forEach(w => sel.appendChild(buildOpt(w)));
  }
}

// ── Admin panel ───────────────────────────────────────────────────────────────
function openAdminPanel() {
  window.location.href = 'admin.html';
}

// ── Today with no job to file on ─────────────────────────────────────────────
// An admin of a company with no project yet gets the five setup steps (each
// opens the admin panel where that step is done); a foreman — or a worker of a
// company with the time clock on — with no project gets "not on a project yet"
// (the office sees who is waiting, admin side). From the cached projects, so
// it reads the same offline. Hidden as soon as there is a job.
function _todayNoJobs() {
  // projectRows unset = the jobs were never read on this phone: unknown, not none
  return !(window.fieldProject && window.fieldProject.id) && Array.isArray(window.projectRows)
    && !filterFilableProjects(window.projectRows).length;
}
function renderStartCard() {
  const card = document.getElementById('dashStartCard');
  if (!card) return;
  const role = window.currentProfile?.role;
  const show = _todayNoJobs() && (role === 'admin' || role === 'foreman' || (role === 'worker' && _clockEnabled()));
  card.hidden = !show;
  if (!show) { card.innerHTML = ''; return; }
  const t = k => _fieldEsc(window.Ordinex.i18n.t(k));
  if (role !== 'admin') {
    card.innerHTML = `<h2 class="fx-start-title">${t('start.noJobTitle')}</h2><p class="fx-start-sub">${t('start.noJobSub')}</p>`;
    return;
  }
  const org = window.currentOrg || {};
  const brandDone = !!(org.branding?.logoPath && org.jurisdiction && org.jurisdiction !== 'generic');
  const step = (n, key, href, done) => `<a class="fx-row${done ? ' fx-row--done' : ''}" href="${href}">
      <span class="fx-start-n" aria-hidden="true">${done ? '✓' : n}</span>
      <span class="fx-row-name">${t(key)}</span>
      <span class="fx-row-chev" aria-hidden="true"><i data-lucide="chevron-right"></i></span>
    </a>`;
  card.innerHTML = `<h2 class="fx-start-title">${t('start.setupTitle')}</h2>
    <p class="fx-start-sub">${t('start.setupSub')}</p>
    <div class="fx-rows">
      ${step(1, 'start.stepBrand', 'admin.html#settings', brandDone)}
      ${step(2, 'start.stepProject', 'admin.html#projects', false)}
      ${step(3, 'start.stepForeman', 'admin.html#foremans', false)}
      ${step(4, 'start.stepWorkers', 'admin.html#workers', false)}
      <div class="fx-row fx-row--static">
        <span class="fx-start-n" aria-hidden="true">5</span>
        <span class="fx-row-txt"><span class="fx-row-name">${t('start.stepTailgate')}</span><span class="fx-row-sub">${t('start.stepTailgateSub')}</span></span>
      </div>
    </div>`;
}

// ── Home Dashboard ────────────────────────────────────────────────────────────

async function showHomeDashboard() {
  // Wrong-job draft reassignment fix — see the matching comment in
  // showFieldJobListScreen() for the full picture. This is the choke point
  // for Back-out-to-Today, the sidebar's "Today", and openFieldProject()
  // switching straight into another job's board.
  _stopAutoSaveDraft();
  if (typeof _flowNavArmed !== 'undefined') _flowNavArmed = false;   // top of the stack
  const homeScreen   = document.getElementById('homeScreen');
  const listScreen   = document.getElementById('fieldJobListScreen');
  const appDiv       = document.querySelector('#mainApp .app');
  const progressWrap = document.querySelector('.progress-wrap');
  const navBar       = document.querySelector('.nav-bar');

  if (homeScreen)   homeScreen.style.display  = 'block';
  if (listScreen)   listScreen.style.display  = 'none';   // project-first nav (Task 4)
  if (appDiv)       appDiv.style.display      = 'none';
  if (progressWrap) progressWrap.style.display = 'none';
  if (navBar)       navBar.style.display      = 'none';
  // Coming home has to CLOSE whatever inner page was open, or the dashboard
  // paints on top of it and both stay on screen — the sidebar's "Today" calls
  // this directly, without going through goHome(). Reported from the field
  // 2026-07-28: dashboard stacked above My Submissions on every visit.
  document.querySelectorAll('.inner-page').forEach(p => p.style.display = 'none');

  // Job-space header (Task 4 skeleton, finished Task 5): reachable whenever
  // a job is picked, including the single-job auto-enter case. The job NAME
  // is the other half — together with "‹ All jobs" it replaces the project
  // selector-bar below (#dashProjectPick, toggled further down this
  // function) as "the header IS the context now".
  const hasJobCtx  = !!(window.fieldProject && window.fieldProject.id);
  const jobNameEl = document.getElementById('dashJobspaceName');
  const jobNameTxt = document.getElementById('dashJobspaceNameTxt');
  if (jobNameEl) jobNameEl.hidden = !hasJobCtx;
  if (jobNameTxt) jobNameTxt.textContent = hasJobCtx ? window.fieldProject.name : '';

  state.allowedTypes  = null;
  state.fromDashboard = false;

  // Greeting
  const hour  = new Date().getHours();
  const greet = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const fullName = window.currentProfile?.full_name || '';
  const first = fullName.split(' ')[0] || '';
  const greetEl = document.getElementById('dashGreeting');
  if (greetEl) greetEl.textContent = `${greet}${first ? ', ' + first : ''}`;

  // Avatar — initials as immediate fallback, then load deterministic image
  const av = document.getElementById('dashAvatarBtn');
  if (av) {
    av.textContent = _initials2(fullName);
    applyUserAvatar(av);
  }

  // Admin destinations — the tile in the "Go to" grid and the bridge in the
  // app bar. Same gate, two affordances: the grid is where a foreman looks for
  // a destination, the bar is where an admin who lives in both looks.
  const isAdmin = window.currentProfile?.role === 'admin';
  const adminSection = document.getElementById('dashAdminSection');
  if (adminSection) adminSection.hidden = !isAdmin;
  const adminLink = document.getElementById('dashAdminLink');
  if (adminLink) adminLink.hidden = !isAdmin;

  // Worker accounts are view-only (roadmap 1.8, decided 2026-07-22): no
  // submission surfaces on the home. The RLS role gates are the real
  // enforcement — this keeps the home honest about what a worker can do
  // (certifications, My Actions, company resources). Documents, Request
  // delivery and Material pickup all need a project, and the projects RLS
  // gives a worker none — those doors only ever answered "Select a project"
  // over an empty picker (stranger sweep 2026-10-17), so they go too.
  const isWorkerHome = window.currentProfile?.role === 'worker';
  if (isWorkerHome) {
    // The whole "forms you use" section goes, label and all — a section header
    // over an empty box reads as a screen that failed to load.
    const quickSec = document.getElementById('dashQuickSec');
    if (quickSec) quickSec.hidden = true;
    WORKER_HIDDEN_DOORS.forEach(a => {
      const btn = document.querySelector(`[data-action="${a}"]`);
      if (btn) btn.hidden = true;
    });
    // Extra tools doors: applyTradePacks() owns the worker rule (it re-runs).
  }

  // Open corrective-actions count on the My Actions card (actions.js). The
  // sidebar badge and the header bell both reuse this SAME resolved count —
  // capture the promise instead of calling refreshMyActionsBadge() a second time.
  const myActionsCountPromise = (typeof refreshMyActionsBadge === 'function')
    ? refreshMyActionsBadge()
    : Promise.resolve(null);

  // Project-first nav (Task 5): the picker wrapper hides once a job context
  // is set — the header above already says which job (dashJobspaceName +
  // "‹ All jobs"). The <select> itself is left alone below: still built,
  // still synced, still readable by every other function in this file that
  // calls dashProjectSelect.value directly (renderProjectPulse,
  // renderTodayForms, startNewFormFlow, startSpecificFormFlow…) — only the
  // visible chrome goes away.
  const projPick = document.getElementById('dashProjectPick');
  // No job at all: the empty picker and everything that needs a job step aside
  // for the start card (renderStartCard).
  const noJobs = _todayNoJobs();
  if (projPick) projPick.hidden = hasJobCtx || noJobs;
  // ...and so do the doors that need a job (a worker never has them, above)
  if (!isWorkerHome) ['documents', 'delivery', 'pickup', 'slip'].forEach(a => {
    const btn = document.querySelector(`[data-action="${a}"]`);
    if (btn) btn.hidden = noJobs;
  });

  // Populate project selector
  const projSel = document.getElementById('dashProjectSelect');
  if (projSel) {
    // Sub-projects: hide any project that has buildings under it — crews
    // pick the building, never the site umbrella.
    const projects = filterFilableProjects(window.projectRows || []).map(r => r.name);
    const prev = projSel.value; // keep previous selection across returns
    projSel.innerHTML = '<option value="">— Select a project to begin —</option>';
    projects.forEach(p => {
      const o = document.createElement('option');
      o.value = p; o.textContent = p;
      projSel.appendChild(o);
    });
    // Restore selection: in-session value first, then the last project this
    // user worked on (localStorage) — survives reloads and next-day opens so
    // the foreman lands one tap closer to today's form. Keyed by user id
    // (stable at both save and restore time — org context loads later than
    // the first dashboard render, so an org-keyed value would split brains).
    const projKeyFor = () => 'ordinex_active_project_' + ((window.currentUser && window.currentUser.id) || 'default');
    // Project-first nav (Task 4): a picked job context always wins over
    // `prev` — without this, opening job B from the list right after job A
    // (Back → list → tap B) would repaint this SAME <select> DOM node, whose
    // .value still reads "Job A" from before the screen was hidden (hiding a
    // screen doesn't reset the form controls inside it), and `prev` would
    // silently keep the board on A's data. openFieldProject() sets both
    // window.fieldProject and state.project before calling here, so this is
    // a no-op (matches `prev`) on every path except exactly that switch.
    let restore = (window.fieldProject && window.fieldProject.name) || prev || state.project || '';
    if (!restore) {
      try {
        restore = localStorage.getItem(projKeyFor())
               || localStorage.getItem('ordinex_active_project_default') || '';
      } catch { /* private mode */ }
    }
    if (restore && projects.includes(restore)) projSel.value = restore;
    // Auto-select if only one project assigned
    if (!projSel.value && projects.length === 1) projSel.value = projects[0];
    projSel.onchange = () => {
      state.project = projSel.value;
      try {
        if (projSel.value) localStorage.setItem(projKeyFor(), projSel.value);
        else localStorage.removeItem(projKeyFor());
      } catch { /* private mode */ }
      // The trade row depends on the project (a tagged project decides on its
      // own and hides the switch), and the form list depends on the trade.
      renderTradeBar().catch(() => {});
      renderTodayForms().catch(() => {});
      renderProjectPulse().catch(() => {});
    };
    state.project = projSel.value;
    renderTradeBar().catch(() => {});
  }

  // Custom org forms (Fase B FB3): inject a dashboard section with one tile
  // per published meta.custom schema. Idempotent — rebuilt on every visit
  //
  // (renderTradeBar lives just below the dashboard renderer — see its comment.)
  // (like the pack-gated grid) so a form published while the app is open
  // shows up on the next return to the dashboard.
  if (!isWorkerHome) renderCustomFormTiles();
  // The crew card is ONE node: the Crew clock page moves it into its own slot, and Today takes it back
  // here, right after the worker's card where it was born, before the surfaces redraw it (ids stay unique).
  const _ccNode = document.getElementById('crewClockCard'); if (_ccNode && !homeScreen?.contains(_ccNode)) document.getElementById('clockCard')?.after(_ccNode);
  _clockRefreshSurfaces();

  // Today shortcuts (due / in progress / most used). Fire-and-forget: it reads
  // history over the network, and a slow read must never hold up the rest of
  // the dashboard. Workers are view-only, so they get nothing to tap.
  if (!isWorkerHome && !noJobs) renderTodayForms().catch(err => console.warn('Today shortcuts failed:', err));
  if (noJobs) ['dashDueSec', 'dashQuickSec'].forEach(id => { const el = document.getElementById(id); if (el) el.hidden = true; });   // the start card took their place (_clockRefreshSurfaces)

  // Wire every form shortcut on the screen — the "forms you use" tiles, the
  // Today rows and the custom-form tiles all carry data-type and all open the
  // same flow. Selector is the attribute, not a class: it survived the class
  // rename once already.
  document.querySelectorAll('#homeScreen [data-type]').forEach(el => {
    el.onclick = () => startSpecificFormFlow(el.dataset.type);
  });

  // Wire data-action cards (delivery, documents, admin)
  document.querySelectorAll('[data-action]').forEach(el => {
    el.onclick = () => handleDashAction(el.dataset.action);
  });

  // Project pulse hero + tailgate streak — foreman/admin only (workers don't
  // submit). The old standalone #dashTailgateAlert banner stays removed from
  // index.html (folded into the tailgate streak banner below instead).
  // Non-blocking: renderProjectPulse() is fire-and-forget, so a slow/offline
  // chart load never delays the rest of the dashboard boot.
  const todayBoard = document.getElementById('dashTodayBoard');
  const newFormBtn = document.getElementById('startNewFormBtn');
  if (newFormBtn) newFormBtn.hidden = !!isWorkerHome || noJobs; // view-only workers don't submit
  if (!isWorkerHome) {
    if (todayBoard) todayBoard.hidden = noJobs;
    renderProjectPulse().catch(err => console.warn('Project pulse render failed:', err));

    // Header bell mirrors the My Actions count — same resolved promise as the
    // sidebar badge (refreshMyActionsBadge), no second request.
    myActionsCountPromise.then(n => {
      const bell = document.getElementById('dashBellCount');
      if (bell) { bell.textContent = String(n ?? 0); bell.hidden = !n; }
    }).catch(() => {});

    // The streak — one query, one place it shows: the STREAK tile.
    loadTailgateStreak().then(info => {
      const kEl = document.getElementById('fdKpiStreak');
      if (kEl) {
        kEl.textContent = String(info?.streak || 0);
        // Singular/plural foot — "1 days on time" read broken.
        const foot = kEl.parentElement?.querySelector('.fd-kpi-foot');
        if (foot) foot.textContent = (info?.streak || 0) === 1 ? 'day on time' : 'days on time';
      }
    }).catch(err => console.warn('streak load failed:', err));
  } else if (todayBoard) {
    todayBoard.hidden = true;
  }

  // Company Resources section (Safety Manual + other shared docs).
  // Background, non-blocking — the rest of the dashboard renders without it.
  loadCompanyResources().catch(err => console.warn('Company resources load failed:', err));

  // Date line under the greeting (model: "Wednesday, Jul 22") — replaced the
  // motivational phrase, which hid the one thing a foreman actually checks.
  const wx = document.getElementById('dashWeather');
  if (wx) {
    wx.textContent = new Date().toLocaleDateString('en-CA', { weekday: 'long', month: 'short', day: 'numeric' });
    wx.hidden = false;
  }

  // Desktop cockpit (OrdinexFieldDesktop): sidebar fill + relocations + KPIs.
  _fdSideFill(isWorkerHome);
  _fdLayout();
  myActionsCountPromise.then(n => {
    const b = document.getElementById('fdSideActionsBadge');
    if (b) { const c = Number(n) || 0; b.hidden = !c; b.textContent = String(c); }
  }).catch(() => {});
  if (!isWorkerHome) _loadFdCockpit().catch(err => console.warn('cockpit load failed:', err));
  _fdSideSync();
}

// The time clock's surfaces and nothing else: the two clock cards (each hides itself off its
// role or with the module off), the Go-to gates and the rail. No network read of its own, so
// the org load, the boot refresh and a wake can redraw them without re-running all of Today.
function _clockRefreshSurfaces() {
  const isWorkerHome = window.currentProfile?.role === 'worker';
  // The Go-to tiles (contract 4.1): a worker reads his own days, a foreman or admin the crew, a company with the
  // clock off sees neither. Both lines set `hidden` both ways, so the module back on shows its tile at the next render.
  const _mt = document.querySelector('[data-action="mytime"]'); if (_mt) _mt.hidden = !isWorkerHome || !_clockEnabled();
  const _cc = document.querySelector('[data-action="crewclock"]'); if (_cc) _cc.hidden = isWorkerHome || !_clockEnabled();
  // Time off: every role asks (a worker for himself, staff for their crew), only the module decides.
  const _to = document.querySelector('[data-action="timeoff"]'); if (_to) _to.hidden = !_clockEnabled();
  renderClockCard();
  renderCrewClockCard();
  // the time off waiting for this foreman or admin (it hides itself with the module off)
  if (!isWorkerHome) renderTimeOffPendingRow().catch(err => console.warn('time-off row failed:', err));
  renderStartCard();   // a worker's "not on a project yet" needs the clock known; the admin's step 1 the org
  _fdSideFill(isWorkerHome);
}

// ── Desktop sidebar + cockpit (OrdinexFieldDesktop.dc.html, ≥1024px) ─────────
// The sidebar exposes the SAME actions the dashboard cards already have —
// no new capabilities, just a fixed home for them on big screens.
function _fdSideActive(key) {
  document.querySelectorAll('.fd-side-btn').forEach(b =>
    b.classList.toggle('active', b.dataset.side === key));
}

// Reads what's actually on screen — never guesses. Called after navigations.
function _fdSideSync() {
  const vis = id => {
    const el = document.getElementById(id);
    return !!el && el.style.display !== 'none';
  };
  // Job list (showFieldJobListScreen, project-first nav Task 4) is the "pick
  // a job" screen — it isn't Today's own dashboard and owns none of the
  // sidebar's other actions either, so it used to fall through every check
  // below to the 'today' default and light up "Today" while a completely
  // different screen showed. Checked first and returns early: no sidebar
  // item reads as current here, same as no page being open at all.
  if (vis('fieldJobListScreen')) { _fdSideActive(null); return; }
  let key = 'today';
  if (vis('mySubsPage'))              key = 'mysubmissions';
  else if (vis('myActionsPage'))      key = 'myactions';
  else if (vis('equipmentPage'))      key = 'equipment';
  else if (vis('certificationsPage')) key = 'certifications';
  else if (vis('myTimePage'))         key = 'mytime';
  else if (vis('crewClockPage'))      key = 'crewclock';
  else if (vis('timeOffPage'))        key = 'timeoff';
  else if (vis('docsPage'))           key = 'documents';
  // One page, three doors: the rail lights the one that matches the mode (the mode names ARE the door names).
  else if (vis('deliveryPage'))       key = typeof _delivMode !== 'undefined' ? _delivMode : 'delivery';
  else {
    const app = document.querySelector('#mainApp .app');
    if (app && app.style.display !== 'none' && app.offsetParent !== null) key = 'newform';
  }
  _fdSideActive(key);
}

function _fdSideGo(key) {
  _fdSideMenu(false);
  // "Today" means home — the job's board if one's picked, the list
  // otherwise (project-first nav, Task 4). "New Form" needs a picked project
  // (its own guard already toasts "select a project first"); the desktop
  // sidebar is always visible, even while the list is showing with no job
  // picked yet, so route it home instead of letting it toast at a hidden
  // selector that was never populated.
  if (key === 'today') {
    _fieldHome();
  } else if (key === 'newform') {
    if (window.fieldProject && window.fieldProject.id) startNewFormFlow();
    else _fieldHome();
  } else {
    handleDashAction(key);
  }
  _fdSideSync();
}

function _fdSideMenu(force) {
  const menu = document.getElementById('fdSideMenu');
  const btn  = document.getElementById('fdSideUserBtn');
  if (!menu) return;
  const show = force !== undefined ? !!force : menu.hidden;
  menu.hidden = !show;
  btn?.setAttribute('aria-expanded', show ? 'true' : 'false');
  const adminItem = document.getElementById('fdSideAdminBtn');
  if (adminItem) adminItem.style.display = (window.currentProfile?.role === 'admin') ? '' : 'none';
}

// The sidebar's own Admin Panel item (outside the foot menu) follows the same
// role gate; called from _fdSideFill so it settles as soon as the profile does.
function _fdSideAdminNavGate() {
  const isAdmin = window.currentProfile?.role === 'admin';
  const navItem = document.getElementById('fdSideAdminNav');
  if (navItem) navItem.style.display = isAdmin ? '' : 'none';
  // Job-list screen's header gear (phone width — the rail doesn't exist there).
  const listBtn = document.getElementById('fieldListAdminBtn');
  if (listBtn) listBtn.style.display = isAdmin ? '' : 'none';
}

// Doors a view-only worker cannot use — hidden on the Today grid (data-action)
// AND the desktop rail (data-side), which share these names. One list, so the
// two never disagree again. A worker has no Crew clock (he reads his own days in My time).
const WORKER_HIDDEN_DOORS = ['equipment', 'mysubmissions', 'documents', 'delivery', 'pickup', 'slip', 'crewclock'];

function _fdSideFill(isWorkerHome) {
  _fdSideAdminNavGate();
  const name = window.currentProfile?.full_name || '—';
  const role = window.currentProfile?.role || '';
  const initials = name.trim().split(/\s+/).map(w => w[0] || '').join('').slice(0, 2).toUpperCase() || '–';
  const av = document.getElementById('fdSideAvatar');
  if (av) av.textContent = initials;
  const n = document.getElementById('fdSideName');
  if (n) n.textContent = name;
  const r = document.getElementById('fdSideRole');
  if (r) r.textContent = role ? role[0].toUpperCase() + role.slice(1) : '';
  // View-only workers: same surfaces the dashboard hides for them
  const workerHide = ['newform', ...WORKER_HIDDEN_DOORS];
  // Staff read their own day through the crew card's (you) row: My time is the worker's door. And a
  // company with the time clock off has neither door.
  const staffHide = ['mytime'];
  document.querySelectorAll('.fd-side-btn').forEach(b => {
    if (b.dataset.pack) return;   // pack-gated (Extra tools): applyTradePacks owns it, worker rule included
    const k = b.dataset.side;
    b.hidden = (!!isWorkerHome && workerHide.includes(k)) || (!isWorkerHome && staffHide.includes(k)) || (['mytime','crewclock','timeoff'].includes(k) && !_clockEnabled());
  });
}

// ≥1024px the notification bell moves into the cockpit header (model); under
// it it returns to its mobile home. The project bar used to relocate too —
// it now stays put as the pulse panel's header at every width.
let _fdHomes = null;
function _fdLayout() {
  const bell  = document.getElementById('dashBellBtn');
  const right = document.getElementById('dashHelloRight');
  if (!right) return;
  if (!_fdHomes) {
    _fdHomes = bell ? { parent: bell.parentNode, next: bell.nextSibling } : null;
  }
  if (window.matchMedia('(min-width: 1024px)').matches) {
    if (bell) right.appendChild(bell);
  } else if (bell && _fdHomes) {
    _fdHomes.parent.insertBefore(bell, _fdHomes.next);
  }
  if (!window._fdMqWired) {
    window._fdMqWired = true;
    window.matchMedia('(min-width: 1024px)').addEventListener('change', _fdLayout);
  }
}

// KPI tiles + recent activity — the foreman's own numbers, nothing invented.
// Redesign v3: no width gate any more. These two numbers and the activity list
// were desktop-only chrome; the field mock puts both on the phone, which is
// where "did that tailgate actually send?" gets asked.
async function _loadFdCockpit() {
  const uid = window.currentUser?.id;
  if (!uid) return;
  const kpis = document.getElementById('fdKpis');

  // "This week" = own submissions since Monday (local)
  try {
    const now = new Date();
    const monday = new Date(now);
    monday.setDate(now.getDate() - ((now.getDay() + 6) % 7));
    monday.setHours(0, 0, 0, 0);
    const { count, error } = await sbClient.from('submissions')
      .select('id', { count: 'exact', head: true })
      .eq('foreman_id', uid)
      .gte('submitted_at', monday.toISOString());
    if (!error) {
      const el = document.getElementById('fdKpiWeek');
      if (el) el.textContent = String(count ?? 0);
      if (kpis) kpis.hidden = false;
    }
  } catch (_) { /* KPI stays hidden — never fake a number */ }

  // Recent activity — last 4 own submissions
  try {
    const { data, error } = await sbClient.from('submissions')
      .select('submission_type, project_name, submitted_at')
      .eq('foreman_id', uid)
      .order('submitted_at', { ascending: false })
      .limit(4);
    if (error || !data || !data.length) return;
    const list = document.getElementById('fdRecent');
    const lbl  = document.getElementById('fdRecentLabel');
    if (!list) return;
    // Today's rows read as a time, older ones as a day — the same shorthand
    // the mock uses ("6:42a" then "Yest"), because on the day itself the hour
    // is the useful fact and a week later it is not.
    const todayKey = _todayLocalDayKey(new Date());
    const yesterdayKey = _todayLocalDayKey(new Date(Date.now() - 864e5));
    const stamp = (iso) => {
      if (!iso) return '—';
      const k = _todayLocalDayKey(iso);
      if (k === todayKey) return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
      if (k === yesterdayKey) return 'Yesterday';
      return new Date(iso).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
    };
    list.innerHTML = data.map(row => `
      <div class="fx-act">
        <span class="fx-act-ic" aria-hidden="true"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg></span>
        <span class="fx-act-txt">${_fEsc(row.submission_type)}${row.project_name && row.project_name !== window.fieldProject?.name ? ' — ' + _fEsc(row.project_name) : ''}</span>
        <span class="fx-act-when">${_fEsc(stamp(row.submitted_at))}</span>
      </div>`).join('');
    list.hidden = false;
    if (lbl) lbl.hidden = false;
    window.lucide && lucide.createIcons();
  } catch (_) { /* section stays hidden */ }
}

// ── Company Resources (Safety Manual + future shared docs) ────────────────────
// One card per active row in company_documents. Click opens the PDF in a new
// tab (the bucket is public so no signed-URL round trip needed). Rows without
// a file_url yet (admin hasn't uploaded the file) render as disabled.
async function loadCompanyResources() {
  const sec   = document.getElementById('dashResourcesSection');
  const label = document.getElementById('dashResourcesLabel');
  if (!sec) return;

  const { data, error } = await sbClient
    .from('company_documents')
    .select('id, slug, title, description, icon, file_url, file_path, version')
    .eq('active', true)
    .order('sort_order', { ascending: true });

  if (error) { console.warn('company_documents load failed:', error.message); return; }
  const docs = data || [];
  if (!docs.length) { sec.hidden = true; if (label) label.hidden = true; return; }

  sec.innerHTML = docs.map(d => {
    const hasFile = !!(d.file_url || d.file_path);
    const ready = hasFile ? '' : ' style="opacity:.55;cursor:not-allowed"';
    const desc  = hasFile
      ? (d.description || 'Tap to view')
      : 'Not yet uploaded by admin';
    // title/description/icon are admin-authored (Company Resources tab) —
    // escape the same way the admin's own list (55-company-resources.js)
    // already does, instead of the old <-only partial replace.
    // No chevron on placeholder rows — the arrow promised a tap that went
    // nowhere (gauntlet 2026-08-08). disabled also drops it from tab order.
    return `
      <button class="fx-row" data-doc-id="${d.id}"${ready}${hasFile ? '' : ' disabled'}>
        <span class="fx-row-ic" aria-hidden="true">${_fEsc(d.icon) || '📘'}</span>
        <span class="fx-row-txt">
          <span class="fx-row-name">${_fEsc(d.title)}</span>
          <span class="fx-row-sub">${_fEsc(desc)}</span>
        </span>
        ${hasFile ? '<span class="fx-row-chev" aria-hidden="true"><i data-lucide="chevron-right"></i></span>' : ''}
      </button>`;
  }).join('');

  sec.hidden = false;
  if (label) label.hidden = false;

  sec.querySelectorAll('button[data-doc-id]').forEach(btn => {
    btn.onclick = async () => {
      const id = btn.dataset.docId;
      const doc = docs.find(d => d.id === id);
      if (!doc) return;
      // Private bucket: derive a fresh signed URL from the path (stored file_url
      // eventually expires); fall back to the cached URL only if there's no path.
      if (doc.file_path) {
        const { data: signed } = await sbClient.storage.from('company-docs').createSignedUrl(doc.file_path, 3600);
        if (signed?.signedUrl) { window.open(signed.signedUrl, '_blank', 'noopener'); return; }
      }
      const safe = _fSafeUrl(doc.file_url); if (safe) { window.open(safe, '_blank', 'noopener'); return; }
      showToast('This document has not been uploaded yet — please contact admin.', 'warning');
    };
  });
}

// Check if a Daily Tailgate was submitted today for this user
// Count consecutive days the foreman has submitted a Daily Tailgate.
// Looks back up to 90 days. If today hasn't been done yet, doesn't penalize —
// starts counting from yesterday backward. Returns { streak, lastDateStr }.
async function loadTailgateStreak() {
  const uid = window.currentUser?.id;
  if (!uid) return { streak: 0 };

  const since = new Date();
  since.setDate(since.getDate() - 90);
  const { data, error } = await sbClient.from('submissions')
    .select('submitted_at')
    .eq('foreman_id', uid)
    .eq('submission_type', 'Daily Tailgate')
    .gte('submitted_at', since.toISOString())
    .order('submitted_at', { ascending: false });

  if (error) { console.warn('streak query:', error.message); return { streak: 0 }; }
  if (!data || data.length === 0) return { streak: 0 };

  // Collect distinct LOCAL dates the foreman submitted on
  const localDate = (iso) => {
    const d = new Date(iso);
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
  };
  const dates = new Set(data.map(r => localDate(r.submitted_at)));

  let streak = 0;
  let cursor = new Date();
  const todayStr = _todayLocal();

  // If today not done yet, start counting from yesterday (no penalty mid-day)
  if (!dates.has(todayStr)) cursor.setDate(cursor.getDate() - 1);

  for (let i = 0; i < 90; i++) {
    const dStr = cursor.getFullYear() + '-' + String(cursor.getMonth() + 1).padStart(2, '0') + '-' + String(cursor.getDate()).padStart(2, '0');
    if (dates.has(dStr)) {
      streak++;
      cursor.setDate(cursor.getDate() - 1);
    } else {
      break;
    }
  }

  return { streak, lastDateStr: localDate(data[0].submitted_at), todayDone: dates.has(todayStr) };
}

// The streak banner is gone (owner call, 2026-08-13): the STREAK tile on Today
// already carries the same number, and a banner repeating it one line lower was
// the same fact twice. loadTailgateStreak() stays — it is what fills that tile.

// ── Project pulse hero ───────────────────────────────────────────────────────
// Production placed per week for the selected project (8-week SVG line),
// read from the relational production_reports table — per-day rows that
// syncProductionReportToDb writes on every Production Report submission and
// that the TM history import filled, so the chart carries real history from
// day one. (The submissions.data JSON was the wrong source: imported rows
// have an empty payload, and Renan cut the forms-count fallback — a project
// with no production shows a plain message, not a filler metric.)
// RLS scopes the rows: a foreman charts what THEY reported on this project;
// an org admin charts the whole project. Acceptable v1 nuance — usually one
// foreman files production per job; a project-wide RPC can lift it later.
let _pulseToken = 0;
async function renderProjectPulse() {
  const body = document.getElementById('pulseBody');
  if (!body || !window.currentUser) return;

  const project = document.getElementById('dashProjectSelect')?.value || '';
  const token = ++_pulseToken;                    // stale-response guard
  if (!project) {
    _setPulseHead('Project pulse', '', null);
    body.innerHTML = '<div class="fh-pulse-empty">Select a project above to see its pulse.</div>';
    return;
  }
  // The project's name is NOT repeated here — it's already the selector text
  // directly above (the panel header). The head carries the metric instead.
  _setPulseHead('Project pulse', '', null);
  body.innerHTML = '<span class="dash-skel" style="width:100%;height:150px"></span>';

  const buckets = window.Ordinex.pulse.weekBuckets(_todayLocal(), 8);
  try {
    // Selector carries the project NAME; production_reports keys on id.
    const { data: proj, error: projErr } = await sbClient.from('projects')
      .select('id').eq('name', project).maybeSingle();
    if (projErr) throw projErr;

    let rows = [];
    if (proj) {
      // Per-material detail (production_line_items); the parent join carries
      // the date and scopes by project. RLS on the line items already walks
      // the same parent (foreman-own or admin), so no extra filter here.
      const prod = await sbClient.from('production_line_items')
        .select('quantity, production_reports!inner(report_date, project_id), material_types(name, unit)')
        .eq('production_reports.project_id', proj.id)
        .gte('production_reports.report_date', buckets[0]);
      if (prod.error) throw prod.error;
      rows = (prod.data || []).map(r => ({
        report_date: r.production_reports?.report_date,
        material:    r.material_types?.name || 'Other',
        unit:        r.material_types?.unit || '',
        quantity:    r.quantity,
      }));
    }
    if (token !== _pulseToken) return;            // user switched projects mid-flight
    _renderPulseChart(body, window.Ordinex.pulse.buildMaterialPulse(rows, buckets));
  } catch (e) {
    console.warn('project pulse:', e?.message || e);
    if (token === _pulseToken) {
      _setPulseHead('Project pulse', '', null);
      body.innerHTML = '<div class="fh-pulse-empty">Couldn&#39;t load — check your connection.</div>';
    }
  }
}

// Head = the brand KPI grammar (uppercase Inter label left; right side is
// the legend — one chip per material with its 8-week total, doubling as the
// KPI block). Chips are built with DOM calls, not innerHTML: material names
// are tenant data. `right` = array of {name, unit, value, colorIdx} or null
// to clear.
function _setPulseHead(kicker, subText, right) {
  const k = document.getElementById('pulseKicker');
  const s = document.getElementById('pulseSub');
  const n = document.getElementById('pulseNow');
  if (k) k.textContent = kicker;
  if (s) { s.textContent = subText; s.hidden = !subText; }
  if (!n) return;
  n.textContent = '';
  n.hidden = !right || !right.length;
  (right || []).forEach(item => {
    const chip = document.createElement('div');
    chip.className = 'fh-pulse-chip';
    const sw = document.createElement('span');
    sw.className = 'fh-pulse-swatch fh-pulse-swatch--s' + item.colorIdx;
    const name = document.createElement('span');
    name.className = 'fh-pulse-chip-name';
    name.textContent = item.name;
    const val = document.createElement('span');
    val.className = 'fh-pulse-chip-val';
    val.textContent = item.value + (item.unit && item.unit !== 'count' ? ' ' + item.unit.toUpperCase() : '');
    chip.append(sw, name, val);
    n.appendChild(chip);
  });
  // Says what the chip numbers are — without it a bare "41.2k" beside a
  // material could be read as this week's placement.
  if (right && right.length) {
    const cap = document.createElement('div');
    cap.className = 'fh-pulse-legend-cap';
    cap.textContent = '8-week total';
    n.appendChild(cap);
  }
}

// Hand-rolled SVG line chart — no chart lib, nothing to cache for offline.
// The drawing itself lives in js/core/pulse-chart.js (window.Ordinex.pulse):
// the admin Today board renders the same chart summed across every site, and
// one shared builder is the only way those two can't drift apart.
let _pulseLast = null;
function _renderPulseChart(body, pulse) {
  _pulseLast = pulse;
  if (pulse.mode === 'empty') {
    _setPulseHead('Production', '', null);
    body.innerHTML = '<div class="fh-pulse-empty">No production reported in the last 8 weeks — the chart fills in from Production Reports.</div>';
    return;
  }
  _setPulseHead('Production per week', 'Last 8 weeks', window.Ordinex.pulse.legend(pulse));
  body.innerHTML = window.Ordinex.pulse.chartSvg(pulse, body.clientWidth);
  _wirePulseResize();
}

// Pixel-unit charts need a redraw when the panel's width changes.
let _pulseResizeWired = false;
function _wirePulseResize() {
  if (_pulseResizeWired) return;
  _pulseResizeWired = true;
  let t;
  window.addEventListener('resize', () => {
    clearTimeout(t);
    t = setTimeout(() => {
      const body = document.getElementById('pulseBody');
      if (body && _pulseLast && body.querySelector('.fh-pulse-svg')) _renderPulseChart(body, _pulseLast);
    }, 150);
  });
}

// ── Form-type icons — Lucide, mirroring the home dashboard tiles ─────────────
// (form-configs.js still carries the legacy emoji as a fallback for any type
// missing here; the MutationObserver in index.html converts data-lucide tags.)
const FORM_TYPE_LUCIDE = {
  'Daily Tailgate':           'shield-check',
  'Weekly Toolbox Talk':      'megaphone',
  'Incident Report':          'triangle-alert',
  'Telehandler Inspection':   'construction',
  'Forklift Inspection':      'package',
  'E-Pallet Jack Inspection': 'battery-charging',
  'Scaffolding Inspection':   'layers',
  'Safety Inspection':        'search-check',
  'QAQC - Foreman':           'clipboard-check',
  'Site Photos Only':         'camera',
  'Weekly Timesheet':         'clock',
  'Production Report':        'bar-chart-3',
  'Extra Work Order':         'hammer',
  'Hazard Observation':       'eye',
  // Drilling & Shotcrete pack — same stroke set as everything above, so a
  // pack form never reads as a second-class citizen next to a core one.
  'Drill Rig Daily Log':          'drill',
  'Air Compressor Daily Log':     'wind',
  'Concrete Pump Daily Log':      'waves',
  'Dry-Mix Pump Daily Log':       'cog',
  'Grout Plant Daily Log':        'container',
  'Truck Monthly Inspection':     'truck',
  'Equipment Maintenance Record': 'wrench',
  'Incident Investigation':       'file-search',
  'First Aid Assessment':         'bandage',
  'Disciplinary Report':          'gavel',
  'Site Orientation Record':      'compass',
  'Daily Site Report':            'notebook-pen',
  'Anchor Installation Log':      'anchor',
  'Anchor Test Record':           'gauge',
  'Shotcrete Placement Record':   'spray-can',
  'Shotcrete Test Panel Log':     'flask-conical',
  'Grout Batch Log':              'receipt',
  // Neutral (cross-trade: hot work, heavy equipment, lifts)
  'Hot Work & Fire Watch Record': 'flame',
  'Heavy Equipment Daily Log':    'tractor',
  'MEWP Daily Log':               'move-vertical',
  // Demolition & Environmental pack
  'Pre-Demolition Readiness Check':  'building-2',
  'Abatement Containment Daily Log': 'biohazard',
  // Mechanical pack
  'Pressure Test Record':             'gauge',
  'Refrigerant Service & Leak Log':   'snowflake',
  // Excavation & Sitework pack
  'Excavation Daily Inspection':  'shovel',
};
function formTypeIconHtml(typeId) {
  const name = FORM_TYPE_LUCIDE[typeId];
  return name ? `<span class="ip-ic"><i data-lucide="${name}"></i></span>` : '';
}

// Everything that belongs to ONE filled-out form. Both entry points call this,
// so opening a second form can never inherit the first one's answers, its
// signature or — the one that silently cost records — its idempotency key.
// `submissions.client_uuid` is UNIQUE: reusing it made every submission after
// the first of a session fail its insert, leaving the foreman with a PDF and
// no row in the log.
function _resetFormEntryState() {
  state.fields        = {};
  state.signature     = null;
  state.gcSignature   = null;
  state.photos        = [];
  state._clientUuid   = null;
  state._pendingDraft = null;
  state._draftSavedAt = null;
  // The week sheet's answer is per FILING, never per session. Left behind, a
  // "Replace the week" tapped on one form skipped the question on the next one
  // and deleted a week that belonged to somebody else.
  state._weekMode     = null;
  state._prodOutlierOk = null;   // "It's right" on unusual production days: per filing too
  // The clock prefill's read, the workers removed from the sheet with ×, and the once-per-entry
  // re-check mark (W4): per filing too.
  state._clock = null; state._clockDismissed = null; state._clockRechecked = null;
  // Same for "whose production is this?": answered per filing, never inherited
  // from the form before. loadDraftData restores it right after, when the
  // draft being resumed carries one.
  state.productionCrewPicked = false;
  state.productionCrewId     = null;
  state.productionCrewName   = '';
  window._signaturePad   = null;
  window._gcSignaturePad = null;
  // Entering a form: an inner page left open (Delivery/Pickup, Docs…) would
  // stay rendered under the wizard — the desktop rail's "New Form" is reachable
  // while one is showing. Same for the install/push offer cards: created on the
  // home screen, they would otherwise sit fixed over the Next bar for the whole
  // fill (they come back on the next boot if still undecided).
  document.querySelectorAll('.inner-page').forEach(p => p.style.display = 'none');
  ['pushCard', 'installCard'].forEach(id => document.getElementById(id)?.remove());
}

// ── Read-only plan (expired trial, no subscription) ──────────────────────────
// The server refuses every new filing then (current_plan_ok() in RLS). Said the
// moment a NEW form opens, so nobody collects a crew's signatures for a form
// that cannot be filed. Drafts and filed forms still open to read. The claim
// comes from the token on this device (auth.js orgPlanReadOnly), so it holds
// offline too.
function _planBlocked() {
  const plan = typeof orgPlanReadOnly === 'function' ? orgPlanReadOnly() : null;
  if (!plan) return false;
  const admin = window.currentProfile?.role === 'admin';
  const key = (plan === 'trial_expired' ? 'plan.sheetTrial' : 'plan.sheetInactive') + (admin ? 'Admin' : '');
  const $ = id => document.getElementById(id);
  if (!$('planSheet')) { showToast(window.Ordinex.i18n.t(key), 'warning'); return true; }
  $('planSheetMsg').textContent = window.Ordinex.i18n.t(key);
  $('planSheetBilling').hidden = !admin;
  $('planSheetOk').hidden = admin;
  $('planSheet').style.display = 'flex';
  $('planSheet').querySelector('.delivery-modal')?.focus();
  return true;
}
function closePlanSheet() { const s = document.getElementById('planSheet'); if (s) s.style.display = 'none'; }
function openHelpSheet() {
  const s = document.getElementById('helpSheet');
  if (!s) return;
  s.style.display = 'flex';
  s.querySelector('.delivery-modal')?.focus();
  if (window.lucide?.createIcons) window.lucide.createIcons();
}
function closeHelpSheet() { const s = document.getElementById('helpSheet'); if (s) s.style.display = 'none'; }

// ── Start form flow for a specific pre-selected type ──────────────────────────
// Project + type are already known from the dashboard — jump straight to the form.
async function startSpecificFormFlow(type) {
  // Defense in depth for view-only worker accounts — the tiles are hidden,
  // and the RLS role gate would reject the insert anyway.
  if (window.currentProfile?.role === 'worker') {
    showToast('Your account is view-only — ask your foreman or admin to submit forms.', 'warning');
    return;
  }
  const projSel = document.getElementById('dashProjectSelect');
  const project = projSel?.value || '';

  if (!project) {
    showToast('Please select a project first ↑', 'warning');
    if (projSel) {
      projSel.focus();
      projSel.classList.add('dash-project-bar-select--pulse');
      setTimeout(() => projSel.classList.remove('dash-project-bar-select--pulse'), 900);
    }
    return;
  }
  if (!DRAFTABLE_FORMS.includes(type) && _planBlocked()) return;

  _resetFormEntryState();
  state.project        = project;
  state.submissionType = type;
  state.allowedTypes   = [type];
  state.fromDashboard  = true;

  // Pre-load draft for draftable forms before rendering
  if (DRAFTABLE_FORMS.includes(type)) {
    const draft = await loadDraftData(type);
    if (draft) {
      state._pendingDraft = draft.form_data;
      state._draftSavedAt = draft.updated_at;
    }
  }
  // Read-only plan: a draft still opens to read (Review & sign is gated), a new form does not.
  if (!state._pendingDraft && _planBlocked()) return;

  const typeGrid = document.getElementById('typeGrid');
  if (typeGrid) typeGrid.innerHTML = '';

  const homeScreen   = document.getElementById('homeScreen');
  const appDiv       = document.querySelector('#mainApp .app');
  const progressWrap = document.querySelector('.progress-wrap');
  const navBar       = document.querySelector('.nav-bar');

  if (homeScreen)   homeScreen.style.display  = 'none';
  if (appDiv)       appDiv.style.display      = '';
  if (progressWrap) progressWrap.style.display = '';
  if (navBar)       navBar.style.display      = '';

  _armFlowBack();
  const resumed = !!state._pendingDraft;   // renderStep(3) consumes it
  state.currentStep = 3;
  renderStep(3);
  updateProgressBar(3);
  updateNavButtons(3);
  window.scrollTo({ top: 0, behavior: _prefersReducedMotion() ? 'auto' : 'smooth' });
  // A draft under a read-only plan opens to read; the sheet says so before anyone signs on it.
  if (resumed) _planBlocked();
}

// ── Generic "Start a new form" (redesign v2 CTA) — opens the palette (step 2)
// with every visible type; the rest of the wizard flow is untouched.
function startNewFormFlow() {
  if (window.currentProfile?.role === 'worker') {
    showToast('Your account is view-only — ask your foreman or admin to submit forms.', 'warning');
    return;
  }
  const projSel = document.getElementById('dashProjectSelect');
  const project = projSel?.value || '';
  if (!project) {
    showToast('Please select a project first ↑', 'warning');
    if (projSel) {
      projSel.focus();
      projSel.classList.add('dash-project-bar-select--pulse');
      setTimeout(() => projSel.classList.remove('dash-project-bar-select--pulse'), 900);
    }
    return;
  }
  if (_planBlocked()) return;

  _resetFormEntryState();
  state.project        = project;
  state.submissionType = null;
  state.allowedTypes   = null;   // full palette
  state.fromDashboard  = true;

  const homeScreen   = document.getElementById('homeScreen');
  const appDiv       = document.querySelector('#mainApp .app');
  const progressWrap = document.querySelector('.progress-wrap');
  const navBar       = document.querySelector('.nav-bar');
  if (homeScreen)   homeScreen.style.display   = 'none';
  if (appDiv)       appDiv.style.display       = '';
  if (progressWrap) progressWrap.style.display = '';
  if (navBar)       navBar.style.display       = '';

  _armFlowBack();
  state.currentStep = 2;
  renderStep(2);
  updateProgressBar(2);
  updateNavButtons(2);
  window.scrollTo({ top: 0, behavior: _prefersReducedMotion() ? 'auto' : 'smooth' });
  if (typeof _fdSideSync === 'function') _fdSideSync();
}

// ── Theme toggle (light-theme phase, Task 1) — sun/moon button in the field
// top bar. Both dash-header instances (job list + Today) carry their own
// button (same .dash-theme-toggle class, different ids) since both screens
// can be the first thing a device shows; this syncs whichever is in the DOM.
// Ordinex.theme already stamped data-theme on <html> before this script ran
// (the inline <head> script, then the boot-sync call right after
// core.min.js loads — see index.html) — this only keeps the button's own
// icon/label in sync with whatever's currently live, and flips it.
function _syncThemeToggleUI() {
  var dark = document.documentElement.getAttribute('data-theme') === 'dark';
  var label = (window.Ordinex && window.Ordinex.i18n)
    ? window.Ordinex.i18n.t(dark ? 'common.themeToLight' : 'common.themeToDark')
    : (dark ? 'Switch to light theme' : 'Switch to dark theme');
  document.querySelectorAll('.dash-theme-toggle').forEach(function (btn) {
    btn.innerHTML = '<i data-lucide="' + (dark ? 'sun' : 'moon') + '"></i>';
    btn.title = label;
    btn.setAttribute('aria-label', label);
  });
  if (window.lucide && lucide.createIcons) lucide.createIcons();
}
function toggleFieldTheme() {
  window.Ordinex.theme.toggleTheme();
  _syncThemeToggleUI();
}
_syncThemeToggleUI();

function handleDashAction(action) {
  if ((action === 'delivery' || action === 'pickup' || action === 'slip') && _planBlocked()) return;   // a request is a filing too
  switch (action) {
    case 'delivery':       return openDeliveryModal();
    case 'pickup':         return openDeliveryModal('pickup');
    case 'slip':           return openDeliveryModal('slip');
    case 'equipment':      return openEquipmentOverlay();
    case 'documents':      return openDocsPage();
    case 'admin':          return openAdminPanel();
    case 'certifications': return openCertificationsPage();
    case 'myactions':      return openMyActionsPage();
    case 'mysubmissions':  return openMySubmissionsPage();
    case 'mytime':         return openMyTimePage();
    case 'crewclock':      return openCrewClockPage();
    case 'timeoff':        return openTimeOffPage();
  }
}

// ── Inner-page navigation ─────────────────────────────────────────────────────
// Hides home + form screens, shows the requested full-page div.
function showInnerPage(pageId) {
  // Wrong-job draft reassignment fix — see showFieldJobListScreen()'s comment.
  // This is the one of the 3 that matters most for the desktop sidebar: on
  // wide screens the sidebar is reachable even while the wizard is open, and
  // its non-"Today" items (My Submissions, My Actions, Equipment,
  // Certifications) route straight here via handleDashAction(), bypassing
  // _fieldHome() entirely — so the stop has to live here too, not just in
  // showHomeDashboard/showFieldJobListScreen.
  _stopAutoSaveDraft();
  const homeScreen   = document.getElementById('homeScreen');
  const listScreen   = document.getElementById('fieldJobListScreen');
  const appDiv       = document.querySelector('#mainApp .app');
  const progressWrap = document.querySelector('.progress-wrap');
  const navBar       = document.querySelector('.nav-bar');
  if (homeScreen)   homeScreen.style.display   = 'none';
  if (listScreen)   listScreen.style.display   = 'none';   // project-first nav (Task 4) — the sidebar can reach an inner page (e.g. My Submissions) while the list is showing, before any job is picked
  if (appDiv)       appDiv.style.display       = 'none';
  if (progressWrap) progressWrap.style.display = 'none';
  if (navBar)       navBar.style.display       = 'none';
  document.querySelectorAll('.inner-page').forEach(p => p.style.display = 'none');
  const page = document.getElementById(pageId);
  if (page) { page.style.display = 'flex'; }
  window.scrollTo({ top: 0, behavior: 'instant' });
  if (typeof _fdSideSync === 'function') _fdSideSync();
}

function goHome() {
  document.querySelectorAll('.inner-page').forEach(p => p.style.display = 'none');
  _fieldHome();   // the job's board if one's picked, the list otherwise (Task 4)
}

// ── Project Documents full page ───────────────────────────────────────────────
async function openDocsPage() {
  const project = state.project;
  if (!project) {
    showToast('Please select a project first ↑', 'warning');
    const projSel = document.getElementById('dashProjectSelect');
    if (projSel) {
      projSel.focus();
      projSel.classList.add('dash-project-bar-select--pulse');
      setTimeout(() => projSel.classList.remove('dash-project-bar-select--pulse'), 900);
    }
    return;
  }

  showInnerPage('docsPage');
  const projEl = document.getElementById('docsPageProject');
  if (projEl) projEl.textContent = project;
  // Also hide the form strip (not needed on inner pages)
  const formNav = document.getElementById('formPageNav');
  if (formNav) formNav.style.display = 'none';

  const listEl = document.getElementById('docsPageList');
  if (listEl) listEl.innerHTML = '<div class="docs-loading">Loading…</div>';

  const docs = await loadProjectDocuments(project);
  if (!listEl) return;

  if (!docs.length) {
    listEl.innerHTML = '<div class="docs-empty">No documents uploaded for this project yet.</div>';
    return;
  }

  // Two things happen here that did not before.
  //
  // 1. The URLs are SIGNED. project-documents is a private bucket and 164 of
  //    the 166 rows in production have an empty `file_url` — only the storage
  //    path in `file_name` was ever written. This page rendered that empty
  //    string straight into <img src="">, so every photo was a blank tile and
  //    every row opened nothing. The admin tab has signed per view since
  //    2026-08-14; this one never did. Same signer now (js/core/doc-urls.js).
  //
  // 2. The photos are held back. They are the bulk of the set and each tile's
  //    src is the ORIGINAL file — 3.6 MB on average. On a phone on site that
  //    is the difference between a page and a download. Documents paint at
  //    once; photos wait behind the folder at the bottom.
  const { photos, files } = window.Ordinex.docUrls.splitPhotos(docs);
  _docsPagePhotos = photos;
  _docsPhotosOpen = false;
  await window.Ordinex.docUrls.signDocumentRows(sbClient, files);

  // Lucide, not emoji: the emoji rendered as a different sticker on every
  // device and sat next to the app's own stroke icons. Same swap Equipment
  // made for its category headers.
  const SECTIONS = [
    { key: 'change_order',  label: 'Change Orders',  icon: 'clipboard-list', isPhotos: false },
    { key: 'rfi',           label: 'RFIs',           icon: 'help-circle',   isPhotos: false },
    { key: 'drawing',       label: 'Drawings',       icon: 'ruler',         isPhotos: false },
    { key: 'submittal',     label: 'Submittals',     icon: 'send',          isPhotos: false },
    { key: 'specification', label: 'Specifications', icon: 'file-text',     isPhotos: false },
    { key: 'general',       label: 'General',        icon: 'file',          isPhotos: false },
  ];

  const byType = {};
  for (const d of docs) {
    const k = d.type || 'general';
    if (!byType[k]) byType[k] = [];
    byType[k].push(d);
  }

  const s = str => String(str ?? '').replace(/[&<>"']/g, c =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  // Route the lightbox open through the doc's id instead of splicing
  // file_url/title straight into an onclick="" string. Any foreman assigned
  // to the project can insert a `documents` row (RLS `member insert`), so
  // title/file_url are attacker-reachable text — embedding them raw inside a
  // quoted JS-string-inside-an-HTML-attribute is exactly the shape that lets
  // a single `'` or `"` break out and inject a new attribute/handler. Only
  // the doc's UUID (safe) goes into the attribute; the real values are read
  // back from this cache when the button is clicked.
  _docsPageCache = docs;

  let html = '';
  for (const sec of SECTIONS) {
    const items = byType[sec.key];
    if (!items?.length) continue;

    html += `<div class="fdoc-section">
      <div class="fdoc-section-hdr">
        <span class="fdoc-section-ic" aria-hidden="true"><i data-lucide="${sec.icon}"></i></span>
        <span class="fdoc-section-title">${sec.label}</span>
        <span class="fdoc-count">${items.length}</span>
      </div>`;

    {
      for (const d of items) {
        // "Aug 2", not "8/2/2026": every other date in the field app reads as
        // a short month and day, and this one was the odd machine format out.
        const when = d.created_at
          ? new Date(d.created_at).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })
          : '';
        html += `<button class="fdoc-row" type="button"
          onclick="openDocLightboxById('${d.id}','doc')">
          <div class="fdoc-row-info">
            <div class="fdoc-row-title">${s(d.title)}</div>
            ${d.notes ? `<div class="fdoc-row-notes">${s(d.notes)}</div>` : ''}
          </div>
          <span class="fdoc-row-date">${s(when)}</span>
          <span class="fdoc-row-arrow" aria-hidden="true"><i data-lucide="chevron-right"></i></span>
        </button>`;
      }
    }

    html += `</div>`;
  }

  html += _fdocPhotoFolderHtml();

  listEl.innerHTML = html || '<div class="docs-empty">No documents uploaded for this project yet.</div>';
  window.lucide && lucide.createIcons();
}

// ── The photo folder (field) ────────────────────────────────────────────────
// Closed, it is one row: what is inside, how many, newest date. Nothing is
// signed and no image is fetched until a thumb is actually going to be shown.
let _docsPagePhotos = [];
let _docsPhotosOpen = false;

function _fdocPhotoFolderHtml() {
  const n = _docsPagePhotos.length;
  if (!n) return '';
  const newest = _docsPagePhotos.reduce((a, d) => (!a || d.created_at > a ? d.created_at : a), null);
  const when = newest
    ? new Date(newest).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })
    : '';
  return `<div class="fdoc-section">
    <button class="fdoc-folder" type="button" id="fdocFolderBtn"
      onclick="fdocTogglePhotos()" aria-expanded="false" aria-controls="fdocPhotoGrid">
      <span class="fdoc-section-ic" aria-hidden="true"><i data-lucide="images"></i></span>
      <span class="fdoc-folder-txt">
        <span class="fdoc-folder-title">Site Photos</span>
        <span class="fdoc-folder-sub">${n} photo${n === 1 ? '' : 's'}${when ? ' · newest ' + when : ''}</span>
      </span>
      <span class="fdoc-row-arrow" aria-hidden="true"><i data-lucide="chevron-down"></i></span>
    </button>
    <div class="fdoc-photo-grid" id="fdocPhotoGrid" hidden></div>
  </div>`;
}

async function fdocTogglePhotos() {
  _docsPhotosOpen = !_docsPhotosOpen;
  const grid = document.getElementById('fdocPhotoGrid');
  const btn  = document.getElementById('fdocFolderBtn');
  if (!grid) return;
  if (btn) btn.setAttribute('aria-expanded', String(_docsPhotosOpen));

  if (!_docsPhotosOpen) { grid.hidden = true; return; }
  grid.hidden = false;
  if (grid.dataset.painted) return;

  grid.innerHTML = '<div class="docs-loading">Loading photos…</div>';
  await window.Ordinex.docUrls.signDocumentRows(sbClient, _docsPagePhotos);
  const s = str => String(str ?? '').replace(/[&<>"']/g, c =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  grid.innerHTML = _docsPagePhotos.map(d => `
    <button class="fdoc-photo-card" type="button" onclick="openDocLightboxById('${d.id}','image')">
      <img class="fdoc-photo-thumb" src="${s(_fSafeUrl(d.file_url))}" alt="${s(d.title)}" loading="lazy" decoding="async">
      <div class="fdoc-photo-cap">${s(d.title)}</div>
    </button>`).join('');
  grid.dataset.painted = '1';
  window.lucide && lucide.createIcons();
}

// Doc rows/photo cards call openDocLightboxById('<uuid>', type) instead of
// splicing file_url/title into onclick="" — see openDocsPage(). Looks the
// document back up from the page's own cache by id.
let _docsPageCache = [];
function openDocLightboxById(id, type) {
  const doc = _docsPageCache.find(d => String(d.id) === String(id));
  if (!doc) return;
  openDocLightbox(doc.file_url, doc.title, type);
}

// Only allow a URL through if a browser will just navigate/display it
// (http/https, or scheme-relative/relative) — rejects javascript:, data:,
// vbscript:, etc. so a poisoned documents.file_url can't run script when
// opened. `documents` rows are insertable by any foreman assigned to the
// project (RLS `member insert`), so file_url is attacker-reachable text.
// One rule for the whole app (js/core/doc-urls.js): plain https only, with
// the tab/newline scheme tricks stripped first.
function _fSafeUrl(url) {
  const u = String(url ?? '').replace(/[\u0000-\u0020\u007f]/g, '');
  if (!u) return '';
  try { return new URL(u).protocol === 'https:' ? u : ''; } catch { return ''; }
}

// ── Document / Photo Viewer ───────────────────────────────────────────────────
// type: 'image' → fullscreen lightbox with pinch-to-zoom
//       'doc'   → opens in real browser (Safari/Chrome) with full native zoom
function openDocLightbox(url, title, type) {
  const safeSrc = _fSafeUrl(url);
  // PDFs go straight to Safari (window.open). The iOS-Safari-in-iframe path
  // we tried before only renders page 1, no scroll, no real zoom — same on
  // most browsers. PDF.js gave us deep zoom but crashed memory on big
  // drawings. Opening in Safari directly hands the file to iOS PDFKit,
  // which is the same engine WhatsApp uses: full pinch-zoom, multi-page
  // scroll, mark-up, share — and after the foreman is done, "Done" brings
  // them right back to the PWA.
  if (type === 'doc') {
    if (safeSrc) window.open(safeSrc, '_blank', 'noopener,noreferrer');
    return;
  }

  // Images keep the in-app fullscreen lightbox (with native pinch-zoom on the <img>)
  document.getElementById('docLightboxOverlay')?.remove();
  const safeTitle = _fEsc(title);
  const safeUrl   = _fEsc(safeSrc);

  const overlay = document.createElement('div');
  overlay.id = 'docLightboxOverlay';
  overlay.className = 'doc-lightbox-overlay';
  overlay.innerHTML = `
    <div class="doc-lightbox-bar">
      <span class="doc-lightbox-title">${safeTitle}</span>
      <div class="doc-lightbox-actions">
        <a class="doc-lightbox-btn" href="${safeUrl}" target="_blank" rel="noopener noreferrer" download>⬇ Save</a>
        <button class="doc-lightbox-btn" onclick="document.getElementById('docLightboxOverlay').remove()">✕ Close</button>
      </div>
    </div>
    <div class="doc-lightbox-body">
      <img class="doc-lightbox-img" src="${safeUrl}" alt="${safeTitle}">
    </div>`;

  overlay.addEventListener('click', e => {
    if (e.target === overlay) overlay.remove();
  });

  document.body.appendChild(overlay);
}

// Display names for the trade switch. Kept in code, not fetched: the field app
// has to name the trade offline, and the pack list is a controlled set in this
// repo. Source of truth is `label` in supabase/packs/<key>.json — add the line
// here when a pack is added (an unknown key falls back to a title-cased key,
// so a missed entry degrades to "Drilling Shotcrete", never to blank).
const PACK_LABELS = {
  concrete:           'Concrete',
  demolition:         'Demolition & Environmental',
  drilling_shotcrete: 'Drilling & Shotcrete',
  drywall:            'Drywall',
  electrical:         'Electrical',
  framing:            'Framing',
  general_contractor: 'General Contractor',
  masonry:            'Masonry',
  mechanical:         'Mechanical — Plumbing & HVAC',
  roofing:            'Roofing',
  sitework:           'Excavation & Sitework',
};
window.PACK_LABELS = PACK_LABELS;

// ── Trade switch (multi-pack orgs) ───────────────────────────────────────────
// An org can run several trade packs at once, and one project can genuinely be
// two trades on the same day. The app resolves the trade from the project's
// tag; with 2+ packs and an untagged project it used to resolve nothing and
// silently serve the neutral core — generic tailgate, the 9 neutral FLRA
// tasks, neutral QAQC, the 12 default inspection items, empty delivery
// catalogue — with no sign on screen (audit 2026-07-29).
//
// So: when the trade is ambiguous, the foreman picks it. The row only exists
// when it has a real decision to offer:
//   • fewer than 2 active packs  → nothing to choose;
//   • project carries a trade tag → the admin already decided, tag wins.
// Everything downstream (form list, tailgate/FLRA/QAQC/inspection content,
// delivery catalogue, offline drafts, the trade recorded on the submission)
// reads activeTradePack() per render, so switching here moves all of it.
async function renderTradeBar() {
  const row = document.getElementById('dashTradeRow');
  const sel = document.getElementById('dashTradeSelect');
  const hint = document.getElementById('dashTradeHint');
  if (!row || !sel) return;

  // The dashboard paints before the ESM bundle and the org context resolve —
  // without this wait the pack list reads empty and the row never appears.
  // Same gate renderTodayForms() uses.
  await _todayCoreReady();

  // Workers are view-only: the trade decision isn't theirs to make, and
  // rendering the row would also persist a default choice on their behalf.
  if (window.currentProfile?.role === 'worker') { row.hidden = true; return; }
  if (_todayNoJobs()) { row.hidden = true; return; }   // no job to pick a trade for: the start card shows

  const feats  = (window.currentOrg && window.currentOrg.features) || {};
  const active = window.Ordinex?.packs ? window.Ordinex.packs.active(feats) : [];
  const tagged = (window.projectTrades || {})[state.project || ''] || '';

  if (active.length < 2 || (tagged && active.includes(tagged))) {
    row.hidden = true;
    return;
  }

  const label = k => (window.PACK_LABELS && window.PACK_LABELS[k])
    || k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  const current = (typeof activeTradePack === 'function' && activeTradePack()) || '';
  sel.innerHTML = '';
  // First option is the unpicked state (value '' = neutral core). The app
  // never guesses a trade: a wrong pack presented as right is worse than the
  // generic one, and the submission would carry the wrong trade stamp
  // (decision 2026-07-29). It doubles as the way back to neutral.
  const ph = document.createElement('option');
  ph.value = ''; ph.textContent = '— pick your trade —';
  sel.appendChild(ph);
  active.forEach(k => {
    const o = document.createElement('option');
    o.value = k; o.textContent = label(k);
    sel.appendChild(o);
  });
  sel.value = active.includes(current) ? current : '';

  if (hint) {
    // The pick follows the user, not the project — say so.
    hint.textContent = state.project
      ? `This project has no trade set — your pick decides which forms and checklists you get, and stays with you across projects.`
      : `Pick the trade you're working in — it decides which forms and checklists you get.`;
  }

  sel.onchange = () => {
    if (typeof setTradeChoice === 'function') setTradeChoice(sel.value);
    // Same refresh the project selector triggers: the list below is derived.
    renderTodayForms().catch(() => {});
    if (typeof renderCustomFormTiles === 'function') renderCustomFormTiles();
    if (typeof applyTradePacks === 'function') applyTradePacks();
  };
  row.hidden = false;
}

// ── Motivational Phrases ──────────────────────────────────────────────────────
// Neutral pool shows for every org; a trade set joins the pool only when that
// pack is active (an electrician must never read bricklayer poetry). Keys
// match the trade_pack_<key> feature flags (activeTradePacks() in
// form-configs.js).
const MOTTO_SETS = {
  neutral: [
    '🏗️ Built by hand, built to last.',
    '🔨 The best crews don\'t just build structures, they build pride.',
    '💼 Sweat today, stand tall in front of what you built tomorrow.',
    '🔥 The toughest jobs create the toughest crews.',
    '🤝 Great sites are built by great teams.',
    '🌟 A craftsman\'s hands tell stories no words can match.',
    '🔨 Precision separates a good job from a great one.',
    '🏆 Show up. Work hard. Go home proud.',
    '💼 The difference between ordinary and extraordinary is the extra effort.',
    '❄️ Cold mornings, hot work — that\'s the life of a true tradesperson.',
    '💪 Your hands are building something that will outlast all of us.',
    '🤝 A safe crew is a strong crew.',
    '🏗️ Trade skills are not just a job — they\'re a craft worth being proud of.',
    '🌟 Quality is remembered long after the deadline is forgotten.',
    '🔨 Do it right the first time — rework costs more than care.',
    '⚒️ No elevator to the top of a scaffold — you climb it one rung at a time.',
    '💼 The best job site is a safe job site.',
    '🔥 Rain or shine — the work gets done.',
    '💪 Built with local hands. Standing for generations.',
    '🛠️ Steady hands, sharp eyes, strong back — that\'s the trade.',
  ],
  masonry: [
    '🧱 Every brick laid is a step closer to something great.',
    '💪 Hard work never lies — the wall speaks for itself.',
    '⚒️ Masonry is the art of turning stone into legacy.',
    '🧱 One brick at a time — that\'s how monuments are made.',
    '🏆 Excellence isn\'t an accident — it\'s laid course by course.',
    '🧱 A plumb line never lies — neither does honest work.',
    '⚡ Work hard in silence. Let the finished wall make the noise.',
    '🏗️ Safety first, quality always, pride in every joint.',
    '💪 You don\'t build walls with shortcuts — and you don\'t build a reputation that way either.',
    '🧱 The foundation you lay today is the legacy you leave tomorrow.',
    '⚒️ Every square foot of this city has a bricklayer behind it.',
    '🔥 Brick by brick, day by day — something permanent grows.',
    '⚡ Character is what you build when no one\'s watching the joint.',
    '🧱 The mortar sets, the wall stands, the crew moves on to the next challenge.',
    '🏆 A tradesperson\'s legacy is built in stone, brick, and hard work.',
  ],
};

function renderMotivationalPhrase() {
  const el = document.getElementById('dashWeather');
  if (!el) return;
  let pool = MOTTO_SETS.neutral;
  try {
    const active = (typeof activeTradePacks === 'function') ? activeTradePacks() : [];
    active.forEach(p => { if (MOTTO_SETS[p]) pool = pool.concat(MOTTO_SETS[p]); });
  } catch (_) { /* org context not ready — neutral pool is always safe */ }
  const phrase = pool[Math.floor(Math.random() * pool.length)];
  el.hidden    = false;
  el.className = 'dash-motto';
  el.textContent = phrase;
}

// ── Navigation ───────────────────────────────────────────────────────────────
// Shared back handler — called by both the header strip button and btnBack
// ── The device Back button belongs to the app, not to the browser ───────────
// The field PWA never touched history, so the phone/browser Back walked OUT of
// a half-filled form. Entering the form flow parks one sentinel entry; popping
// it runs the app's own Back and re-arms while there is still flow to unwind.
// Once the crew is back on Today the sentinel is spent, so the next Back leaves
// the app — which is what a foreman expects at the top of the stack.
let _flowNavArmed = false;
function _inFormFlow() {
  const home = document.getElementById('homeScreen');
  return !!home && home.style.display === 'none';
}
function _armFlowBack() {
  if (_flowNavArmed) return;
  try { history.pushState({ ordinexFlow: 1 }, ''); _flowNavArmed = true; } catch (_) { /* no history */ }
}
window.addEventListener('popstate', () => {
  if (!_flowNavArmed) return;          // not our entry — let the browser be
  _flowNavArmed = false;
  if (!_inFormFlow()) return;          // already home; the sentinel is spent
  _handleBack();
  if (_inFormFlow()) _armFlowBack();   // deeper still — keep the guard up
});

function _handleBack() {
  // Dashboard entry points skip step 1 (name/date come from the profile), so
  // Back from the palette (step 2) or the form (step 3) returns to Today —
  // never into a step-1 form the flow never used.
  if (state.fromDashboard && state.currentStep === 3 && !state.allowedTypes) {
    goToStep(2);                 // came in through the palette — back to it
  } else if (state.fromDashboard && (state.currentStep === 2 || state.currentStep === 3)) {
    _fieldHome();
  } else if (state.currentStep > 1) {
    // Back from Review rebuilds step 3, and renderStep3 starts every form from
    // empty, refilling only from _pendingDraft. Without this the form came
    // back blank and the next autosave wrote the blank over the draft. Same
    // road a resumed draft takes; covers the header, the rail and the phone's
    // own Back (popstate), which all land here.
    if (state.currentStep === 4) state._pendingDraft = { ...state.fields };
    goToStep(state.currentStep - 1);
  } else {
    _fieldHome();
  }
}

function attachNavListeners() {
  document.getElementById('btnBack').addEventListener('click', _handleBack);
  document.getElementById('formBtnBack').addEventListener('click', _handleBack);
  document.getElementById('btnNext').addEventListener('click', async () => {
    // Read-only plan: a draft can be read, never taken to signing (or uploaded). Step 4 is
    // left alone: a form already signed when the plan lapsed queues on this phone, never lost.
    if (state.currentStep === 3 && _planBlocked()) return;
    if (!validateStep(state.currentStep)) return;
    // A named crew member with an empty signature pad weakens the record's
    // audit trail, but legitimate cases exist (absent worker still on the
    // roster) — so confirm rather than block.
    if (state.currentStep === 3 && !_confirmUnsignedCrew()) return;
    // A Production Report day far above the company's history: asked, not refused.
    if (state.currentStep === 3 && !(await _prodOutlierGate())) return;
    // Site Photos submits directly from step 3 (no PDF, no step 4)
    if (state.submissionType === 'Site Photos Only' && state.currentStep === 3) { handleSubmit(); return; }
    if (state.currentStep === 4) { handleSubmit(); return; }

    // For draftable forms, pre-load draft before rendering step 3
    if (state.currentStep === 2 && DRAFTABLE_FORMS.includes(state.submissionType)) {
      state._pendingDraft = null;
      state._draftSavedAt = null;
      const draft = await loadDraftData(state.submissionType);
      if (draft) {
        state._pendingDraft = draft.form_data;
        state._draftSavedAt = draft.updated_at;
      }
    }
    const resumedDraft = state.currentStep === 2 && !!state._pendingDraft;
    if (state.currentStep === 2 && !resumedDraft && _planBlocked()) return;
    goToStep(state.currentStep + 1);
    if (resumedDraft) _planBlocked();   // read-only plan: said before anyone signs on the draft
  });
}

function _confirmUnsignedCrew() {
  const fields = (typeof fieldsFor === 'function') ? (fieldsFor(state.submissionType) || []) : [];
  const crewField = fields.find(f => f.type === 'crew-signin');
  if (!crewField) return true;
  const rows = state.fields[crewField.id] || [];
  const unsigned = rows.filter(m => (m?.name || '').trim() && !m.sig);
  if (!unsigned.length) return true;
  const names = unsigned.map(m => m.name.trim()).slice(0, 3).join(', ');
  const extra = unsigned.length > 3 ? ` +${unsigned.length - 3} more` : '';
  return window.confirm(
    `${unsigned.length} crew member${unsigned.length > 1 ? 's have' : ' has'} not signed yet (${names}${extra}).\n\nContinue to review anyway?`
  );
}

function goToStep(n) {
  state.currentStep = n;
  renderStep(n);
  updateProgressBar(n);
  updateNavButtons(n);
  window.scrollTo({ top: 0, behavior: _prefersReducedMotion() ? 'auto' : 'smooth' });
}

// The steps this filing actually shows, in order. Opening from Today skips
// "Who's submitting?"; a form opened from its own tile skips the palette too;
// Site Photos uploads from the fill step. Counting all four made every form
// open on "Step 3 of 4" — "did I skip something?".
function _visibleSteps() {
  const steps = [];
  if (!state.fromDashboard) steps.push(1);
  if (!(state.fromDashboard && state.allowedTypes)) steps.push(2);
  steps.push(3);
  if (state.submissionType !== 'Site Photos Only') steps.push(4);
  return steps;
}
// "Step 1 of 2", or '' when there is only one step to show.
function _stepOfLine(step) {
  const vs = _visibleSteps();
  const pos = vs.indexOf(step) + 1;
  return vs.length > 1 && pos ? window.Ordinex.i18n.t('field.stepOf', { n: pos, total: vs.length }) : '';
}

function updateProgressBar(step) {
  for (let i = 1; i <= 4; i++) {
    const el = document.getElementById(`stepDot${i}`);
    if (!el) continue;
    el.className = 'step-dot' + (i < step ? ' done' : i === step ? ' active' : '');
  }
  // Redesign v3: one dash per step the person sees, filled up to this one.
  const dashes = document.getElementById('fillSteps');
  if (dashes) {
    const vs = _visibleSteps();
    while (dashes.children.length < vs.length) dashes.appendChild(document.createElement('span'));
    while (dashes.children.length > vs.length) dashes.lastElementChild.remove();
    const pos = vs.indexOf(step) + 1;
    [...dashes.children].forEach((s, i) => s.classList.toggle('on', i < pos));
  }
}

function updateNavButtons(step) {
  const back = document.getElementById('btnBack');
  const next = document.getElementById('btnNext');
  const formBackBtn = document.getElementById('formBtnBack');

  // Visibility of the rail/gutter Back is CSS's call (hidden on phones, where
  // the head's Back serves; a flanking pill on wide desktops) — an inline
  // display here would beat the media query and pin it shut.
  back.style.removeProperty('display');
  const home = step === 1 || (state.fromDashboard && step === 2);
  back.textContent = home ? '← Home' : '← Back';
  back.setAttribute('aria-label', home ? 'Home' : 'Back');
  if (formBackBtn) {
    formBackBtn.setAttribute('aria-label', home ? 'Home' : 'Back');
    const txt = document.getElementById('formBtnBackTxt');
    if (txt) txt.textContent = home ? 'Home' : 'Back';   // say where it lands
  }
  _updateFillHead(step);

  const isSitePhotosSubmit = (step === 3 && state.submissionType === 'Site Photos Only');
  const isSubmitStep = step === 4 || isSitePhotosSubmit;
  // Site Photos flow uploads only — label as such. All other forms generate
  // PDF + send email + record submission, so "Submit Form" is more accurate.
  next.textContent = isSubmitStep
    ? (isSitePhotosSubmit ? 'Upload Photos' : 'Submit Form')
    : 'Next →';
  next.className   = isSubmitStep ? 'btn btn-submit' : 'btn btn-next';
  // Palette step with nothing picked: tapping a form card is what advances,
  // so Next could only error ("select a submission type") — it hides. It
  // returns once a type is set (e.g. stepping Back from the fill step).
  next.style.display = (step === 2 && !state.submissionType) ? 'none' : '';
  // With Next hidden the bar is an empty strip over the palette tiles on
  // phones — CSS drops it (.nav-bar--empty) below the desktop-rail width.
  next.closest('.nav-bar')?.classList.toggle('nav-bar--empty', step === 2 && !state.submissionType);

  _renderFormRail(step);
  _updateSubmitGate();
}

// ── Wizard head (OrdinexField.dc.html) ───────────────────────────────────────
// Palette/review show back + big 24px title; the fill step (3) collapses into
// the compact sticky head with the step line + the progress dashes.
function _updateFillHead(step) {
  const head  = document.getElementById('fillHead');
  if (!head) return;
  head.style.display = '';
  const title = document.getElementById('fillTitle');
  const sub   = document.getElementById('fillSub');

  const compact = step === 3;
  head.classList.toggle('fill-head--compact', compact);
  updateProgressBar(step);

  // The sub-line is the same everywhere now: which step, then where the form
  // files. The dashes on the right show the same progress at a glance; this is
  // the version a screen reader reads out.
  // Job only, not job + date: on a 390px phone the date pushed this to two
  // lines and the head grew a row. The date is asked for on step 1 and shown
  // again on the review card, which is where it matters.
  const stepLine = [_stepOfLine(step), state.project].filter(Boolean).join(' · ');

  let t = 'New form';
  if (step === 1)      t = "Who's submitting?";
  else if (step === 2) t = 'New form';
  else if (step === 3) t = state.submissionType || 'Form';
  else                 t = 'Review & sign';
  const s = stepLine;
  if (title) title.textContent = t;
  if (sub)   { sub.hidden = !s; sub.textContent = s; }
}

// Local HTML escape — app.js builds most DOM via createElement; the few
// template-string surfaces need this (esc() is an admin-scope global).
const _fEsc = s => String(s ?? '').replace(/[&<>"']/g, c =>
  ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

// Two-letter initials (model: "Mike Radu" → "MR") — was a single first-name
// letter on the account avatar/menu.
const _initials2 = n => { const t = (n || '').trim(); return t
  ? t.split(/\s+/).map(w => w[0] || '').join('').slice(0, 2).toUpperCase()
  : '?'; };

// ── Desktop right rail (≥1024px — OrdinexFieldDesktop.dc.html) ───────────────
// Live summary of the submission in progress. Everything here is read from
// `state` — no new data, no new requests. Hidden by CSS on phones.
function _renderFormRail(step) {
  const rail = document.getElementById('formRail');
  if (!rail) return;
  const type = state.submissionType || 'New form';
  const proj = state.project || '—';
  const date = state.fields?.date || _todayLocal();
  const sig  = state.signature ? 'Captured' : 'Not yet';
  const photoCount = (state.photos || []).length;

  // Trade in effect for this submission. Shown only in multi-pack orgs, where
  // it is a real variable (and where the trade switch on Today exists) — in a
  // single-pack org it would be a constant taking up rail space. The foreman
  // is about to sign: he should see which trade's checklist he signed.
  let tradeRow = '';
  try {
    const feats  = (window.currentOrg && window.currentOrg.features) || {};
    const active = window.Ordinex?.packs ? window.Ordinex.packs.active(feats) : [];
    if (active.length > 1) {
      const t = (typeof activeTradePack === 'function' && activeTradePack()) || '';
      const name = t
        ? ((window.PACK_LABELS && window.PACK_LABELS[t])
            || t.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()))
        : 'General (no trade)';
      tradeRow = `<div class="rail-kv"><span class="rail-kv-k">Trade</span><span class="rail-kv-v" title="${_fEsc(name)}">${_fEsc(name)}</span></div>`;
    }
  } catch (_) { /* org context not ready — rail just omits the row */ }

  const steps = _visibleSteps().map(n =>
    `<span class="rail-step${n <= step ? ' done' : ''}"></span>`).join('');

  rail.innerHTML = `
    <div class="rail-title">${_fEsc(type)}</div>
    <div class="rail-sub">${_fEsc(_stepOfLine(step))}</div>
    <div class="rail-steps">${steps}</div>
    <div class="rail-kicker">Summary</div>
    <div class="rail-kv"><span class="rail-kv-k">Project</span><span class="rail-kv-v" title="${_fEsc(proj)}">${_fEsc(proj)}</span></div>
    ${tradeRow}
    <div class="rail-kv"><span class="rail-kv-k">Date</span><span class="rail-kv-v">${_fEsc(date)}</span></div>
    <div class="rail-kv"><span class="rail-kv-k">Foreman</span><span class="rail-kv-v" title="${_fEsc(state.foremanName || '')}">${_fEsc(state.foremanName || '—')}</span></div>
    ${photoCount ? `<div class="rail-kv"><span class="rail-kv-k">Photos</span><span class="rail-kv-v">${photoCount}</span></div>` : ''}
    <div class="rail-kv"><span class="rail-kv-k">Signature</span><span class="rail-kv-v ${state.signature ? 'ok' : 'pending'}">${sig}</span></div>`;
}

// ── Step Rendering ───────────────────────────────────────────────────────────
function renderStep(n) {
  document.querySelectorAll('.step-panel').forEach(p => p.classList.remove('active'));
  const panel = document.getElementById(`step${n}`);
  if (panel) panel.classList.add('active');
  // Project-first nav (Task 5): sync AFTER the step renderer, not before —
  // renderStep2()'s own "reset project if it's no longer in the filable
  // list" logic can still clear state.project (pre-existing behavior, e.g.
  // Back from step 3 into a palette whose project was unassigned since);
  // syncing first would leave the lock display showing a project state.
  // project no longer holds. try/finally so an unrelated throw deeper in a
  // step renderer (e.g. the type grid) can never skip this and leave the
  // lock display stale — cheap either way: null-safe DOM lookups + a
  // hidden/textContent toggle, no side effects of its own.
  try {
    if (n === 2) renderStep2();
    if (n === 3) renderStep3();
    if (n === 4) renderStep4();
  } finally {
    _syncFormProjectLock();
  }
}

// Locked, read-only project display for the New Form wizard — same
// identity-lock styling "Your Name" (step 1) already uses. Shown whenever
// state.project is already known (job-space context, or a resumed draft
// that pins its own project — see loadDraftData's DRAFT RULE comment)
// instead of an editable picker asking the crew to pick what's already
// decided. The true fallback (state.project empty — no context at all,
// shouldn't happen once boot always resolves to a job or the list) still
// shows the live #projectSelect on step 2; step 1 has no editable fallback
// because it isn't reachable on the dashboard fast path today (see
// startSpecificFormFlow/startNewFormFlow) — the field is kept anyway so
// the wizard renders correctly end-to-end if that ever changes.
function _syncFormProjectLock() {
  const known = !!state.project;

  const s1Group = document.getElementById('step1ProjectLockedGroup');
  const s1Text  = document.getElementById('step1ProjectLockedText');
  if (s1Group) s1Group.hidden = !known;
  if (s1Text)  s1Text.textContent = state.project || '';

  const s2Group  = document.getElementById('step2ProjectGroup');
  const s2Locked = document.getElementById('step2ProjectLocked');
  const s2Text   = document.getElementById('step2ProjectLockedText');
  if (s2Group)  s2Group.hidden  = known;
  if (s2Locked) s2Locked.hidden = !known;
  if (s2Text)   s2Text.textContent = state.project || '';
}

// ── Step 2: form palette ─────────────────────────────────────────────────────
// Model groups (OrdinexField.dc.html palette). Anything not listed here —
// org-built custom forms — lands in "Your forms".
// Every catalog form belongs to one of these three sections — a pre-op daily
// log IS an inspection, a per-panel/per-hole record IS production. "Your forms"
// below is reserved for what the tenant built in the admin builder; a catalog
// form landing there means it is missing from a group here.
const PALETTE_GROUPS = [
  { name: 'Safety', ids: ['Daily Tailgate', 'Weekly Toolbox Talk', 'Hazard Observation', 'Incident Report',
      'Incident Investigation', 'First Aid Assessment', 'Site Orientation Record', 'Disciplinary Report',
      'Hot Work & Fire Watch Record', 'Pre-Demolition Readiness Check'] },
  { name: 'Inspections', ids: ['Telehandler Inspection', 'Forklift Inspection', 'E-Pallet Jack Inspection',
      'MEWP Daily Log', 'Scaffolding Inspection', 'Safety Inspection', 'Drill Rig Daily Log', 'Air Compressor Daily Log',
      'Concrete Pump Daily Log', 'Dry-Mix Pump Daily Log', 'Grout Plant Daily Log', 'Truck Monthly Inspection',
      'Equipment Maintenance Record', 'Excavation Daily Inspection', 'Heavy Equipment Daily Log',
      'Refrigerant Service & Leak Log', 'Abatement Containment Daily Log'] },
  { name: 'Site & Production', ids: ['Site Photos Only', 'QAQC - Foreman', 'Weekly Timesheet',
      'Production Report', 'Extra Work Order', 'Daily Site Report', 'Anchor Installation Log', 'Anchor Test Record',
      'Shotcrete Placement Record', 'Shotcrete Test Panel Log', 'Grout Batch Log',
      'Pressure Test Record'] },
];

function renderStep2() {
  const sel = document.getElementById('projectSelect');
  // Fast path from the dashboard already fixed the project — the palette
  // shouldn't ask twice (model). Project-first nav (Task 5): the show/hide
  // between #step2ProjectGroup (the live picker) and #step2ProjectLocked
  // (the read-only display) is handled by _syncFormProjectLock(), called
  // from renderStep() right before this function — not duplicated here.
  if (sel) {
    // Projects come from Supabase (loaded at login into window.userProjects).
    // Sub-projects: hide any project that has buildings under it — crews
    // pick the building, never the site umbrella.
    const userProjects = filterFilableProjects(window.projectRows || []).map(r => r.name);
    sel.innerHTML = '<option value="">Select project...</option>';
    userProjects.forEach(p => {
      const o = document.createElement('option');
      o.value = p; o.textContent = p;
      if (p === state.project) o.selected = true;
      sel.appendChild(o);
    });
    // Reset project if it's no longer in the list
    if (state.project && !userProjects.includes(state.project)) {
      state.project = '';
    }
    sel.value = state.project || '';
    sel.onchange = e => { state.project = e.target.value; };

    // Documents button — opens drawer with docs for the selected project
    const docsHintId = 'projectDocsHint';
    let docsHint = document.getElementById(docsHintId);
    if (!docsHint) {
      docsHint = document.createElement('button');
      docsHint.id = docsHintId;
      docsHint.type = 'button';
      docsHint.className = 'docs-trigger-btn';
      docsHint.innerHTML = '<i data-lucide="file-text"></i> View Project Documents';
      docsHint.addEventListener('click', () => {
        const proj = document.getElementById('projectSelect').value;
        if (proj) showDocumentsDrawer(proj);
        else showToast('Select a project first', 'warning');
      });
      sel.parentNode.appendChild(docsHint);
    }
  }

  rebuildTypeGrid();

  const _projSel = document.getElementById('projectSelect');
  if (_projSel && !_projSel._tpBound) {
    _projSel._tpBound = true;
    _projSel.addEventListener('change', rebuildTypeGrid);
  }
}

function rebuildTypeGrid() {
  const grid = document.getElementById('typeGrid');
  if (grid) {
    // Rebuilt on every visit: the visible list depends on the org's trade
    // packs (W5e), which resolve async after login.
    grid.innerHTML = '';
    const typesToShow = state.allowedTypes
      ? visibleSubmissionTypes().filter(t => state.allowedTypes.includes(t.id))
      : visibleSubmissionTypes();

    const mkCard = (t) => {
      const card = document.createElement('button');
      card.type = 'button';
      card.className = 'type-card' + (state.submissionType === t.id ? ' selected' : '');
      card.dataset.search = (t.id + ' ' + (t.desc || '')).toLowerCase();
      // t.id/t.desc/t.icon can come from an org's custom form (Forms builder,
      // meta.icon/meta.description are free text an org admin types) — never
      // trust them into innerHTML unescaped, even though FORM_TYPE_LUCIDE-
      // mapped built-in types never hit the t.icon branch.
      // Redesign v3: two columns of names, the way the mock's catalogue reads.
      // The description is gone from the tile but NOT from the screen's
      // behaviour — dataset.search above still carries it, so searching for
      // "near miss" still finds Incident Report. `title` keeps it for a mouse
      // and for assistive tech.
      if (t.desc) card.title = t.desc;
      card.innerHTML =
        `<span class="type-icon">${FORM_TYPE_LUCIDE[t.id] ? `<i data-lucide="${FORM_TYPE_LUCIDE[t.id]}"></i>` : _fEsc(t.icon)}</span>` +
        `<span class="type-name">${_fEsc(t.id)}</span>`;
      card.addEventListener('click', () => {
        document.querySelectorAll('.type-card').forEach(c => c.classList.remove('selected'));
        card.classList.add('selected');
        state.submissionType = t.id;
        // Redesign v2: tapping a form goes straight in (reference palette).
        // Reuses the Next button's full path — validation, draft preload, all.
        document.getElementById('btnNext')?.click();
      });
      return card;
    };

    // Grouped per the model. A form the tenant built states its own group in
    // schema.meta.category (the builder asks on creation), so it lands with
    // the catalog forms it belongs with instead of in a leftovers pile.
    const CAT_TO_GROUP = { safety: 'Safety', inspections: 'Inspections', production: 'Site & Production' };
    const customGroup = t => CAT_TO_GROUP[metaFor(t.id, 'category', null)] || null;
    const grouped = new Set();
    PALETTE_GROUPS.forEach(g => {
      const items = g.ids.map(id => typesToShow.find(t => t.id === id)).filter(Boolean)
        .concat(typesToShow.filter(t => !g.ids.includes(t.id) && customGroup(t) === g.name));
      items.forEach(t => grouped.add(t.id));
      if (!items.length) return;
      grid.appendChild(_palGroupLabel(g.name, items.length));
      const gWrap = document.createElement('div');
      gWrap.className = 'pal-grid';
      items.forEach(t => gWrap.appendChild(mkCard(t)));
      grid.appendChild(gWrap);
    });
    const rest = typesToShow.filter(t => !grouped.has(t.id));
    if (rest.length) {
      grid.appendChild(_palGroupLabel('Your forms', rest.length));
      const gWrap = document.createElement('div');
      gWrap.className = 'pal-grid';
      rest.forEach(t => gWrap.appendChild(mkCard(t)));
      grid.appendChild(gWrap);
    }
    window.lucide && lucide.createIcons();

    // Search filters tiles + collapses empty groups (model search bar).
    const search = document.getElementById('typeSearch');
    if (search) {
      const applyFilter = () => {
        const q = search.value.trim().toLowerCase();
        grid.querySelectorAll('.type-card').forEach(c => {
          c.hidden = !!q && !c.dataset.search.includes(q);
        });
        grid.querySelectorAll('.pal-grid').forEach(gw => {
          const shown = [...gw.children].filter(c => !c.hidden).length;
          gw.hidden = !shown;
          const label = gw.previousElementSibling;
          if (label?.classList.contains('pal-group-label')) {
            label.hidden = !shown;
            // The group count follows the filter. A header that keeps saying
            // "9" over two visible tiles is the literal-total defect this
            // redesign exists to stop.
            const n = label.querySelector('.pal-group-n');
            if (n) n.textContent = String(shown);
          }
        });
      };
      if (!search.dataset.wired) { search.dataset.wired = '1'; search.addEventListener('input', applyFilter); }
      applyFilter();
    }
  }
}

// "SAFETY · 9" — the count is the mock's, and it earns its place: it says how
// long the group is before the foreman scrolls it.
function _palGroupLabel(name, count) {
  const el = document.createElement('div');
  el.className = 'pal-group-label';
  el.innerHTML = `<span>${_fEsc(name)}</span><span class="pal-group-n">${count || 0}</span>`;
  return el;
}

// ── Step 3: Form ──────────────────────────────────────────────────────────────
// ── PDF dependency lazy-loader ───────────────────────────────────────────────
// jsPDF (140 KB) + our pdf-generator.js (128 KB) + logo.js
// (15 KB) total ~283 KB the foreman doesn't need to download until they hit
// Submit. We pre-warm the cache when they reach Step 3 (which they spend
// minutes filling out), so by the time they hit Submit the deps are ready.
let _pdfDepsPromise = null;
function _ensurePdfDeps() {
  if (_pdfDepsPromise) return _pdfDepsPromise;
  // Read the cache-bust version from the app's own script tag so the lazy-loaded
  // modules share the same cache key as the rest of the app (a fresh URL per page
  // load would defeat the SW cache and re-download ~283 KB every time).
  //
  // The selector used to look for "js/app.js" — the page loads js/app.MIN.js, so
  // it never matched and every device fell through to v=0. That URL never changes,
  // so browsers pinned the very first pdf-generator they ever downloaded: PDF
  // fixes shipped and simply never arrived. Match both names, fall back to the
  // stylesheet's version, and only then to a timestamp — re-downloading beats
  // serving a stale generator forever.
  const _vFrom = (u) => (u || '').match(/[?&]v=(\d+)/)?.[1];
  const v = _vFrom(document.querySelector('script[src*="js/app.min.js"], script[src*="js/app.js"]')?.src)
         || _vFrom(document.querySelector('link[href*="styles.css"]')?.href)
         || window.APP_VERSION
         || String(Date.now());
  const load = (src) => new Promise((resolve, reject) => {
    // Skip if already in DOM (e.g. CDN script loaded twice = no-op)
    if (document.querySelector(`script[data-pdf-dep="${src}"]`)) return resolve();
    const s = document.createElement('script');
    s.src = src; s.async = false;
    s.dataset.pdfDep = src;
    s.onload  = resolve;
    s.onerror = () => reject(new Error('Failed to load ' + src));
    document.head.appendChild(s);
  });

  _pdfDepsPromise = (async () => {
    // jsPDF first — our pdf-generator depends on it.
    // Vendored locally (js/vendor/) + precached by sw.js so PDF generation
    // never depends on a CDN being reachable — offline submit is core UX.
    await load('js/vendor/jspdf.umd.min.js');
    await load('js/vendor/plex-fonts.js');
    // Then everything else can load in parallel
    await Promise.all([
      load(`js/logo.min.js?v=${v}`),
      load(`js/pdf-generator.min.js?v=${v}`),
    ]);
  })().catch(err => {
    // Reset the promise on failure so a retry can try again
    _pdfDepsPromise = null;
    throw err;
  });
  return _pdfDepsPromise;
}

function renderStep3() {
  // Pre-warm PDF deps in the background. Foreman is about to spend minutes
  // filling the form, so this is a great time to fetch the heavy bits.
  _ensurePdfDeps().catch(err => console.warn('PDF deps pre-warm failed:', err));

  // Always restore the standard photo section first (may have been hidden by Site Photos flow)
  const _ps = document.querySelector('.photo-section');
  if (_ps) _ps.style.display = '';

  // Site Photos has its own simplified flow — bypass the generic form builder
  if (state.submissionType === 'Site Photos Only') {
    renderSitePhotosStep();
    return;
  }

  const titleEl = document.getElementById('formTypeTitle');
  const typeInfo = SUBMISSION_TYPES.find(t => t.id === state.submissionType);
  if (titleEl) {
    const ic = formTypeIconHtml(state.submissionType);
    // state.submissionType is a custom form's id (= its admin-typed title)
    // for org-built forms — not a fixed list once custom forms exist — so it
    // has to be escaped like any other admin-authored string reaching innerHTML.
    if (ic) titleEl.innerHTML = ic + _fEsc(state.submissionType);
    else titleEl.textContent = `${typeInfo?.icon || ''} ${state.submissionType}`;
  }

  const container = document.getElementById('dynamicFields');
  if (!container) return;
  container.innerHTML = '';
  state.fields    = {};        // clear stale field data on every form switch
  state.signature   = null;
  state.gcSignature = null;
  window._signaturePad   = null;
  window._gcSignaturePad = null;

  // ── Restore draft if available ────────────────────────────────────────────
  if (state._pendingDraft) {
    Object.assign(state.fields, state._pendingDraft);
    state._pendingDraft = null;
  }

  // Read BEFORE the banner block below clears it: the crew picker says so when
  // a resumed draft carries no answer to its question.
  const _resumedDraft = !!state._draftSavedAt;

  // ── Draft banner (draftable forms only) ───────────────────────────────────
  if (DRAFTABLE_FORMS.includes(state.submissionType)) {
    const savedAt = state._draftSavedAt
      ? new Date(state._draftSavedAt).toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
      : null;

    const banner = document.createElement('div');
    banner.className = 'draft-banner';
    banner.innerHTML = savedAt
      ? `<span class="draft-banner-msg">Draft restored · <span id="draftSavedAt">last saved ${savedAt}</span></span>
         <div class="draft-banner-actions">
           <button type="button" class="draft-save-btn" onclick="saveDraft()">Save now</button>
           <button type="button" class="draft-clear-btn" onclick="clearDraftAndReset()">Clear</button>
         </div>`
      : `<span class="draft-banner-msg">Auto-saving every 30 seconds · <span id="draftSavedAt">not saved yet</span></span>
         <div class="draft-banner-actions">
           <button type="button" class="draft-save-btn" onclick="saveDraft()">Save now</button>
         </div>`;
    container.appendChild(banner);
    state._draftSavedAt = null;
    // Start the auto-save heartbeat (cleared in resetApp / on submit success)
    _startAutoSaveDraft();
  }

  // ── Production Report: which crew owns these units (admin only, no-op for
  // every other form and role). Injected at the top of Step 3 because the
  // dashboard fast-path skips Step 1. ─────────────────────────────────────────
  renderProductionCrewPicker(container, { resumedDraft: _resumedDraft });

  // ── Date field (always first — was previously in step 1) ──────────────────
  const dateWrapper = document.createElement('div');
  dateWrapper.className = 'field-group';
  const dateLbl = document.createElement('label');
  dateLbl.className = 'field-label';
  dateLbl.htmlFor = 'formStepDate';
  dateLbl.textContent = 'Date *';
  const dateInput = document.createElement('input');
  dateInput.type = 'date';
  dateInput.className = 'field-input';
  dateInput.id = 'formStepDate';
  dateInput.value = state.date;
  dateInput.max = _todayLocal();   // a document dated in the future is always wrong
  // Another week: the clock fills the sheet again (offline from that week's kept copy), so the old
  // week's FILED / Filed by cells and clock-only rows go.
  dateInput.addEventListener('change', e => { const was = _tsClockMonday(); state.date = e.target.value; _followDateWeek(); if (state.submissionType === 'Weekly Timesheet' && _tsClockMonday() !== was) _tsClockPrefill().then(c => { if (c) _tsRerenderRows(); }); });
  dateWrapper.appendChild(dateLbl);
  dateWrapper.appendChild(dateInput);
  container.appendChild(dateWrapper);

  // Pre-fill defaults for Daily Tailgate
  if (state.submissionType === 'Daily Tailgate') applyDailyTailgateDefaults();

  // Signature pads live in Review & sign (step 4) per the reference — the
  // signature is the LAST act, captured over the summary, locked on submit.
  const fields = fieldsFor(state.submissionType)
    .filter(f => f.type !== 'signature' && f.type !== 'gc-signature');
  _prefillWeekField();   // after the draft merge above — a resumed value stays
  fields.forEach(field => renderField(field, container));
  _syncWeekFieldBounds();

  // Conditional logic (Onda 3c): show/hide `visibleIf` fields now + on any change.
  _applyVisibleIf(container);
  if (!container.dataset.visWired) {
    container.dataset.visWired = '1';
    const reapply = () => _applyVisibleIf(container);
    container.addEventListener('input', reapply);
    container.addEventListener('change', reapply);
  }

  setupPhotoUpload();
}

// Show/hide fields whose `visibleIf` condition isn't met, using the current form
// values. Runs after render + on every input/change in the form container.
function _applyVisibleIf(container) {
  const isVis = window.Ordinex?.forms?.builder?.isFieldVisible;
  if (!isVis) return;
  (fieldsFor(state.submissionType) || []).forEach(field => {
    if (!field.visibleIf) return;
    const wrap = container.querySelector(`[data-field-id="${field.id}"]`);
    if (wrap) wrap.style.display = isVis(field, state.fields) ? '' : 'none';
  });
}

// ── Today: due, in progress, and what this crew actually uses ────────────────
// Replaces the old hardcoded four-grid catalogue on the dashboard, which froze
// at 15 tiles while the app grew past 40 forms — every pack form and every
// neutral added after the redesign was simply unreachable from here. This
// renders from visibleSubmissionTypes(), so it cannot fall behind again.
//
// Two rows of meaning, in the order a foreman needs them:
//   #dashDue    — the two recurring records the GC audits: today's tailgate and
//                 this week's toolbox talk, each with a done / not-done chip,
//                 plus any draft left half-finished.
//   #dashQuick  — the six types this foreman actually submits, learned from
//                 history; on a fresh org, the trade's own pack forms instead.
// The full catalogue is one tap away on the "Start a new form" CTA above.
//
// Fails soft on purpose: any query error leaves the sections hidden and the
// CTA still works — a dashboard with no shortcuts beats a dashboard that
// won't paint.
// Eight, not six: the old dashboard put ~15 forms one tap away, and cutting to
// six sent the weekly staples (timesheet, extra work order) hunting through the
// full palette. Reported from the field the day this shipped.
const _TODAY_QUICK_MAX = 8;

function _todayLocalDayKey(d) {
  const x = new Date(d);
  return `${x.getFullYear()}-${x.getMonth() + 1}-${x.getDate()}`;
}
function _todayMondayKey() {
  const d = new Date();
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  d.setHours(0, 0, 0, 0);
  return d;
}

// visibleSubmissionTypes() needs TWO things before it can name a pack form:
// the ESM core (window.Ordinex.packs — module bundle, lands after first paint)
// and the org context (window.currentOrg.features — separate fetch, holds the
// trade_pack_* flags). Miss either and it falls back to "neutral forms only"
// by design, which silently drops every pack form from the shortcuts.
//
// Caught in production twice on 2026-07-28: first pass waited on the bundle
// alone and still rendered three neutral tiles, because the org context was
// the piece that had not landed. Wait for both, then read.
function _todayCoreReady(timeoutMs = 5000) {
  const ready = () => !!(window.Ordinex?.packs && window.currentOrg?.features);
  if (ready()) return Promise.resolve(true);
  return new Promise(resolve => {
    const started = Date.now();
    const tick = () => {
      if (ready()) return resolve(true);
      if (Date.now() - started > timeoutMs) return resolve(false);
      setTimeout(tick, 100);
    };
    tick();
  });
}

async function renderTodayForms() {
  const dueWrap   = document.getElementById('dashDue');
  const quickWrap = document.getElementById('dashQuick');
  if (!dueWrap || !quickWrap) return;
  // Section wrappers carry the label; hiding only the list would leave a
  // heading over nothing.
  const dueSec = document.getElementById('dashDueSec');
  const quickSec = document.getElementById('dashQuickSec');
  dueWrap.hidden = true; quickWrap.hidden = true;
  if (dueSec) dueSec.hidden = true;
  if (quickSec) quickSec.hidden = true;

  const uid = window.currentUser?.id;
  if (!uid) return;
  await _todayCoreReady();
  const project = document.getElementById('dashProjectSelect')?.value || '';
  const visible = new Set(visibleSubmissionTypes().map(t => t.id));

  // One history read serves both jobs: "is it done?" and "what do they use?".
  let rows = [], drafts = [];
  try {
    const since = new Date(Date.now() - 45 * 864e5).toISOString();
    const [subRes, draftRes] = await Promise.all([
      sbClient.from('submissions')
        .select('submission_type, project_name, submitted_at')
        .eq('foreman_id', uid).gte('submitted_at', since)
        .order('submitted_at', { ascending: false }).limit(200),
      sbClient.from('drafts')
        .select('form_type, week_of, updated_at').eq('foreman_id', uid)
        .order('updated_at', { ascending: false }).limit(40),
    ]);
    rows   = subRes.data   || [];
    // Drafts are keyed (form_type, week_of), so yesterday's tailgate and last
    // month's timesheet are still rows here. Only the draft whose scope MATCHES
    // today is the one the app would actually reopen — the rest are history and
    // would read as "you have 9 things unfinished" when the crew has one.
    drafts = (draftRes.data || [])
      .filter(d => d.week_of === _draftScopeFor(d.form_type));
  } catch (_) { return; }

  // ── Due ───────────────────────────────────────────────────────────────────
  const todayKey = _todayLocalDayKey(new Date());
  const monday   = _todayMondayKey();
  // Tailgate is per project per day; toolbox is per crew per week.
  const tailgateDone = rows.some(r => r.submission_type === 'Daily Tailgate' &&
    _todayLocalDayKey(r.submitted_at) === todayKey &&
    (!project || r.project_name === project));
  const toolboxDone = rows.some(r => r.submission_type === 'Weekly Toolbox Talk' &&
    new Date(r.submitted_at) >= monday);

  const draftFor = t => drafts.find(d => d.form_type === t);
  const savedAt = d => d?.updated_at
    ? new Date(d.updated_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : '';

  // A row is 44px of glove-sized tap target carrying one fact: a dot for the
  // state, the record's name, and what is owed. `state` is one of
  // done | draft | open — the accent rail is reserved for the draft, the one
  // row that is genuinely half-finished.
  const dueHtml = [];
  const row = (type, side, rowState) => {
    if (!visible.has(type)) return '';
    return `<button class="fx-row fx-row--${rowState}" data-type="${_fEsc(type)}">
      <span class="fx-row-dot" aria-hidden="true"></span>
      <span class="fx-row-name">${_fEsc(type)}</span>
      <span class="fx-row-side">${side}</span>
    </button>`;
  };
  // The time it was filed, not just "Done": on a screen whose whole job is
  // "what still needs doing today", the clock is the proof.
  const doneAt = (type) => {
    const hit = rows.find(r => r.submission_type === type &&
      (type !== 'Daily Tailgate' || _todayLocalDayKey(r.submitted_at) === todayKey));
    if (!hit?.submitted_at) return 'Done';
    return 'Done ' + new Date(hit.submitted_at)
      .toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  };
  // One line per recurring record, never two: a tailgate that is started but
  // not submitted is ONE thing to finish, not a "due" item plus a "draft".
  const dueRow = (type, done, openLabel) => {
    const d = draftFor(type);
    if (done)  return row(type, `<span class="fx-row-ok">${_fEsc(doneAt(type))}</span>`, 'done');
    if (d)     return row(type, `<span class="fx-row-go">Resume</span>`, 'draft');
    return row(type, `<span class="fx-row-mut">${_fEsc(openLabel)}</span>`, 'open');
  };
  dueHtml.push(dueRow('Daily Tailgate', tailgateDone, 'Not yet'));
  dueHtml.push(dueRow('Weekly Toolbox Talk', toolboxDone, 'Due this week'));

  // Anything else left half-finished. Capped: this section answers "what do I
  // owe right now", and a list of nine kills that.
  const others = drafts
    .filter(d => !['Daily Tailgate', 'Weekly Toolbox Talk'].includes(d.form_type))
    .slice(0, 4);
  for (const d of others) {
    if (!visible.has(d.form_type)) continue;
    const when = savedAt(d) ? `<span class="fx-row-mut">saved ${_fEsc(savedAt(d))}</span>` : '';
    dueHtml.push(row(d.form_type, `${when}<span class="fx-row-go">Resume</span>`, 'draft'));
  }

  const dueMarkup = dueHtml.filter(Boolean).join('');
  if (dueMarkup) {
    dueWrap.innerHTML = dueMarkup;
    dueWrap.hidden = false;
    const sec = document.getElementById('dashDueSec');
    if (sec) sec.hidden = false;
  }

  // ── Quick tiles: learned from history, seeded from the trade ──────────────
  const shownAlready = new Set(['Daily Tailgate', 'Weekly Toolbox Talk',
    ...drafts.map(d => d.form_type)]);
  const freq = new Map();
  for (const r of rows) {
    const t = r.submission_type;
    if (!visible.has(t) || shownAlready.has(t)) continue;
    freq.set(t, (freq.get(t) || 0) + 1);
  }
  let quick = [...freq.entries()].sort((a, b) => b[1] - a[1]).map(([t]) => t);

  // Fresh org with no history: lead with the trade's own forms, then the
  // records every crew needs. Never an empty section.
  if (quick.length < _TODAY_QUICK_MAX) {
    const trade = typeof activeTradePack === 'function' ? activeTradePack() : null;
    // The trade's own forms first — nobody else can offer those — then the
    // records every crew files week in, week out. Timesheet and extra work
    // order earn their place: one pays the crew, the other bills the GC.
    const seed = [
      ...visibleSubmissionTypes().filter(t => t.pack && t.pack === trade).map(t => t.id),
      'Safety Inspection', 'Weekly Timesheet', 'Extra Work Order', 'Hazard Observation',
      'Site Photos Only', 'Incident Report', 'QAQC - Foreman',
    ];
    for (const t of seed) {
      if (quick.length >= _TODAY_QUICK_MAX) break;
      if (visible.has(t) && !shownAlready.has(t) && !quick.includes(t)) quick.push(t);
    }
  }
  quick = quick.slice(0, _TODAY_QUICK_MAX);

  if (quick.length) {
    // Two columns of name-only tiles, like the form catalogue's own grid: the
    // description belongs on the catalogue where forms are chosen, not on a
    // shortcut to a form this crew already files every week.
    quickWrap.innerHTML = quick.map(t => {
      const ic = FORM_TYPE_LUCIDE[t];
      return `<button class="fx-tilebtn" data-type="${_fEsc(t)}">
        ${ic ? `<i data-lucide="${ic}"></i>` : ''}<span>${_fEsc(t)}</span>
      </button>`;
    }).join('');
    quickWrap.hidden = false;
    if (quickSec) quickSec.hidden = false;
  }

  // Same click path as every other tile on this screen.
  [dueWrap, quickWrap].forEach(w => w.querySelectorAll('[data-type]').forEach(el => {
    el.onclick = () => startSpecificFormFlow(el.dataset.type);
  }));
  window.lucide && lucide.createIcons();
}

// ── Custom form tiles (Fase B FB3) ───────────────────────────────────────────
// One dashboard tile per org-created form (published schema, meta.custom).
// Injected as its own labeled section after the Safety grid; removed and
// rebuilt on every dashboard visit. Click path is the same
// startSpecificFormFlow the static tiles use (wired right after this runs).
function renderCustomFormTiles() {
  // The label lives INSIDE the section now, so removing the section is enough;
  // the old two-node removal left an orphan heading behind on a rebuild.
  document.getElementById('dashCustomFormsSection')?.remove();

  let custom = [];
  try { custom = window.Ordinex?.forms?.custom() || []; } catch (_) { /* not loaded yet */ }
  if (!custom.length) return;

  // Anchored to the "forms you use" section, not to a tile inside it: the
  // redesign replaced those tiles (.dash-item → .fx-tilebtn) and an anchor
  // that names one tile by form type meant a whole org's custom forms vanished
  // from Today the moment the shortcut list stopped containing Daily Tailgate.
  const anchor = document.getElementById('dashQuickSec');
  if (!anchor) return;

  const section = document.createElement('div');
  section.className = 'fx-sec';
  section.id = 'dashCustomFormsSection';

  const label = document.createElement('div');
  label.className = 'fx-label';
  label.id = 'dashCustomFormsLabel';
  label.textContent = 'Your company forms';
  section.appendChild(label);

  const grid = document.createElement('div');
  grid.className = 'fx-tilegrid';
  custom.forEach(c => {
    const b = document.createElement('button');
    b.className = 'fx-tilebtn';
    b.dataset.type = c.id;
    if (c.desc) b.title = c.desc;
    const name = document.createElement('span');
    name.textContent = c.id;
    b.appendChild(name);
    grid.appendChild(b);
  });
  section.appendChild(grid);

  anchor.after(section);
}

// ── Daily Tailgate — pre-fill defaults ───────────────────────────────────────
function applyDailyTailgateDefaults() {
  // Checkboxes: standard daily-operations items already checked
  state.fields.items_reviewed = { ...metaFor(state.submissionType, 'tailgate_checked_defaults', DEFAULT_TAILGATE_CHECKED) };

  // FLRA: tasks flagged preselected are included by default (rest unchecked)
  state.fields.flra = metaFor(state.submissionType, 'flra.tasks', DEFAULT_FLRA_TASKS).map(({ preselected, ...t }) => ({
    ...t,
    hazards: [...t.hazards],
    included: preselected === true
  }));
}

// Bridge from the field app's global `state` to the shared form registry's ctx
// (Onda 3a). The registry's handlers only ever talk to this object, never to
// `state`/`window` directly, so the same engine also runs in the admin preview.
function _fieldCtx(container) {
  return {
    container,
    get: (id) => state.fields[id],
    set: (id, v) => { state.fields[id] = v; },
    context: {
      project: state.project,
      foremanName: state.foremanName,
      effForemanName: _submitterName(),
    },
    helpers: {
      micButton: (el, id) => _buildMicButton(el, id),
      // Keep the app-specific side effect: the EWO sync reads state._ewoNextNumber.
      peekEwoNumber: () => _peekNextEwoNumber().then(n => { if (n != null) state._ewoNextNumber = n; return n; }),
      // Photo field (Onda 3d): open the camera/picker and append to state.photos
      // (the existing pipeline — compressed by Onda 2a, saved + embedded in the PDF).
      pickPhotos: (cb) => {
        const inp = document.createElement('input');
        inp.type = 'file'; inp.accept = 'image/*'; inp.multiple = true; inp.capture = 'environment';
        inp.addEventListener('change', async () => {
          await addPhotos([...inp.files]);
          if (typeof cb === 'function') cb(state.photos.length);
        });
        inp.click();
      },
    },
  };
}

// Register the 14 composite widgets behind the registry contract (Onda 3a). Their
// bodies stay in this file and still use the global `state`; only the dispatch
// goes through the registry. ctx.container is the mount point. Simple types are
// registered by the core bundle (js/forms/fields.js) at boot.
function _registerCompositeFields() {
  const reg = window.Ordinex?.forms?.registry;
  if (!reg) return;
  const R = (type, fn) => reg.register(type, { render: (f, ctx) => fn(f, ctx.container) });
  R('tailgate-items', renderTailgateItems);
  R('flra-table', renderFLRATable);
  R('crew-signin', renderCrewSignin);
  R('signature', renderSignaturePad);
  reg.register('gc-signature', { render: (f, ctx) => renderSignaturePad(f, ctx.container, {
    sigKey: 'gcSignature', padKey: '_gcSignaturePad', canvasId: 'gcSignatureCanvas',
    disclaimer: 'GC supervisor signs to confirm the labour and materials recorded above are accurate.' }) });
  R('timesheet-table', renderTimesheetTable);
  R('production-table', renderProductionTable);
  R('ewo-labour-table', renderEwoLabourTable);
  R('ewo-materials-table', renderEwoMaterialsTable);
  R('weekly-inspection', renderWeeklyInspection);
  R('daily-inspection', renderDailyInspection);
  R('qaqc-table', renderQAQCTable);
  R('toolbox-hazards', renderToolboxHazards);
}
_registerCompositeFields();

function renderField(field, container) {
  // Every type — simple (js/forms/fields.js) and composite (_registerCompositeFields)
  // — renders through the one shared engine now (Onda 3a). No visible change: the
  // handlers produce the same DOM the inline code did, bridged to `state` via ctx.
  if (window.Ordinex?.forms?.registry?.render(field, _fieldCtx(container))) return;
  // Unknown type = this bundle predates the field type. Rendering nothing
  // while the required check still blocks submit is an invisible wall — show
  // a muted placeholder so the crew knows to update instead of hunting.
  const ph = document.createElement('div');
  ph.className = 'field-group';
  ph.dataset.fieldId = field.id;
  const lbl = document.createElement('label');
  lbl.className = 'field-label';
  lbl.textContent = field.label || field.id;
  const note = document.createElement('p');
  note.className = 'muted';
  note.style.margin = '4px 0 0';
  note.textContent = 'This field needs an app update to fill in — close and reopen the app to update.';
  ph.appendChild(lbl);
  ph.appendChild(note);
  container.appendChild(ph);
}

// ── Voice-to-text mic button (Web Speech API) ────────────────────────────────
// Drops a small 🎤 button in the corner of a textarea. Tap to start dictation;
// tap again to stop. The transcript is appended to whatever is already in the
// textarea, so foreman can dictate in chunks. Auto-detects browser language
// (pt-BR for Brazilian foremen, en-US for English) — falls back to en-US.
//
// If the browser doesn't support the API (rare on phones — Chrome/Edge/Safari
// all support it), returns null and the textarea is shown alone.
function _buildMicButton(textarea, fieldId) {
  const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!SR) return null;

  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'textarea-mic';
  btn.title = 'Voice to text — tap to speak';
  btn.innerHTML = '🎤';
  btn.setAttribute('aria-label', 'Dictate text into ' + (fieldId || 'this field'));

  let recognition = null;
  let listening = false;

  const start = () => {
    try {
      recognition = new SR();
      recognition.lang = navigator.language || 'en-US';
      recognition.continuous = true;
      recognition.interimResults = false;   // only final results — cleaner
      recognition.maxAlternatives = 1;

      recognition.onresult = (event) => {
        // Append the final transcript to the textarea + sync state
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const r = event.results[i];
          if (!r.isFinal) continue;
          const txt = r[0].transcript.trim();
          if (!txt) continue;
          const sep = textarea.value && !/\s$/.test(textarea.value) ? ' ' : '';
          textarea.value = textarea.value + sep + txt;
          state.fields[fieldId] = textarea.value;
        }
        // Scroll to bottom of textarea so the new text is visible
        textarea.scrollTop = textarea.scrollHeight;
      };

      recognition.onend = () => { stop(); };
      recognition.onerror = (e) => {
        console.warn('speech recognition error:', e.error);
        stop();
        if (e.error === 'not-allowed' || e.error === 'service-not-allowed') {
          showToast('Microphone permission denied — enable it in browser settings.', 'warning');
        }
      };

      recognition.start();
      listening = true;
      btn.classList.add('textarea-mic--listening');
      btn.innerHTML = '⏺️';
      btn.title = 'Tap to stop dictation';
    } catch (err) {
      console.warn('SpeechRecognition start failed:', err);
      stop();
    }
  };

  const stop = () => {
    listening = false;
    btn.classList.remove('textarea-mic--listening');
    btn.innerHTML = '🎤';
    btn.title = 'Voice to text — tap to speak';
    if (recognition) { try { recognition.stop(); } catch (_) {} recognition = null; }
  };

  btn.addEventListener('click', (e) => {
    e.preventDefault();
    if (listening) stop(); else start();
  });

  return btn;
}

// ── Tailgate Items Reviewed ───────────────────────────────────────────────────
function renderTailgateItems(field, container) {
  const header = document.createElement('div');
  header.className = 'section-header';
  header.textContent = field.label;
  container.appendChild(header);

  const body = document.createElement('div');
  body.className = 'section-body';

  if (!state.fields[field.id]) state.fields[field.id] = {};

  // jsonb scrambles object key order — an explicit order array (arrays keep
  // order in jsonb) restores the authored section sequence for org rows.
  const _tgItems = metaFor(state.submissionType, 'tailgate_items', TAILGATE_ITEMS);
  const _tgOrder = metaFor(state.submissionType, 'tailgate_sections_order', null);
  const _tgEntries = Array.isArray(_tgOrder)
    ? _tgOrder.filter(k => _tgItems[k]).map(k => [k, _tgItems[k]])
    : Object.entries(_tgItems);
  // Each section collapses behind a header with live progress, and "All OK"
  // marks the whole section then folds it — the masonry tailgate is ~40 items
  // (~9 phone screens flat). Sections start EXPANDED on purpose: the foreman
  // reviews the items; the shortcut only compresses what's already reviewed.
  _tgEntries.forEach(([section, items]) => {
    const sec = document.createElement('div');
    sec.className = 'tailgate-section';

    const head = document.createElement('button');
    head.type = 'button';
    head.className = 'tailgate-section-head';
    head.setAttribute('aria-expanded', 'true');

    const secTitle = document.createElement('span');
    secTitle.className = 'tailgate-section-title';
    secTitle.textContent = section;

    const prog = document.createElement('span');
    prog.className = 'tailgate-section-progress';
    prog.setAttribute('aria-hidden', 'true');

    const chev = document.createElement('span');
    chev.className = 'tailgate-section-chevron';
    chev.textContent = '▾';
    chev.setAttribute('aria-hidden', 'true');

    head.append(secTitle, prog, chev);
    sec.appendChild(head);

    const secBody = document.createElement('div');
    secBody.className = 'tailgate-section-body';

    const allBtn = document.createElement('button');
    allBtn.type = 'button';
    allBtn.className = 'tailgate-allok-btn';
    allBtn.innerHTML = '<span class="allok-check">✓</span> All OK';
    secBody.appendChild(allBtn);

    const list = document.createElement('div');
    list.className = 'tailgate-checkbox-list';

    const cbs = [];
    const updateProgress = () => {
      const done = items.filter(i => !!state.fields[field.id][i]).length;
      prog.textContent = `${done}/${items.length}`;
      prog.classList.toggle('complete', done === items.length && items.length > 0);
    };

    items.forEach(item => {
      const lbl = document.createElement('label');
      lbl.className = 'tailgate-checkbox-item';
      const cb = document.createElement('input');
      cb.type = 'checkbox';
      cb.checked = !!(state.fields[field.id][item]);
      cb.addEventListener('change', () => {
        state.fields[field.id][item] = cb.checked;
        updateProgress();
      });
      cbs.push(cb);
      const box = document.createElement('span');
      box.className = 'tg-box';
      box.innerHTML = '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>';
      const txt = document.createElement('span');
      txt.className = 'tg-label';
      txt.textContent = item;
      lbl.appendChild(cb);
      lbl.appendChild(box);
      lbl.appendChild(txt);
      list.appendChild(lbl);
    });

    allBtn.addEventListener('click', () => {
      items.forEach(i => { state.fields[field.id][i] = true; });
      cbs.forEach(cb => { cb.checked = true; });
      updateProgress();
      // Tactile confirm before the fold, so the tap registers as a deliberate
      // "yes, all clear" instead of the section just vanishing instantly.
      allBtn.classList.add('tailgate-allok-btn--confirm');
      const collapse = () => {
        sec.classList.add('collapsed');
        head.setAttribute('aria-expanded', 'false');
      };
      if (_prefersReducedMotion()) collapse();
      else setTimeout(collapse, 260);
    });

    head.addEventListener('click', () => {
      const collapsed = sec.classList.toggle('collapsed');
      head.setAttribute('aria-expanded', String(!collapsed));
    });

    secBody.appendChild(list);
    sec.appendChild(secBody);
    updateProgress();
    body.appendChild(sec);
  });

  container.appendChild(body);
}

// ── FLRA Table ────────────────────────────────────────────────────────────────
function renderFLRATable(field, container) {
  const header = document.createElement('div');
  header.className = 'section-header';
  header.textContent = field.label;
  container.appendChild(header);

  const body = document.createElement('div');
  body.className = 'section-body';

  // Initialize: presets start unchecked — foreman selects what applies today
  const flraTasks = metaFor(state.submissionType, 'flra.tasks', DEFAULT_FLRA_TASKS);
  if (!state.fields[field.id] || state.fields[field.id].length === 0) {
    state.fields[field.id] = flraTasks.map(({ preselected, ...t }) => ({
      ...t, hazards: [...t.hazards], included: false
    }));
  }

  const presetCount = flraTasks.length;

  // ── Preset task selector ──
  const presetLabel = document.createElement('p');
  presetLabel.className = 'flra-section-label';
  presetLabel.textContent = 'Tap to select tasks for today:';
  body.appendChild(presetLabel);

  const presetList = document.createElement('div');
  presetList.className = 'flra-preset-list';
  presetList.id = 'flraPresetList';
  body.appendChild(presetList);

  for (let i = 0; i < presetCount; i++) {
    renderFLRAPresetItem(presetList, i);
  }

  // ── Custom tasks ──
  const customLabel = document.createElement('p');
  customLabel.className = 'flra-section-label';
  customLabel.style.marginTop = '16px';
  customLabel.textContent = 'Additional tasks (if needed):';
  body.appendChild(customLabel);

  const customRowsDiv = document.createElement('div');
  customRowsDiv.id = 'flraRows';
  body.appendChild(customRowsDiv);

  for (let i = presetCount; i < state.fields[field.id].length; i++) {
    renderFLRARow(customRowsDiv, i);
  }

  const addBtn = document.createElement('button');
  addBtn.type = 'button';
  addBtn.className = 'add-flra-btn';
  addBtn.textContent = '+ Add Custom Task';
  addBtn.addEventListener('click', () => {
    state.fields[field.id].push({ task: '', hazards: [], riskLevel: 'Low', controls: '', included: true });
    renderFLRARow(customRowsDiv, state.fields[field.id].length - 1);
  });
  body.appendChild(addBtn);
  container.appendChild(body);
}

// ── Compact preset item (tap to include) ─────────────────────────────────────
function renderFLRAPresetItem(container, index) {
  const row = state.fields.flra[index];
  if (row.included === undefined) row.included = false;

  const item = document.createElement('div');
  item.className = 'flra-preset-item' + (row.included ? ' included' : '');
  item.id = `flraPreset${index}`;

  // Header: checkbox + task name + risk pill
  const headerLabel = document.createElement('label');
  headerLabel.className = 'flra-preset-header';

  const cb = document.createElement('input');
  cb.type = 'checkbox';
  cb.className = 'flra-preset-cb';
  cb.checked = !!row.included;

  const nameSpan = document.createElement('span');
  nameSpan.className = 'flra-preset-name';
  nameSpan.textContent = row.task;

  const riskPill = document.createElement('span');
  riskPill.className = `flra-risk-pill ${(row.riskLevel || 'low').toLowerCase()}`;
  riskPill.textContent = row.riskLevel || 'Low';

  headerLabel.appendChild(cb);
  headerLabel.appendChild(nameSpan);
  headerLabel.appendChild(riskPill);
  item.appendChild(headerLabel);

  // Detail block — visible only when included
  const detail = document.createElement('div');
  detail.className = 'flra-preset-detail';

  if (row.hazards && row.hazards.length > 0) {
    const chips = document.createElement('div');
    chips.className = 'flra-hazard-chips';
    row.hazards.forEach(h => {
      const chip = document.createElement('span');
      chip.className = 'flra-hazard-chip';
      chip.textContent = h;
      chips.appendChild(chip);
    });
    detail.appendChild(chips);
  }

  if (row.controls) {
    const ctrl = document.createElement('div');
    ctrl.className = 'flra-preset-ctrl-text';
    ctrl.textContent = row.controls;
    detail.appendChild(ctrl);
  }

  item.appendChild(detail);
  container.appendChild(item);

  cb.addEventListener('change', () => {
    state.fields.flra[index].included = cb.checked;
    item.classList.toggle('included', cb.checked);
  });
}

// ── Custom task row (full form) ───────────────────────────────────────────────
function renderFLRARow(container, index) {
  const row = state.fields.flra[index];
  const customNum = index - metaFor(state.submissionType, 'flra.tasks', DEFAULT_FLRA_TASKS).length + 1;

  const div = document.createElement('div');
  div.className = 'flra-row';
  div.id = `flraRow${index}`;

  div.innerHTML = `
    <div class="flra-row-header">
      <span class="flra-row-num">Custom Task ${customNum}</span>
      <button type="button" class="flra-remove" data-idx="${index}">×</button>
    </div>
    <div class="field-group" style="margin-bottom:12px">
      <label class="field-label">Task Description</label>
      <input class="field-input" type="text" aria-label="Task description" placeholder="Describe the work task..." value="${row.task || ''}" data-flra-task="${index}">
    </div>
    <div class="field-group" style="margin-bottom:12px">
      <label class="field-label">Hazards Present</label>
      <div class="checkbox-grid" id="flraHazards${index}"></div>
    </div>
    <div class="field-group" style="margin-bottom:12px">
      <label class="field-label">Hazard Level</label>
      <div class="flra-risk-btns">
        <button type="button" class="risk-btn ${row.riskLevel === 'High' ? 'high' : ''}" data-risk="High" data-flra-risk="${index}">High</button>
        <button type="button" class="risk-btn ${row.riskLevel === 'Medium' ? 'medium' : ''}" data-risk="Medium" data-flra-risk="${index}">Medium</button>
        <button type="button" class="risk-btn ${row.riskLevel === 'Low' ? 'low' : ''}" data-risk="Low" data-flra-risk="${index}">Low</button>
      </div>
    </div>
    <div class="field-group" style="margin-bottom:0">
      <label class="field-label">Controls in Place</label>
      <textarea class="field-input field-textarea" rows="2" aria-label="Controls in place" placeholder="Controls applied to reduce risk..." data-flra-controls="${index}">${row.controls || ''}</textarea>
    </div>`;

  container.appendChild(div);

  // Hazard checkboxes
  const hazardGrid = div.querySelector(`#flraHazards${index}`);
  metaFor(state.submissionType, 'flra.hazards', FLRA_HAZARDS).forEach(h => {
    const lbl = document.createElement('label');
    lbl.className = 'checkbox-item';
    const hcb = document.createElement('input');
    hcb.type = 'checkbox';
    hcb.checked = (row.hazards || []).includes(h);
    hcb.addEventListener('change', () => {
      const cur = state.fields.flra[index].hazards || [];
      state.fields.flra[index].hazards = hcb.checked ? [...cur, h] : cur.filter(v => v !== h);
    });
    lbl.appendChild(hcb);
    lbl.appendChild(document.createTextNode(' ' + h));
    hazardGrid.appendChild(lbl);
  });

  div.querySelector(`[data-flra-task="${index}"]`).addEventListener('input', e => {
    state.fields.flra[index].task = e.target.value;
  });

  div.querySelector(`[data-flra-controls="${index}"]`).addEventListener('input', e => {
    state.fields.flra[index].controls = e.target.value;
  });

  div.querySelectorAll(`[data-flra-risk="${index}"]`).forEach(btn => {
    btn.addEventListener('click', () => {
      div.querySelectorAll(`[data-flra-risk="${index}"]`).forEach(b => b.className = 'risk-btn');
      btn.classList.add(btn.dataset.risk.toLowerCase());
      state.fields.flra[index].riskLevel = btn.dataset.risk;
    });
  });

  // Remove button — only re-renders custom rows
  const removeBtn = div.querySelector(`[data-idx="${index}"]`);
  if (removeBtn) {
    removeBtn.addEventListener('click', () => {
      state.fields.flra.splice(index, 1);
      const rowsContainer = document.getElementById('flraRows');
      rowsContainer.innerHTML = '';
      const presetCount = metaFor(state.submissionType, 'flra.tasks', DEFAULT_FLRA_TASKS).length;
      for (let i = presetCount; i < state.fields.flra.length; i++) {
        renderFLRARow(rowsContainer, i);
      }
    });
  }
}


// renderCloseout lived here — the widget for the four end-of-day
// questions. No form ever had a closeout field, so it was never rendered;
// removed with the rest of the close-out chain on 2026-08-14.
// ── Toolbox Hazard Review ─────────────────────────────────────────────────────
function renderToolboxHazards(field, container) {
  if (!state.fields.hazard_review) {
    state.fields.hazard_review = metaFor(state.submissionType, 'toolbox_hazards', TOOLBOX_HAZARDS).map(h => ({ hazard: h, risk: '', control: '', included: false }));
    // add one blank custom row
    state.fields.hazard_review.push({ hazard: '', risk: '', control: '', included: false, custom: true });
  }
  const rows = state.fields.hazard_review;

  const wrap = document.createElement('div');
  wrap.className = 'tbx-list';

  function rebuild() { wrap.innerHTML = ''; buildRows(); }

  function buildRows() {
    rows.forEach((row, i) => {
      const card = document.createElement('div');
      card.className = 'tbx-card';

      // Hazard name (per the model: bold label above an always-visible
      // control input — custom row gets an editable name instead)
      let nameEl;
      if (row.custom) {
        nameEl = document.createElement('input');
        nameEl.type = 'text'; nameEl.className = 'field-input tbx-name-input';
        nameEl.placeholder = 'Other hazard / task...';
        nameEl.value = row.hazard || '';
        nameEl.addEventListener('input', e => { rows[i].hazard = e.target.value; });
      } else {
        nameEl = document.createElement('div');
        nameEl.className = 'tbx-name';
        nameEl.textContent = row.hazard;
      }
      card.appendChild(nameEl);

      // Control measure — always visible (the model has no gate here; it was
      // hiding behind "included" before, which is the P1 defect being fixed).
      const ctrl = document.createElement('input');
      ctrl.type = 'text';
      ctrl.className = 'field-input tbx-ctrl-input';
      ctrl.placeholder = 'Control measure — how do we prevent it?';
      ctrl.value = row.control || '';
      ctrl.addEventListener('input', e => {
        rows[i].control = e.target.value;
        if (e.target.value.trim() && !rows[i].included) { rows[i].included = true; syncFoot(); }
      });
      card.appendChild(ctrl);

      // Secondary row: applies-today + risk — kept for the PDF hazard-review
      // table (risk pill + included checkbox), de-emphasized vs. the control
      // input above since the model treats every listed hazard as reviewed.
      const foot = document.createElement('div');
      foot.className = 'tbx-foot';
      const cb = document.createElement('input');
      cb.type = 'checkbox'; cb.className = 'tbx-foot-cb';
      cb.id = `tbxInc${i}`;
      cb.checked = !!row.included;
      const cbLbl = document.createElement('label');
      cbLbl.className = 'tbx-foot-lbl';
      cbLbl.htmlFor = `tbxInc${i}`;
      cbLbl.textContent = 'Applies today';
      const syncFoot = () => { cb.checked = !!rows[i].included; };
      cb.addEventListener('change', () => { rows[i].included = cb.checked; });
      const riskWrap = document.createElement('div');
      riskWrap.className = 'tbx-risk-wrap';
      ['H', 'M', 'L'].forEach(level => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.textContent = level;
        const cls = level === 'H' ? 'high' : level === 'M' ? 'medium' : 'low';
        btn.className = `risk-btn${row.risk === level ? ' ' + cls : ''}`;
        btn.addEventListener('click', () => { rows[i].risk = level; rebuild(); });
        riskWrap.appendChild(btn);
      });
      foot.append(cb, cbLbl, riskWrap);
      card.appendChild(foot);

      wrap.appendChild(card);
    });
  }

  buildRows();
  container.appendChild(wrap);
}

// ── Crew Sign-In ──────────────────────────────────────────────────────────────
function renderCrewSignin(field, container) {
  const header = document.createElement('div');
  header.className = 'section-header';
  header.textContent = field.label;
  container.appendChild(header);

  const body = document.createElement('div');
  body.className = 'section-body';

  const rowsWrap = document.createElement('div');
  rowsWrap.id = 'crewRows';
  body.appendChild(rowsWrap);

  const addBtn = document.createElement('button');
  addBtn.type = 'button';
  addBtn.className = 'add-crew-btn';
  addBtn.textContent = '+ Add Worker';
  addBtn.addEventListener('click', () => {
    state.fields[field.id].push({ worker_id: null, name: '', sig: null });
    renderCrewRow(rowsWrap, state.fields[field.id].length - 1);
  });
  body.appendChild(addBtn);
  container.appendChild(body);

  // Load roster asynchronously — renders once data arrives (non-blocking)
  _loadCrewRoster(field.id, rowsWrap);
}

async function _loadCrewRoster(fieldId, rowsWrap) {
  // Only pre-fill from DB if the list is empty (don't override existing/draft data).
  // Today's crew is almost always yesterday's crew with small edits, so the
  // roster LEARNS: 1) the crew from this foreman's most recent submission on
  // this project; 2) first time on a project → the admin-assigned roster
  // (worker_assignments); 3) nothing → one empty row. Signatures and times
  // are never carried over — only who was on the crew.
  // With no signal: who checked in on this job today (time clock), else the
  // last crew this phone saw here (_saveLastCrew), else the one empty row.
  if (!state.fields[fieldId] || state.fields[fieldId].length === 0) {
    const foremanId = (typeof _submitterId === 'function' && _submitterId()) || window.currentUser?.id;
    let crew = null, asked = navigator.onLine !== false;

    try {
      if (asked && foremanId && state.project) {
        const { data: prev, error } = await _withNetTimeout(sbClient
          .from('submissions')
          .select('data')
          .eq('foreman_id', foremanId)
          .eq('project_name', state.project)
          .not('data->crew', 'is', null)
          .order('submitted_at', { ascending: false })
          .limit(1), 'last crew');
        if (error) asked = false;
        const last = prev?.[0]?.data?.crew;
        if (Array.isArray(last)) {
          const members = last
            .filter(m => m && (m.name || '').trim())
            .map(m => ({ worker_id: m.worker_id || null, name: m.name.trim(), sig: null }));
          if (members.length) crew = members;
        }
      }
    } catch (_) { asked = false; }

    try {
      if (asked && !crew && foremanId) {
        const { data: rows } = await _withNetTimeout(sbClient
          .from('worker_assignments')
          .select('workers(id, name, active)')
          .eq('foreman_id', foremanId), 'crew roster');
        const ws = (rows || [])
          .map(r => r.workers)
          .filter(w => w && w.active && (w.name || '').trim())
          .sort((a, b) => a.name.localeCompare(b.name));
        if (ws.length) crew = ws.map(w => ({ worker_id: w.id, name: w.name.trim(), sig: null }));
      }
    } catch (_) {}

    if (crew) _saveLastCrew(state.project, crew);
    else crew = (await _checkedInCrew(state.project)) || _loadLastCrew(state.project);
    state.fields[fieldId] = crew && crew.length ? crew : [{ worker_id: null, name: '', sig: null }];
  }

  // Render all rows now that data is ready
  rowsWrap.innerHTML = '';
  state.fields[fieldId].forEach((_, i) => renderCrewRow(rowsWrap, i));
}

// The crew last used on a job, kept on this phone per user + job (names and
// worker ids only — never a signature) for the tailgate's offline prefill.
// Written when the crew loads with signal and on every submitted crew form.
function _lastCrewKey(project) {
  return 'ordinexLastCrew:' + (window.currentUser?.id || 'default') + ':' + project;
}
function _saveLastCrew(project, crew) {
  if (!project || !Array.isArray(crew)) return;
  const members = crew.filter(m => m && (m.name || '').trim())
    .map(m => ({ worker_id: m.worker_id || null, name: m.name.trim() }));
  if (!members.length) return;
  try { localStorage.setItem(_lastCrewKey(project), JSON.stringify(members)); } catch { /* quota / private mode */ }
}
function _loadLastCrew(project) {
  try {
    const v = JSON.parse(localStorage.getItem(_lastCrewKey(project)) || 'null');
    return Array.isArray(v) && v.length ? v.map(m => ({ worker_id: m.worker_id || null, name: m.name, sig: null })) : null;
  } catch { return null; }
}
// Who is checked in on this job right now, from what the phone holds (the clock
// context, the last read of the job, the queued punches) — the crew card's own
// data, no network. Null with the clock off, nobody in, or the job unknown.
async function _checkedInCrew(project) {
  try {
    if (!_clockEnabled() || !window.clockCtx || !project) return null;
    const job = filterFilableProjects(window.projectRows || []).find(p => p.name === project);
    if (!job) return null;
    const v = await _crewData(job, false);
    const ins = v.rows.filter(r => r.cur && !r.you && (r.name || '').trim())
      .map(r => ({ worker_id: r.id, name: r.name.trim(), sig: null }));
    return ins.length ? ins : null;
  } catch { return null; }
}

function renderCrewRow(container, index) {
  const member = state.fields.crew[index];
  const card = document.createElement('div');
  card.className = 'crew-card';

  // ── Name row ─────────────────────────────────────────────
  const nameRow = document.createElement('div');
  nameRow.className = 'crew-card-header';

  // Avatar — initials once a name is typed, falls back to the row number
  // (model: 32px --s3 circle, Archivo). Not a gradient — that read as a
  // decorative badge rather than an identity mark.
  const num = document.createElement('span');
  num.className = 'crew-num';
  const _crewInitials = n => { const t = (n || '').trim(); return t
    ? t.split(/\s+/).map(w => w[0] || '').join('').slice(0, 2).toUpperCase()
    : String(index + 1); };
  num.textContent = _crewInitials(member.name);

  // Select-only (Renan, 2026-07-26): the name always comes from the org's
  // workers master list, so a crew member can never be a typo — and each row
  // carries the worker_id, which keeps certs/hours/history linked. A one-off
  // helper must be added by the admin in Workers & Certs first.
  const nameInput = document.createElement('select');
  nameInput.className = 'field-input crew-name';
  const ph = document.createElement('option');
  ph.value = '';
  ph.textContent = 'Select worker…';
  nameInput.appendChild(ph);
  let _matched = false;
  _appendGroupedWorkerOptions(nameInput, w => {
    const o = document.createElement('option');
    o.value = String(w.id);
    o.textContent = w.trade ? `${w.name} — ${w.trade}` : w.name;
    o.dataset.name = w.name;
    if ((member.worker_id && String(member.worker_id) === String(w.id)) ||
        (!member.worker_id && member.name &&
         member.name.trim().toLowerCase() === (w.name || '').trim().toLowerCase())) {
      o.selected = true;
      _matched = true;
      member.worker_id = w.id;
      member.name = w.name;
    }
    return o;
  });
  if (!_matched && member.name) {
    // A name from a previous crew that is no longer in the active list
    // (deactivated worker) — keep it visible so the carried-over crew renders.
    const o = document.createElement('option');
    o.value = '__legacy__';
    o.textContent = member.name;
    o.dataset.name = member.name;
    o.selected = true;
    nameInput.appendChild(o);
  }
  nameInput.addEventListener('change', e => {
    const opt = e.target.selectedOptions[0];
    const nm = (opt && opt.dataset.name) || '';
    state.fields.crew[index].name = nm;
    state.fields.crew[index].worker_id =
      (opt && opt.value && opt.value !== '__legacy__') ? opt.value : null;
    num.textContent = _crewInitials(nm);
  });

  // Signed / Pending — reflects whether this member's own pad has a stroke
  // on it yet (model: green check "Signed" vs grey pen "Pending").
  const status = document.createElement('span');
  status.className = 'crew-status';
  const setCrewStatus = (signed) => {
    status.classList.toggle('signed', signed);
    status.innerHTML = signed
      ? '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="m8.5 12.5 2.5 2.5 4.5-5"/></svg><span class="crew-status-txt">Signed</span>'
      : '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg><span class="crew-status-txt">Pending</span>';
  };
  setCrewStatus(!!member.sig);

  const removeBtn = document.createElement('button');
  removeBtn.type = 'button';
  removeBtn.className = 'crew-remove';
  removeBtn.textContent = '×';
  removeBtn.addEventListener('click', () => {
    if (state.fields.crew.length <= 1) return;
    state.fields.crew.splice(index, 1);
    const wrap = document.getElementById('crewRows');
    wrap.innerHTML = '';
    state.fields.crew.forEach((_, i) => renderCrewRow(wrap, i));
  });

  nameRow.append(num, nameInput, status, removeBtn);
  card.appendChild(nameRow);

  // ── Signature area ────────────────────────────────────────
  const sigLabel = document.createElement('div');
  sigLabel.className = 'crew-sig-label';
  sigLabel.textContent = 'Signature';
  card.appendChild(sigLabel);

  const sigWrap = document.createElement('div');
  sigWrap.className = 'crew-sig-wrap';

  const canvas = document.createElement('canvas');
  canvas.className = 'crew-sig-canvas';
  // Set pixel resolution after mount (rAF gives layout time to settle)
  sigWrap.appendChild(canvas);

  const sigActions = document.createElement('div');
  sigActions.className = 'crew-sig-actions';

  const hint = document.createElement('span');
  hint.className = 'crew-sig-hint';
  hint.textContent = 'Sign with finger';

  const clearBtn = document.createElement('button');
  clearBtn.type = 'button';
  clearBtn.className = 'crew-sig-clear';
  clearBtn.textContent = 'Clear';

  sigActions.append(hint, clearBtn);
  sigWrap.appendChild(sigActions);
  card.appendChild(sigWrap);
  container.appendChild(card);

  // Init SignaturePad after the canvas is in the DOM
  requestAnimationFrame(() => {
    const ratio = window.devicePixelRatio || 1;
    canvas.width  = canvas.offsetWidth  * ratio;
    canvas.height = canvas.offsetHeight * ratio;
    const ctx = canvas.getContext('2d');
    ctx.scale(ratio, ratio);

    const pad = new SignaturePad(canvas, {
      penColor: '#1a1a2e',
      backgroundColor: 'rgba(255,255,255,0)',
      minWidth: 1,
      maxWidth: 2.5,
    });

    // Restore saved signature
    if (member.sig) {
      pad.fromDataURL(member.sig, { ratio: 1, width: canvas.offsetWidth, height: canvas.offsetHeight });
    }

    pad.addEventListener('endStroke', () => {
      state.fields.crew[index].sig = pad.toDataURL('image/png');
      setCrewStatus(true);
    });

    clearBtn.addEventListener('click', () => {
      pad.clear();
      state.fields.crew[index].sig = null;
      setCrewStatus(false);
    });
  });
}

// ── Signature Pad ─────────────────────────────────────────────────────────────
// Signature pad renderer. Default config writes to state.signature (the
// foreman's signature, used by every safety form). For Extra Work Order's
// GC supervisor pad, pass { sigKey: 'gcSignature', padKey: '_gcSignaturePad' }
// so the two pads coexist without stomping each other's state.
function renderSignaturePad(field, container, opts = {}) {
  const sigKey   = opts.sigKey   || 'signature';
  const padKey   = opts.padKey   || '_signaturePad';
  const canvasId = opts.canvasId || 'signatureCanvas';
  const isOptional = opts.optional ?? (sigKey === 'gcSignature');   // GC sig is optional
  const disclaimer = opts.disclaimer
    ?? `I certify that I have reviewed this questionnaire with each crew member and am complying with the guidelines outlined by ${(window.currentOrg?.name || 'the company')} and applicable health and safety regulations.`;

  // Model card (OrdinexField review): --s1 card, uppercase micro-label, white
  // 120px pad with a dashed border + "Tap to sign here" hint that clears on
  // the first stroke, and a green "locks on submit" note once captured.
  const wrapper = document.createElement('div');
  wrapper.className = 'sign-card';

  const label = document.createElement('div');
  label.className = 'sign-card-label';
  label.textContent = field.label + (isOptional ? ' (optional)' : '');
  wrapper.appendChild(label);

  const disclaimerEl = document.createElement('p');
  disclaimerEl.className = 'signature-disclaimer';
  disclaimerEl.textContent = disclaimer;
  wrapper.appendChild(disclaimerEl);

  const wrap = document.createElement('div');
  wrap.className = 'signature-wrap';

  const canvas = document.createElement('canvas');
  canvas.className = 'signature-canvas';
  canvas.id = canvasId;

  const hintEl = document.createElement('div');
  hintEl.className = 'sign-hint';
  hintEl.innerHTML =
    '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.85" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>' +
    '<span>Tap to sign here</span>';

  const lockNote = document.createElement('div');
  lockNote.className = 'sign-locknote';
  lockNote.innerHTML =
    '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.85" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="m8.5 12.5 2.5 2.5 4.5-5"/></svg>' +
    'Signature captured · record will lock on submit';

  const clearBtn = document.createElement('button');
  clearBtn.type = 'button';
  clearBtn.className = 'signature-clear-btn';
  clearBtn.textContent = 'Clear';

  const actions = document.createElement('div');
  actions.className = 'signature-actions';
  actions.appendChild(lockNote);
  actions.appendChild(clearBtn);

  wrap.appendChild(canvas);
  wrap.appendChild(hintEl);
  wrapper.appendChild(wrap);
  wrapper.appendChild(actions);
  container.appendChild(wrapper);

  const setSigned = (on) => {
    hintEl.style.display   = on ? 'none' : '';
    lockNote.style.display = on ? '' : 'none';
    wrap.classList.toggle('signature-wrap--signed', on);
  };
  setSigned(!!state[sigKey]);

  // Init after element is in DOM
  setTimeout(() => {
    // A previous visit to step 4 may have queued this same timer against a
    // canvas that has since been thrown away (the area is emptied on every
    // render). Letting a stale timer finish would publish a pad bound to a
    // detached canvas as window[padKey] — strokes on the visible pad would
    // then never satisfy the submit gate.
    if (!canvas.isConnected) return;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const canvasW = canvas.offsetWidth || canvas.parentElement?.offsetWidth || 320;
    canvas.width = canvasW * ratio;
    canvas.height = 120 * ratio;
    canvas.style.height = '120px';
    canvas.getContext('2d').scale(ratio, ratio);

    if (!window.SignaturePad) {
      canvas.parentElement.insertAdjacentHTML('afterend', '<p style="color:var(--error-ink);font-size:12px;margin-top:6px">Signature pad failed to load. Check internet connection.</p>');
      return;
    }

    const pad = new SignaturePad(canvas, {
      backgroundColor: 'rgb(255,255,255)',
      penColor: '#1E3A6E',
      minWidth: 1.5,
      maxWidth: 3
    });

    if (state[sigKey]) pad.fromDataURL(state[sigKey]);

    // The desktop rail + the step-4 submit gate mirror signature state, so
    // refresh both on both edges.
    pad.addEventListener('endStroke', () => {
      state[sigKey] = pad.toDataURL('image/png');
      setSigned(true);
      if (typeof _renderFormRail === 'function') _renderFormRail(state.currentStep);
      if (typeof _updateSubmitGate === 'function') _updateSubmitGate();
    });
    clearBtn.addEventListener('click', () => {
      pad.clear(); state[sigKey] = null;
      setSigned(false);
      if (typeof _renderFormRail === 'function') _renderFormRail(state.currentStep);
      if (typeof _updateSubmitGate === 'function') _updateSubmitGate();
    });
    window[padKey] = pad;
    // The gate was evaluated 150ms ago, before this pad existed. Re-check now
    // so a restored signature un-gates Submit without needing a fresh stroke.
    if (typeof _updateSubmitGate === 'function') _updateSubmitGate();
  }, 150);
}

// ── Timesheet Table ───────────────────────────────────────────────────────────
const TS_DAYS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

function emptyTsEmployee() {
  const days = {};
  TS_DAYS.forEach(d => { days[d.toLowerCase()] = { r: '', ot: '', sick: false }; });
  return { name: '', worker_id: '', days, notes: '' };
}

function renderTimesheetTable(field, container) {
  const header = document.createElement('div');
  header.className = 'section-header';
  header.textContent = field.label;
  container.appendChild(header);

  const body = document.createElement('div');
  body.className = 'section-body';

  if (!state.fields[field.id] || state.fields[field.id].length === 0) {
    state.fields[field.id] = [emptyTsEmployee()];
  }

  // ── Activity bar (Phase 4) — which task the week's hours book against.
  // A project with no tasks in its Contract renders nothing here: zero taps,
  // and the sync writes task_id null throughout (see _tsActivityTasks below).
  const _tsTasks = _tsActivityTasks();
  const _tsActivity = _ensureActivityState(_tsTasks);
  if (_tsActivity && _tsTasks.length) {
    const activityWrap = document.createElement('div');
    activityWrap.className = 'ts-activity-bar';
    activityWrap.id = 'tsActivityBar';
    body.appendChild(activityWrap);
    _renderActivityBarContent(activityWrap, _tsTasks);
  }

  // ── Clock bar (W4): the week's punch count and Refresh. W3's one gate: a company with the clock
  // off, or not loaded yet, sees the table as before (the bar comes on the next render).
  const _tsClock = _clockEnabled() && state.submissionType === 'Weekly Timesheet';   // _tsClockPrefill's own gate
  if (_tsClock) {
    const bar = document.createElement('div');
    bar.className = 'ts-clock-bar';
    bar.id = 'tsClockBar';
    bar.innerHTML = '<span></span><button type="button" id="tsClockRefresh">Refresh</button>';
    bar.lastChild.addEventListener('click', e => {   // one read at a time: a double tap would read and repaint twice
      const btn = e.currentTarget;
      btn.disabled = true;
      _tsClockPrefill().then(_tsRerenderRows).finally(() => { btn.disabled = false; });
    });
    _tsClockBarCount(bar);
    body.appendChild(bar);
  }

  const rowsWrap = document.createElement('div');
  rowsWrap.id = 'tsRows';
  body.appendChild(rowsWrap);
  state.fields[field.id].forEach((_, i) => renderTsEmployee(rowsWrap, i));

  const addBtn = document.createElement('button');
  addBtn.type = 'button';
  addBtn.className = 'add-crew-btn';
  addBtn.textContent = '+ Add Employee';
  addBtn.addEventListener('click', () => {
    state.fields.timesheet.push(emptyTsEmployee());
    renderTsEmployee(rowsWrap, state.fields.timesheet.length - 1);
    updateTsGrandTotal();
  });
  body.appendChild(addBtn);

  // Grand total — sum of every employee's Reg+OT, all crew this week.
  const grand = document.createElement('div');
  grand.className = 'ts-grand-total';
  grand.id = 'tsGrandTotal';
  grand.innerHTML = '<span class="ts-grand-total-lbl">Week total — all crew</span><span class="ts-grand-total-val">0 hrs</span>';
  body.appendChild(grand);

  container.appendChild(body);
  updateTsGrandTotal();
  // Fill from the clock once painted; the rows are painted again only when that changed the sheet
  // (a repaint under the foreman's thumb would drop what he is typing).
  if (_tsClock) _tsClockPrefill().then(changed => (changed ? _tsRerenderRows() : _tsClockBarCount()));
}

function updateTsGrandTotal() {
  const el = document.getElementById('tsGrandTotal');
  if (!el) return;
  const emps = state.fields.timesheet || [];
  let grand = 0;
  emps.forEach(emp => {
    TS_DAYS.forEach(d => {
      const cell = emp.days[d.toLowerCase()];
      grand += parseFloat(cell?.r || 0) + parseFloat(cell?.ot || 0);
    });
  });
  el.querySelector('.ts-grand-total-val').textContent =
    `${grand % 1 === 0 ? grand : grand.toFixed(1)} hrs`;
}

// The hours a day held when SICK was ticked, keyed by the day cell itself, so
// un-ticking puts them back. Lives as long as the cell (repaints reuse it).
const _tsSickStash = new WeakMap();

function renderTsEmployee(container, index) {
  const emp = state.fields.timesheet[index];
  const card = document.createElement('div');
  card.className = 'ts-employee-card';
  card.id = `tsEmp${index}`;

  // ── Name row ──
  const nameRow = document.createElement('div');
  nameRow.className = 'ts-name-row';

  // Worker picker — dropdown only, no freehand text. This forces every entry
  // to map to a real row in the workers table so syncTimesheetToDb can never
  // silently skip an unrecognised name. Pre-loaded into window.workersFull.
  const nameInput = document.createElement('select');
  nameInput.className = 'field-input ts-name-input ts-name-select';
  const placeholder = document.createElement('option');
  placeholder.value = '';
  placeholder.textContent = `— Select employee ${index + 1} —`;
  placeholder.disabled = true;
  placeholder.selected = !emp.worker_id && !emp.name;
  nameInput.appendChild(placeholder);

  const workers = Array.isArray(window.workersFull) ? window.workersFull : [];
  if (!workers.length) {
    const opt = document.createElement('option');
    opt.value = ''; opt.disabled = true;
    opt.textContent = 'Loading workers… reopen this form if list stays empty';
    nameInput.appendChild(opt);
  } else {
    _appendGroupedWorkerOptions(nameInput, w => {
      const o = document.createElement('option');
      o.value = w.id;
      o.dataset.name  = w.name || '';
      o.dataset.trade = w.trade || '';
      o.textContent = w.trade ? `${w.name} — ${w.trade}` : w.name;
      // Restore prior selection by id first (most accurate), then by name (legacy drafts)
      if ((emp.worker_id && emp.worker_id === w.id) ||
          (!emp.worker_id && emp.name && emp.name.toLowerCase() === (w.name || '').toLowerCase())) {
        o.selected = true;
      }
      return o;
    });
  }
  nameInput.addEventListener('change', e => {
    const opt = e.target.selectedOptions[0];
    state.fields.timesheet[index].worker_id = e.target.value || '';
    state.fields.timesheet[index].name      = opt?.dataset.name || '';
    updateTsTotals(card, index);
  });

  const removeBtn = document.createElement('button');
  removeBtn.type = 'button';
  removeBtn.className = 'crew-remove';
  removeBtn.textContent = '×';
  removeBtn.addEventListener('click', () => {
    if (state.fields.timesheet.length <= 1) return;
    if (emp.worker_id) (state._clockDismissed ||= new Set()).add(emp.worker_id);   // the clock does not bring him back
    state.fields.timesheet.splice(index, 1);
    _reindexActivityWorkers(index);   // keep per-worker split overrides on the right people
    _tsRerenderRows();
  });

  // Avatar — initials of the selected worker (model: 30px --s3 circle, Archivo).
  const avatar = document.createElement('span');
  avatar.className = 'ts-avatar';
  const _tsInitials = n => (n || '').trim().split(/\s+/).map(w => w[0] || '').join('').slice(0, 2).toUpperCase() || '–';
  avatar.textContent = _tsInitials(emp.name);
  nameInput.addEventListener('change', () => { avatar.textContent = _tsInitials(state.fields.timesheet[index].name); });

  nameRow.appendChild(avatar);
  nameRow.appendChild(nameInput);
  nameRow.appendChild(removeBtn);
  card.appendChild(nameRow);

  // ── Days grid — 4 cols first row, 3 cols second row ──
  const grid = document.createElement('div');
  grid.className = 'ts-days-grid';
  // Fixed REG / OT tags to the left of the two rows: the boxes' own "R"/"OT"
  // placeholders vanished once typed, leaving an "8" over a "0" with no word.
  // Outside the grid so its weekend columns (nth-child 6/7) stay where they are.
  const daysWrap = document.createElement('div');
  daysWrap.className = 'ts-days-wrap';
  const rowTags = document.createElement('div');
  rowTags.className = 'ts-row-tags';
  rowTags.setAttribute('aria-hidden', 'true');   // each input's title says Regular / Overtime
  const tagSpacer = document.createElement('div');
  tagSpacer.className = 'ts-day-label ts-row-tags-spacer';
  tagSpacer.textContent = 'MON';
  rowTags.appendChild(tagSpacer);
  ['REG', 'OT'].forEach(txt => {
    const tag = document.createElement('div');
    tag.className = 'field-input ts-hours-input ts-row-tag';
    tag.textContent = txt;
    rowTags.appendChild(tag);
  });
  daysWrap.appendChild(rowTags);
  daysWrap.appendChild(grid);

  TS_DAYS.forEach((day, i) => {
    const dayCol = document.createElement('div');
    dayCol.className = 'ts-day-col';

    const dayKey = day.toLowerCase();
    const dayLabel = document.createElement('div');
    dayLabel.className = 'ts-day-label';
    dayLabel.textContent = day.toUpperCase();
    // Another foreman's filed day, or approved vacation: nothing to type (W4, spec 6.2).
    const cell = emp.days[dayKey] ?? {};
    const locked = cell.off === 'vacation' || !!cell.filedBy;
    // Typing makes the cell the foreman's (the clock never writes over it); both inputs emptied hand
    // it back to the clock. cell.clock stays, so the hint still says where the hours came from.
    const typed = d => { if (!d.r && !d.ot) delete d.src; else d.src = 'manual'; };

    const regInput = document.createElement('input');
    regInput.className = 'field-input ts-hours-input';
    regInput.type = 'number';
    regInput.min = '0'; regInput.max = '24'; regInput.step = '0.1';
    regInput.placeholder = 'R';
    regInput.value = emp.days[dayKey]?.r || '';
    regInput.title = `${day} Regular`;
    regInput.addEventListener('input', e => {
      const d = state.fields.timesheet[index].days[dayKey];
      d.r = e.target.value;
      typed(d);
      updateTsTotals(card, index);
    });

    const otInput = document.createElement('input');
    otInput.className = 'field-input ts-hours-input ts-ot-input';
    otInput.type = 'number';
    otInput.min = '0'; otInput.max = '24'; otInput.step = '0.1';
    otInput.placeholder = 'OT';
    otInput.value = emp.days[dayKey]?.ot || '';
    otInput.title = `${day} Overtime`;
    otInput.addEventListener('input', e => {
      const d = state.fields.timesheet[index].days[dayKey];
      d.ot = e.target.value;
      typed(d);
      updateTsTotals(card, index);
    });

    // SICK toggle — marks the whole day as a sick day. Clears/locks the hour
    // inputs; on submit it lands in the sick_days table (admin Sick Days tab).
    // A locked day takes no SICK: it would pay a day the cell never sends.
    const sickBtn = document.createElement('button');
    sickBtn.type = 'button';
    sickBtn.className = 'ts-sick-btn';
    sickBtn.textContent = 'SICK';
    sickBtn.title = `${day} — mark as sick day`;
    sickBtn.disabled = locked;
    const applySick = (on) => {
      regInput.disabled = otInput.disabled = on || locked;
      sickBtn.classList.toggle('active', on);
    };
    applySick(!!emp.days[dayKey]?.sick);
    sickBtn.addEventListener('click', () => {
      const d = state.fields.timesheet[index].days[dayKey];
      d.sick = !d.sick;
      if (d.sick) {
        // Kept off the record (not in state.fields): a wrong tap on SICK
        // and a tap back must not cost the hours that were typed.
        _tsSickStash.set(d, { r: d.r, ot: d.ot, src: d.src });
        d.r = ''; d.ot = ''; regInput.value = ''; otInput.value = ''; d.src = 'manual';
      } else {
        const was = _tsSickStash.get(d);
        _tsSickStash.delete(d);
        if (was && (was.r || was.ot)) {
          d.r = was.r || ''; d.ot = was.ot || '';
          regInput.value = d.r; otInput.value = d.ot;
          if (was.src) d.src = was.src; else delete d.src;
        } else typed(d);
      }
      applySick(d.sick);
      updateTsTotals(card, index);
    });

    dayCol.appendChild(dayLabel);
    dayCol.appendChild(regInput);
    dayCol.appendChild(otInput);
    dayCol.appendChild(sickBtn);
    const hint = _tsClockLine(cell, day, () => _tsOpenDaySheet(emp.worker_id, window.Ordinex.guards.weekDatesFor(state.date)[i],
      () => _tsClockPrefill().then(_tsRerenderRows)));
    if (hint) dayCol.appendChild(hint);
    grid.appendChild(dayCol);
  });

  card.appendChild(daysWrap);

  // A punch on approved time off: a phone's day column holds only "⚠ VAC", so the sentence goes here, full width.
  // CSS shows this note or the in-day sentence, by screen width.
  const conflicts = TS_DAYS.filter(d => emp.days[d.toLowerCase()]?.clock?.offConflict);
  if (conflicts.length) {
    const note = document.createElement('div');
    note.className = 'ts-card-conflicts';
    for (const d of conflicts) {
      const row = document.createElement('div');
      row.textContent = `${d}: ${_tsConflictText(emp.days[d.toLowerCase()].clock)}`;
      note.append(row);
    }
    card.appendChild(note);
  }

  // ── Totals bar ──
  const totalsBar = document.createElement('div');
  totalsBar.className = 'ts-totals-bar';
  totalsBar.id = `tsTotals${index}`;
  card.appendChild(totalsBar);
  container.appendChild(card);
  updateTsTotals(card, index);
}

function updateTsTotals(card, index) {
  const emp = state.fields.timesheet[index];
  if (!emp) return;
  let totalR = 0, totalOT = 0, sickCt = 0;
  TS_DAYS.forEach(d => {
    const cell = emp.days[d.toLowerCase()];
    totalR  += parseFloat(cell.r  || 0);
    totalOT += parseFloat(cell.ot || 0);
    if (cell.sick) sickCt++;
  });
  const totalAll = totalR + totalOT;
  const bar = card.querySelector(`#tsTotals${index}`);
  if (bar) {
    bar.innerHTML = `
      <span class="ts-total-chip reg">REG <strong>${totalR % 1 === 0 ? totalR : totalR.toFixed(1)}</strong></span>
      <span class="ts-total-chip ot">O/T <strong>${totalOT % 1 === 0 ? totalOT : totalOT.toFixed(1)}</strong></span>
      <span class="ts-total-chip total">TOTAL <strong>${totalAll % 1 === 0 ? totalAll : totalAll.toFixed(1)}</strong></span>${sickCt ? `
      <span class="ts-total-chip sick">SICK <strong>${sickCt}</strong></span>` : ''}`;
  }
  updateTsGrandTotal();
}

// Every row painted again from state (a row removed, the clock refilled the sheet, a day sheet saved),
// with the totals and the clock bar's count. Nothing when the table is gone (a late prefill answer).
function _tsRerenderRows() {
  const wrap = document.getElementById('tsRows');
  if (!wrap) return;
  wrap.innerHTML = '';
  state.fields.timesheet.forEach((_, i) => renderTsEmployee(wrap, i));
  updateTsGrandTotal();
  _tsClockBarCount();
}

// "Clock: 23 punches this week": the punches of the read the sheet was filled from (every job the
// filer reads).
function _tsClockBarCount(bar = document.getElementById('tsClockBar')) {
  const n = state._clock?.week?.punches ?? 0;
  if (bar) bar.firstChild.textContent = `Clock: ${n} ${n === 1 ? 'punch' : 'punches'} this week`;
}

// Where a clock day's hours came from: "7:02–15:31 · 30m"; while the shift is open only its start
// (the red OPEN chip says the rest).
function _tsHint(cell) {
  const c = cell.clock;
  if (!c) return '';
  return c.open ? (c.start ?? '') : window.Ordinex.timeClock.fmtHint({ start: c.start, end: c.end, breakMin: c.brk, open: c.open });
}

// "Approved vacation but punched 0 h": a punch on a day of approved time off (the punch won the cell).
function _tsConflictText(c) {
  return `Approved ${c.offConflict} but punched ${window.Ordinex.timeClock.round1(c.regular + c.ot15 + c.ot2)} h`;
}

// The line under a day's inputs: the hint, then the chips in this order: OPEN, FILED, Filed by, VAC,
// STAT, the GPS-far and corrected dots, and a punch on approved time off. null when the cell has none
// of it (a typed cell, an old draft, the clock off): the column is as it was. A clock cell's line
// opens its day sheet (openDay); VAC asks before turning the day into a plain 0 h cell.
function _tsClockLine(cell, day, openDay) {
  const TC = window.Ordinex.timeClock, c = cell.clock;
  const tap = !!c || cell.src === 'clock';
  const line = document.createElement(tap ? 'button' : 'div');
  line.className = 'ts-clock-hint';
  const hint = _tsHint(cell);
  if (hint) {
    const time = document.createElement('span');
    time.className = 'ts-clock-time';
    time.textContent = hint;
    line.append(time);
  }
  const chip = (mod, text = '', title = '') => {
    const el = document.createElement(mod === 'vac' ? 'button' : 'span');
    el.className = `ts-clock-chip ts-clock-chip--${mod}`;
    el.textContent = text;
    if (title) el.title = title;
    line.append(el);
    return el;
  };
  if (c?.open) chip('open', 'OPEN');
  if (cell.src === 'filed') chip('filed', 'FILED');
  if (cell.filedBy) chip('filedby', `Filed by ${cell.filedBy}`);
  if (cell.off === 'vacation') {
    const vac = chip('vac', 'VAC', `Remove vacation on ${day}`);
    vac.type = 'button';
    vac.addEventListener('click', () => {
      if (!confirm(`Remove vacation on ${day}?`)) return;
      delete cell.off;
      cell.src = 'manual';   // 0 h, his: the clock does not put the vacation back
      _tsRerenderRows();
    });
  }
  if (cell.off === 'stat') chip('stat', 'STAT');
  if (c?.far) chip('far', 'GPS', TC.flagLabel('far'));   // a word, not a dot: a phone cannot show a title
  if (c?.corrected) chip('corrected', 'FIXED', `Corrected by ${c.correctedBy || 'your foreman'}`);
  if (c?.offConflict) {
    chip('conflict', `⚠ ${c.offConflict === 'sick' ? 'SICK' : 'VAC'}`);   // a phone's ~39 px column: the sentence goes under the week
    const note = document.createElement('span');
    note.className = 'ts-clock-conflict';
    note.textContent = _tsConflictText(c);
    line.append(note);
  }
  if (!line.children.length) return null;
  if (tap) {
    line.type = 'button';
    line.addEventListener('click', openDay);
  }
  return line;
}

// ── Timesheet Activity (Phase 4) ──────────────────────────────────────────────
// Which task/contract-line the week's hours book against. A project with no
// tasks defined in its Contract shows none of this — the field never learns
// the feature exists, and every hour syncs with task_id null, exactly like
// before this phase. Names only reach the field via window.projectTasks
// (js/auth.js, project_task_names() — no money in that RPC by design).
function _tsActivityTasks() {
  const raw = (window.projectTasks || {})[state.project] || [];
  return raw.slice().sort((a, b) =>
    (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0) ||
    String(a.description || '').localeCompare(String(b.description || '')));
}

// Foreman's last explicit pick for this project, so a repeat weekly timesheet
// on the same job costs zero extra taps.
function _tsLastActivity(projectName) {
  try { return localStorage.getItem('ordinexLastActivity:' + (projectName || '')); }   // null = never picked here
  catch (_) { return null; }
}
function _tsSaveLastActivity(projectName, taskId) {
  try { localStorage.setItem('ordinexLastActivity:' + (projectName || ''), taskId || ''); } catch (_) {}
}

function _defaultWeekActivity(projectName, tasks) {
  const last = _tsLastActivity(projectName);
  if (last && tasks.some(t => t.task_id === last)) return last;
  if (last === '') return '';                 // General, picked on purpose last time
  if (tasks.length === 1) return tasks[0].task_id;
  // Two or more tasks and nothing remembered: the bar opens on "Choose the
  // activity…" and step 3 refuses to sign until one is picked (decision
  // 2b-A, 2026-10-19). null, never '' — '' is a real answer (General).
  return null;
}

// Stale ids (a task renamed or deleted in Settings after the draft was
// written) never reach the sync — js/core/timesheet-activity.js
// sanitizeActivityIds, the same pass the admin's re-file runs. Mutates
// `activity` in place; the crew size bounds the per-worker overrides.
function _sanitizeActivityIds(activity, tasks) {
  window.Ordinex.timesheetActivity.sanitizeActivityIds(
    activity, tasks, (state.fields.timesheet || []).length);
}

// A per-worker split override (activity.days[*].workers) is keyed by the
// employee's POSITION in the crew list — the same contract
// timesheet-activity.js's resolveWorkerDay expects. Removing an employee
// shifts every later position down one; the override keys MUST shift with
// them, or a split silently lands on whoever inherited the old index. That's
// the exact payroll risk this feature exists to avoid: wrong person, wrong
// task, no error (validateActivity re-checks against the same reindexed
// array, so day totals still match).
function _reindexActivityWorkers(removedIdx) {
  const days = state.fields?.activity?.days || {};
  Object.values(days).forEach(cfg => {
    if (!cfg || !cfg.workers) return;
    const next = {};
    Object.entries(cfg.workers).forEach(([k, rows]) => {
      const i = Number(k);
      if (i === removedIdx) return;            // the removed worker's override dies with them
      next[i > removedIdx ? i - 1 : i] = rows;
    });
    cfg.workers = next;
    if (!Object.keys(next).length) delete cfg.workers;
  });
}

// Runs on every render of the timesheet field. If the project has tasks and
// `state.fields.activity` hasn't been touched yet this session (a brand-new
// form, or an old draft saved before this phase shipped), seeds the "one tap
// already done" default. Always sanitizes against the CURRENT task list —
// covers a stale draft, a task deleted mid-week, or a project swapped after
// the draft was written.
function _ensureActivityState(tasks) {
  if (state.fields.activity) {
    if (!state.fields.activity.days) state.fields.activity.days = {};
    _sanitizeActivityIds(state.fields.activity, tasks);
    // A deleted task left the week unanswered; with one task left there is
    // nothing to ask (same rule as the seed below).
    if (state.fields.activity.week == null && tasks.length === 1) state.fields.activity.week = tasks[0].task_id;
    return state.fields.activity;
  }
  if (!tasks.length) return null;   // no bar to show — nothing to seed
  state.fields.activity = { week: _defaultWeekActivity(state.project, tasks), days: {} };
  return state.fields.activity;
}

// Tasks can arrive after the field rendered (js/auth.js visibilitychange
// refresh): create the bar if the job has tasks now, else just re-mount it
// so the week select and Split dialog list the current catalogue.
function _refreshActivityBar() {
  const tasks = _tsActivityTasks();
  let wrap = document.getElementById('tsActivityBar');
  if (!wrap) {
    const rows = document.getElementById('tsRows');
    if (!rows || !tasks.length || !_ensureActivityState(tasks)) return;
    wrap = document.createElement('div');
    wrap.className = 'ts-activity-bar';
    wrap.id = 'tsActivityBar';
    rows.parentNode.insertBefore(wrap, rows);
  }
  _renderActivityBarContent(wrap, tasks);
}

function _renderActivityBarContent(wrap, tasks) {
  // The editor is js/core/timesheet-activity-ui.js (lifted 2026-10-19): it
  // mutates state.fields.activity IN PLACE, so the draft, the auto-save and
  // the outbox keep seeing the same object they always did.
  window.Ordinex.timesheetActivityUi.mountActivityBar(wrap, {
    tasks, activity: state.fields.activity, employees: state.fields.timesheet,
    onWeekPick: id => _tsSaveLastActivity(state.project, id),
  });
}

// ── Clock prefill (W4) ──
// The Weekly Timesheet filled from the time clock (spec §9.1, contract §4.2). Rows: the crew who
// punched on this job (an admin's: everyone who punched on it), anyone the filer punched here, a worker with a day the filer already filed,
// and the crew with approved time off this week. Cells: a typed one (src 'manual', or an old draft's
// value with no src) is never touched; every other cell is worked out again on each run — my filed
// day (src 'filed', my numbers, cell.clock when it has punches), another foreman's (filedBy, empty), a clock day (src 'clock' and
// cell.clock), approved time off with no punch and no hours on another job that day, a stat holiday's hint. The clock never writes the
// draft: the cells say where they came from, state._clock holds the read itself.

// The Monday of the sheet's week, 'YYYY-MM-DD'.
function _tsClockMonday() {
  return _dateFromWeekStart(_weekStartFor(state.date), 0);
}

// The sheet's job id; null on the old offline cache that keys a job by its name (auth.js "names
// only"): that device simply gets no prefill.
function _tsClockProjectId() {
  const id = (window.projectRows ?? []).find(p => p.name === state.project)?.id;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id ?? '') ? id : null;
}

// The prefill's offline copy, one per job (filed and sick days are per job: job A's must never fill
// job B's sheet), never W3's ox_clock_<uid>_<monday>, where My time keeps { punches, at } (Decision 14).
function _tsClockCacheKey(uid, monday, projectId) {
  return `ox_ts_${uid}_${monday}_${projectId}`;
}

// 'HH:MM' on the phone's clock, zero-padded (time_entries.start_time / end_time), or null.
function _hhmm(iso) {
  const d = new Date(iso ?? NaN);
  return Number.isNaN(d.getTime()) ? null : `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

// A cell the foreman typed: src 'manual', or an old draft's value with no src. Never written over.
function _tsClockTyped(c) {
  const has = v => v != null && v !== '';
  return c.src === 'manual' || (c.src === undefined && (has(c.r) || has(c.ot) || !!c.sick));
}

// Another foreman's filed day as this sheet shows it (the prefill and the signing re-check alike):
// emptied, never sent, the STAT hint kept.
function _tsFiledByCell(ctx, date, by) {
  return { r: '', ot: '', sick: false, filedBy: by, ...(ctx.statDates.has(date) && { off: 'stat' }) };
}

// Equal values, whatever the key order (a draft comes back from jsonb with its keys reordered).
function _tsClockSame(a, b) {
  if (a === b) return true;
  if (!a || !b || typeof a !== 'object' || typeof b !== 'object' || Array.isArray(a) !== Array.isArray(b)) return false;
  const keys = Object.keys(a);
  return keys.length === Object.keys(b).length && keys.every(k => Object.prototype.hasOwnProperty.call(b, k) && _tsClockSame(a[k], b[k]));   // the ES2018 way: the ES2022 helper is missing on iOS < 15.4
}

// Drops this user's copies older than last week (they hold the crew's punches and GPS, and
// localStorage is small): a sheet that old, opened offline, starts empty as before the clock.
function _tsClockPrune(uid) {
  const prefix = `ox_ts_${uid}_`;
  const lastMonday = _dateFromWeekStart(_weekStartFor(window.Ordinex.period.localDateStr(new Date())), -7);
  for (const k of Object.keys(localStorage)) {
    if (k.startsWith(prefix) && k.slice(prefix.length, prefix.length + 10) < lastMonday) localStorage.removeItem(k);
  }
}

// The worker-days ('worker|date') of the week with hours filed on ANOTHER job: an approved time-off day there is no
// time off on this sheet (nobody is paid twice). RLS answers an admin with every sheet's rows, a foreman with his own
// (another foreman's sheet on another job stays unread: the admin's "Sick day on 2 sheets" flag is the net there).
// A row corrected to 0 h pays nothing and does not count. Paged like the punches: PostgREST stops at 1000 rows
// without a word.
async function _tsHoursElsewhere(projectId, monday, sunday) {
  const keys = new Set();
  for (let from = 0; ; from += 1000) {
    const { data, error } = await _withNetTimeout(sbClient.from('time_entries').select('worker_id, work_date, project_id, regular_hours, overtime_hours')
      .gte('work_date', monday).lte('work_date', sunday).order('id').range(from, from + 999), 'timesheet prefill');
    if (error) throw error;
    for (const e of data) if (e.project_id !== projectId && (+e.regular_hours || 0) + (+e.overtime_hours || 0) > 0) keys.add(`${e.worker_id}|${e.work_date}`);
    if (data.length < 1000) break;
  }
  return [...keys];
}

// The week's clock facts for the sheet: { punches, timeOff, filed, sick, elsewhere, at }. Online it always asks
// (spec §9.1), the five reads at once, each under the network deadline: the punches of every job the
// filer reads (W3's paged reader), approved time off, the days already filed on this job, the
// sick days ("sick_days: staff select": every admin and foreman of the org reads them; without them
// my filed paid sick day would come back as 8 worked hours; `sheet` = the timesheet that recorded each,
// null when it is another foreman's, which RLS hides), and the days with hours on another job (_tsHoursElsewhere).
// The answer is kept per job. Offline, or
// when ANY read fails or outlasts the deadline: the kept copy, normalised (none kept: empty). Then this phone's own
// punches of the week the read may not have yet — landed since the last context read, the crew
// card's read of this job, the outbox — after the read's rows (the first copy of an id counts,
// contract §2.3); an outbox payload carries no author, so it is stamped as the filer's.
async function _tsClockLoad({ projectId, monday }) {
  const uid = _submitterId(), dates = window.Ordinex.timeClock.weekDates(monday), sunday = dates[6];
  const key = _tsClockCacheKey(uid, monday, projectId);
  let out = null;
  if (navigator.onLine) {
    try {
      const wait = q => _withNetTimeout(q, 'timesheet prefill');
      const [punches, off, filed, sick, elsewhere] = await Promise.all([
        _clockProjectPunches(null, monday, sunday),
        wait(sbClient.from('time_off_requests').select('id, worker_id, kind, days, status, day_hours')
          .eq('status', 'approved').overlaps('days', dates)),
        wait(sbClient.rpc('timesheet_filed_days', { p_project_id: projectId, p_week_of: monday })),
        wait(sbClient.from('sick_days').select('worker_id, sick_date, submission_id, sheet:submissions(project_name, foreman_id)')
          .gte('sick_date', monday).lte('sick_date', sunday)),
        _tsHoursElsewhere(projectId, monday, sunday),
      ]);
      if (punches.unread) throw new Error('the punches are unread');
      if (off.error || filed.error || sick.error) throw off.error || filed.error || sick.error;
      out = { punches, timeOff: off.data ?? [], filed: filed.data ?? [], sick: sick.data ?? [], elsewhere, at: new Date().toISOString() };
      try { localStorage.setItem(key, JSON.stringify(out)); _tsClockPrune(uid); } catch { /* storage full: this render still has it */ }
    } catch (e) {
      console.warn('[clock] the timesheet prefill read failed, the kept copy stands in:', e?.message ?? e);
      out = null;
    }
  }
  if (!out) {
    let c = {};
    try { c = JSON.parse(localStorage.getItem(key)) || {}; } catch { /* unreadable: none kept */ }
    out = { punches: c.punches ?? [], timeOff: c.timeOff ?? [], filed: c.filed ?? [], sick: c.sick ?? [], elsewhere: c.elsewhere ?? [], at: c.at ?? null };
  }
  const mine = { punched_by: uid, punched_by_name: state.foremanName, received_at: undefined };
  const queued = [..._clockLanded, ...(_crewRead.get(projectId) ?? []), ...((await window._outboxPendingClock?.())?.punches ?? [])]
    .filter(p => p.work_date >= monday && p.work_date <= sunday)
    .map(p => (p.punched_by ? p : { ...p, ...mine }));
  const seen = new Set(out.punches.map(p => p.id));
  for (const p of queued) if (!seen.has(p.id)) { seen.add(p.id); out.punches.push(p); }
  return out;
}

// What the sheet is filled from (state._clock), for the sheet's job and week: the week worked out
// from the punches — break and overtime over every job this filer reads, this job's typed cells
// standing for its clock days in the weekly 40 h (contract §2.4, project_id is a MUST) — and the
// sets rows and cells are matched on. The crew and the job sites come from the clock context.
// A block that runs on into another foreman's job is cut at that edge here (RLS), so each sheet
// may deduct its own break: accepted for v1, the admin Weeks view reads every job.
function _tsClockCtx(loaded) {
  const TC = window.Ordinex.timeClock, uid = _submitterId(), settings = TC.settingsFrom(window.currentOrg);
  const projectId = _tsClockProjectId(), monday = _tsClockMonday(), dates = TC.weekDates(monday);
  const cc = window.clockCtx ?? {};
  const jobs = Object.fromEntries((cc.projects ?? []).map(p => [p.id, p]));
  const sites = Object.fromEntries(Object.keys(jobs).map(id => [id, TC.siteOf(jobs[id], jobs)]));
  const dayKeys = Object.keys(emptyTsEmployee().days);
  const manualDays = (state.fields?.timesheet ?? []).filter(row => row.worker_id).flatMap(row => dates.map((date, i) => {
    // every typed cell, 0 h too: a typed 0 or SICK over a punched day replaces that day in the 40 h walk
    const c = row.days?.[dayKeys[i]];
    const hours = (Number(c?.r) || 0) + (Number(c?.ot) || 0);
    return c && _tsClockTyped(c) ? { worker_id: row.worker_id, work_date: date, project_id: projectId, hours } : null;
  })).filter(Boolean);
  const pair = r => `${r.worker_id}|${r.work_date}`;
  const filedOther = {};   // tested with `in`: a foreman with no name on record still locks his day
  for (const r of loaded.filed) if (r.foreman_id !== uid) filedOther[pair(r)] = r.foreman_name || 'another foreman';
  const crewIds = new Set((cc.crew ?? []).map(c => c.worker_id));
  const ids = new Set([...crewIds, ...loaded.punches.map(p => p.worker_id), ...loaded.filed.map(r => r.worker_id), ...loaded.timeOff.map(t => t.worker_id)]);
  const week = TC.weekFromPunches(loaded.punches, settings, { sites, manualDays, monday });
  // The days he worked on another job: hours filed there (_tsHoursElsewhere), or punched there — still in, or closed
  // with hours a sheet keeps. His approved time off that day is no time off on this sheet: he worked, and is paid there.
  const workedElsewhere = new Set(loaded.elsewhere);
  for (const [w, days] of Object.entries(week.byWorker)) {
    for (const [d, jobs] of Object.entries(days)) {
      if (Object.entries(jobs).some(([pid, day]) => pid !== projectId && (day.open || TC.hasSheetHours(day)))) workedElsewhere.add(`${w}|${d}`);
    }
  }
  return {
    week,
    punches: loaded.punches,   // what a cell's day sheet stands on offline (_tsOpenDaySheet)
    projectId, monday, dates, uid, crewIds,
    isAdmin: window.currentProfile?.role === 'admin',   // no crew of his own: he files the whole job
    // checked in or out by me ON THIS JOB (an undone punch does not count)
    punchedByMe: new Set(TC.effectivePunches(loaded.punches).filter(p => p.punched_by === uid && p.project_id === projectId).map(p => p.worker_id)),
    filed: loaded.filed,
    filedMineKeys: new Set(loaded.filed.filter(r => r.foreman_id === uid).map(pair)),
    filedOther,
    timeOff: loaded.timeOff,
    sickKeys: new Set(loaded.sick.map(s => `${s.worker_id}|${s.sick_date}`)),
    // sick days already on a sheet that is not mine on this job (my other job's, another foreman's):
    // paid there, so an approved sick request is not ticked again here. An admin's typed one has no sheet.
    sickElsewhere: new Set(loaded.sick.filter(s => s.submission_id && !(s.sheet?.foreman_id === uid && s.sheet?.project_name === state.project))
      .map(s => `${s.worker_id}|${s.sick_date}`)),
    workedElsewhere,
    statDates: new Set(settings.statHolidays),
    names: Object.fromEntries([...ids].map(id => [id, _clockWorkerName(id)])),
    dismissed: state._clockDismissed ?? new Set(),
    settings,
  };
}

// The sheet's rows and cells from a clock context, IN PLACE: the activity bar and its Split dialog
// hold this very array (push / splice, never a new one). Pure: no DOM, no network. removed: the
// indexes spliced out, highest first, for _reindexActivityWorkers.
function _tsClockPrefillSync(timesheet, ctx) {
  const TC = window.Ordinex.timeClock, { week, projectId, dates } = ctx;
  const dayKeys = Object.keys(emptyTsEmployee().days);
  const key = (w, date) => `${w}|${date}`;
  const hereOn = (w, date) => week.byWorker[w]?.[date]?.[projectId];
  const offOn = (w, date) => ctx.timeOff.find(t => t.worker_id === w && t.days?.includes(date));
  // The request as this sheet takes it: none on a day he worked on another job (workedElsewhere: he is paid there),
  // and a sick day another sheet already recorded is paid once, on the sheet signed first (a two-job foreman's crew
  // has the request on both jobs).
  // ponytail: two sheets open at once, or the first signed offline, both tick: the admin's "Sick day on 2 sheets" flag catches it.
  const offHere = (w, date) => {
    const t = offOn(w, date);
    return t && !ctx.workedElsewhere.has(key(w, date)) && !(t.kind === 'sick' && ctx.sickElsewhere.has(key(w, date))) ? t : undefined;
  };
  // A clock day as a cell carries it: the hint, the flags, the day sheet, and p_rows' start/end/break.
  const clockOf = (day, off) => ({ start: day.start, end: day.end, brk: day.breakMin, start24: _hhmm(day.startAt), end24: _hhmm(day.endAt),
    open: day.open, far: day.far, corrected: day.corrected, correctedBy: day.correctedBy, late: day.late, skew: day.skew, dup_in: day.dup_in,
    dup_out: day.dup_out, noBreak: day.noBreak, breakClosedAtOut: day.breakClosedAtOut, longShift: day.longShift,
    regular: day.regular, ot15: day.ot15, ot2: day.ot2, rule: day.rule, punch_ids: day.punch_ids, offConflict: off?.kind ?? null });
  // Punched on this job: my crew's — or, for an admin (no crew), anyone's: he files the whole job. A foreman never
  // gets another foreman's crew (decision D); a day another foreman filed is his `Filed by` cell either way.
  const wanted = w => ((ctx.isAdmin || ctx.crewIds.has(w)) && dates.some(d => hereOn(w, d)))
    || ctx.punchedByMe.has(w)
    || dates.some(d => ctx.filedMineKeys.has(key(w, d)))
    || (ctx.crewIds.has(w) && dates.some(d => offHere(w, d)));
  let changed = false;

  // 1. A row for every wanted worker the sheet lacks, by name; the lone untouched seed row makes way.
  const listed = new Set(timesheet.map(r => r.worker_id).filter(Boolean));
  const add = [...new Set([...ctx.crewIds, ...ctx.punchedByMe, ...ctx.filed.map(r => r.worker_id), ...Object.keys(week.byWorker)])]
    .filter(w => !listed.has(w) && !ctx.dismissed.has(w) && wanted(w))
    .sort((a, b) => (ctx.names[a] || '').localeCompare(ctx.names[b] || '') || a.localeCompare(b));
  const seed = timesheet.length === 1 && !timesheet[0].worker_id && !timesheet[0].name && !timesheet[0].notes
    && Object.values(timesheet[0].days ?? {}).every(c => c.r === '' && c.ot === '' && !c.sick);
  if (add.length && seed) timesheet.splice(0, 1);
  for (const w of add) timesheet.push({ ...emptyTsEmployee(), worker_id: w, name: ctx.names[w] || '', src: 'clock' });
  if (add.length) changed = true;

  // 2. Every cell the foreman did not type, worked out again from a clean slate.
  for (const row of timesheet) {
    const w = row.worker_id;
    if (!w) continue;
    dayKeys.forEach((k, i) => {
      const cell = row.days?.[k], date = dates[i], kk = key(w, date);
      if (!cell) return;
      const off = offOn(w, date), day = hereOn(w, date);
      if (_tsClockTyped(cell)) {   // his numbers stay; the clock hint he typed over follows the punches (a shift closed since is no longer OPEN)
        const clock = cell.clock && day ? clockOf(day, off) : undefined;
        if (!cell.clock || _tsClockSame(cell.clock, clock)) return;
        if (clock) cell.clock = clock; else delete cell.clock;
        changed = true;
        return;
      }
      let next = { r: '', ot: '', sick: false };
      if (ctx.filedMineKeys.has(kk)) {   // first: a day filed by me AND another foreman keeps my numbers
        next.src = 'filed';
        if (ctx.sickKeys.has(kk)) next.sick = true;
        else {
          const rows = ctx.filed.filter(f => f.foreman_id === ctx.uid && key(f.worker_id, f.work_date) === kk);
          next.r = String(TC.round1(rows.reduce((h, f) => h + Number(f.regular_hours), 0)));
          next.ot = String(TC.round1(rows.reduce((h, f) => h + Number(f.overtime_hours), 0)));
        }
        // The filed rows hold hours only: the day's punches give the times back, or the forced Replace
        // would store the day with no start/end/break. Still open: it blocks signing like any open shift.
        if (day) next.clock = clockOf(day, off);
      } else if (kk in ctx.filedOther) {
        next = _tsFiledByCell(ctx, date, ctx.filedOther[kk]);
      } else if (day) {   // the punch wins over approved time off, flagged
        next = { r: day.open ? '' : String(TC.round1(day.regular)), ot: day.open ? '' : String(TC.round1(day.ot15 + day.ot2)), sick: false, src: 'clock',
          clock: clockOf(day, off) };
      } else if (offHere(w, date) && !ctx.statDates.has(date)) {
        if (off.kind === 'sick') Object.assign(next, { sick: true, src: 'clock' });   // the clock's: a punch or a withdrawn request clears it
        else if (off.kind === 'vacation') next.off = 'vacation';
      }
      if (ctx.statDates.has(date) && next.off !== 'vacation') next.off = 'stat';   // a hint, the inputs stay free
      if (_tsClockSame(cell, next)) return;
      for (const p of Object.keys(cell)) delete cell[p];
      Object.assign(cell, next);
      changed = true;
    });
  }

  // 3. A clock row nobody touched whose worker is no longer wanted (another week, an undone punch) goes.
  const removed = [];
  for (let i = timesheet.length - 1; i >= 0; i--) {
    const r = timesheet[i];
    if (r.src !== 'clock' || r.notes || wanted(r.worker_id) || Object.values(r.days ?? {}).some(_tsClockTyped)) continue;
    timesheet.splice(i, 1);
    removed.push(i);
    changed = true;
  }
  return { changed, removed };
}

// Fills the open Weekly Timesheet from the clock; render and Refresh call it alike (online it always
// reads, offline the kept copy). True when the sheet changed. Never saves the draft. W3's one gate
// (the org loaded, the module on, the clock context known), another form, or a job with no id: it
// does nothing. An answer that lands after the Date, the job or the form moved is dropped: the
// move's own call fills the sheet.
async function _tsClockPrefill() {
  if (!_clockEnabled() || state.submissionType !== 'Weekly Timesheet') return false;
  const projectId = _tsClockProjectId(), monday = _tsClockMonday();
  if (!projectId) return false;
  const loaded = await _tsClockLoad({ projectId, monday });
  if (state.submissionType !== 'Weekly Timesheet' || !Array.isArray(state.fields?.timesheet)
    || _tsClockProjectId() !== projectId || _tsClockMonday() !== monday) return false;
  const ctx = _tsClockCtx(loaded);
  const { changed, removed } = _tsClockPrefillSync(state.fields.timesheet, ctx);
  removed.forEach(i => _reindexActivityWorkers(i));
  state._clock = ctx;
  return changed;
}

// A cell's day sheet (W3's). Offline that sheet stands on the crew card's kept read of the job, which
// a page that never showed the crew card does not have: the sheet's own read of this job stands in
// (never over the crew card's), so an open shift from the kept copy can still be closed with no signal.
function _tsOpenDaySheet(workerId, date, onSaved) {
  const pid = _tsClockProjectId();
  if (!pid || !_clockEnabled()) return;   // a draft filled while the module was on: no day sheet once it is off
  if (state._clock && !_crewRead.has(pid)) _crewRead.set(pid, state._clock.punches.filter(p => p.project_id === pid));
  openClockDaySheet(workerId, pid, date, { onSaved });
}

// ── Signing (W4): what the clock read means once the foreman signs ──
// The Date moved to another week and that week's prefill has not landed: the cells on screen are the
// old week's, so they neither force Replace nor block signing.
function _tsWeekStale() {
  return !!state._clock && state._clock.monday !== _tsClockMonday();
}

// Day indexes → 'Mon', 'Mon–Thu', 'Mon, Wed', 'Mon–Wed, Fri' (a run stays a run).
function _tsDayList(idxs) {
  const runs = [];
  for (const i of [...new Set(idxs)].sort((a, b) => a - b)) {
    const run = runs[runs.length - 1];   // the ES2018 way: the ES2022 helper is missing on iOS < 15.4
    if (run && i === run[1] + 1) run[1] = i; else runs.push([i, i]);
  }
  return runs.map(([a, b]) => (a === b ? TS_DAYS[a] : `${TS_DAYS[a]}–${TS_DAYS[b]}`)).join(', ');
}

// The days of my earlier filing on the sheet (FILED cells): the week is then replaced, never added to.
// A day of that filing the foreman typed over (src 'manual' now) is still in it: Add would pay it twice.
function _tsFiledDays() {
  if (_tsWeekStale()) return [];
  const keys = Object.keys(emptyTsEmployee().days), c = state._clock;
  return keys.flatMap((k, i) => ((state.fields?.timesheet ?? []).some(r => r.days?.[k]?.src === 'filed'
    || (c && r.worker_id && c.filedMineKeys.has(`${r.worker_id}|${c.dates[i]}`))) ? [i] : []));
}
function _tsFiledCells() { return _tsFiledDays().length > 0; }
function _tsFiledRange() { return _tsDayList(_tsFiledDays()); }

// The sheet's shifts still checked in: [{ name, day, worker_id, date }]. None with no clock read
// (W3's gate, G3) or a stale one.
function _tsOpenShifts() {
  const c = state._clock;
  if (!c || _tsWeekStale()) return [];
  const keys = Object.keys(emptyTsEmployee().days);
  return (state.fields?.timesheet ?? []).filter(r => r.worker_id).flatMap(r => keys.flatMap((k, i) =>
    (r.days?.[k]?.clock?.open ? [{ name: (r.name || '').trim(), day: TS_DAYS[i], worker_id: r.worker_id, date: c.dates[i] }] : [])));
}
// …as validateTimesheetWeek names them.
function _tsOpenCells() { return _tsOpenShifts().map(({ name, day }) => ({ name, day })); }

// Workers who clocked hours on this job this week and are not on the sheet: the ones removed with ×, and — a punch
// may land after the sheet was filled (the signing re-check reads it) — my crew, or anyone for an admin, with a
// worked day here that no other foreman filed. Never another foreman's crew. A worked day has closed hours of 0.1 h
// or more (an open shift counts 0 h): an in and out under 3 minutes apart is 0 h on any sheet, nothing to ask about,
// and a closed morning with the afternoon still open is asked about.
function _tsClockedMissing() {
  const c = state._clock;
  if (!c || _tsWeekStale()) return [];
  const listed = new Set((state.fields?.timesheet ?? []).map(r => r.worker_id).filter(Boolean));
  const gone = state._clockDismissed ?? new Set();
  const worked = (w, d) => window.Ordinex.timeClock.round1(c.week.byWorker[w]?.[d]?.[c.projectId]?.worked) > 0;
  return [...new Set([...gone, ...Object.keys(c.week.byWorker)])].filter(w => !listed.has(w) && c.dates.some(d => worked(w, d)
      && (gone.has(w) || ((c.isAdmin || c.crewIds.has(w)) && !(`${w}|${d}` in c.filedOther)))))
    .map(w => c.names[w] || 'A worker');
}

// The last look at who filed what, on entering step 4 and again at signing (spec §6.2/§9.3): a day
// on this sheet that another foreman filed since it was filled becomes his `Filed by` cell (emptied,
// never sent), with one notice per worker and foreman. A day I filed myself stays mine, as in the
// prefill. Online only, under the deadline: offline, a timeout or a failed read leaves the sheet as it
// is (the kept copy filled it). The Date moved and its prefill has not landed: the whole fill runs for
// the new week instead, with no notice. Nothing read the clock (module off, old job id, gate closed):
// nothing to check, no read at all. An answer that lands after the Date or the job moved is dropped.
async function _tsRecheckFiled() {
  if (!state._clock || !navigator.onLine) return [];
  const projectId = _tsClockProjectId(), monday = _tsClockMonday();
  if (!projectId) return [];
  let loaded;
  try { loaded = await _withNetTimeout(_tsClockLoad({ projectId, monday }), 'filed check'); } catch { return []; }
  if (state.submissionType !== 'Weekly Timesheet' || !Array.isArray(state.fields?.timesheet)
    || _tsClockProjectId() !== projectId || _tsClockMonday() !== monday) return [];
  try {   // a throw here would leave Submit on 'Checking the week…' (handleSubmit awaits this bare)
    return _tsRecheckApply(loaded);
  } catch (e) {
    console.warn('[clock] the filed check failed, the sheet stands as it is:', e?.message ?? e);
    return [];
  }
}
function _tsRecheckApply(loaded) {
  const ctx = _tsClockCtx(loaded);
  if (_tsWeekStale()) {
    const { removed } = _tsClockPrefillSync(state.fields.timesheet, ctx);
    removed.forEach(i => _reindexActivityWorkers(i));
    state._clock = ctx;
    _tsRerenderRows();
    return [];
  }
  const keys = Object.keys(emptyTsEmployee().days), hits = new Map();   // "row|foreman" → { name, other, days }
  state.fields.timesheet.forEach((row, ri) => {
    if (!row.worker_id) return;
    keys.forEach((k, i) => {
      const cell = row.days?.[k], kk = `${row.worker_id}|${ctx.dates[i]}`;
      if (!cell || cell.src === 'filed' || cell.filedBy || !(kk in ctx.filedOther) || ctx.filedMineKeys.has(kk)) return;
      const other = ctx.filedOther[kk];
      for (const p of Object.keys(cell)) delete cell[p];
      Object.assign(cell, _tsFiledByCell(ctx, ctx.dates[i], other));
      const g = `${ri}|${other}`;
      if (!hits.has(g)) hits.set(g, { name: (row.name || '').trim(), other, days: [] });
      hits.get(g).days.push(i);
    });
  });
  state._clock = ctx;
  if (hits.size) _tsRerenderRows();
  return [...hits.values()].map(h => `${h.name}'s ${_tsDayList(h.days)} were filed by ${h.other} — removed from your sheet`);
}

// Step 4: the shifts still open, under the Photos row, each with "Set end time" (the day sheet; its
// save fills the sheet again and redraws this). Removed when there is none.
function _tsRenderOpenShifts() {
  const open = _tsOpenShifts();
  let box = document.getElementById('tsOpenShifts');
  if (!open.length) { box?.remove(); return; }
  const photosRow = document.getElementById('reviewPhotos')?.parentElement;
  if (!photosRow) return;
  if (!box) {
    box = document.createElement('div');
    box.className = 'ts-open-shifts'; box.id = 'tsOpenShifts';
    photosRow.after(box);
  }
  box.innerHTML = '';
  const head = document.createElement('div');
  head.className = 'ts-open-shifts-head';
  head.textContent = `${open.length} shift${open.length === 1 ? '' : 's'} still open — set an end time before signing`;
  box.appendChild(head);
  for (const s of open) {
    const row = document.createElement('div');
    row.className = 'fx-row';
    const who = document.createElement('span');
    who.className = 'fx-row-name';
    who.textContent = `${s.name} · ${s.day}`;
    const btn = document.createElement('button');
    btn.type = 'button'; btn.className = 'fx-actbtn'; btn.textContent = 'Set end time';
    btn.addEventListener('click', () => _tsOpenDaySheet(s.worker_id, s.date, () => _tsClockPrefill().then(_tsRenderOpenShifts)));
    row.append(who, btn);
    box.appendChild(row);
  }
}

// The office approved (closed) the week this filing lands in. Only the submissions guard puts
// approved_at in its detail; replace_timesheet_week says the week only, so no date there.
function _tsWeekClosedMsg(err) {
  let d = null;
  try { d = JSON.parse(err?.details || '{}').approved_at; } catch { /* no date */ }
  const when = d ? ' on ' + new Date(d).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' }) : '';
  return 'This week was closed by the office' + when + '. Ask them to reopen it.';
}
// ── /Clock prefill ──

// ── Weekly Inspection Checklist ───────────────────────────────────────────────
function renderWeeklyInspection(field, container) {
  const config = metaFor(state.submissionType, 'inspection', INSPECTION_CONFIGS[state.submissionType]);
  if (!config) return;

  // Initialise state: each item gets 6 slots [null|'ok'|'x'] for MON–SAT
  if (!state.fields[field.id]) {
    const s = {};
    Object.entries(config.sections).forEach(([sec, items]) => {
      s[sec] = {};
      items.forEach(item => { s[sec][item] = new Array(6).fill(null); });
    });
    state.fields[field.id] = s;
  }

  // Legend notice
  const notice = document.createElement('div');
  notice.className = 'insp-notice';
  notice.innerHTML = '<strong>Tap each day to cycle:</strong>&nbsp; <span class="insp-ok-eg">✓</span> OK &nbsp;·&nbsp; <span class="insp-x-eg">✗</span> Fix needed &nbsp;·&nbsp; tap again to clear';
  container.appendChild(notice);

  Object.entries(config.sections).forEach(([secName, items]) => {
    // Section header
    const secHdr = document.createElement('div');
    secHdr.className = 'insp-section-header';
    secHdr.textContent = secName;
    container.appendChild(secHdr);

    const secBody = document.createElement('div');
    secBody.className = 'insp-section-body';

    // Day labels row
    const dayHdrRow = document.createElement('div');
    dayHdrRow.className = 'insp-day-hdr-row';
    const spacer = document.createElement('div');
    spacer.className = 'insp-item-spacer';
    dayHdrRow.appendChild(spacer);
    const dayHdrs = document.createElement('div');
    dayHdrs.className = 'insp-day-cells';
    INSP_DAYS.forEach(d => {
      const lbl = document.createElement('div');
      lbl.className = 'insp-day-lbl';
      lbl.textContent = d.substring(0, 3);
      dayHdrs.appendChild(lbl);
    });
    dayHdrRow.appendChild(dayHdrs);
    secBody.appendChild(dayHdrRow);

    items.forEach((item, itemIdx) => {
      const row = document.createElement('div');
      row.className = 'insp-item-row' + (itemIdx % 2 === 0 ? '' : ' insp-row-alt');

      const textDiv = document.createElement('div');
      textDiv.className = 'insp-item-text';
      textDiv.textContent = item;

      const cellsDiv = document.createElement('div');
      cellsDiv.className = 'insp-day-cells';

      INSP_DAYS.forEach((_, di) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        const val = state.fields[field.id][secName][item][di];
        btn.className = 'insp-toggle' + (val === 'ok' ? ' ok' : val === 'x' ? ' x' : '');
        btn.textContent = val === 'ok' ? '✓' : val === 'x' ? '✗' : '';
        btn.setAttribute('aria-label', `${INSP_DAYS[di]}: ${val || 'not checked'}`);

        btn.addEventListener('click', () => {
          const cur = state.fields[field.id][secName][item][di];
          const next = cur === null ? 'ok' : cur === 'ok' ? 'x' : null;
          state.fields[field.id][secName][item][di] = next;
          btn.className = 'insp-toggle' + (next === 'ok' ? ' ok' : next === 'x' ? ' x' : '');
          btn.textContent = next === 'ok' ? '✓' : next === 'x' ? '✗' : '';
        });

        cellsDiv.appendChild(btn);
      });

      row.appendChild(textDiv);
      row.appendChild(cellsDiv);
      secBody.appendChild(row);
    });

    container.appendChild(secBody);
  });
}

// ── Daily Inspection Checklist (single date — no day columns) ────────────────
function renderDailyInspection(field, container) {
  const config = metaFor(state.submissionType, 'inspection', INSPECTION_CONFIGS[state.submissionType]);
  if (!config) return;

  // Initialise state: each item gets a single value (null | 'ok' | 'x')
  // Always rebuild if structure doesn't match this form's sections (guards against stale state)
  const expectedSecs = Object.keys(config.sections);
  const existing = state.fields[field.id];
  if (!existing || !expectedSecs.every(k => k in existing)) {
    const s = {};
    Object.entries(config.sections).forEach(([sec, items]) => {
      s[sec] = {};
      items.forEach(item => { s[sec][item] = null; });
    });
    state.fields[field.id] = s;
  }

  // Regulatory notice (model: info-bordered card, ok-badge icon) — was only
  // ever drawn into the PDF; the foreman filling the form never saw it.
  const regText = typeof resolveRegNotice === 'function' ? resolveRegNotice(config) : (config.notice || '');
  if (regText) {
    const reg = document.createElement('div');
    reg.className = 'insp-reg-notice';
    reg.innerHTML = '<span class="insp-reg-notice-ic">' +
      '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg></span>' +
      _fEsc(regText);
    container.appendChild(reg);
  }

  // Legend notice (redesign v2: two round buttons per item — no cycling)
  const notice = document.createElement('div');
  notice.className = 'insp-notice';
  notice.innerHTML = '<span class="insp-ok-eg">✓</span> OK &nbsp;·&nbsp; <span class="insp-x-eg">✗</span> Fix needed &nbsp;·&nbsp; tap again to clear';
  container.appendChild(notice);

  Object.entries(config.sections).forEach(([secName, items]) => {
    const secHdr = document.createElement('div');
    secHdr.className = 'insp-section-header';
    secHdr.textContent = secName;
    container.appendChild(secHdr);

    const secBody = document.createElement('div');
    secBody.className = 'insp-section-body dinsp-body';

    items.forEach(item => {
      const row = document.createElement('div');
      row.className = 'dinsp-item-row';

      const textDiv = document.createElement('div');
      textDiv.className = 'insp-item-text';
      textDiv.textContent = item;

      // Two 34px round buttons (reference): pass = green fill, fail = red
      // fill; tapping the active one clears it. Same ok/x/null data as before.
      const okBtn = document.createElement('button');
      okBtn.type = 'button';
      okBtn.textContent = '✓';
      const xBtn = document.createElement('button');
      xBtn.type = 'button';
      xBtn.textContent = '✕';

      const sync = () => {
        const val = state.fields[field.id][secName][item];
        okBtn.className = 'dinsp-round dinsp-round--ok' + (val === 'ok' ? ' on' : '');
        xBtn.className  = 'dinsp-round dinsp-round--x'  + (val === 'x'  ? ' on' : '');
        okBtn.setAttribute('aria-pressed', String(val === 'ok'));
        xBtn.setAttribute('aria-pressed', String(val === 'x'));
        okBtn.setAttribute('aria-label', `${item}: OK`);
        xBtn.setAttribute('aria-label', `${item}: fix needed`);
      };
      okBtn.addEventListener('click', () => {
        const cur = state.fields[field.id][secName][item];
        state.fields[field.id][secName][item] = cur === 'ok' ? null : 'ok';
        sync();
      });
      xBtn.addEventListener('click', () => {
        const cur = state.fields[field.id][secName][item];
        state.fields[field.id][secName][item] = cur === 'x' ? null : 'x';
        sync();
      });
      sync();

      row.appendChild(textDiv);
      row.appendChild(okBtn);
      row.appendChild(xBtn);
      secBody.appendChild(row);
    });

    container.appendChild(secBody);
  });

  // Footer note — same string the PDF has always printed; now visible on the
  // form itself too.
  if (config.footerNote) {
    const footer = document.createElement('div');
    footer.className = 'insp-footer-note';
    footer.textContent = config.footerNote;
    container.appendChild(footer);
  }
}

// ── QAQC Table ────────────────────────────────────────────────────────────────
function renderQAQCTable(field, container) {
  // Item list: staged Field QAQC tables carry a stageKey into the qaqc_stages;
  // the original QAQC form falls back to the classic qaqc_items list.
  const _stages = metaFor(state.submissionType, 'qaqc_stages',
    typeof FIELD_QAQC_STAGES !== 'undefined' ? FIELD_QAQC_STAGES : []);
  const _stage = field.stageKey ? _stages.find(s => s.key === field.stageKey) : null;
  const ITEMS = _stage ? _stage.items : metaFor(state.submissionType, 'qaqc_items', QAQC_ITEMS);

  // Initialise state: array of { result: null|'pass'|'fail'|'na', comment: '' }
  // Also reinitialise if stale state from another form type is sitting there
  if (!Array.isArray(state.fields[field.id]) || state.fields[field.id].length !== ITEMS.length) {
    state.fields[field.id] = ITEMS.map(() => ({ result: null, comment: '' }));
  }

  // The walkthrough notice only makes sense once — show it on the classic
  // single-table form and on the first stage of the staged (ITP) form.
  if (!field.stageKey || field.stageKey === 'checklist_pre') {
    const notice = document.createElement('div');
    notice.className = 'qaqc-notice';
    notice.innerHTML = 'Inspect each item during the walkthrough. Mark <strong>PASS</strong>, <strong>FAIL</strong>, or <strong>N/A</strong>. A FAIL needs a comment describing the deficiency.';
    container.appendChild(notice);
  }

  // Toolbar: bulk actions + live progress. On a typical all-good walkthrough the
  // foreman taps "All PASS" once instead of 15 times, then overrides exceptions.
  const bar = document.createElement('div');
  bar.className = 'qaqc-toolbar';
  const allBtn = document.createElement('button');
  allBtn.type = 'button';
  allBtn.textContent = '✓ Mark all PASS';
  allBtn.className = 'qaqc-tool-btn qaqc-tool-btn--pass';
  const clrBtn = document.createElement('button');
  clrBtn.type = 'button';
  clrBtn.textContent = 'Clear';
  clrBtn.className = 'qaqc-tool-btn';
  const counter = document.createElement('span');
  counter.className = 'qaqc-counter';
  bar.appendChild(allBtn); bar.appendChild(clrBtn); bar.appendChild(counter);
  container.appendChild(bar);

  const rowRefreshers = [];
  const refreshCounter = () => {
    const rows = state.fields[field.id];
    const n = { pass: 0, fail: 0, na: 0 };
    rows.forEach(r => { if (r.result) n[r.result]++; });
    const done = n.pass + n.fail + n.na;
    counter.innerHTML = `${done}/${rows.length} inspected` +
      (n.fail ? ` · <span class="qaqc-counter-fail">${n.fail} FAIL</span>` : '') +
      (done === rows.length && !n.fail ? ' · <span class="qaqc-counter-ok">all good ✓</span>' : '');
  };

  allBtn.addEventListener('click', () => {
    state.fields[field.id].forEach(r => { r.result = 'pass'; });
    rowRefreshers.forEach(fn => fn());
    refreshCounter();
  });
  clrBtn.addEventListener('click', () => {
    state.fields[field.id].forEach(r => { r.result = null; });
    rowRefreshers.forEach(fn => fn());
    refreshCounter();
  });

  ITEMS.forEach((itemText, idx) => {
    const row = document.createElement('div');
    row.className = 'qaqc-row' + (idx % 2 === 0 ? '' : ' qaqc-row-alt');

    // Row number + text
    const header = document.createElement('div');
    header.className = 'qaqc-row-header';
    // itemText comes from qaqc_items/qaqc_stages meta, which an org admin can
    // author (trade-pack override or custom form) — escape before innerHTML.
    header.innerHTML = `<span class="qaqc-num">${idx + 1}</span><span class="qaqc-item-text">${_fEsc(itemText)}</span>`;
    row.appendChild(header);

    // PASS / FAIL / N/A buttons
    const btns = document.createElement('div');
    btns.className = 'qaqc-btns';

    // Comment input (declared early so the button refresh can highlight it on FAIL)
    const commentInput = document.createElement('input');

    const refreshRow = () => {
      btns.querySelectorAll('.qaqc-btn').forEach((b, bi) => {
        const bval = ['pass', 'fail', 'na'][bi];
        b.className = 'qaqc-btn qaqc-' + bval + (state.fields[field.id][idx].result === bval ? ' active' : '');
      });
      const failed = state.fields[field.id][idx].result === 'fail';
      commentInput.style.borderColor = failed && !commentInput.value.trim() ? '#ef4444' : '';
      commentInput.placeholder = failed ? 'Required — describe the deficiency…' : 'Comments / deficiency notes…';
    };
    rowRefreshers.push(refreshRow);

    ['pass', 'fail', 'na'].forEach(val => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.textContent = val === 'na' ? 'N/A' : val.toUpperCase();
      btn.className = 'qaqc-btn qaqc-' + val + (state.fields[field.id][idx].result === val ? ' active' : '');
      btn.addEventListener('click', () => {
        const cur = state.fields[field.id][idx].result;
        state.fields[field.id][idx].result = (cur === val) ? null : val;
        refreshRow();
        refreshCounter();
      });
      btns.appendChild(btn);
    });
    row.appendChild(btns);

    const commentWrap = document.createElement('div');
    commentWrap.className = 'qaqc-comment-wrap';
    commentInput.type = 'text';
    commentInput.className = 'qaqc-comment-input';
    commentInput.placeholder = 'Comments / deficiency notes…';
    commentInput.value = state.fields[field.id][idx].comment;
    commentInput.addEventListener('input', e => {
      state.fields[field.id][idx].comment = e.target.value;
      if (state.fields[field.id][idx].result === 'fail') {
        commentInput.style.borderColor = e.target.value.trim() ? '' : '#ef4444';
      }
    });
    commentWrap.appendChild(commentInput);
    row.appendChild(commentWrap);

    container.appendChild(row);
  });

  refreshCounter();
}

// ── Production Report Table ───────────────────────────────────────────────────
const PROD_DAYS    = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
// BLOCK_TYPES ('8"','4"','6"','10"','12"') and VENEER_TYPES ('Stone','Brick',
// 'Other') stood here until 2026-07-30. Both were dead — the Production Report
// has been driven by the org's own material_types since the form stopped being
// masonry-only — but they read like live defaults to anyone opening this file.

function emptyProdDays() {
  const d = {};
  PROD_DAYS.forEach(day => { d[day.toLowerCase()] = ''; });
  return d;
}

// Material catalog for the add-from-list production form. Loaded from
// material_types on first render; falls back to a hardcoded list if the DB
// call fails. Each item the foreman adds gets its own quantity + bricklayer
// + helper rows (so productivity can be computed per material later).
// Every production item the org has, UNFILTERED, kept in localStorage so a
// foreman with no signal still gets a list. The per-trade filter happens at
// read time (_prodCatalogList) rather than before caching, so moving to a
// project of another trade works offline too.
//
// This became load-bearing when the Production Report stopped being masonry-only
// (2026-07-30): masonry always had PROD_CATALOG_FALLBACK to fall back on, so the
// missing cache never showed. Every other trade would have been told "no
// production items set up yet" — with the items sitting right there in the
// database, unreachable.
let _prodCatalogAll = [];
let _prodCatalogFetched = false;
function _prodCacheKey() {
  const uid = window.currentUser?.id;
  return uid ? 'ordinex_prod_catalog_' + uid : null;   // per user: Renan hops between orgs on one device
}
function _prodReadCachedCatalog() {
  const k = _prodCacheKey();
  if (!k) return [];
  try { const r = JSON.parse(localStorage.getItem(k) || 'null'); return Array.isArray(r) ? r : []; }
  catch (_) { return []; }
}
const PROD_CATALOG_FALLBACK = [
  { name: '8" Block',     category: 'block',  unit: 'count' },
  { name: '4" Block',     category: 'block',  unit: 'count' },
  { name: '6" Block',     category: 'block',  unit: 'count' },
  { name: '10" Block',    category: 'block',  unit: 'count' },
  { name: '12" Block',    category: 'block',  unit: 'count' },
  { name: 'Veneer Stone', category: 'veneer', unit: 'sf' },
  { name: 'Veneer Brick', category: 'veneer', unit: 'sf' },
  { name: 'Veneer Other', category: 'veneer', unit: 'sf' },
  { name: 'Brick',        category: 'brick',  unit: 'count' },
  { name: 'Firestop',     category: 'firestop', unit: 'lf' },
];

// Human label for a catalog unit. 'count' stays blank (the day boxes already
// read as a quantity); the three legacy keys keep their uppercase form; anything
// else the admin typed — yd³, tonnes, each — is shown exactly as typed, since
// the unit is free text now (Rates & Defaults → Production Items).
function _prodUnitLbl(unit) {
  const u = (unit || '').trim();
  if (!u || u.toLowerCase() === 'count') return '';
  if (u.toLowerCase() === 'sf') return 'SF';
  if (u.toLowerCase() === 'lf') return 'LF';
  return u;
}

// The fallback is masonry, so it only serves a masonry org (cold offline boot,
// before material_types has been read). Handing blocks to a drywall or
// electrical crew was worse than handing them nothing: they would report
// against items that mean nothing for their trade. Everyone else gets an empty
// list and the message below it. Mirrors the guard on MASONRY_FALLBACK in
// js/forms/delivery-catalog.js.
//
// Trade-filtered + active catalog, BEFORE the per-project narrowing below.
// "active" here is the org-wide eye (Rates & Defaults → hide item) — except
// a project whose contract bills a hidden item by name still needs to show
// it: the eye means "we don't use this," the contract means "this project
// bills it," and the contract is the more specific, stronger signal. So an
// item stays in the base list if it's active OR the current project's
// contract names its id.
function _prodCatalogFull() {
  const trade = typeof activeTradePack === 'function' ? activeTradePack() : null;
  if (!_prodCatalogAll.length) _prodCatalogAll = _prodReadCachedCatalog();
  // Items seeded by another pack stay in the database (so re-adding that pack
  // restores the admin's edits) but must not reach the form. A NULL key is the
  // org's own item and always shows.
  const mine = _prodCatalogAll.filter(m => m.pack_key == null || m.pack_key === trade);
  const allowed = _prodAllowedMaterialIds();
  const base = mine.filter(m => m.active !== false || (m.id && allowed.has(m.id)));
  if (base.length) return base;
  return trade === 'masonry' ? PROD_CATALOG_FALLBACK : [];
}

// The project's reportable set: material types referenced by THIS project's
// contract quantity-lines (project_contract_lines.earn_method='quantity' →
// material_type_id, carried through project_task_names() as of the
// 2026-08-04 migration). A percent-earn line never carries a material_type_id
// (verified live), so no earn_method check is needed here — a non-null id
// already means "quantity line." window.projectTasks entries from a stale
// pre-migration cache simply lack the field, so they never add to this set —
// that's the "no filtering" degrade the fallback below relies on.
function _prodAllowedMaterialIds() {
  const tasks = (window.projectTasks || {})[state.project] || [];
  const ids = new Set();
  tasks.forEach(t => { if (t && t.material_type_id) ids.add(t.material_type_id); });
  return ids;
}

// The crew reads the CONTRACT LINE's name, not the production item's — the
// owner renames a line on the job ("8" Block wall") and the field follows,
// per project, without touching the org-wide catalogue that nine other jobs
// share. The rule lives in js/core/production-labels.js; this reads it fresh
// on every paint from window.projectTasks, so a rename in the office shows up
// on the next repaint with nothing to sync.
//
// STORED VALUES ARE UNTOUCHED: item.type stays the production item's real
// name, because submit resolves material_type_id by that exact string.
function _prodLabels() {
  const tasks = (window.projectTasks || {})[state.project] || [];
  return window.Ordinex.productionLabels.contractLabels(tasks, _prodCatalogFull());
}
function _prodLabel(itemName, labels) {
  return window.Ordinex.productionLabels.labelFor(labels || _prodLabels(), itemName);
}

// The PDF has no idea which job's contract is open, and generatePDF is a pure
// function of (state, photos) that must stay that way — so the label is
// stamped onto a COPY of the state just before it is drawn. A copy, not the
// draft: writing `label` into state.fields would push it through auto-save and
// into the offline record, where a name captured mid-week would go stale the
// moment the owner edited the contract line.
//
// The stamp itself is the right thing for a SIGNED document: the PDF keeps the
// name the line had on the day it was signed, which is what a record is for.
function _prodStateForPdf(st) {
  const items = st?.fields?.production?.items;
  if (!Array.isArray(items) || !items.length) return st;
  const labels = _prodLabels();
  return {
    ...st,
    fields: {
      ...st.fields,
      production: {
        ...st.fields.production,
        items: items.map(it => ({ ...it, label: _prodLabel(it.type, labels) })),
      },
    },
  };
}

// Per-field, per-project "show all" reveal. Session-only and deliberately
// NOT written into state.fields/state.drafts — tapping through is a UI
// choice about what the picker offers this visit, not data about the report.
const _prodShowAllKeys = new Set();
function _prodShowAllKey(fieldId) { return fieldId + '::' + (state.project || ''); }
function _prodShowAll(fieldId) { return _prodShowAllKeys.has(_prodShowAllKey(fieldId)); }
function _prodSetShowAll(fieldId) { _prodShowAllKeys.add(_prodShowAllKey(fieldId)); }

// The catalog actually offered to a given field: the full (trade+eye+contract)
// list, narrowed to the project's contract items — with two escape valves.
// 1) No narrowing applies at all (no project selected, no contract quantity-
//    lines for it, or the cached catalog predates `id` — old localStorage from
//    before this shipped): today's behavior, unfiltered, no toggle anywhere.
// 2) The foreman tapped "Show all items" for this field+project this session.
// Otherwise: filtered set UNION any item already on the current draft — a
// draft written before a contract changed (or before a CO added a line) never
// loses the hours it already has; only the ADD-MATERIAL picker narrows.
function _prodCatalogList(fieldId) {
  const full = _prodCatalogFull();
  const allowed = _prodAllowedMaterialIds();
  if (!allowed.size || !full.some(c => c.id)) return full;
  if (fieldId && _prodShowAll(fieldId)) return full;
  const draftNames = fieldId
    ? new Set((state.fields[fieldId]?.items || []).map(i => i.type))
    : new Set();
  return full.filter(c => (c.id && allowed.has(c.id)) || draftNames.has(c.name));
}

// Whether the "Show all items" control should be offered right now: true
// only when project-narrowing is actually removing something from view.
function _prodFilteringActive(fieldId) {
  const full = _prodCatalogFull();
  return _prodCatalogList(fieldId).length < full.length;
}

function emptyProdItem(typeName, unit) {
  // bricklayers/helpers were collected here too — removed in 2026-06-01 since
  // labor cost comes from time_entries (in-house) or per-block sub rates (sub),
  // so per-material headcount on the production report was redundant.
  return {
    type:  typeName,
    unit:  unit || 'count',
    qty:   emptyProdDays(),
  };
}

// ══════════════════════════════════════════════════════════════════════════════
//  EXTRA WORK ORDER (EWO) — FOREMAN-SIDE RENDERERS
//  Foreman only enters WHO worked + HOW MANY HOURS, and WHAT materials/equipment
//  were used + HOW MUCH. No prices, no rates, no $ anywhere. Admin fills the
//  pricing layer from the "Extra Work" admin tab.
// ══════════════════════════════════════════════════════════════════════════════

// The foreman picks materials from the SAME canonical list the admin prices
// (names + units only — no prices ever reach the foreman). This keeps the
// description text identical so the admin's price picker matches by name.
// Loaded live from the ewo_material_options() rpc (W9: guarded function —
// names/units only, never sell_rate), cached for offline, and backed by this
// fallback (mirrors the seed) when neither is available.
const EWO_FALLBACK_MATERIALS = [
  { name: '4" Block', unit: 'unit' },  { name: '6" Block', unit: 'unit' },
  { name: '8" Block', unit: 'unit' },  { name: '10" Block', unit: 'unit' },
  { name: '12" Block', unit: 'unit' }, { name: 'Brick', unit: 'unit' },
  { name: 'Veneer Brick', unit: 'sf' },{ name: 'Veneer Stone', unit: 'sf' },
  { name: 'Veneer Other', unit: 'sf' },{ name: 'Mortar', unit: 'bag' },
  { name: 'Blockfill', unit: 'bag' },
];
function _ewoEsc(s) {
  return String(s == null ? '' : s).replace(/[&<>"']/g,
    c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
function _ewoReadCachedMatOptions() {
  try { const r = JSON.parse(localStorage.getItem('ordinexEwoMatOptions') || 'null'); return Array.isArray(r) && r.length ? r : null; } catch (_) { return null; }
}
let _ewoMatOptions = _ewoReadCachedMatOptions();
// Same guard as _prodCatalogList / MASONRY_FALLBACK: the fallback list IS
// masonry, so it only serves a masonry org. A drywall or electrical crew
// writing an extra work order used to be offered 4"–12" block, brick veneer
// and mortar when the rate sheet had not loaded; they now get an empty list,
// which tells the truth.
function _ewoOptions() {
  if (_ewoMatOptions) return _ewoMatOptions;
  const trade = typeof activeTradePack === 'function' ? activeTradePack() : null;
  return trade === 'masonry' ? EWO_FALLBACK_MATERIALS : [];
}
async function _fetchEwoMatOptions() {
  try {
    const { data, error } = await sbClient.rpc('ewo_material_options');
    if (!error && Array.isArray(data) && data.length) {
      _ewoMatOptions = data.map(d => ({ name: d.name, unit: d.unit || '' }));
      try { localStorage.setItem('ordinexEwoMatOptions', JSON.stringify(_ewoMatOptions)); } catch (_) {}
      _populateEwoMatPicker();   // refresh the dropdown if it's on screen
    }
  } catch (_) { /* offline — cache / fallback already in use */ }
}
function _populateEwoMatPicker(sel) {
  sel = sel || document.getElementById('ewoMatPicker');
  if (!sel) return;
  const cur = sel.value;
  sel.innerHTML = '<option value="">+ Pick a material…</option>' +
    _ewoOptions().map(o => `<option value="${_ewoEsc(o.name)}">${_ewoEsc(o.name)}${o.unit ? ' (' + _ewoEsc(o.unit) + ')' : ''}</option>`).join('');
  sel.value = cur;
}

// Look up the next available EWO number for the currently selected project.
// Best-effort: failure leaves the placeholder visible — submit will retry.
async function _peekNextEwoNumber() {
  if (!state.project) return null;
  try {
    const { data: proj } = await sbClient.from('projects')
      .select('id').eq('name', state.project).maybeSingle();
    if (!proj?.id) return null;
    const { data, error } = await sbClient.rpc('next_ewo_number', { p_project_id: proj.id });
    if (error) { console.warn('peek ewo number:', error.message); return null; }
    return Number(data) || null;
  } catch (e) { console.warn('peek ewo number threw:', e); return null; }
}

function _ensureEwoState() {
  if (!Array.isArray(state.fields.ewo_labour))    state.fields.ewo_labour    = [];
  // Start empty — foreman adds the materials actually used via the picker.
  if (!Array.isArray(state.fields.ewo_materials)) state.fields.ewo_materials = [];
}

function _emptyEwoEmployee() {
  return { worker_id: '', worker_name: '', worker_trade: '',
           reg_hours: 0, ot_hours: 0, td_hours: 0 };
}

// ── Labour table — worker + hours only (no rate, no amount) ──────────────────
function renderEwoLabourTable(field, container) {
  _ensureEwoState();
  const header = document.createElement('div');
  header.className = 'section-header';
  header.textContent = field.label;
  container.appendChild(header);

  const body = document.createElement('div');
  body.className = 'section-body';

  const table = document.createElement('div');
  table.className = 'ewo-tbl';
  const thead = document.createElement('div');
  thead.className = 'ewo-tbl-head';
  thead.innerHTML = '<div>Worker</div><div style="text-align:center">Reg</div><div style="text-align:center">OT</div><div></div>';
  table.appendChild(thead);

  const rows = document.createElement('div');
  rows.id = 'ewoLabourRows';
  table.appendChild(rows);
  body.appendChild(table);

  const addBtn = document.createElement('button');
  addBtn.type = 'button';
  addBtn.className = 'ewo-add-btn';
  addBtn.textContent = '＋ Add worker';
  addBtn.onclick = () => {
    state.fields.ewo_labour.push(_emptyEwoEmployee());
    _renderEwoLabourRows();
  };
  body.appendChild(addBtn);

  // No rates or pricing here — office applies them from the Extra Work tab.
  const note = document.createElement('div');
  note.className = 'ewo-note';
  note.textContent = 'No rates or pricing here — admin fills those from the Extra Work tab.';
  body.appendChild(note);

  container.appendChild(body);
  _renderEwoLabourRows();
}

function _renderEwoLabourRows() {
  const wrap = document.getElementById('ewoLabourRows');
  if (!wrap) return;
  wrap.innerHTML = '';
  const list = state.fields.ewo_labour || [];
  if (!list.length) {
    const empty = document.createElement('div');
    empty.className = 'empty-hint';
    empty.textContent = 'No employees added yet. Tap "＋ Add worker" to start.';
    wrap.appendChild(empty);
  }
  list.forEach((row, i) => wrap.appendChild(_buildEwoLabourCard(row, i)));
}

// td_hours is kept as a legacy DB column (always 0 for new rows) — TD was a
// "Travel Day" premium from the Excel template that wasn't being used.
function _buildEwoLabourCard(row, index) {
  const rowEl = document.createElement('div');
  rowEl.className = 'ewo-tbl-row';

  const sel = document.createElement('select');
  sel.className = 'field-input ts-name-select ewo-tbl-sel';
  sel.appendChild(new Option(`— Select employee ${index + 1} —`, ''));
  _appendGroupedWorkerOptions(sel, w => {
    const o = new Option(w.trade ? `${w.name} — ${w.trade}` : w.name, w.id);
    o.dataset.name  = w.name || '';
    o.dataset.trade = w.trade || '';
    if (row.worker_id === w.id) o.selected = true;
    return o;
  });
  sel.addEventListener('change', () => {
    const opt = sel.selectedOptions[0];
    row.worker_id    = sel.value || '';
    row.worker_name  = opt?.dataset.name  || '';
    row.worker_trade = opt?.dataset.trade || '';
  });

  const mkNum = (key) => {
    const inp = document.createElement('input');
    inp.type = 'number'; inp.step = '0.25'; inp.min = '0';
    inp.className = 'field-input ewo-tbl-num';
    inp.value = row[key] || '';
    inp.addEventListener('input', () => { row[key] = Number(inp.value) || 0; });
    return inp;
  };

  const rm = document.createElement('button');
  rm.type = 'button';
  rm.className = 'crew-remove';
  rm.textContent = '×';
  rm.onclick = () => {
    state.fields.ewo_labour.splice(index, 1);
    _renderEwoLabourRows();
  };

  rowEl.appendChild(sel);
  rowEl.appendChild(mkNum('reg_hours'));
  rowEl.appendChild(mkNum('ot_hours'));
  rowEl.appendChild(rm);
  return rowEl;
}

// ── Materials table — description + qty + unit only ─────────────────────────
function renderEwoMaterialsTable(field, container) {
  _ensureEwoState();

  const header = document.createElement('div');
  header.className = 'section-header';
  header.textContent = field.label;
  container.appendChild(header);

  const body = document.createElement('div');
  body.className = 'section-body';

  const hint = document.createElement('div');
  hint.className = 'ewo-note';
  hint.innerHTML = 'Pick <strong>what was used</strong> and <strong>how much</strong>. Office will set vendor prices when they review.';
  body.appendChild(hint);

  // Refresh the canonical material names from the admin list (async; the
  // cached/fallback list shows immediately so the picker is never empty).
  _fetchEwoMatOptions();

  // Quick-add from the shared material list (keeps names matching the admin's)
  const pickRow = document.createElement('div');
  pickRow.style.cssText = 'display:flex;gap:8px;margin-bottom:12px';
  const sel = document.createElement('select');
  sel.id = 'ewoMatPicker';
  sel.className = 'field-input';
  sel.style.cssText = 'flex:1;min-width:0;padding:8px;font-size:15px';
  const addPick = document.createElement('button');
  addPick.type = 'button';
  addPick.className = 'btn-add';
  addPick.textContent = '+ Add';
  addPick.style.flex = '0 0 auto';
  addPick.onclick = () => {
    const name = sel.value; if (!name) return;
    const opt = _ewoOptions().find(o => o.name === name);
    state.fields.ewo_materials.push({ description: name, unit: opt?.unit || '', qty: 0, from_list: true, line_order: state.fields.ewo_materials.length });
    sel.value = '';
    _renderEwoMaterialRows();
  };
  pickRow.appendChild(sel);
  pickRow.appendChild(addPick);
  body.appendChild(pickRow);
  _populateEwoMatPicker(sel);

  const rows = document.createElement('div');
  rows.id = 'ewoMaterialRows';
  body.appendChild(rows);

  const addBtn = document.createElement('button');
  addBtn.type = 'button';
  addBtn.className = 'ewo-add-btn';
  addBtn.textContent = '+ Add other item (custom)';
  addBtn.onclick = () => {
    state.fields.ewo_materials.push({ description: '', qty: 0, unit: '', from_list: false, line_order: state.fields.ewo_materials.length });
    _renderEwoMaterialRows();
  };
  body.appendChild(addBtn);

  container.appendChild(body);
  _renderEwoMaterialRows();
}

function _renderEwoMaterialRows() {
  const wrap = document.getElementById('ewoMaterialRows');
  if (!wrap) return;
  wrap.innerHTML = '';
  (state.fields.ewo_materials || []).forEach((m, i) => wrap.appendChild(_buildEwoMaterialCard(m, i)));
}

function _buildEwoMaterialCard(m, index) {
  const card = document.createElement('div');
  card.className = 'crew-card';
  card.style.padding = '10px 12px';

  // Description line: read-only label for templates, editable for custom items
  const top = document.createElement('div');
  top.style.cssText = 'display:flex;align-items:center;gap:8px;margin-bottom:8px';
  if (m.from_list) {
    // Picked from the shared list — name is fixed so it matches the admin's list
    const desc = document.createElement('div');
    desc.style.cssText = 'font-weight:600;font-size:14px;flex:1';
    desc.textContent = m.description;
    top.appendChild(desc);
  } else {
    const inp = document.createElement('input');
    inp.type = 'text';
    inp.className = 'field-input';
    inp.placeholder = 'Description (e.g. Plywood sheets)';
    inp.style.cssText = 'padding:6px 8px;font-size:14px;flex:1';
    inp.value = m.description || '';
    inp.addEventListener('input', () => { m.description = inp.value; });
    top.appendChild(inp);
  }
  const rm = document.createElement('button');
  rm.type = 'button';
  rm.className = 'crew-remove';
  rm.textContent = '×';
  rm.onclick = () => {
    state.fields.ewo_materials.splice(index, 1);
    _renderEwoMaterialRows();
  };
  top.appendChild(rm);
  card.appendChild(top);

  // Qty + Unit (no rate, no amount)
  const grid = document.createElement('div');
  grid.style.cssText = 'display:grid;grid-template-columns:1fr 1.4fr;gap:8px;align-items:end';

  const qtyWrap = document.createElement('div');
  const qtyLbl = document.createElement('label');
  qtyLbl.style.cssText = 'font-size:10px;color:var(--text-muted);display:block;margin-bottom:3px;text-transform:uppercase';
  qtyLbl.textContent = 'Qty';
  const qtyInp = document.createElement('input');
  qtyInp.type = 'number'; qtyInp.step = '0.01'; qtyInp.min = '0';
  qtyInp.className = 'field-input';
  qtyInp.style.cssText = 'padding:8px;font-size:16px;text-align:center';
  qtyInp.value = m.qty != null ? m.qty : '';
  qtyInp.addEventListener('input', () => { m.qty = Number(qtyInp.value) || 0; });
  qtyWrap.appendChild(qtyLbl); qtyWrap.appendChild(qtyInp);
  grid.appendChild(qtyWrap);

  const unitWrap = document.createElement('div');
  const unitLbl = document.createElement('label');
  unitLbl.style.cssText = 'font-size:10px;color:var(--text-muted);display:block;margin-bottom:3px;text-transform:uppercase';
  unitLbl.textContent = 'Unit';
  const unitInp = document.createElement('input');
  unitInp.type = 'text';
  unitInp.className = 'field-input';
  unitInp.style.cssText = 'padding:8px;font-size:14px';
  unitInp.placeholder = 'each / bag / load / hr';
  unitInp.value = m.unit || '';
  unitInp.addEventListener('input', () => { m.unit = unitInp.value; });
  unitWrap.appendChild(unitLbl); unitWrap.appendChild(unitInp);
  grid.appendChild(unitWrap);

  card.appendChild(grid);
  return card;
}

function renderProductionTable(field, container) {
  const header = document.createElement('div');
  header.className = 'section-header';
  header.textContent = field.label;
  container.appendChild(header);

  const body = document.createElement('div');
  body.className = 'section-body';
  container.appendChild(body);

  if (!state.fields[field.id] || !Array.isArray(state.fields[field.id].items)) {
    state.fields[field.id] = { items: [] };
  }

  const addRow = document.createElement('div');
  addRow.className = 'prod-add-row';
  const addSelect = document.createElement('select');
  addSelect.className = 'field-input prod-add-select';
  addSelect.id = 'prodAddSelect';
  const addBtn = document.createElement('button');
  addBtn.type = 'button';
  addBtn.className = 'add-crew-btn';
  addBtn.textContent = '+ Add Material';
  const itemsWrap = document.createElement('div');
  itemsWrap.id = 'prodItemsWrap';

  addBtn.addEventListener('click', () => {
    const name = addSelect.value;
    if (!name) return;
    const cat = _prodCatalogList(field.id).find(c => c.name === name);
    state.fields[field.id].items.push(emptyProdItem(name, cat && cat.unit));
    _renderProdItems(field.id, itemsWrap, addSelect);
  });
  addRow.appendChild(addSelect);
  addRow.appendChild(addBtn);
  body.appendChild(addRow);
  body.appendChild(itemsWrap);

  (async () => {
    if (!_prodCatalogFetched) {
      try {
        // active is no longer filtered server-side: a contract-listed item
        // outranks the org-wide "eye" (see _prodCatalogFull), so an item the
        // admin hid can still need to show up on a project whose contract
        // names it. `id` is new too — it's what lets a project's contract
        // lines (material_type_id) match rows in this catalog at all.
        const { data, error } = await sbClient.from('material_types')
          .select('id, name, category, unit, sort_order, pack_key, active')
          .order('sort_order');
        // Cache the WHOLE list, not the trade-filtered one — see the note on
        // _prodCatalogAll. Filtering happens in _prodCatalogList().
        if (!error && Array.isArray(data)) {
          _prodCatalogAll = data;
          _prodCatalogFetched = true;
          const k = _prodCacheKey();
          if (k) { try { localStorage.setItem(k, JSON.stringify(data)); } catch (_) { /* quota */ } }
        }
      } catch (_) { /* offline — the last online run's cache is what we render */ }
    }
    _renderProdItems(field.id, itemsWrap, addSelect);
    _prodFetchDayStats();   // background: the outlier check's history, kept on the device
  })();
}

// Fill the "+ Add Material" dropdown via DOM (names contain " so innerHTML is unsafe)
function _populateProdAddSelect(fieldId, addSelect) {
  const used = new Set((state.fields[fieldId].items || []).map(i => i.type));
  const catalog = _prodCatalogList(fieldId);
  const avail = catalog.filter(c => !used.has(c.name));
  addSelect.innerHTML = '';
  const ph = document.createElement('option');
  ph.value = '';
  // Nothing to pick from at all: say why, instead of an empty dropdown the
  // foreman would keep tapping. The admin sets the list on Rates & Defaults.
  ph.textContent = catalog.length ? '+ choose material...'
                                  : 'No production items set up yet — ask your admin';
  addSelect.disabled = !catalog.length;
  addSelect.appendChild(ph);
  // value stays the production item's own name — it is the key submit resolves
  // the material_type_id by. Only the words the foreman reads change.
  const labels = _prodLabels();
  avail.forEach(c => {
    const o = document.createElement('option');
    o.value = c.name;
    // In words — "(square feet)", not "(SF)"; a counted item adds nothing.
    const u = _prodUnitLbl(c.unit) && window.Ordinex.productionOutliers.unitWords(c.unit, window.Ordinex.i18n.t).name;
    o.textContent = _prodLabel(c.name, labels) + (u ? ` (${u})` : '');
    addSelect.appendChild(o);
  });
  addSelect.disabled = avail.length === 0;
}

function _renderProdItems(fieldId, wrap, addSelect) {
  const items = state.fields[fieldId].items || [];
  wrap.innerHTML = '';
  if (!items.length) {
    wrap.innerHTML = '<div class="prod-empty">No materials added yet. Pick one above and tap "+ Add Material".</div>';
  } else {
    // No "Total this week" across items: 300 blocks + 120 sq ft is not 420 of
    // anything. Each card carries its own total, in its own unit.
    items.forEach((_, idx) => _renderProdItemCard(fieldId, wrap, idx, addSelect));
  }
  _populateProdAddSelect(fieldId, addSelect);

  // Escape hatch, at the end of the list: only when the project's contract
  // is actually narrowing this org's catalog right now. Tapping this is the
  // signal a change order might be missing — the foreman needed to report
  // against a material the contract doesn't (yet) list. Kept discreet on
  // purpose: this is a rare, deliberate override, not a normal control.
  if (_prodFilteringActive(fieldId)) {
    const showAllBtn = document.createElement('button');
    showAllBtn.type = 'button';
    showAllBtn.className = 'prod-showall-btn';
    showAllBtn.textContent = 'Show all items';
    showAllBtn.addEventListener('click', () => {
      _prodSetShowAll(fieldId);
      _renderProdItems(fieldId, wrap, addSelect);
    });
    wrap.appendChild(showAllBtn);
  }
}

function _prodItemWeekTotal(item) {
  return PROD_DAYS.reduce((sum, day) => sum + (parseFloat(item.qty[day.toLowerCase()]) || 0), 0);
}

function _renderProdItemCard(fieldId, wrap, idx, addSelect) {
  const item = state.fields[fieldId].items[idx];
  const card = document.createElement('div');
  card.className = 'prod-block-card';

  // The unit in words, beside the name: "SF" in a 9px chip read as nothing,
  // and a week of brick COUNTS went into a square-foot item (2026-10-30).
  const PO = window.Ordinex.productionOutliers;
  const uw = PO.unitWords(item.unit, window.Ordinex.i18n.t);
  const head = document.createElement('div');
  head.className = 'prod-block-label-row';
  const title = document.createElement('span');
  title.className = 'prod-block-type';
  title.textContent = _prodLabel(item.type) + ' ';
  const weekTotal = document.createElement('span');
  weekTotal.className = 'prod-week-total';
  const _niceTot = n => (n % 1 === 0 ? n : n.toFixed(1)) + (uw.short ? ' ' + uw.short : '');
  weekTotal.textContent = _niceTot(_prodItemWeekTotal(item));
  title.appendChild(weekTotal);
  const unitWordsEl = document.createElement('span');
  unitWordsEl.className = 'prod-unit-words';   // its own line, under the name
  unitWordsEl.textContent = PO.unitPhrase(item.unit, window.Ordinex.i18n.t);
  title.appendChild(unitWordsEl);
  const removeBtn = document.createElement('button');
  removeBtn.type = 'button';
  removeBtn.className = 'crew-remove';
  removeBtn.textContent = 'x';
  removeBtn.addEventListener('click', () => {
    state.fields[fieldId].items.splice(idx, 1);
    _renderProdItems(fieldId, wrap, addSelect);
  });
  head.appendChild(title);
  head.appendChild(removeBtn);
  card.appendChild(head);

  // Only quantity per day — bricklayer/helper headcount was removed; cost comes
  // from time_entries (in-house) or sub rates × production (sub).
  const subs = [
    // Whole numbers when the item is counted, decimals when it is measured.
    ['qty', window.Ordinex.i18n.t('prodUnit.perDay', { name: uw.name }), _prodUnitLbl(item.unit) ? '0.1' : '1'],
  ];
  subs.forEach(function(s) {
    const key = s[0], label = s[1], step = s[2];
    const subLbl = document.createElement('div');
    subLbl.className = 'prod-sub-label';
    subLbl.textContent = label;
    card.appendChild(subLbl);

    const grid = document.createElement('div');
    grid.className = 'ts-days-grid';
    PROD_DAYS.forEach(day => {
      const dayKey = day.toLowerCase();
      const col = document.createElement('div');
      col.className = 'ts-day-col';
      const dl = document.createElement('div');
      dl.className = 'ts-day-label';
      dl.textContent = day.toUpperCase();
      const inp = document.createElement('input');
      inp.className = 'field-input ts-hours-input prod-block-input';
      // The unit sits inside the box itself until a number goes in.
      inp.type = 'number'; inp.min = '0'; inp.step = step; inp.placeholder = uw.short || '0';
      inp.setAttribute('aria-label', `${day} — ${uw.name}`);
      inp.dataset.prodDay = idx + ':' + dayKey;   // the outlier sheet's "Fix it" lands here
      inp.value = item[key][dayKey] || '';
      inp.addEventListener('input', e => {
        state.fields[fieldId].items[idx][key][dayKey] = e.target.value;
        weekTotal.textContent = _niceTot(_prodItemWeekTotal(state.fields[fieldId].items[idx]));
      });
      col.appendChild(dl); col.appendChild(inp);
      grid.appendChild(col);
    });
    card.appendChild(grid);
  });

  wrap.appendChild(card);
}

// ── Production Report: is this day's number believable? ─────────────────────
// Per item, the company's day history ({ n, max, median }) — read once per
// session online and kept on the device, so the check below works with no
// signal. Company-wide via production_item_day_stats() (a foreman's RLS shows
// only his own reports); on a database without that function, whatever this
// account can read: every report for an admin, his own for a foreman.
let _prodStatsFetched = false;
function _prodStatsKey() {
  const uid = window.currentUser?.id;
  return uid ? 'ordinex_prod_daystats_' + uid : null;
}
function _prodReadDayStats() {
  const k = _prodStatsKey();
  if (!k) return null;
  try { const r = JSON.parse(localStorage.getItem(k) || 'null'); return r && r.byId ? r : null; }
  catch (_) { return null; }
}
async function _prodFetchDayStats() {
  const k = _prodStatsKey();
  if (_prodStatsFetched || !k || navigator.onLine === false) return;
  try {
    const byId = {};
    let scope = 'company';
    const { data, error } = await _withNetTimeout(sbClient.rpc('production_item_day_stats'), 'production history');
    if (!error && Array.isArray(data)) {
      data.forEach(r => { byId[r.material_type_id] = { n: Number(r.n) || 0, max: Number(r.max_qty) || 0, median: Number(r.median_qty) || 0 }; });
    } else {
      const qtys = {};
      for (let from = 0; ; from += 1000) {
        const { data: page, error: e2 } = await _withNetTimeout(sbClient.from('production_line_items')
          .select('material_type_id, quantity').gt('quantity', 0).order('id').range(from, from + 999), 'production history');
        if (e2 || !Array.isArray(page)) return;      // cannot tell — keep the last kept copy
        page.forEach(r => { (qtys[r.material_type_id] ||= []).push(r.quantity); });
        if (page.length < 1000) break;
      }
      Object.keys(qtys).forEach(id => { byId[id] = window.Ordinex.productionOutliers.dayStats(qtys[id]); });
      scope = window.currentProfile?.role === 'admin' ? 'company' : 'own';
    }
    _prodStatsFetched = true;
    try { localStorage.setItem(k, JSON.stringify({ scope, byId })); } catch (_) { /* quota */ }
  } catch (_) { /* no signal / timeout — the kept copy answers */ }
}

// Every day box of the report that stands out against its item's history.
function _prodOutliers() {
  const PO = window.Ordinex?.productionOutliers;
  const kept = _prodReadDayStats();
  const items = state.fields?.production?.items;
  if (!PO || !kept || !Array.isArray(items)) return [];
  const byName = {};
  (_prodCatalogAll.length ? _prodCatalogAll : _prodReadCachedCatalog()).forEach(c => { byName[c.name] = c; });
  const hits = [];
  items.forEach((it, idx) => {
    const stat = kept.byId[byName[it?.type]?.id];
    PROD_DAYS.forEach(day => {
      const dayKey = day.toLowerCase();
      const qty = parseFloat(it?.qty?.[dayKey]) || 0;
      const why = PO.outlierOf(qty, stat);
      if (why) hits.push({ idx, day, dayKey, qty, why, stat, item: it, scope: kept.scope });
    });
  });
  return hits;
}

// Asked at Next and again at Submit, never refused. "It's right" holds for
// these exact numbers; change one and it asks again.
async function _prodOutlierGate() {
  if (state.submissionType !== 'Production Report') return true;
  const hits = _prodOutliers();
  if (!hits.length) return true;
  const sig = hits.map(h => h.idx + ':' + h.dayKey + ':' + h.qty).join('|');
  if (state._prodOutlierOk === sig) return true;
  if (await _prodOutlierSheet(hits)) { state._prodOutlierOk = sig; return true; }
  // Fix it: back on the form, on the first box in question.
  const first = hits[0];
  if (state.currentStep !== 3) _handleBack();
  setTimeout(() => {
    const inp = document.querySelector(`[data-prod-day="${first.idx}:${first.dayKey}"]`);
    if (!inp) return;
    inp.scrollIntoView({ behavior: _prefersReducedMotion() ? 'auto' : 'smooth', block: 'center' });
    inp.focus({ preventScroll: true });
    inp.classList.add('field-flash');
    setTimeout(() => inp.classList.remove('field-flash'), 2000);
  }, 60);
  return false;
}

// Same sheet shape as the week question (_weekModeSheet). Escape and the
// backdrop mean "Fix it" — backing out must never send the number.
function _prodOutlierSheet(hits) {
  const t = window.Ordinex.i18n.t;
  const PO = window.Ordinex.productionOutliers;
  return new Promise(resolve => {
    const wrap = document.createElement('div');
    wrap.className = 'delivery-modal-overlay';
    wrap.style.cssText = 'display:flex;z-index:10002';
    wrap.innerHTML = `
      <div class="delivery-modal" style="max-width:440px;width:calc(100vw - 24px)" role="dialog" aria-modal="true" aria-labelledby="prodOutlierTitle">
        <div class="delivery-modal-header">
          <h3 id="prodOutlierTitle" style="margin:0"></h3>
        </div>
        <div class="prod-outlier-list" style="padding:16px 20px 4px;line-height:1.5"></div>
        <div style="display:flex;flex-direction:column;gap:10px;padding:8px 20px 20px">
          <button type="button" class="btn btn-next" data-pick="fix" style="width:100%"></button>
          <button type="button" class="btn" data-pick="ok" style="width:100%"></button>
        </div>
      </div>`;
    wrap.querySelector('#prodOutlierTitle').textContent = t('prodOutlier.title');
    const list = wrap.querySelector('.prod-outlier-list');
    hits.forEach(h => {
      const p = document.createElement('p');
      p.style.margin = '0 0 10px';
      p.textContent = t('prodOutlier.day', { day: h.day, msg: PO.outlierMsg({
        qty: h.qty, item: _prodLabel(h.item.type), unit: h.item.unit, stat: h.stat, why: h.why, scope: h.scope }, t) });
      list.appendChild(p);
    });
    wrap.querySelector('[data-pick="fix"]').textContent = t('prodOutlier.fix');
    wrap.querySelector('[data-pick="ok"]').textContent = t('prodOutlier.ok');
    const done = (val) => {
      document.removeEventListener('keydown', onKey, true);
      wrap.remove();
      resolve(val);
    };
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); done(false); } };
    document.addEventListener('keydown', onKey, true);
    // A gloved double tap on Next/Submit lands its second tap on this sheet:
    // ignore taps for a moment so the question is read, not bounced.
    const openedAt = Date.now();
    wrap.addEventListener('click', (e) => {
      if (Date.now() - openedAt < 400) return;
      if (e.target === wrap) { done(false); return; }
      const b = e.target.closest('[data-pick]');
      if (b) done(b.dataset.pick === 'ok');
    });
    document.body.appendChild(wrap);
    wrap.querySelector('[data-pick="fix"]').focus();
  });
}

// Production Report: the Monday of a week that has quantities on a day that
// has not happened yet, or null. (2026-09-21: filed on a Monday with Tuesday-
// to-Saturday numbers because the Date was still today.)
function _prodFutureWeek() {
  if (state.submissionType !== 'Production Report') return null;
  const ws = _weekStartFor(state.date), today = _todayLocal();
  const items = Array.isArray(state.fields?.production?.items) ? state.fields.production.items : [];
  const future = PROD_DAYS.some((d, i) => _dateFromWeekStart(ws, i) > today
    && items.some(it => (parseFloat(it?.qty?.[d.toLowerCase()]) || 0) > 0));
  return future ? _dateFromWeekStart(ws, 0) : null;
}

// ── Photo Upload ──────────────────────────────────────────────────────────────
function setupPhotoUpload() {
  const zone = document.getElementById('photoZone');
  const input = document.getElementById('photoInput');
  if (!zone || !input) return;

  zone.addEventListener('click', () => input.click());
  zone.addEventListener('dragover', e => { e.preventDefault(); zone.classList.add('drag-over'); });
  zone.addEventListener('dragleave', () => zone.classList.remove('drag-over'));
  zone.addEventListener('drop', e => { e.preventDefault(); zone.classList.remove('drag-over'); addPhotos(Array.from(e.dataTransfer.files)); });
  input.addEventListener('change', () => { addPhotos(Array.from(input.files)); input.value = ''; });
  renderPhotoPreview();
}

async function addPhotos(files) {
  const accepted = files.filter(f => f.type.startsWith('image/'));
  const compressed = await Promise.all(
    accepted.map(f => window.Ordinex.image.compress(f))
  );
  state.photos = [...state.photos, ...compressed];
  renderPhotoPreview();
}

function renderPhotoPreview() {
  const preview = document.getElementById('photoPreview');
  const countEl = document.getElementById('photoCount');
  if (!preview) return;
  preview.innerHTML = '';
  if (countEl) countEl.textContent = state.photos.length > 0 ? `${state.photos.length} photo${state.photos.length !== 1 ? 's' : ''} selected` : '';
  state.photos.forEach((file, i) => {
    const item = document.createElement('div');
    item.className = 'photo-thumb';
    const img = document.createElement('img');
    img.src = URL.createObjectURL(file);
    const remove = document.createElement('button');
    remove.className = 'photo-remove';
    remove.textContent = '×';
    remove.addEventListener('click', e => { e.stopPropagation(); state.photos.splice(i, 1); renderPhotoPreview(); });
    item.appendChild(img);
    item.appendChild(remove);
    preview.appendChild(item);
  });

  if (typeof _renderFormRail === 'function') _renderFormRail(state.currentStep);
}

// ── Step 4: Review ────────────────────────────────────────────────────────────
// The Done screen is painted OVER the step-4 panel (showSuccessScreen replaces
// its innerHTML), which throws away the review summary and #reviewSignArea for
// the rest of the session. The second form then reached step 4 with nowhere to
// sign while the submit gate kept demanding a signature. Snapshot the pristine
// markup the first time step 4 renders, and put it back whenever the sign area
// is missing.
let _step4Pristine = null;
function _restoreStep4Panel() {
  const panel = document.getElementById('step4');
  if (!panel) return;
  if (_step4Pristine === null && document.getElementById('reviewSignArea')) {
    _step4Pristine = panel.innerHTML;
    return;
  }
  if (_step4Pristine !== null && !document.getElementById('reviewSignArea')) {
    panel.innerHTML = _step4Pristine;
  }
}

async function renderStep4() {
  _restoreStep4Panel();
  const typeInfo = SUBMISSION_TYPES.find(t => t.id === state.submissionType);
  document.getElementById('reviewForeman').textContent = state.foremanName;
  // Read as a date, not as a database value — the PDF this becomes has said
  // "Thu, Aug 13" all along and the screen above it said "2026-08-13".
  document.getElementById('reviewDate').textContent = state.date
    ? new Date(state.date + 'T12:00').toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' })
    : '—';
  document.getElementById('reviewProject').textContent = state.project;
  document.getElementById('reviewType').textContent = state.submissionType;
  document.getElementById('reviewPhotos').textContent = state.photos.length > 0 ? `${state.photos.length} photo(s)` : 'No photos';
  const safeName  = (state.submissionType || '').replace(/\s+/g, '-');
  const safeProj  = (state.project || '').replace(/[^a-zA-Z0-9\-_ ]/g, '').trim().replace(/\s+/g, '-');
  const pdfName   = `${state.date}_${safeName}_${safeProj}.pdf`;
  const pathEl    = document.getElementById('reviewPdfName');
  if (pathEl) { pathEl.textContent = pdfName; pathEl.style.opacity = '1'; }

  // Sign-off pads (foreman + GC when the form has one) render below the
  // summary — the model's "check it, sign it" order.
  _renderReviewSignArea();

  // A clocked Weekly Timesheet (W4): the shifts still open, and — once per entry, not on this
  // step's own redraws — the filed re-check, so another foreman's days show BEFORE he signs.
  _tsRenderOpenShifts();
  if (state.submissionType === 'Weekly Timesheet' && state._clock && state._clockRechecked !== _tsClockMonday()) {
    state._clockRechecked = _tsClockMonday();
    _tsRecheckFiled().then(notices => { notices.forEach(n => showToast(n, 'warning')); if (notices.length) _tsRenderOpenShifts(); });
  }

  // ── Load CSO contact + render send-to-safety toggle ──────────────────────
  state._csoContact = null;
  let csoUnread = navigator.onLine === false;   // couldn't ask: never claim the project has none
  if (!csoUnread) {
    try {
      const { data, error } = await _withNetTimeout(sbClient
        .from('project_safety_contacts')
        .select('cso_email, cso_name')
        .eq('project_name', state.project)
        .maybeSingle(), 'safety contact');
      if (error) csoUnread = true;
      state._csoContact = data || null;
    } catch (e) { csoUnread = true; }
  }

  const toggleWrap = document.getElementById('reviewSafetyToggle');
  if (toggleWrap && !_isSafetyEmailForm(state.submissionType)) {
    // Non-safety forms never email the safety officer — hide the toggle entirely.
    toggleWrap.innerHTML = '';
  } else if (toggleWrap) {
    if (state._csoContact) {
      // cso_name/cso_email are admin-authored (Safety Contacts tab) — escape
      // before both the title attribute and the text node.
      const who = _fEsc([state._csoContact.cso_name, state._csoContact.cso_email].filter(Boolean).join(' · '));
      toggleWrap.innerHTML = `
        <div style="display:flex;align-items:center;gap:12px;padding:12px 14px;
          background:rgba(248,144,16,.08);border:1px solid rgba(248,144,16,.25);
          border-radius:10px;">
          <label style="display:flex;align-items:center;gap:8px;cursor:pointer;flex:1;user-select:none;">
            <input type="checkbox" id="sendToSafetyChk" ${state.sendToSafety ? 'checked' : ''}
              style="width:16px;height:16px;accent-color:var(--accent,#F89010);cursor:pointer;flex-shrink:0;">
            <span style="font-size:13px;font-weight:600;color:var(--text);">📧 Send to Safety Officer</span>
          </label>
          <span style="font-size:11px;color:var(--text-muted);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:180px;"
            title="${who}">${who}</span>
        </div>`;
      document.getElementById('sendToSafetyChk').addEventListener('change', e => {
        state.sendToSafety = e.target.checked;
      });
    } else {
      // The PDF reaches the office either way: say so, in words for this role. Only an
      // admin can add the contact (Safety & Company Docs) — a new tab, so the signing survives.
      const t = window.Ordinex.i18n.t;
      const add = !csoUnread && window.currentProfile?.role === 'admin'
        ? `<a href="admin.html#safety" target="_blank" rel="noopener" style="display:flex;align-items:center;min-height:44px;width:fit-content;font-weight:600;color:var(--accent-ink);">${_fEsc(t('cso.addLink'))}</a>`
        : '';
      toggleWrap.innerHTML = `
        <div style="padding:10px 14px;background:rgba(245,158,11,.08);border:1px solid rgba(245,158,11,.25);
          border-radius:10px;font-size:12px;color:var(--text-muted);">
          ${_fEsc(t(csoUnread ? 'cso.unknown' : 'cso.none'))}${add}
        </div>`;
    }
  }

  const summary = document.getElementById('reviewFieldSummary');
  if (!summary) return;
  summary.innerHTML = '';
  const fields = fieldsFor(state.submissionType);
  const skipTypes = ['tailgate-items','flra-table','crew-signin','signature'];
  fields.filter(f => !skipTypes.includes(f.type)).forEach(f => {
    const text = _fieldSummaryText(f, state.fields[f.id]);
    if (text == null) return;
    const row = document.createElement('div');
    row.className = 'review-row';
    // f.label can be an org-admin-authored custom-form label; text can be
    // whatever the foreman typed into a free-text field. Both need escaping —
    // every other field.label render in this file uses .textContent (safe by
    // construction), this review row was the one spot still using innerHTML.
    row.innerHTML = `<span class="review-label">${_fEsc(f.label)}</span><span class="review-value">${_fEsc(text)}</span>`;
    summary.appendChild(row);
  });
  if (summary.children.length === 0) summary.innerHTML = '<p class="muted">Form ready to submit.</p>';
}

// One-line summary of a field's value for the review step, or null to skip.
// Structured values (checklist/table/qaqc rows) get counts instead of the
// old [object Object] join.
function _fieldSummaryText(field, val) {
  if (val == null || val === '' || (Array.isArray(val) && val.length === 0)) return null;
  // Production table: one human line per material with its week total —
  // "Steel Stud Walls — 205 LF". The raw value is { items: [...] }, which used
  // to fall through to String(val) and print "[object Object]".
  if (field.type === 'production-table') {
    const items = Array.isArray(val.items) ? val.items : [];
    const lines = items.map(it => {
      const tot = PROD_DAYS.reduce((s, d) => s + (parseFloat(it.qty?.[d.toLowerCase()]) || 0), 0);
      const u = window.Ordinex.productionOutliers.unitWords(it.unit, window.Ordinex.i18n.t).short;   // "sq ft", "pcs"
      // Trim float noise from decimal units (0.1 steps): 2 decimals, no tail zeros.
      const qty = Number.isInteger(tot) ? tot : Number(tot.toFixed(2));
      return `${it.type} — ${qty}${u ? ' ' + u : ''}`;
    });
    return lines.length ? lines.join(' · ') : 'No production entered';
  }
  if (field.type === 'checklist') {
    const rows = Array.isArray(val) ? val : [];
    const n = rows.filter(r => r && r.answer).length;
    return n ? `${n}/${rows.length} answered` : null;
  }
  if (field.type === 'table') {
    const rows = (Array.isArray(val) ? val : [])
      .filter(r => r && Object.values(r).some(v => String(v || '').trim() !== ''));
    return rows.length ? `${rows.length} row(s)` : null;
  }
  if (Array.isArray(val)) {
    return val.some(v => typeof v === 'object' && v !== null)
      ? `${val.length} item(s)`
      : val.join(', ');
  }
  return String(val);
}

// ── Review & sign (step 4) — signature pads + submit gate ───────────────────
// The pads render inside #reviewSignArea on every visit to step 4. Signature
// data survives round-trips via state.signature/.gcSignature (fromDataURL).
function _renderReviewSignArea() {
  const area = document.getElementById('reviewSignArea');
  if (!area) return;
  area.innerHTML = '';
  window._signaturePad   = null;
  window._gcSignaturePad = null;
  const sigFields = fieldsFor(state.submissionType)
    .filter(f => f.type === 'signature' || f.type === 'gc-signature');
  sigFields.forEach(f => {
    if (f.type === 'gc-signature') {
      renderSignaturePad(f, area, {
        sigKey: 'gcSignature', padKey: '_gcSignaturePad', canvasId: 'gcSignatureCanvas',
        disclaimer: 'GC supervisor signs to confirm the labour and materials recorded above are accurate.',
      });
    } else {
      renderSignaturePad(f, area);
    }
  });
  _updateSubmitGate();
}

// True while a REQUIRED signature is still blank (pad content counts).
function _requiredSigMissing() {
  const needsSig = fieldsFor(state.submissionType).some(f => f.type === 'signature');
  if (!needsSig) return false;
  if (state.signature) return false;
  return !(window._signaturePad && !window._signaturePad.isEmpty());
}

// Model: Submit sits at 50% until the supervisor signs. Not `disabled` — a tap
// still lands and explains what's missing (kinder than a dead button).
function _updateSubmitGate() {
  const next = document.getElementById('btnNext');
  if (!next) return;
  const gated = state.currentStep === 4
    && state.submissionType !== 'Site Photos Only'
    && _requiredSigMissing();
  next.classList.toggle('btn-gated', gated);
  next.setAttribute('aria-disabled', gated ? 'true' : 'false');
}

// ── Validation ────────────────────────────────────────────────────────────────
function validateStep(step) {
  clearErrors();

  if (step === 1) {
    // The name is the account's, not typed input (the field is read-only). An
    // empty one means the profile has no full_name and no email to fall back
    // on — filing a nameless safety document is worse than stopping here.
    _syncNameFromAccount();
    const name = state.foremanName.trim();
    const date = document.getElementById('foremanDate').value;
    if (!name) { showError('foremanName', 'Your account has no name yet — ask your admin to set it.'); return false; }
    if (!date) { showError('foremanDate', 'Please select a date.'); return false; }
    // Safety net: the date picker has max=today, but a foreman could still paste
    // or type a future date directly. Refuse it — this is what caused Dez's
    // 65 blocks to land on Jun 26 instead of May 29.
    const today = _todayLocal();
    if (date > today) {
      showError('foremanDate', "Date can't be in the future. Today is the latest.");
      // also refresh the max in case the tab has been open past midnight
      document.getElementById('foremanDate').max = today;
      return false;
    }
    state.foremanName = name;
    state.date = date;
    _followDateWeek();
    return true;
  }

  if (step === 2) {
    if (!state.project) { showError('projectSelect', 'Please select a project.'); return false; }
    if (!state.submissionType) { showGlobalError('Please select a submission type.'); return false; }
    return true;
  }

  if (step === 3) {
    // Site Photos has its own validation inside handleSitePhotosUpload()
    if (state.submissionType === 'Site Photos Only') return true;

    // The Date above has max=today, but typing or paste bypasses it, and the
    // dashboard fast-path skips step 1 (whose check read #foremanDate — a
    // different input). A tailgate dated 2062 used to file, PDF and email.
    const G = window.Ordinex.guards;
    const _dateMsg = G.futureDateMsg(state.date, _todayLocal());
    if (_dateMsg) {
      showGlobalError(_dateMsg);
      document.getElementById('formStepDate')?.scrollIntoView({ block: 'center' });
      return false;
    }
    // Production on a day that has not happened yet: the Date above is in the
    // wrong week. Asked here, where that Date is on screen.
    const _futureWeek = _prodFutureWeek();
    if (_futureWeek) {
      showGlobalError(window.Ordinex.i18n.t('guard.prodFutureDays', { week: _futureWeek }));
      document.getElementById('formStepDate')?.scrollIntoView({ block: 'center' });
      return false;
    }

    // Production Report, admin, org with sub crews: whose production it is has
    // to be ANSWERED — own crew or a sub crew. There is no default (see
    // renderProductionCrewPicker). A foreman is never asked.
    if (_productionCrewMissing()) {
      const _c = document.getElementById('dynamicFields');
      const _say = () => showError('prodCrewSelect', 'Choose whose production this is — your own crew or a subcontract crew.');
      // The picker is inserted a tick after the call (it awaits the list), so
      // the inline error is hung on it only once it is really there.
      if (!document.getElementById('prodCrewSelect') && _c) renderProductionCrewPicker(_c).then(_say, () => {});
      else _say();
      showGlobalError('Choose whose production this is, at the top of the form.');
      return false;
    }

    // Weekly Timesheet: a day split that doesn't add to 100%, or a per-worker
    // split that doesn't match that worker's day total, must not reach the
    // DB — it would corrupt payroll math downstream (js/core/timesheet-
    // activity.js is the one place that math is allowed to live).
    if (state.submissionType === 'Weekly Timesheet') {
      const errs = window.Ordinex.timesheetActivity.validateActivity(
        state.fields.timesheet, state.fields.activity);
      if (errs.length) { showGlobalError(errs.join(' ')); return false; }
      // Decision 2b-A (2026-10-19): a job with two or more tasks must be told
      // which one the week is — "General" is a valid answer, silence is not.
      // A remembered pick, a one-task job and an older draft (week already
      // '' or a task) are never asked; the outbox replays nothing through here.
      // Seeded from the LIVE catalogue: tasks can land after the sheet rendered
      // (js/auth.js visibilitychange refresh), with no bar and no activity yet.
      const _tsAct = _ensureActivityState(_tsActivityTasks());
      if (_tsActivityTasks().length >= 2 && _tsAct && _tsAct.week == null) {
        _refreshActivityBar();   // bar may not exist yet — tasks arrived after the field rendered
        showGlobalError('Choose the activity for this week at the top of the timesheet — General is a valid choice.');
        _scrollToInvalidField('timesheet');
        return false;
      }
      // Rows: hours with no employee picked were dropped from the database
      // AND the PDF while the on-screen total counted them; a day had no
      // 24h ceiling; hours on days that have not happened yet landed in
      // next week's payroll. Refuse the first two; ask about the third —
      // crews fill the week on Friday, so that one is a warning.
      const { errors: _rowErrs, future: _future } = G.validateTimesheetWeek(
        state.fields.timesheet, G.weekDatesFor(state.date), _todayLocal());
      if (_rowErrs.length) { showGlobalError(_rowErrs[0]); _scrollToInvalidField('timesheet'); return false; }
      if (_future.length && !window.confirm(
            window.Ordinex.i18n.t('guard.tsFutureConfirm', { list: _future.join('\n') }))) {
        return false;
      }
      // Snapshot task names now, while they're certain to resolve — Task 5's
      // PDF (and any later regeneration) prints from this snapshot even if a
      // task is renamed or archived in Settings after the fact.
      const _tasks = (window.projectTasks || {})[state.project] || [];
      state.fields.activity_names = {};
      _tasks.forEach(t => { state.fields.activity_names[t.task_id] = t.description; });
    }

    // Extra Work Order: a labour row with hours but no worker was dropped from
    // the database while the GC-signed PDF printed it against '—'; an order
    // with no labour and no materials filed as a sentence with two signatures.
    if (state.submissionType === 'Extra Work Order') {
      const _ewoErrs = G.validateEwoLines(state.fields.ewo_labour, state.fields.ewo_materials);
      if (_ewoErrs.length) { showGlobalError(_ewoErrs[0]); _scrollToInvalidField('ewo_labour'); return false; }
    }

    const fields = fieldsFor(state.submissionType);

    // QAQC checklists: every item must be inspected, and a FAIL needs a
    // comment describing the deficiency — a half-blank sheet can't go to a GC.
    for (const field of fields) {
      if (field.type !== 'qaqc-table') continue;
      const rows = state.fields[field.id] || [];
      const missing = rows.findIndex(r => !r || !r.result);
      if (missing >= 0) {
        showGlobalError(`${field.label}: item ${missing + 1} not inspected yet — mark PASS, FAIL or N/A.`);
        return false;
      }
      const failNoComment = rows.findIndex(r => r && r.result === 'fail' && !(r.comment || '').trim());
      if (failNoComment >= 0) {
        showGlobalError(`${field.label}: item ${failNoComment + 1} FAILED — add a comment describing the deficiency.`);
        return false;
      }
    }

    for (const field of fields) {
      // No `field.required` gate here — each handler's validate decides what
      // required means. The checklist demands a comment on a Deficient row
      // even when the checklist itself is optional; every other handler (and
      // the registry default) returns null for a non-required empty value.
      // A conditionally-hidden field must not block the submit (Onda 3c).
      if (field.visibleIf && window.Ordinex?.forms?.builder
          && !window.Ordinex.forms.builder.isFieldVisible(field, state.fields)) continue;
      // Signature is collected on step 4 (Review & sign) now — nothing to
      // check here; the submit gate + handleSubmit enforce it.
      if (field.type === 'signature' || field.type === 'gc-signature') continue;
      // Simple types: ask the shared engine (Onda 3a). Composites register no
      // registry `validate`, so the registry's default required-empty check runs
      // — the exact same rule + message ("Please fill in: …") this replaces.
      const reg = window.Ordinex?.forms?.registry;
      const msg = reg ? reg.validate(field, state.fields[field.id]) : null;
      if (msg) {
        showGlobalError(msg);
        _scrollToInvalidField(field.id);
        return false;
      }
    }
    // Week Of / Week Ending must sit in the week the Date places (the
    // database week) — otherwise the PDF and the dashboards disagree.
    const _wk = _weekFieldId();
    if (_wk) {
      const _wf = fields.find(f => f.id === _wk);
      const _wmsg = G.weekMismatchMsg(_wf?.label || _wk, state.fields[_wk], G.weekDatesFor(state.date));
      if (_wmsg) { showGlobalError(_wmsg); _scrollToInvalidField(_wk); return false; }
    }
    return true;
  }

  return true;
}

// Scroll the offending field into view and flash it — the toast alone strands
// the foreman in a multi-screen form hunting for the gap.
function _scrollToInvalidField(fieldId) {
  const el = document.getElementById('field_' + fieldId)
          || document.querySelector(`[data-field-id="${CSS.escape(String(fieldId))}"]`);
  if (!el) return;
  const wrap = el.closest('.field-group') || el;
  wrap.scrollIntoView({ behavior: _prefersReducedMotion() ? 'auto' : 'smooth', block: 'center' });
  wrap.classList.add('field-flash');
  setTimeout(() => wrap.classList.remove('field-flash'), 2000);
}

function showError(id, msg) {
  const el = document.getElementById(id);
  if (el) {
    el.classList.add('field-error');
    const err = document.createElement('span');
    err.className = 'error-msg';
    err.textContent = msg;
    el.parentNode.appendChild(err);
    el.scrollIntoView({ behavior: _prefersReducedMotion() ? 'auto' : 'smooth', block: 'center' });
  }
}

function showGlobalError(msg) { showToast(msg, 'error'); }

function clearErrors() {
  document.querySelectorAll('.field-error').forEach(el => el.classList.remove('field-error'));
  document.querySelectorAll('.error-msg').forEach(el => el.remove());
}

// ── Auto-download helper ──────────────────────────────────────────────────────
function buildPDFFilename() {
  // Letters, digits, apostrophe and dash only: a form titled "Hot Work / Fire
  // Watch" must not add a folder level to the storage path (the database
  // refuses a path outside <org>/<user>/<file>).
  const type = (state.submissionType || 'Report').replace(/[^A-Za-z0-9'-]+/g, '-');
  const proj = (state.project || '').replace(/[^a-zA-Z0-9\- ]/g, '').trim().replace(/\s+/g, '-');
  // Append HHMMSS so two submissions on the same date+type+project don't
  // collide in Storage (would 400 Bad Request on upload with upsert: false).
  const now = new Date();
  const hhmmss = String(now.getHours()).padStart(2, '0')
               + String(now.getMinutes()).padStart(2, '0')
               + String(now.getSeconds()).padStart(2, '0');
  const base = proj ? `${state.date}_${type}_${proj}` : `${state.date}_${type}`;
  return `${base}_${hhmmss}.pdf`;
}

function triggerDownload(buffer, filename) {
  try {
    const blob = new Blob([buffer], { type: 'application/pdf' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  } catch (e) {
    console.warn('Auto-download failed:', e);
  }
}

// ── Signature compression (PNG → small JPEG before PDF) ──────────────────────
function compressSig(dataUrl, maxW = 380, maxH = 100, quality = 0.55) {
  return new Promise(resolve => {
    if (!dataUrl || !dataUrl.startsWith('data:image')) { resolve(dataUrl); return; }
    const img = new Image();
    img.onload = () => {
      const ratio = Math.min(maxW / img.width, maxH / img.height, 1);
      const w = Math.max(1, Math.round(img.width  * ratio));
      const h = Math.max(1, Math.round(img.height * ratio));
      const cv = document.createElement('canvas');
      cv.width = w; cv.height = h;
      const ctx = cv.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, w, h);
      ctx.drawImage(img, 0, 0, w, h);
      resolve(cv.toDataURL('image/jpeg', quality));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

// ── Draft Save / Load ────────────────────────────────────────────────────────
// Every form that takes the foreman more than 30 seconds to fill has draft
// support — auto-save every 30s, plus a manual Save Progress button.
// Weekly forms (Timesheet, Production Report) scope drafts by the Monday of
// the week, monthly ones (Truck Monthly Inspection) by the 1st of the month.
// Everything else scopes by the day so two Daily Tailgates on the same week
// don't trample each other.
const DRAFTABLE_FORMS = [
  'Weekly Timesheet',
  'Production Report',
  'Daily Tailgate',
  'Weekly Toolbox Talk',
  'Incident Report',
  'Hazard Observation',
  'QAQC - Foreman',
  'Telehandler Inspection',
  'Forklift Inspection',
  'E-Pallet Jack Inspection',
  'Scaffolding Inspection',
  'Extra Work Order',

  // ── Drilling & Shotcrete pack ─────────────────────────────────────────────
  // Every one of the 17 is a long form — 16-to-22-item checklists, multi-row
  // tables, or several sections of narrative — so all of them get the 30s
  // auto-save and the Save Progress button.
  'Drill Rig Daily Log',
  'Air Compressor Daily Log',
  'Concrete Pump Daily Log',
  'Dry-Mix Pump Daily Log',
  'Grout Plant Daily Log',
  'Truck Monthly Inspection',
  'Equipment Maintenance Record',
  'Incident Investigation',
  'First Aid Assessment',
  'Disciplinary Report',
  'Site Orientation Record',
  'Daily Site Report',
  'Anchor Installation Log',
  'Anchor Test Record',
  'Shotcrete Placement Record',
  'Shotcrete Test Panel Log',
  'Grout Batch Log',

  // ── Hot work (neutral) ────────────────────────────────────────────────────
  // Daily draft: a torch day spans hours between lighting up and the end of
  // the fire watch — the record stays open that whole time.
  'Hot Work & Fire Watch Record',

  // Heavy equipment (neutral) — 20-item walkaround, filled at the machine.
  'Heavy Equipment Daily Log',
  // MEWP (neutral) — 21 items, machine plus the workplace around it.
  'MEWP Daily Log',

  // ── Excavation & Sitework pack ────────────────────────────────────────────
  'Excavation Daily Inspection',

  // ── Mechanical pack ───────────────────────────────────────────────────────
  // A pressure test is filled over the hours it is held; a refrigerant call
  // spans the visit. Both stay open on screen while the work happens.
  'Pressure Test Record',
  'Refrigerant Service & Leak Log',

  // ── Demolition & Environmental pack ───────────────────────────────────────
  // The readiness check is worked through as each utility is confirmed; the
  // containment log runs the length of a shift.
  'Pre-Demolition Readiness Check',
  'Abatement Containment Daily Log',
];
const WEEKLY_DRAFT_FORMS = ['Weekly Timesheet', 'Production Report'];
// Filled once a month, so a day-scoped draft would be orphaned the moment the
// inspection spills into the next day — the exact loss drafts exist to prevent.
const MONTHLY_DRAFT_FORMS = ['Truck Monthly Inspection'];

// Local calendar week, not UTC — toISOString() here filed a draft saved after
// ~5pm in Vancouver under the NEXT day, so the lookup the next morning missed
// it and the foreman restarted from a blank form.
function currentWeekMonday() {
  const d = new Date();
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Local calendar month, not UTC — a draft started at 9pm on the 31st must not
// key to the next month.
function currentMonthFirst() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

// Pick the right scope key for the draft: Monday of the week for weekly forms,
// the 1st for monthly ones, today's local date for everything else.
function _draftScopeFor(formType) {
  if (WEEKLY_DRAFT_FORMS.includes(formType))  return currentWeekMonday();
  if (MONTHLY_DRAFT_FORMS.includes(formType)) return currentMonthFirst();
  return _todayLocal();
}

async function saveDraft(opts = {}) {
  if (!window.currentUser) return;
  const scope = _draftScopeFor(state.submissionType);
  const fieldsToSave = Object.assign({}, state.fields);
  delete fieldsToSave.signature;     // never persist signatures
  delete fieldsToSave.gcSignature;

  // Stamp the trade the draft was filled under. In a multi-pack org the
  // foreman can switch trades between sessions, and the answers only match the
  // schema they were written against — resuming a drywall tailgate under the
  // roofing schema would silently re-label the sections. Rides inside
  // form_data (jsonb, no migration) under a reserved key that the restore
  // strips before the fields are applied.
  try {
    const t = (typeof activeTradePack === 'function' && activeTradePack()) || '';
    if (t) fieldsToSave.__trade = t;
  } catch (_) { /* no trade context — draft stays trade-less, as before */ }

  // Same reserved-key trick for the Production Report's sub crew: without it, an
  // admin who picks a sub crew, leaves and comes back would resume with no crew
  // and the week's units would be costed as in-house — silently wrong money.
  // Saved once ANSWERED — own crew included ({ id: null }), so a resumed draft
  // does not ask again. Unanswered writes no key, and the resume asks.
  if (state.submissionType === 'Production Report') {
    const _crew = window.Ordinex.productionCrew.crewToDraft(_productionCrewChoice());
    if (_crew) fieldsToSave.__crew = _crew;
  }

  // Project-first nav (Task 5) — DRAFT RULE: a draft is scoped by (foreman,
  // form_type, week_of) only, never by project, so it has no memory of its
  // own job unless we stamp one. Without this, resuming a draft after
  // switching the job-space context (Back → list → a different job) would
  // silently re-file it under the NEW job — the fields stay the old job's,
  // but the submission (and PDF) would say the new one. Same reserved-key
  // trick as __trade/__crew above; restored (and made to WIN over whatever
  // context set state.project beforehand) in loadDraftData.
  if (state.project) fieldsToSave.__project = state.project;

  // Photos are NOT in a draft (only state.fields is persisted). Remember how
  // many there were so the save and the resume can say so, instead of three
  // hazard photos vanishing between 'Progress saved!' and the filed PDF.
  if (Array.isArray(state.photos) && state.photos.length) fieldsToSave.__photos = state.photos.length;

  // Workers the foreman removed from the timesheet with × (W4): without this the clock prefill
  // re-adds them, with their hours, on the next entry or after a restart.
  if (state.submissionType === 'Weekly Timesheet' && state._clockDismissed?.size) fieldsToSave.__clockDismissed = [...state._clockDismissed];

  // Device mirror FIRST (wave A1): it cannot fail for lack of signal, so a
  // dead zone or an OS kill mid-fill no longer costs the crew their answers.
  // The server upsert below stays the cross-device truth when it lands.
  let localSaved = false;
  try {
    localSaved = await window.Ordinex.localDrafts.put(
      window.currentUser.id, state.submissionType, scope, fieldsToSave);
  } catch (_) { /* storage broken — server path below is unchanged */ }

  // Offline: the device copy above IS the save — don't await a client that
  // may be wedged in a token-refresh retry loop (PWA item 1).
  if (navigator.onLine === false) {
    if (localSaved) {
      if (!opts.silent) showToast('Saved on this device — will sync when back online.', 'info');
      const ts = document.getElementById('draftSavedAt');
      if (ts) ts.textContent = 'saved on this device ' +
        new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (!opts.silent) {
      showToast('Could not save draft.', 'error');
    }
    return;
  }

  const { error } = await sbClient.from('drafts').upsert({
    foreman_id: window.currentUser.id,
    form_type:  state.submissionType,
    week_of:    scope,
    form_data:  fieldsToSave,
    updated_at: new Date().toISOString()
  }, { onConflict: 'foreman_id,form_type,week_of' });

  if (error) {
    if (localSaved) {
      // The device copy landed — that IS a save. Say so instead of alarming
      // the one crew member who is doing everything right in a dead zone.
      if (!opts.silent) showToast('Saved on this device — will sync when back online.', 'info');
      const ts = document.getElementById('draftSavedAt');
      if (ts) ts.textContent = 'saved on this device ' +
        new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      return;
    }
    if (!opts.silent) showToast('Could not save draft.', 'error');
    console.warn('Draft save failed:', error);
    return;
  }
  if (!opts.silent) {
    if (fieldsToSave.__photos) showToast(window.Ordinex.i18n.t('guard.draftSavedNoPhotos'), 'warning');
    else showToast('Progress saved!', 'success');
  }
  const ts = document.getElementById('draftSavedAt');
  if (ts) ts.textContent = 'last saved ' + new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

async function loadDraftData(formType) {
  if (!window.currentUser) return null;
  const scope = _draftScopeFor(formType);
  // Two copies can exist: the server row (cross-device truth) and the device
  // mirror (wave A1 — written even with no signal). Newer updated_at wins, so
  // a fill that died offline beats a stale server copy, and a draft continued
  // on another device beats yesterday's mirror.
  let server = null;
  // Offline: don't even ask. Beyond being pointless, a supabase client whose
  // token expired offline is wedged in a refresh retry loop, and awaiting it
  // would freeze the form open (PWA item 1).
  if (navigator.onLine !== false) {
    try {
      const { data, error } = await sbClient
        .from('drafts')
        .select('form_data, updated_at')
        .eq('foreman_id', window.currentUser.id)
        .eq('form_type', formType)
        .eq('week_of', scope)
        .maybeSingle();
      if (!error) server = data || null;
    } catch (_) { /* offline — the device mirror below still restores */ }
  }
  let local = null;
  try {
    local = await window.Ordinex.localDrafts.get(window.currentUser.id, formType, scope);
  } catch (_) { /* no mirror — server-only, as before */ }
  const data = window.Ordinex?.localDrafts?.newer
    ? window.Ordinex.localDrafts.newer(server, local)
    : (server || local);
  if (!data) return null;

  const fd = data.form_data || {};

  // Photos never travel in a draft (see saveDraft) — say so on resume.
  const draftPhotos = Number(fd.__photos) || 0;
  delete fd.__photos;
  if (draftPhotos) showToast(window.Ordinex.i18n.t('guard.draftHadPhotos', { n: draftPhotos }), 'warning');

  // The timesheet's × removals (see saveDraft). Runs after _resetFormEntryState, so it wins.
  state._clockDismissed = formType === 'Weekly Timesheet' && Array.isArray(fd.__clockDismissed) ? new Set(fd.__clockDismissed) : null;
  delete fd.__clockDismissed;

  // ── Project-first nav (Task 5) — DRAFT RULE: the draft is truth ──────────
  // Reopening a draft keeps the DRAFT's OWN project even when it differs
  // from the current job-space context. The caller (startSpecificFormFlow,
  // or the step2→3 transition in attachNavListeners' btnNext handler) has
  // already set state.project from window.fieldProject/the palette BEFORE
  // calling here — this overrides it. Placed before the __trade check below
  // so that check reads the DRAFT's project, not the context's, when they
  // differ (correct: the trade-tag lookup should match what's actually
  // being resumed). Never reaches state.fields.
  const draftProject = fd.__project;
  delete fd.__project;
  if (draftProject && draftProject !== state.project) {
    const contextProject = state.project;
    state.project = draftProject;
    showToast(
      `Resumed a saved draft for ${draftProject}` +
      (contextProject ? ` — different from ${contextProject}.` : '.'),
      'info'
    );
  } else if (draftProject) {
    state.project = draftProject;
  }

  // Restore the trade this draft was written under (see saveDraft), so the
  // answers meet the schema they were filled against. Only honoured when that
  // pack is still active and the project itself carries no trade tag — an
  // admin's tag outranks a stale draft. __trade never reaches the fields.
  const draftTrade = fd.__trade;
  delete fd.__trade;

  // Restore the sub crew the draft was started for (admins only — a foreman's
  // draft can't carry one, and the DB would refuse the write anyway). Never
  // reaches the fields. renderProductionCrewPicker re-selects it in the dropdown.
  // A draft with no __crew (saved before the question existed, or before it was
  // answered) restores as UNANSWERED — the picker asks once. { id: null } is
  // "own crew", chosen.
  const draftCrew = fd.__crew;
  delete fd.__crew;
  if (formType === 'Production Report') {
    _setProductionCrew(window.Ordinex.productionCrew.crewFromDraft(
      draftCrew, window.currentProfile?.role === 'admin'));
  }
  if (draftTrade) {
    try {
      const feats  = (window.currentOrg && window.currentOrg.features) || {};
      const active = window.Ordinex?.packs ? window.Ordinex.packs.active(feats) : [];
      const tagged = (window.projectTrades || {})[state.project || ''] || '';
      if (!tagged && active.includes(draftTrade) && typeof setTradeChoice === 'function') {
        if (typeof getTradeChoice === 'function' && getTradeChoice() !== draftTrade) {
          setTradeChoice(draftTrade);
          const name = (window.PACK_LABELS && window.PACK_LABELS[draftTrade]) || draftTrade;
          showToast(`Resumed in ${name} — the trade this draft was started in.`, 'info');
        }
      }
    } catch (_) { /* keep the draft; worst case it renders under the current trade */ }
  }
  return data;
}

async function deleteDraft() {
  if (!window.currentUser) return;
  const scope = _draftScopeFor(state.submissionType);
  // Device mirror goes first — it always succeeds offline, so a form
  // submitted in a dead zone can't resurrect its own draft on this phone.
  // (The server row can't be deleted offline; it dies on the next online
  // save/submit of the same scope, same as before this wave.)
  try {
    await window.Ordinex.localDrafts.remove(window.currentUser.id, state.submissionType, scope);
  } catch (_) { /* mirror already gone or storage broken — proceed */ }
  // .select() so a refusal can be told apart from "there was nothing to
  // delete". RLS refuses a DELETE by filtering the row, not by raising, so
  // without this the call resolves clean either way — which is how 236 stale
  // draft rows piled up over three months with nobody able to notice.
  const { data, error } = await sbClient.from('drafts').delete()
    .eq('foreman_id', window.currentUser.id)
    .eq('form_type', state.submissionType)
    .eq('week_of', scope)
    .select('id');
  if (error) throw error;
  // Zero rows is normal (nothing was ever saved for this scope) — worth a line
  // in the console, never a message on screen: the form was just filed and the
  // draft is bookkeeping the foreman never sees.
  if (!data || !data.length) console.debug('[draft] nothing to delete for', state.submissionType, scope);
}

// ── Auto-save heartbeat — fires every 30s while on Step 3 of a draftable form
let _autoSaveTimer = null;
function _startAutoSaveDraft() {
  _stopAutoSaveDraft();
  _autoSaveTimer = setInterval(() => {
    if (!DRAFTABLE_FORMS.includes(state.submissionType)) { _stopAutoSaveDraft(); return; }
    // Silent save — don't toast every 30s. Banner timestamp updates.
    saveDraft({ silent: true }).catch(err => console.warn('auto-save failed:', err));
  }, 30000);
}
function _stopAutoSaveDraft() {
  if (_autoSaveTimer) { clearInterval(_autoSaveTimer); _autoSaveTimer = null; }
}

async function clearDraftAndReset() {
  await deleteDraft();
  state.fields = {};
  state._clockDismissed = null;   // removed workers come back too (the next autosave would re-store them)
  // A cleared draft is a fresh form: the crew question is asked again.
  _setProductionCrew({ picked: false });
  renderStep3();
  showToast('Draft cleared.', 'info');
}

// ══════════════════════════════════════════════════════════════════════════════
//  SITE PHOTOS — dedicated lightweight flow (no PDF, direct to Storage)
// ══════════════════════════════════════════════════════════════════════════════
let _spPhotos = []; // [{file, caption, objectUrl}]

function renderSitePhotosStep() {
  const titleEl = document.getElementById('formTypeTitle');
  if (titleEl) titleEl.textContent = 'Site Photos';

  const container = document.getElementById('dynamicFields');
  if (!container) return;

  // Hide the standard photo-section (always present in step 3 HTML)
  const photoSection = document.querySelector('.photo-section');
  if (photoSection) photoSection.style.display = 'none';

  // Release any previous object URLs
  _spPhotos.forEach(p => URL.revokeObjectURL(p.objectUrl));
  _spPhotos = [];

  container.innerHTML = `
    <div id="spPhotoList" class="sp-photo-list"></div>
    <div class="sp-add-row">
      <button type="button" class="sp-cam-btn" aria-label="Add photos" onclick="document.getElementById('spFileInput').click()"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linejoin="round"><rect x="3" y="7" width="18" height="13" rx="2"/><circle cx="12" cy="13.5" r="3.5"/><path d="M8 7l1.5-2.5h5L16 7"/></svg></button>
      <input id="spCapInput" type="text" class="sp-cap-input" aria-label="Photo caption" placeholder="Caption, then tap the camera…">
      <input type="file" id="spFileInput" accept="image/*" multiple hidden>
    </div>
  `;

  document.getElementById('spFileInput').addEventListener('change', async e => {
    const caption = (document.getElementById('spCapInput')?.value || '').trim();
    // Accept image/* types AND HEIC (iOS sometimes reports empty type for HEIC)
    const picked = Array.from(e.target.files)
      .filter(f => f.type.startsWith('image/') || f.name.toLowerCase().endsWith('.heic') || f.name.toLowerCase().endsWith('.heif'));
    e.target.value = '';
    const capEl = document.getElementById('spCapInput');
    if (capEl) capEl.value = '';
    for (const f of picked) {
      const c = await window.Ordinex.image.compress(f);
      _spPhotos.push({ file: c, caption, objectUrl: URL.createObjectURL(c) });
      _renderSpList();
    }
  });

  _renderSpList();
  _consumeSharedPhotos();
}

// PWA item 5: photos shared from the OS camera roll — sw.js stashed them in
// the 'ordinex-share' IDB when the share sheet POSTed to /share-photos. Pulled
// in (and cleared) whenever the Site Photos screen opens, so they survive a
// detour through login or the project picker.
async function _consumeSharedPhotos() {
  let items = [];
  try {
    items = await new Promise((resolve, reject) => {
      const r = indexedDB.open('ordinex-share', 1);
      r.onupgradeneeded = () => {
        if (!r.result.objectStoreNames.contains('incoming')) {
          r.result.createObjectStore('incoming', { autoIncrement: true });
        }
      };
      r.onerror = () => reject(r.error);
      r.onsuccess = () => {
        const db = r.result;
        if (!db.objectStoreNames.contains('incoming')) { resolve([]); return; }
        const tx = db.transaction('incoming', 'readwrite');
        const g = tx.objectStore('incoming').getAll();
        g.onsuccess = () => { tx.objectStore('incoming').clear(); resolve(g.result || []); };
        g.onerror = () => reject(g.error);
      };
    });
  } catch (_) { return; }
  for (const it of items) {
    try {
      const f = new File([it.blob], it.name || 'photo.jpg', { type: it.type || 'image/jpeg' });
      const c = await window.Ordinex.image.compress(f);
      _spPhotos.push({ file: c, caption: '', objectUrl: URL.createObjectURL(c) });
    } catch (_) { /* skip a broken file, keep the rest */ }
  }
  if (items.length) {
    _renderSpList();
    showToast(`${items.length} shared photo${items.length > 1 ? 's' : ''} added.`, 'success');
  }
}

function _renderSpList() {
  const list = document.getElementById('spPhotoList');
  if (!list) return;
  if (_spPhotos.length === 0) {
    list.innerHTML = '<p class="sp-empty">No photos yet — type a caption, then tap the camera.</p>';
    return;
  }
  list.innerHTML = _spPhotos.map((p, i) => `
    <div class="sp-photo-item">
      <div class="sp-thumb-wrap">
        <img class="sp-thumb" src="${p.objectUrl}" alt="">
        <button type="button" class="sp-remove" onclick="spRemovePhoto(${i})">×</button>
      </div>
      <input class="sp-caption field-input" type="text"
        aria-label="Photo caption" placeholder="Caption for this photo (optional)"
        value="${(p.caption||'').replace(/"/g,'&quot;')}"
        oninput="_spPhotos[${i}].caption = this.value">
    </div>
  `).join('');
}

function spRemovePhoto(i) {
  URL.revokeObjectURL(_spPhotos[i].objectUrl);
  _spPhotos.splice(i, 1);
  _renderSpList();
}

// Shared by the online submit and the offline replay (Onda 2c). Deterministic
// paths ({clientUuid}-{i}) + upsert + a documents dedup make it idempotent — a
// re-run never duplicates a photo, a storage object, or a document row.
async function uploadSitePhotos({ photos, projectId, orgId, uploadedBy, clientUuid }) {
  let uploaded = 0;
  for (let i = 0; i < photos.length; i++) {
    const p = photos[i];
    const file = p.file || p.blob;
    if (!file) continue;
    const ext = ((p.name || file.name || 'jpg').split('.').pop() || 'jpg').toLowerCase();
    const mimeType = (p.type || file.type) || (ext === 'heic' ? 'image/heic' : 'image/jpeg');
    const path = `${orgId}/${projectId}/site-photos/${clientUuid}-${i}.${ext}`;
    const { error: upErr } = await sbClient.storage.from('project-documents')
      .upload(path, file, { contentType: mimeType, upsert: true });
    if (upErr) { console.warn('site photo upload failed:', path, upErr.message); continue; }
    // Idempotent dedup: skip the documents insert if a row for this path exists.
    const { data: existing } = await sbClient.from('documents')
      .select('id').eq('file_name', path).maybeSingle();
    if (existing) { uploaded++; continue; }
    const { data: sp } = await sbClient.storage.from('project-documents')
      .createSignedUrl(path, 60 * 60 * 24 * 365);
    const { error: dbErr } = await sbClient.from('documents').insert({
      project_id: projectId, title: (p.caption || '').trim() || 'Site Photo',
      type: 'site_photo', file_name: path, file_url: sp?.signedUrl || null,
      uploaded_by: uploadedBy,
    });
    if (dbErr) { console.warn('site photo document insert failed:', dbErr.message); continue; }
    uploaded++;
  }
  return uploaded;
}
window.uploadSitePhotos = uploadSitePhotos;

async function handleSitePhotosUpload() {
  const btn   = document.getElementById('btnNext');
  const errEl = document.getElementById('globalError');

  if (_spPhotos.length === 0) {
    showToast('Add at least one photo before uploading.', 'warning');
    return;
  }

  // Offline: queue the (already-compressed) photos and show the saved screen —
  // they upload themselves on reconnect. See saveSitePhotosOffline / _replaySitePhotos.
  if (navigator.onLine === false && typeof saveSitePhotosOffline === 'function') {
    await saveSitePhotosOffline(state, _spPhotos);
    if (typeof showOfflineSavedScreen === 'function') showOfflineSavedScreen(null);
    return;
  }

  btn.disabled = true;
  btn.textContent = 'Uploading…';
  if (errEl) errEl.hidden = true;

  try {
    const { data: project } = await sbClient.from('projects')
      .select('id').eq('name', state.project).single();
    if (!project) throw new Error('Project not found — go back and re-select.');

    const clientUuid = state._clientUuid || (crypto?.randomUUID?.() || String(Date.now()));
    const uploaded = await uploadSitePhotos({
      photos: _spPhotos, projectId: project.id, orgId: window.currentOrg?.id,
      uploadedBy: window.currentUser?.id, clientUuid,
    });

    // Log submission (best-effort; client_uuid dedups an offline re-submit).
    try {
      await sbClient.from('submissions').insert({
        foreman_id:      _submitterId(),
        foreman_name:    _submitterName(),
        project_name:    state.project,
        submission_type: 'Site Photos Only',
        trade_pack:      typeof activeTradePack === 'function' ? activeTradePack() : null,
        client_uuid:     clientUuid,
      });
    } catch (_) {}

    // Show success in place
    const container = document.getElementById('dynamicFields');
    if (container) {
      container.innerHTML = `
        <div class="success-screen">
          <div class="success-icon"><i data-lucide="check-circle-2"></i></div>
          <h2 class="success-title">${uploaded} Photo${uploaded !== 1 ? 's' : ''} Uploaded!</h2>
          <p class="success-msg">Saved to Site Documentation for <strong>${state.project}</strong>.</p>
          <button class="btn btn-next" onclick="resetApp()" style="margin-top:24px;width:100%">Back to Today</button>
        </div>`;
    }
    btn.style.display = 'none';
    document.getElementById('btnBack').style.display = 'none';

  } catch (err) {
    console.error(err);
    btn.disabled = false;
    btn.textContent = 'Upload Photos';
    showGlobalError('Upload failed: ' + err.message);
  }
}

// ── Submit ────────────────────────────────────────────────────────────────────
// Only genuine safety / inspection forms go to the safety inbox. Production,
// timesheets, extra work, deliveries, photos and QAQC are stored + synced to
// the dashboard but never emailed to the safety officer.
const SAFETY_EMAIL_TYPES = new Set([
  'Daily Tailgate', 'Weekly Toolbox Talk', 'Incident Report', 'Hazard Observation',
  'Telehandler Inspection', 'Forklift Inspection', 'E-Pallet Jack Inspection', 'Scaffolding Inspection',
  // MEWP (neutral) — sits with the other pre-use equipment inspections above,
  // not with the daily LOGS (drilling, heavy equipment) that stay out.
  'MEWP Daily Log',
  // Hot work (neutral) — a fire-watch record is a genuine safety document; the
  // safety officer wants it the day it happens, not at month end.
  'Hot Work & Fire Watch Record',
  // Sitework pack — the competent person's go / no-go on an excavation is a
  // safety document too. The equipment log stays out, like the drilling logs.
  'Excavation Daily Inspection',
  // Demolition pack — one is the go / no-go before anything is touched, the
  // other reports a containment breach. Both belong in the safety inbox.
  'Pre-Demolition Readiness Check',
  'Abatement Containment Daily Log',
]);
function _isSafetyEmailForm(type) {
  // A tenant-built form states its own answer (the builder asks when the form
  // is created); the catalog list stays the authority for everything else.
  const own = metaFor(type, 'safety_email', null);
  if (own !== null && own !== undefined) return own === true;
  return SAFETY_EMAIL_TYPES.has(type);
}

// Structured Phase A payload: a copy of the field values with heavy base64
// blobs (signatures, embedded photos) stripped — those live in the PDF (and,
// later, the signatures bucket), not the jsonb. Keeps `submissions.data` a
// small, queryable record of what was entered.
function _stripBlobs(v) {
  if (typeof v === 'string') return v.startsWith('data:') ? '[image]' : v;
  if (Array.isArray(v)) return v.map(_stripBlobs);
  if (v && typeof v === 'object') {
    const out = {};
    for (const [k, val] of Object.entries(v)) out[k] = _stripBlobs(val);
    return out;
  }
  return v;
}
function _buildSubmissionData() {
  // sendToSafety rides along: the server-side email sender (2026-09-01)
  // resolves the CSO by project and needs the foreman's choice to respect it.
  try { return { ..._stripBlobs(state.fields || {}), sendToSafety: state.sendToSafety !== false }; }
  catch { return { sendToSafety: state.sendToSafety !== false }; }
}

// Weak-signal guard: on a jobsite the browser often still reports
// navigator.onLine=true while every request hangs. Racing the network steps
// of handleSubmit against a deadline lets the submit fall back to the offline
// outbox instead of stranding the foreman on a spinner. The replay path is
// idempotent (client_uuid dedup on insert, upsert on storage), so a request
// that actually landed server-side before the deadline can't double-insert.
const _NET_STEP_TIMEOUT_MS = 15000;
function _withNetTimeout(promise, label) {
  return Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(
      () => reject(new Error(`network timeout — ${label} took too long`)),
      _NET_STEP_TIMEOUT_MS)),
  ]);
}
// Matches both our timeout errors and the messages fetch failures carry
// (supabase-js returns those as { error } instead of throwing).
function _looksLikeNetFailure(msg) {
  return /timeout|offline|network|failed to fetch|load failed|fetch/i.test(msg || '');
}

// ── PDF preview (Step 4, before signing/submit) ─────────────────────────────
// generatePDF() is a pure function of (state, photos) with no network I/O of
// its own — reuse it exactly as handleSubmit() does, but earlier, so the
// foreman can see the real document and go back to fix something before it's
// final. Operates on a shallow copy so it never touches the live
// state.signature/state.fields — handleSubmit() still does its own
// compression pass at actual submit time, and running compressSig() twice on
// the same signature (once here, once for real) would be a needless
// generational quality loss.
let _pdfPreviewUrl = null;

async function previewPDF() {
  const modal  = document.getElementById('pdfPreviewModal');
  const status = document.getElementById('pdfPreviewStatus');
  const frame  = document.getElementById('pdfPreviewFrame');
  const openTab = document.getElementById('pdfPreviewOpenTab');
  if (!modal || !status || !frame) return;

  if (_pdfPreviewUrl) { URL.revokeObjectURL(_pdfPreviewUrl); _pdfPreviewUrl = null; }
  frame.style.display = 'none';
  frame.src = 'about:blank';
  openTab.style.display = 'none';
  status.style.display = '';
  status.textContent = 'Generating preview…';
  modal.style.display = 'flex';
  document.body.style.overflow = 'hidden';

  try {
    await _ensurePdfDeps();
    const previewState = { ...state, fields: { ...state.fields } };
    if (previewState.signature)   previewState.signature   = await compressSig(previewState.signature);
    if (previewState.gcSignature) previewState.gcSignature = await compressSig(previewState.gcSignature);
    const buf = await generatePDF(_prodStateForPdf(previewState), previewState.photos);
    _pdfPreviewUrl = URL.createObjectURL(new Blob([buf], { type: 'application/pdf' }));
    frame.src = _pdfPreviewUrl;
    openTab.href = _pdfPreviewUrl;
    openTab.style.display = '';
    status.style.display = 'none';
    frame.style.display = '';
  } catch (err) {
    status.textContent = "Couldn't generate the preview. You can still submit normally.";
    console.warn('previewPDF failed:', err);
  }
}

function closePdfPreview() {
  const modal = document.getElementById('pdfPreviewModal');
  const frame = document.getElementById('pdfPreviewFrame');
  if (modal) modal.style.display = 'none';
  document.body.style.overflow = '';
  if (frame) frame.src = 'about:blank';
  if (_pdfPreviewUrl) { URL.revokeObjectURL(_pdfPreviewUrl); _pdfPreviewUrl = null; }
}

async function handleSubmit() {
  // FIRST, before anything else can take time: stop the 30-second auto-save.
  //
  // It was never stopped on this path. _stopAutoSaveDraft() is called from
  // five places and none of them is submit, despite the comment at the
  // interval's own definition claiming it is "cleared on submit success". So
  // the heartbeat kept running through the PDF build, the upload and the Done
  // screen — and re-created the draft that deleteDraft had just removed, on
  // the server and on the device. 255 draft rows piled up that way, 75 of them
  // written within two minutes of the submission they belong to.
  //
  // Here rather than in showSuccessScreen because the window that matters is
  // the slow part: building and uploading the PDF is where a 30s tick lands.
  _stopAutoSaveDraft();

  // Site Photos uses its own upload flow (no PDF, no email)
  if (state.submissionType === 'Site Photos Only') {
    await handleSitePhotosUpload();
    return;
  }
  // Submit gate: a required signature that's still blank stops everything
  // here — the button sits at 50% and this explains why.
  if (_requiredSigMissing()) {
    showGlobalError('Sign the document first — tap the white box above.');
    document.querySelector('#reviewSignArea .signature-canvas')
      ?.scrollIntoView({ behavior: _prefersReducedMotion() ? 'auto' : 'smooth', block: 'center' });
    return;
  }
  // Whose production is this — the same gate as step 3, again, because this is
  // the last line before a row is written (and before the offline fallback):
  // a crew list that landed after step 3 was passed must still stop the form.
  if (_productionCrewMissing()) {
    showGlobalError('Go back one step and choose whose production this is — your own crew or a subcontract crew.');
    return;
  }
  // The step-3 Date, again: nothing below may build a PDF dated in the future.
  const _dateMsg = window.Ordinex.guards.futureDateMsg(state.date, _todayLocal());
  if (_dateMsg) { showGlobalError(_dateMsg); return; }
  // Future-day gate, again (the fill step asks first — see _prodFutureWeek).
  const _futureWeek = _prodFutureWeek();
  if (_futureWeek) {
    showGlobalError(window.Ordinex.i18n.t('guard.prodFutureDaysBack', { week: _futureWeek }));
    return;
  }
  // A day far above the company's history for its item: asked, never refused.
  if (!(await _prodOutlierGate())) return;
  // Week collision gate — BEFORE the PDF exists, BEFORE any row is written.
  // If this project+week already has a filing by this account, the person
  // decides among THREE things: add on top, replace the week, or walk away.
  // The first version of this was window.confirm's two buttons, which forced
  // "Cancel" to mean REPLACE — someone trying to back out of the question
  // would have wiped the week. Renan caught it before anyone did it for real.
  // A cancel here costs nothing: nothing has been generated or written yet.
  // Disable BEFORE the gate: the gate makes network calls, and a second tap
  // during them re-entered handleSubmit, stacked two sheets, and queued a
  // duplicate of a form that was already filed.
  const _gateBtn = document.getElementById('btnNext');
  const _gateLabel = _gateBtn ? _gateBtn.textContent : '';
  if (_gateBtn) { _gateBtn.disabled = true; _gateBtn.textContent = 'Checking the week…'; }
  // A clocked Weekly Timesheet, last chance before anything is generated (W4): another foreman's
  // days filed since step 4 opened leave the sheet; that can empty a day a By-worker split still
  // gives hours to, so the split is checked again; an open shift refuses; a worker removed with ×
  // who clocked here is a question. With the clock off each of these finds nothing.
  if (state.submissionType === 'Weekly Timesheet') {
    const G = window.Ordinex.guards;
    (await _tsRecheckFiled()).forEach(n => showToast(n, 'warning'));
    const ae = window.Ordinex.timesheetActivity.validateActivity(state.fields.timesheet, state.fields.activity);
    if (ae.length) {
      if (_gateBtn) { _gateBtn.disabled = false; _gateBtn.textContent = _gateLabel; }
      showGlobalError(ae.join(' '));
      return;
    }
    const g = G.validateTimesheetWeek(state.fields.timesheet, G.weekDatesFor(state.date), _todayLocal(),
      { openCells: _tsOpenCells(), clockedMissing: _tsClockedMissing() });
    if (g.errors[0]) {
      if (_gateBtn) { _gateBtn.disabled = false; _gateBtn.textContent = _gateLabel; }
      showGlobalError(g.errors[0]);
      renderStep4();
      return;
    }
    if (g.clocked[0] && !window.confirm(g.clocked[0])) {
      if (_gateBtn) { _gateBtn.disabled = false; _gateBtn.textContent = _gateLabel; }
      return;
    }
  }
  const _mayFile = await _confirmWeekModeOrAbort();
  if (_gateBtn) { _gateBtn.disabled = false; _gateBtn.textContent = _gateLabel; }
  if (!_mayFile) {
    showToast('Nothing was filed.', 'info');
    return;
  }

  // One idempotency key per submission attempt — shared by the online insert
  // and the offline outbox record so a reconnect replay can't double-insert.
  if (!state._clientUuid) {
    state._clientUuid = (crypto?.randomUUID?.() || (Date.now() + '-' + Math.random().toString(36).slice(2)));
  }
  const btn = document.getElementById('btnNext');
  btn.disabled = true;
  btn.textContent = 'Generating PDF...';
  showToast('Generating PDF report...', 'info');

  try {
    // Capture signatures from pads at submit time (in case endStroke didn't fire)
    if (window._signaturePad && !window._signaturePad.isEmpty()) {
      state.signature = window._signaturePad.toDataURL('image/png');
    }
    if (window._gcSignaturePad && !window._gcSignaturePad.isEmpty()) {
      state.gcSignature = window._gcSignaturePad.toDataURL('image/png');
    }

    // Compress signature PNGs → small JPEGs to reduce PDF + DB size
    if (state.signature)   state.signature   = await compressSig(state.signature);
    if (state.gcSignature) state.gcSignature = await compressSig(state.gcSignature);
    // Compress crew sign-in member signatures too. The field's ID is `crew`
    // ('crew-signin' is the field TYPE — using it here was a long-standing
    // bug that silently skipped both this compression and the count below).
    if (Array.isArray(state.fields.crew)) {
      state.fields.crew = await Promise.all(
        state.fields.crew.map(async m =>
          m.sig ? { ...m, sig: await compressSig(m.sig) } : m
        )
      );
    }

    // Auto-fill "Workers on site" from the crew sign-in count so the PDF
    // header and the email metadata both reflect who actually attended. This
    // covers Daily Tailgate + Weekly Toolbox Talk (the two forms with crew
    // sign-in); other form types stay blank and render "—" as before.
    if (!state.workersOnSite && Array.isArray(state.fields?.crew)) {
      const count = state.fields.crew.filter(m => (m?.name || '').trim()).length;
      if (count > 0) state.workersOnSite = String(count);
    }
    // Tomorrow's tailgate on this job starts from this crew, signal or not.
    _saveLastCrew(state.project, state.fields?.crew);

    // Make sure the PDF generator + jsPDF have finished loading.
    // If the pre-warm on Step 3 was fast enough this is an instant no-op;
    // worst case it waits for the network — slower phones / first visits.
    btn.textContent = 'Loading PDF tools...';
    await _ensurePdfDeps();
    btn.textContent = 'Generating PDF...';

    // Weekly Timesheet: flag sick days beyond the 5/year allowance as unpaid
    // BEFORE the PDF is built, so the PDF and the hours sync agree. If the
    // balance check fails (offline / transient), default to paid — overpaying
    // a sick day beats blocking the whole submission.
    try { await _annotateUnpaidSickDays(state); }
    catch (e) { console.warn('sick allowance check failed (defaulting to paid):', e); }

    const pdfBuffer   = await generatePDF(_prodStateForPdf(state), state.photos);
    const pdfFilename = buildPDFFilename();
    // Log PDF size for monitoring (visible in browser console)
    const sizeKB = Math.round(pdfBuffer.byteLength / 1024);
    console.log(`[PDF] ${pdfFilename} — ${sizeKB} KB (${(sizeKB/1024).toFixed(2)} MB) | ${state.photos?.length || 0} photo(s)`);

    // ── Offline shortcut ──────────────────────────────────────────────────────
    // If this form supports offline use and there's no connection, queue it now
    // (the PDF is already built) and skip every network step. It will send itself
    // automatically when connectivity returns. See js/offline.js.
    // Custom org forms (meta.custom) are simple-fields-only, so the generic
    // offline path covers them — they're always eligible alongside the list.
    const _offlineEligible = ((typeof OFFLINE_FORMS !== 'undefined') && OFFLINE_FORMS.includes(state.submissionType))
      || window.Ordinex?.forms?.resolve(state.submissionType)?.schema?.meta?.custom === true;
    if (_offlineEligible && navigator.onLine === false && typeof saveSubmissionOffline === 'function') {
      await saveSubmissionOffline(state, pdfBuffer, pdfFilename);
      triggerDownload(pdfBuffer, pdfFilename);
      if (DRAFTABLE_FORMS.includes(state.submissionType)) deleteDraft().catch(e => console.warn('draft delete failed:', e));
      showOfflineSavedScreen(pdfFilename);
      return;
    }

    // ── Upload PDF to Supabase Storage ────────────────────────────────────────
    let pdfStorageUrl  = null;
    let pdfStoragePath = null;
    let pdfUploaded    = false;   // the PDF reached storage: My submissions has it, no local copy needed
    try {
      // Path comes from the SESSION's own claims, never from window.currentOrg
      // — that global loads after first paint, and a submit racing it filed
      // two SIGNED tailgates with no PDF at all (Sam, 26+28/08). The session
      // is the same authority RLS judges the upload by.
      const _sess = (await sbClient.auth.getSession()).data.session;
      const _sessOrgId = _sess?.user?.app_metadata?.org_id || window.currentOrg?.id || null;
      const _sessUserId = _sess?.user?.id || window.currentUser?.id || null;
      if (_sessOrgId && _sessUserId) {
        const fileName = `${_sessOrgId}/${_sessUserId}/${pdfFilename}`;
        pdfStoragePath = fileName; // always store path so admin can regenerate URL
        const pdfBlob  = new Blob([pdfBuffer], { type: 'application/pdf' });
        // Deadline-raced: a hung upload must not stall the whole submit —
        // this block is best-effort (path is stored; admin can re-sign later).
        const { error: upErr } = await _withNetTimeout(
          sbClient.storage
            .from('form-submissions')
            .upload(fileName, pdfBlob, { contentType: 'application/pdf', upsert: false }),
          'PDF upload');
        if (!upErr) {
          pdfUploaded = true;
          const { data: urlData } = await _withNetTimeout(
            sbClient.storage
              .from('form-submissions')
              .createSignedUrl(fileName, 60 * 60 * 24 * 365), // 1 year
            'signed URL');
          pdfStorageUrl = urlData?.signedUrl || null;
        }
      }
    } catch (upErr) {
      console.warn('PDF upload to storage failed:', upErr);
    }

    // ── Log submission to Supabase ────────────────────────────────────────────
    // For Weekly Timesheet + Production Report we also stamp `week_of` (Monday)
    // so the admin can pair them up by (project, foreman, week_of) and see
    // which weeks have one report but not the other.
    let _submissionId = null;
    try {
      if (window.currentUser) {
        let weekOfIso = null;
        if (state.submissionType === 'Weekly Timesheet' || state.submissionType === 'Production Report') {
          const ws = _weekStartFor(state.date);
          weekOfIso = ws.getFullYear() + '-' +
                      String(ws.getMonth() + 1).padStart(2, '0') + '-' +
                      String(ws.getDate()).padStart(2, '0');
        }
        // Supabase returns { data, error } — destructure BOTH so an RLS rejection
        // (or any other DB error) is logged loudly instead of being swallowed
        // by the outer try/catch (which only catches thrown exceptions).
        const _tradePack = typeof activeTradePack === 'function' ? activeTradePack() : null;
        const _schema = window.Ordinex?.forms?.resolve(state.submissionType, _tradePack) || null;
        const { data: subRow, error: subErr } = await _withNetTimeout(sbClient.from('submissions').insert({
          foreman_id:      _submitterId(),
          foreman_name:    _submitterName(),
          project_name:    state.project,
          submission_type: state.submissionType,
          form_key:        window.Ordinex?.forms?.keyFor(state.submissionType) || null,
          schema_id:       _schema?.id || null,
          schema_version:  _schema?.version || null,
          trade_pack:      _tradePack,
          data:            _buildSubmissionData(),
          // Email ledger (2026-09-01): 'pending' arms the server-side sender
          // and the 15-min sweeper; 'not_required' documents that this form
          // type never emails. Only edge functions move it after this.
          safety_email_status: _isSafetyEmailForm(state.submissionType) ? 'pending' : 'not_required',
          client_uuid:     state._clientUuid || null,
          pdf_url:         pdfStorageUrl,
          pdf_path:        pdfStoragePath,
          week_of:         weekOfIso,
          // Signed forms are immutable from birth: the DB trigger blocks any
          // client UPDATE/DELETE once locked_at is set (roadmap §4 Phase A).
          locked_at:       state.signature ? new Date().toISOString() : null,
        }).select('id').single(), 'submission save');
        if (subErr && String(subErr.code) === '23505') {
          // This exact filing already landed — the foreman is RETRYING after a
          // later step (the safety email) failed. Reuse the existing row
          // instead of refusing: Mike Radu, 2026-08-28, pressed submit six
          // times while the email step 401'd, and because the idempotency key
          // was being discarded right after insert (see below), every press
          // filed another signed copy.
          const { data: existing } = await sbClient.from('submissions')
            .select('id').eq('client_uuid', state._clientUuid).maybeSingle();
          _submissionId = existing?.id || null;
        } else if (subErr) {
          // ANY refusal queues, not just network-shaped ones.
          //
          // This used to test _looksLikeNetFailure(subErr.message), a regex of
          // /timeout|offline|network|failed to fetch|load failed|fetch/i. An
          // RLS refusal — "new row violates row-level security policy" — makes
          // none of those, so it fell through to a console.error, a toast
          // nobody reads on the way past, and then the ordinary success path:
          // the draft was deleted and the foreman got the green "Submitted /
          // Signed, locked and filed" screen while NOTHING had been saved.
          // A lapsed plan is enough to trigger it, which is not exotic.
          //
          // Queueing preserves the only copy of the work and retries on its
          // own. The message says which kind of problem it was, because
          // "we'll send it when you're back on signal" is a lie for a
          // permission error and would leave him waiting for a truck that is
          // never coming.
          const netShaped = _looksLikeNetFailure(subErr.message);
          if (_offlineEligible && typeof saveSubmissionOffline === 'function') {
            await saveSubmissionOffline(state, pdfBuffer, pdfFilename);
            triggerDownload(pdfBuffer, pdfFilename);
            if (DRAFTABLE_FORMS.includes(state.submissionType)) deleteDraft().catch(e => console.warn('draft delete failed:', e));
            // A refusal is not a lack of signal: the screen says why it waits, not "when you have signal".
            let refusedMsg = null;
            if (!netShaped) {
              console.error('[submissions insert] refused, queued for retry:', subErr);
              // A week the office approved: it waits in the queue until they reopen it (W4).
              const closed = /week_closed/.test(subErr.message || '');
              refusedMsg = closed ? _tsWeekClosedMsg(subErr) : window.Ordinex.i18n.t('done.refused');
            }
            showOfflineSavedScreen(pdfFilename, refusedMsg);
            if (refusedMsg) showToast(refusedMsg, 'warning');
            return;
          }
          // Not offline-eligible: there is nowhere to keep it, so the one thing
          // we must not do is claim it was filed.
          console.error('[submissions insert] failed (Activity log row missing):', subErr);
          const refused = new Error(subErr.message || 'the server refused this submission');
          refused.refused = true;   // final: the catch below must not swallow it into a success screen
          throw refused;
        }
        else _submissionId = subRow?.id || null;
        // The idempotency key stays alive until the success screen. It used to
        // die here, the moment the row landed — so when the email step AFTER
        // this failed, the retry minted a fresh key and filed a duplicate
        // signed record (six copies, 2026-08-28). Cross-form leakage is
        // already impossible: _resetFormEntryState and startAnotherForm both
        // null it on every new form entry.
      }
    } catch (logErr) {
      if (logErr?.refused) throw logErr;   // no row and nowhere to queue it: "Submission failed", the form stays
      // Same fallback for a hung request (deadline throw) or a thrown fetch error.
      if (_offlineEligible && _looksLikeNetFailure(logErr?.message) && typeof saveSubmissionOffline === 'function') {
        await saveSubmissionOffline(state, pdfBuffer, pdfFilename);
        triggerDownload(pdfBuffer, pdfFilename);
        if (DRAFTABLE_FORMS.includes(state.submissionType)) deleteDraft().catch(e => console.warn('draft delete failed:', e));
        showOfflineSavedScreen(pdfFilename);
        return;
      }
      console.warn('Could not log submission to Supabase:', logErr);
    }

    // ── Structured per-day save for the production dashboard ──────────────────
    // Production Report + Weekly Timesheet are submitted as a weekly batch in
    // the form, but the admin dashboard needs per-day rows. We expand the
    // weekly form into 7 day rows in production_reports / time_entries here.
    // submission_id links them so deleting the submission cascades the cleanup.
    // Failure is logged but doesn't block the submission — PDF + email still go.
    if (window.currentUser && state.fields) {
      try {
        if (state.submissionType === 'Production Report') {
          await syncProductionReportToDb(state, _submissionId, state._weekMode);
        } else if (state.submissionType === 'Weekly Timesheet') {
          await syncTimesheetToDb(state, _submissionId, state._weekMode);
        } else if (state.submissionType === 'Extra Work Order') {
          await syncExtraWorkOrderToDb(state, _submissionId);
        }
      } catch (syncErr) {
        console.warn('Could not sync structured data for dashboard:', syncErr);
        // The form is filed and the PDF is safe; what failed is the per-day
        // money write. Queue exactly that half for automatic retry — outbox
        // replays on login, reconnect, Background Sync and the manual pill.
        // The retry carries the answer the person just gave (weekMode on the
        // record, not inside the payload — see _confirmWeekModeOrAbort).
        if (WEEKLY_DRAFT_FORMS.includes(state.submissionType)
            && _submissionId && typeof saveSyncRepairOffline === 'function') {
          try {
            await saveSyncRepairOffline({
              submissionType: state.submissionType,
              project: state.project, date: state.date, fields: state.fields,
              _effForemanId: _submitterId(), _effForemanName: _submitterName(),
              _crewForemanId: state.productionCrewId || null,
              _crewForemanName: state.productionCrewName || '',
              // The person answered this a moment ago, for THIS filing, and it
              // has not been written yet — so the immediate retry may carry it.
              // The watchdog's rebuilds never do (see _syncSelfCheck).
              weekMode: state._weekMode || null,
            }, _submissionId);
            showToast(/week_closed/.test(syncErr?.message || '') ? _tsWeekClosedMsg(syncErr)
              : 'Filed — the dashboard numbers hit a snag and will finish syncing automatically.', 'warning');
          } catch (qErr) {
            console.warn('could not queue sync repair:', qErr);
          }
        }
      }
    }

    // Need connectivity to have stored the PDF + submission above. If we lost
    // it, queue offline-eligible forms; otherwise surface an error.
    const online = await checkBackendHealth();
    if (!online) {
      if (_offlineEligible && typeof saveSubmissionOffline === 'function') {
        await saveSubmissionOffline(state, pdfBuffer, pdfFilename);
        triggerDownload(pdfBuffer, pdfFilename);
        if (DRAFTABLE_FORMS.includes(state.submissionType)) deleteDraft().catch(e => console.warn('draft delete failed:', e));
        showOfflineSavedScreen(pdfFilename);
        return;
      }
      throw new Error('Server is offline. Please try again in a moment.');
    }

    // ── Non-safety forms: stored + synced already, no safety email. ──
    // Online the PDF is in My submissions: no download into the phone's
    // Downloads after every form. Only when the upload or the row failed is it the one copy.
    if (!_isSafetyEmailForm(state.submissionType)) {
      if (!pdfUploaded || !_submissionId) triggerDownload(pdfBuffer, pdfFilename);
      // Only once the row actually exists. The draft is the only other copy of
      // this work; throwing it away because we REACHED this line is not the
      // same as the submission having landed.
      if (_submissionId && DRAFTABLE_FORMS.includes(state.submissionType)) deleteDraft().catch(e => console.warn('draft delete failed:', e));
      showSuccessScreen({ unconfirmed: !_submissionId }, pdfFilename);
      return;
    }

    // ── Safety / inspection forms — email the safety inbox (+ CSO if chosen) ──
    btn.textContent = 'Sending email...';
    showToast('Sending email...', 'info');

    try {
      const result = await _withNetTimeout(submitForm(state, pdfBuffer, state.photos, _submissionId), 'email send');

      // Local copy only when the PDF or its row never landed (see above).
      if (!pdfUploaded || !_submissionId) triggerDownload(pdfBuffer, pdfFilename);

      // ── Delete draft on successful submission ───────────────────────────────
      if (DRAFTABLE_FORMS.includes(state.submissionType)) {
        deleteDraft().catch(e => console.warn('draft delete failed:', e));
      }

      showSuccessScreen({ ...result, unconfirmed: !_submissionId }, pdfFilename);
    } catch (netErr) {
      // Connection dropped (or hung past the deadline) mid-submit. For
      // offline-capable forms, save locally instead of losing the foreman's
      // work — it'll send on reconnect. The row may already be inserted;
      // client_uuid dedups the replay, so worst case is a duplicate email.
      // The row IS filed and its email status says 'pending' — the 15-minute
      // sweeper will deliver it from the stored PDF. Showing the old failure
      // screen here is how one tailgate became six (Mike, 28/08): tell the
      // truth instead — filed, email on its way.
      if (_submissionId) {
        if (!pdfUploaded) triggerDownload(pdfBuffer, pdfFilename);
        if (DRAFTABLE_FORMS.includes(state.submissionType)) deleteDraft().catch(e => console.warn('draft delete failed:', e));
        showSuccessScreen({ meta: window.Ordinex.i18n.t('done.safetyPending') }, pdfFilename);
        return;
      }
      const looksOffline = navigator.onLine === false || _looksLikeNetFailure(netErr?.message);
      if (_offlineEligible && looksOffline && typeof saveSubmissionOffline === 'function') {
        await saveSubmissionOffline(state, pdfBuffer, pdfFilename);
        // Same rule as the main path (T7): with signal the outbox sends it
        // in a moment, so no PDF lands in Downloads — unless the PDF never
        // reached storage and this phone holds the only copy.
        const keepCopy = navigator.onLine === false || !pdfUploaded;
        if (keepCopy) triggerDownload(pdfBuffer, pdfFilename);
        if (DRAFTABLE_FORMS.includes(state.submissionType)) deleteDraft().catch(e => console.warn('draft delete failed:', e));
        showOfflineSavedScreen(keepCopy ? pdfFilename : null);   // no "copy on this phone" line when there is none
        return;
      }
      throw netErr;
    }

  } catch (err) {
    console.error(err);
    btn.disabled = false;
    btn.textContent = '📤 Submit Form';
    showGlobalError(`Submission failed: ${err.message}`);
  }
}

function showSuccessScreen(result, pdfFilename) {
  // The in-progress toast ('Generating PDF report...', 'Sending email...')
  // has no success-side counterpart for most forms, so when the filing landed
  // in under its 3.2 s it sat over the word "Submitted" — always, offline.
  if (typeof hideToast === 'function') hideToast();
  // Belt and braces: handleSubmit already stops the heartbeat, but this screen
  // is also reached from the offline path and from the outbox replay, and a
  // tick landing here would re-create the draft the submission just replaced.
  _stopAutoSaveDraft();
  // The filing is DONE — only now does its idempotency key die (see the
  // insert above: a retry of a half-finished submission must reuse it).
  state._clientUuid = null;
  const panel = document.getElementById('step4');
  if (!panel) return;
  // The Done screen stands alone (model) — no wizard head above it, and no
  // action bar under it: "Submit Form" sitting below the word "Submitted" is
  // an offer to file the same form twice.
  const _fh = document.getElementById('fillHead');
  if (_fh) _fh.style.display = 'none';
  const _nb = document.querySelector('.nav-bar');
  if (_nb) _nb.style.display = 'none';
  // One sentence for every form (the office has it, here is where to find it),
  // then the safety email's own line — only for a form that sends one.
  const t = window.Ordinex.i18n.t;
  let meta = result.meta || '';
  if (!meta && Array.isArray(result.recipients)) {
    const n = result.recipients.length || 1;
    meta = t('done.safetySent', { n, s: n === 1 ? '' : 's', extra: result.salus?.sent ? ' (+ Salus Pro)' : '' });
  }
  // Redesign v2: the ring closes green at success — the brand gauge, complete.
  panel.innerHTML = `
    <div class="success-screen">
      <div class="success-ring" aria-hidden="true">
        <svg width="120" height="120" viewBox="0 0 120 120">
          <circle class="success-ring-base" cx="60" cy="60" r="52" fill="none" stroke-width="9"/>
          <circle class="success-ring-arc" cx="60" cy="60" r="52" fill="none" stroke-width="9"
            stroke-linecap="round" stroke-dasharray="326.7 326.7" transform="rotate(-90 60 60)"/>
          <path class="success-ring-check" d="M42 61.5 54 73.5 79 48.5" fill="none" stroke-width="7" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <h2 class="success-title">Submitted</h2>
      <p class="success-msg">${_fEsc(t(result.unconfirmed ? 'done.unconfirmed' : 'done.sent'))}</p>
      ${meta ? `<p class="success-meta">${_fEsc(meta)}</p>` : ''}
      ${pdfFilename ? (result.unconfirmed   // no row: the downloaded PDF is the copy, not a filing
        ? `<div class="success-filed"><i data-lucide="smartphone"></i>${_fEsc(t('done.localCopy'))} <b>${_fEsc(pdfFilename)}</b></div>`
        : `<div class="success-filed"><i data-lucide="folder-check"></i>Filed as <b>${_fEsc(pdfFilename)}</b></div>`) : ''}
      <button class="btn btn-next" onclick="startAnotherForm()" style="margin-top:24px;width:100%">Start another form</button>
      <button class="btn btn-back" onclick="resetApp()" style="margin-top:10px;width:100%">Back to Today</button>
    </div>`;
}

// "Start another form" on the Done screen: the crew usually files two or three
// in a row on the same project, so keep the project and drop them straight on
// the type palette instead of routing back through Today.
function startAnotherForm() {
  _stopAutoSaveDraft();
  const project = state.project;
  _resetFormEntryState();
  state.submissionType = null;
  state.allowedTypes   = null;
  state.fromDashboard  = true;
  state.project        = project;
  state.sendToSafety   = true;
  state._csoContact    = null;
  _restoreStep4Panel();                      // the Done screen ate step 4
  const fh = document.getElementById('fillHead');
  if (fh) fh.style.display = '';
  // The Done screen hides the action bar; filing another form needs it back.
  const nb = document.querySelector('.nav-bar');
  if (nb) nb.style.display = '';
  const btnNext = document.getElementById('btnNext');
  if (btnNext) { btnNext.disabled = false; btnNext.textContent = 'Next →'; }
  const btnBack = document.getElementById('btnBack');
  if (btnBack) { btnBack.disabled = false; btnBack.style.display = ''; }
  state.currentStep = 2;
  renderStep(2);
  updateProgressBar(2);
  updateNavButtons(2);
  window.scrollTo({ top: 0, behavior: _prefersReducedMotion() ? 'auto' : 'smooth' });
}

function resetApp() {
  _stopAutoSaveDraft();
  _restoreStep4Panel();
  state.currentStep = 1;
  state.project = '';
  state.submissionType = '';
  state.allowedTypes = null;
  state.fields = {};
  state.photos = [];
  state.signature = null;
  state.gcSignature = null;
  state._clientUuid = null;
  state._weekMode = null;      // see _resetFormEntryState — answer dies with the filing
  state._prodOutlierOk = null;
  state.sendToSafety = true;
  state._csoContact = null;
  // Restore the account's own identity, and clear the Production Report crew so
  // the next report ASKS again — whose production it is must be chosen on
  // purpose every time, never inherited from the last form.
  _syncNameFromAccount();
  _setProductionCrew({ picked: false });
  const _crewSel = document.getElementById('prodCrewSelect');
  if (_crewSel) _crewSel.value = '';
  window._signaturePad   = null;
  window._gcSignaturePad = null;

  document.getElementById('typeGrid').innerHTML = '';
  document.getElementById('projectSelect').innerHTML = '<option value="">Select project...</option>';
  const _today = _todayLocal();
  const _dateEl = document.getElementById('foremanDate');
  _dateEl.value = _today;
  _dateEl.max   = _today;

  // Re-enable nav buttons (disabled during submission)
  const btnNext = document.getElementById('btnNext');
  if (btnNext) { btnNext.disabled = false; btnNext.textContent = 'Next →'; }
  const btnBack = document.getElementById('btnBack');
  if (btnBack) { btnBack.disabled = false; }

  showToast('Ready for next submission', 'success');
  _fieldHome();   // always the job's board here — a submission requires a picked project
}

// showToast(msg, type) lives in js/core/toast.js (window.showToast) — shared
// with the admin panel since 2026-10-17.

// ══════════════════════════════════════════════════════════════════════════════
//  CERTIFICATIONS PAGE  (Foreman — view all workers + add/view certs)
// ══════════════════════════════════════════════════════════════════════════════

let _certWorkers    = [];   // { id, name, trade, active }[] — what the list renders (scoped)
let _certAllWorkers = [];   // full org roster behind the scope toggle
let _certShowAll    = false; // foreman pressed "Show all workers"
let _certTypes      = [];   // { id, name, validity_months }[]
let _certRecords    = [];   // worker_certifications rows (joined with cert type name)
let _certCurrentWId = null; // worker currently open in the modal

// ── Open the page ─────────────────────────────────────────────────────────────
async function openCertificationsPage() {
  showInnerPage('certificationsPage');
  const listEl = document.getElementById('certPageList');
  if (listEl) listEl.innerHTML = '<div class="docs-loading">Loading…</div>';

  const [{ data: workers }, { data: types }, { data: records }] = await Promise.all([
    sbClient.from('workers').select('id, name, trade, active').eq('active', true).order('name'),
    sbClient.from('certification_types').select('id, name, validity_months').order('name'),
    sbClient.from('worker_certifications')
      .select('id, worker_id, cert_type_id, issued_date, expires_date, document_path, notes, certification_types(name, validity_months)')
  ]);

  _certAllWorkers = workers  || [];
  _certTypes      = types    || [];
  _certRecords    = records  || [];
  _certShowAll    = false;
  _applyCertScope();

  renderCertWorkersList('');
}

// ══════════════════════════════════════════════════════════════════════════════
//  TIME CLOCK  (check in / check out: the worker's Clock card and the punch path)
// ══════════════════════════════════════════════════════════════════════════════
// A punch is one row of time_punches, written online or queued in the outbox
// (js/offline.js, syncKind 'punch') and replayed in order. What a card shows is
// derived, never stored: clock_context() (window.clockCtx, cached per user in
// ordinexClock:<uid>), the user's own punches of two weeks (ox_clock_<uid>_<monday>)
// and what the outbox still holds, read through the pure math of Ordinex.timeClock.
// Every tap works with no signal. punchMany() is the one path every punch takes.

let _clockCtxInFlight = null;
// The punches of this page that landed, in the order they landed, until a clock context asked after them
// answers: the card counts them as queued ones, so a check-in that landed never reads "Not checked in"
// because the read that followed it fell back to the cache.
const _clockLanded = [];
// The crew card's last good read of each job's punches (this page only): its paints from the phone
// reuse it, so an older open day or a GPS flag does not blink away while the server is asked again,
// and the job's rows stay known offline. A punch that lands on that job is added to it (_clockLand).
const _crewRead = new Map();
// Read tickets: a read takes the next number when it starts, and so does a punch that lands. A read is
// kept only when nothing was written to its job's kept read since it started (no landing, no newer read
// kept), so an answer that left before a check-out landed never writes over it: that check-out's
// overlay is gone once the next context read answers, and the kept read is then all that holds it.
let _crewTicket = 0;
const _crewReadAt = new Map();   // project id → the ticket of the last write to its kept read
// The cached clock_context() first (into window.clockCtx); then, online and when asked, the
// server's, which replaces the cache. Returns the fresh context, or the cached one when the
// request fails, takes longer than the network deadline, or the device is offline: never null over
// a good cache, since the worker's job list is built from it (auth.js loadUserProjects). null only
// with no cache. Never throws.
// One request at a time: a network call while one is in flight shares it, so a wake asks once;
// a cache-only call never waits for it (boot stays radio-off).
async function _loadClockContext({ network = true } = {}) {
  const key = 'ordinexClock:' + window.currentUser?.id;
  let cached = null;
  try { cached = JSON.parse(localStorage.getItem(key)); } catch { /* unreadable: no cache */ }
  if (cached) window.clockCtx = cached;
  if (!network || !navigator.onLine) return cached;
  let asked = 0;
  _clockCtxInFlight ??= Promise.resolve()
    .then(() => { asked = Date.now(); return _withNetTimeout(sbClient.rpc('clock_context'), 'clock context'); })
    .then(({ data, error }) => {
      if (error || !data) throw error || new Error('clock_context answered nothing');
      try { localStorage.setItem(key, JSON.stringify(data)); } catch { /* storage full: this session keeps it */ }
      while (_clockLanded.length && _clockLanded[0].landedAt <= asked) _clockLanded.shift();   // this answer has them
      return (window.clockCtx = data);
    })
    .catch(e => { console.warn('[clock] clock_context failed, the cached copy stays:', e); return cached; })
    .finally(() => { _clockCtxInFlight = null; });
  return _clockCtxInFlight;
}

// The time clock is on for this company and its context is known. False while the org has not
// loaded (an offline boot paints before org.load answers): the surfaces appear when it does.
function _clockEnabled() {
  return !!window.currentOrg && window.Ordinex.timeClock.enabled(window.currentOrg) && !!window.clockCtx;
}
function _clockSettings() {
  return window.Ordinex.timeClock.settingsFrom(window.currentOrg);
}
// THE rule for "today's open shift" against "an older open day" ({ since } of the open in).
function _clockIsCurrent(open) {
  return window.Ordinex.clockCorrection.isCurrentOpen(open.since, _clockSettings());
}

// Where the phone is at the tap, or null: refused, no answer in 8 s, or no API. The punch keeps
// the tap's time, never the answer's. The race also covers a permission prompt nobody answers
// (the API's own timeout only starts once the person has answered it).
function _clockGps() {
  if (!navigator.geolocation) return Promise.resolve(null);
  return new Promise(resolve => {
    setTimeout(() => resolve(null), 8000);
    navigator.geolocation.getCurrentPosition(
      pos => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, gps_accuracy_m: pos.coords.accuracy }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 });
  });
}

// BC PIPA: before the first location read of this user on this device, the card says what is
// collected and why, and waits for OK (spec 6.1). The ONE #clockNotice node moves into the clock
// card on screen: the worker's own, or the crew card of a foreman or admin, whose GPS punches come
// from there. No card to show it in: nothing to wait for.
function _clockNoticeOk() {
  const key = 'ordinexClockNotice:' + window.currentUser?.id;
  try { if (localStorage.getItem(key) === '1') return Promise.resolve(); } catch { /* storage off: ask */ }
  const node = document.getElementById('clockNotice');
  const host = document.getElementById(window.currentProfile?.role === 'worker' ? 'clockCard' : 'crewClockCard');
  if (!node || !host) return Promise.resolve();
  host.prepend(node);
  node.hidden = false;
  host.classList.add('fx-clock--asking');   // its OK is the one filled orange while it is up
  node.focus();
  return new Promise(resolve => {
    document.getElementById('clockNoticeOk').addEventListener('click', () => {
      try { localStorage.setItem(key, '1'); } catch { /* storage off: asked again next time */ }
      node.hidden = true;
      host.classList.remove('fx-clock--asking');
      resolve();
    }, { once: true });
  });
}

// A punch that landed: the cards count it until a clock context read has it (_clockLanded), and the
// crew card's kept read of its job holds it from now on (that read is older than the punch).
function _clockLand(row) {
  _clockLanded.push(row);
  _crewRead.get(row.project_id)?.push(row);
  _crewReadAt.set(row.project_id, ++_crewTicket);   // a read asked before it must not write over it
}

// The one path every punch takes (the Clock card, the crew card, a Move, the day sheets): one
// INSERT per punch, in order. Each punch is written to the outbox before it goes (write-ahead) and
// taken out when its INSERT is answered, so a reload or a crash mid-request never loses it: its
// replay answers 23505, done (migration 20261027100320). The store is no condition of an online punch:
// one that refuses the write (iOS dropping its IndexedDB connection, a full quota, storage blocked)
// leaves the punch to go out unkept, written again only if it has to wait. A punch that cannot go now
// (no signal, the line failing in this batch, a worker with punches still in the outbox: his day
// replays in order, a new punch never overtakes it) stays queued and, online, goes on the next tick.
// A refusal only a foreman can fix is kept, waiting for him (spec 8: never consumed); so is a punch the
// server did not answer (a 5xx, a gateway's error page, a statement timeout) or could not take yet (a
// closed week): it waits like one the line dropped. It stops PER WORKER: one refusal never drops the
// rest of a crew. gps: one notice and one position read for the whole batch, spread into every payload.
// From the tap until every punch is saved (landed or queued), window.__punchesInFlight keeps a
// deploy's reload away (offline.js _safeToReload); window.__punchesSending holds the ids this batch is
// sending, whose write-ahead copies a flush leaves to it. A punch that lands joins _clockLanded.
async function punchMany(payloads, { projectName, pair = null, gps = false } = {}) {
  const OR = window.Ordinex;
  let landed = 0, queued = 0, stuck = 0, lost = 0, queuedAny = false, netDown = false;
  const sending = (window.__punchesSending ??= new Set());
  window.__punchesInFlight = (window.__punchesInFlight || 0) + 1;
  try {
    let fix = {};
    if (gps) {
      await _clockNoticeOk();
      const g = await _clockGps();
      fix = { lat: g?.lat ?? null, lng: g?.lng ?? null, gps_accuracy_m: g?.gps_accuracy_m ?? null };
    }
    // Every worker with any of this user's punches in the outbox before the batch (sending, stuck, or
    // held behind a stuck one); the batch's own write-ahead copies never block its later payloads.
    const mine = await _outboxAll().then(all => all.filter(r => r.syncKind === 'punch' && _isMine(r)), () => []);
    const blocked = new Set(mine.map(r => r.workerId));
    const skipped = new Set();   // a worker whose punch the store could not keep either: his later payloads are dropped with it
    const nameOf = id => window.clockCtx?.projects?.find(p => p.id === id)?.name ?? projectName ?? null;
    for (const p of payloads) {
      const payload = { ...p, ...fix };
      if (skipped.has(payload.worker_id)) continue;
      let rec;
      try {
        rec = OR.offlineRecord.buildClock({ syncKind: 'punch', punch: payload, projectName: nameOf(payload.project_id), pair,
          workerName: _clockWorkerName(payload.worker_id) || null,
          orgId: window.currentOrg?.id, userId: window.currentUser?.id, userName: window.currentProfile?.full_name });
      } catch (e) { console.warn('[clock] a punch with no id, worker or time was skipped:', e); continue; }
      try { rec.id = await _outboxAdd(rec); } catch (e) { console.warn('[clock] punch not written ahead, sent anyway:', e); }
      sending.add(payload.id);
      // It waits: its copy kept (written now when the write-ahead failed, saved again once stuck), the worker's
      // later punches behind it. A store that keeps nothing: the punch is lost, his later ones with it, and said.
      const keep = async () => {
        try {
          if (rec.id == null) rec.id = await _outboxAdd(rec);
          else if (rec.stuck) await _outboxPut(rec);   // unsaved, its copy still waits, plain: the flush asks again
        } catch (e) { console.warn('[clock] punch could not be kept on this phone:', e); }
        if (rec.id == null) { lost++; skipped.add(payload.worker_id); return; }
        blocked.add(payload.worker_id);
        queued++;
      };
      try {
        if (!navigator.onLine || netDown || blocked.has(payload.worker_id)) { await keep(); queuedAny = true; continue; }
        let error, status;
        try {
          ({ error, status } = await _withNetTimeout(sbClient.from('time_punches')
            .insert({ ...payload, offline: false, sent_at: new Date().toISOString() }), 'punch'));
        } catch (e) { error = e; }
        // supabase-js answers a lost connection as { error } ("Failed to fetch"): queued, never toasted, and
        // the rest of the batch is queued too, without waiting on the line again
        if (error && _isNetworkError(error)) { netDown = true; await keep(); queuedAny = true; continue; }
        const r = error ? OR.offlineRecord.classifyPunch(error, rec, OR.timeClock.fmtTime, status) : { action: 'done' };
        if (r.action === 'stuck_now') {
          Object.assign(rec, { stuck: true, waiting: 'foreman', waitingText: r.waitingText, waitingPill: r.pillText });
          await keep();
          stuck++;   // kept, and counted with the queued ones: a caller tells it apart (it is no "saved on this phone")
          if (r.waitingText) showToast(r.waitingText, 'warning');
          continue;
        }
        // The server did not answer the question (a 5xx, a gateway's error page, a statement timeout: `retry`) or the
        // week is closed (`wait`): the punch is neither refused nor lost. It waits like one the line dropped: its copy
        // kept, the flush sends it (a server's own failure counts no try there), his later payloads behind it, the
        // other workers on their way. Only an answer (landed, already there, done by someone else) takes the copy out.
        if (r.action !== 'done' && r.action !== 'done_toast') { await keep(); queuedAny = true; continue; }
        // A copy the store will not let go of stays for a flush, whose replay is a duplicate (23505: done)
        if (rec.id != null) {
          try { await _outboxDelete(rec.id); } catch (e) { console.warn('[clock] an answered punch stays in the outbox:', e); }
        }
        if (r.action === 'done') {
          landed++;
          _clockLand({ ...payload, punched_by: window.currentUser?.id, punched_by_name: window.currentProfile?.full_name ?? null, landedAt: Date.now() });
        } else showToast(r.toast, 'info');
      } finally {
        sending.delete(payload.id);
      }
    }
    if (lost) {
      showToast(`Could not save ${lost > 1 ? `${lost} punches` : 'the punch'} on this phone — try again when you have signal.`, 'error');
    }
  } finally {
    window.__punchesInFlight--;
  }
  navigator.vibrate?.(40);
  _renderOfflineUI();
  if (queuedAny && navigator.onLine) setTimeout(() => flushOutbox({ toast: false }), 0);
  // The tap is answered from what the phone holds now, the context read again first (within the
  // network deadline); the server's rows repaint the card after, without holding its buttons.
  const network = navigator.onLine && !netDown;
  await _loadClockContext({ network });
  await renderClockCard({ network: false });
  await renderCrewClockCard(null, { network: false });
  if (network) { renderClockCard({ network: true }); renderCrewClockCard(); }
  return { landed, queued, stuck };
}

// One punch INSERT payload (contract 3.1) at the tap's instant, with an id made at the tap (a replay
// can never land it twice). workDate: the day the punch belongs to; an out or a break takes its open
// shift's (the database does the same), so a night shift's break after midnight reads as one day on
// the phone too.
function _punchPayload(kind, { workerId, projectId, source = 'self', replacesId = null, reason = null,
  punchedAt = new Date().toISOString(), workDate = Ordinex.clockCorrection.localDate(punchedAt) } = {}) {
  return {
    id: crypto.randomUUID(), project_id: projectId, worker_id: workerId, kind, punched_at: punchedAt,
    work_date: workDate, source, replaces_id: replacesId, reason,
    lat: null, lng: null, gps_accuracy_m: null,
  };
}
// One punch, now.
async function punch(kind, { projectName, gps = true, ...payload } = {}) {
  return punchMany([_punchPayload(kind, payload)], { projectName, gps });
}

// This user's own punches of last week and this one (work_date monday-7 .. monday+6): the server's
// rows (kept per week for an offline open), then the copies still in the outbox and the ones that
// landed since the last context read, stamped as his own on this phone so Undo works offline too (the
// stamp is on the copy only; the INSERT never carries punched_by). Offline, or when the read fails or
// outlasts the network deadline: the kept rows. With none kept for this Monday either, the list is
// marked `unread` (as _clockProjectPunches marks its own): it then says nothing about the weeks, only
// what is queued on this phone.
// ponytail: one read, no paging; PostgREST stops at 1000 rows, 70 punches a day for two weeks.
async function _clockOwnPunches(monday, { network = navigator.onLine } = {}) {
  const uid = window.currentUser?.id, wid = window.clockCtx?.worker_id;
  if (!wid) return [];
  const tc = window.Ordinex.timeClock, key = `ox_clock_${uid}_${monday}`;
  let rows = null;
  if (network) {
    const { data, error } = await _withNetTimeout(sbClient.from('time_punches')
      .select('*, punched_by_name:profiles!punched_by(full_name)')
      .eq('worker_id', wid)
      .gte('work_date', tc.weekDates(window.Ordinex.period.dayBefore(monday))[0])
      .lte('work_date', tc.weekDates(monday)[6])
      .order('punched_at'), 'own punches').catch(error => ({ error }));
    if (error) console.warn('[clock] own punches unread, the kept copy stays:', error.message);
    else {
      rows = data.map(r => ({ ...r, punched_by_name: r.punched_by_name?.full_name ?? null }));
      try { localStorage.setItem(key, JSON.stringify({ punches: rows, at: new Date().toISOString() })); } catch { /* storage full */ }
    }
  }
  let unread = false;
  if (!rows) {
    try { rows = JSON.parse(localStorage.getItem(key))?.punches; } catch { /* unreadable: none kept */ }
    if (!Array.isArray(rows)) { rows = []; unread = true; }   // a kept [] is a week read and found empty: not this
  }
  const queued = [..._clockLanded, ...(await _outboxPendingClock()).punches]
    .filter(p => p.worker_id === wid).map(p => ({ ...p, punched_by: uid }));
  const all = _clockWithQueued(rows, queued);
  return unread ? Object.assign(all, { unread: true }) : all;
}

// The server's rows first, then the outbox copies: effectivePunches keeps the first copy of an id,
// so a punch that landed but still sits in the outbox counts as the server has it.
function _clockWithQueued(serverRows, queued) {
  return [...serverRows, ...queued];
}

// The CURRENT open shift of this user's worker, in the clockCtx.open shape plus its work_date, or
// null. Nothing of his queued: the server's answer (clockCtx.open), when it is today's shift. Something
// queued, or landed since the last context read: DERIVED from the punches, never overlaid: the kept
// server rows of this week and those copies, on the job-day of the latest one (at one instant the in
// wins: a queued Move leaves him on the new job), through dayFromPunches. No network read (boot is
// radio-off). Kept in ordinexClockOpen:<uid>. work_date: the day his check-out and breaks belong to
// (the open in's, as the database stamps them); the in's local day when its row is not on the phone.
async function _clockOptimisticOpen() {
  const ctx = window.clockCtx, uid = window.currentUser?.id, tc = window.Ordinex.timeClock;
  let open = null;
  try {
    let kept = [];
    try { kept = JSON.parse(localStorage.getItem(`ox_clock_${uid}_${tc.weekDates(_todayLocal())[0]}`))?.punches ?? []; } catch { /* none */ }
    const queued = [..._clockLanded, ...(await _outboxPendingClock()).punches].filter(p => p.worker_id === ctx?.worker_id);
    if (!queued.length) {
      open = ctx?.open && _clockIsCurrent(ctx.open) ? {
        ...ctx.open,
        work_date: kept.find(p => p.id === ctx.open.punch_id)?.work_date ?? window.Ordinex.clockCorrection.localDate(ctx.open.since),
      } : null;
    } else {
      const at = p => Date.parse(p.punched_at);
      const last = queued.reduce((a, p) => (at(p) > at(a) || (at(p) === at(a) && p.kind === 'in') ? p : a));
      const rows = tc.effectivePunches(_clockWithQueued(kept, queued))
        .filter(p => p.project_id === last.project_id && p.work_date === last.work_date);
      const day = tc.dayFromPunches(rows, _clockSettings());
      const shift = day?.open ? day.shifts[day.shifts.length - 1] : null;
      if (shift && _clockIsCurrent({ since: shift.startAt })) {
        open = {
          project_id: last.project_id,
          project_name: ctx.projects?.find(p => p.id === last.project_id)?.name ?? null,
          since: shift.startAt,
          punch_id: rows.find(p => p.kind === 'in' && at(p) === Date.parse(shift.startAt))?.id ?? null,
          on_break_since: day.onBreakSince,
          work_date: last.work_date,
        };
      }
    }
  } catch (e) { console.warn('[clock] open shift unreadable:', e); }
  try { localStorage.setItem('ordinexClockOpen:' + uid, JSON.stringify(open)); } catch { /* storage off */ }
  return open;
}

// Every open job-day of these punches, per worker, oldest first: { [worker_id]: DayResult[] }, each
// day with `openIn`, the row of its open shift's in. An open day's time is its OPEN shift's start
// (D5): in 7:00, out 8:00, in 22:00 read at 00:30 is tonight's shift, not an old one.
function _clockOpenDaysOf(rows) {
  const tc = window.Ordinex.timeClock, settings = _clockSettings(), groups = {}, open = {};
  for (const p of tc.effectivePunches(rows)) (groups[`${p.worker_id}|${p.project_id}|${p.work_date}`] ??= []).push(p);
  const since = d => Date.parse(d.shifts[d.shifts.length - 1].startAt);
  for (const g of Object.values(groups)) {
    const d = tc.dayFromPunches(g, settings);
    if (!d?.open) continue;
    d.openIn = g.find(p => p.kind === 'in' && Date.parse(p.punched_at) === since(d)) ?? null;
    (open[d.workerId] ??= []).push(d);
  }
  for (const days of Object.values(open)) days.sort((a, b) => since(a) - since(b));
  return open;
}

// The open days of the worker that are NOT today's shift, any job, oldest first: the red "still
// checked in since yesterday" line.
function _clockOlderOpenDays(ownPunches) {
  return Object.values(_clockOpenDaysOf(ownPunches)).flat()
    .filter(d => !_clockIsCurrent({ since: d.shifts[d.shifts.length - 1].startAt }))
    .sort((a, b) => Date.parse(a.shifts[a.shifts.length - 1].startAt) - Date.parse(b.shifts[b.shifts.length - 1].startAt));
}

// What Undo offers: his own last punch, while it is still the last (clockCorrection.undoTarget). The
// check-in of the shift he is on; or a check-out whose shift would be today's again if reopened (the
// card's own rule, _clockIsCurrent), so yesterday's check-out is not one slip away the next morning:
// a closed day reopened by mistake is the foreman's to fix. After a Move (its last row is the out of
// the old job, and he is on site) nothing. null: no Undo.
function _clockUndoOffer(own, open) {
  const target = window.Ordinex.clockCorrection.undoTarget(own, window.currentUser?.id);
  if (target?.kind === 'in') return open?.punch_id === target.id ? target : null;
  if (target?.kind !== 'out' || open) return null;
  const tc = window.Ordinex.timeClock;
  const day = tc.dayFromPunches(tc.effectivePunches(own)
    .filter(p => p.project_id === target.project_id && p.work_date === target.work_date), _clockSettings());
  const shift = day?.shifts.find(sh => Date.parse(sh.endAt) === Date.parse(target.punched_at));
  return shift && _clockIsCurrent({ since: shift.startAt }) ? target : null;
}

let _clockView = null;          // what the card shows: the last render's data, painted by _clockPaint
let _clockBusy = null;          // the id of the card button whose punch is on its way
let _clockConfirm = false;      // Check out tapped once: a second tap within 5 s confirms
let _clockConfirmTimer = null;
let _clockTick = null;          // repaints the minutes of a break every 30 s while the page is visible
let _clockRenderSeq = 0;        // only the latest render paints: an older, slower one never overwrites it

// The worker's Clock card on Today. Hidden unless the user is a worker and the time clock is on; a
// foreman or admin punches himself through the (you) row of the crew card, never this card. The
// first paint comes from what the phone holds, then the server's rows repaint it: a line that
// hangs (signal without data) never keeps the card off the screen.
async function renderClockCard({ network = navigator.onLine } = {}) {
  const seq = ++_clockRenderSeq;
  try {
    const card = document.getElementById('clockCard');
    if (!card) return;
    if (!(window.currentProfile?.role === 'worker' && _clockEnabled())) { card.hidden = true; return; }
    if (network && card.hidden) await _clockDraw(card, seq, false);
    await _clockDraw(card, seq, network);
  } catch (e) { console.warn('[clock] the clock card could not be drawn:', e); }
}
// One paint of the card from the data (network: also the server's rows); the latest render only.
async function _clockDraw(card, seq, network) {
  const ctx = window.clockCtx, tc = window.Ordinex.timeClock;
  let view = { linked: false };
  if (ctx.worker_id) {
    const own = await _clockOwnPunches(tc.weekDates(_todayLocal())[0], { network });
    const open = await _clockOptimisticOpen();
    const target = _clockUndoOffer(own, open);
    // the day of the shift he is on: his check-out and his breaks carry it, as the database does
    const openWorkDate = open?.work_date ?? null;
    // Yesterday from ONE weekFromPunches over both weeks: a shift chained into it from another
    // job or from the day before counts as the week's math counts it. Shown once it is closed.
    const days = Object.values(tc.weekFromPunches(own, _clockSettings())
      .byWorker[ctx.worker_id]?.[window.Ordinex.period.dayBefore(_todayLocal())] ?? {});
    const first = days.reduce((a, d) => (!a || Date.parse(d.startAt) < Date.parse(a.startAt) ? d : a), null);
    const lastOut = days.reduce((a, d) => (!a || Date.parse(d.endAt) > Date.parse(a.endAt) ? d : a), null);
    const last = days.length && days.every(d => d.endAt)
      ? { start: tc.fmtTime(first.startAt), end: tc.fmtTime(lastOut.endAt), hours: tc.round1(days.reduce((h, d) => h + d.worked, 0)) }
      : null;
    view = { linked: true, open, openWorkDate, target, undo: target?.kind ?? null, older: _clockOlderOpenDays(own)[0] ?? null, last };
  }
  if (seq !== _clockRenderSeq) return;
  _clockView = view;
  document.getElementById('clockBtn').onclick = _clockTapIn;
  document.getElementById('clockOutBtn').onclick = _clockTapOut;
  document.getElementById('clockBreakBtn').onclick = _clockTapBreak;
  document.getElementById('clockUndo').onclick = _clockTapUndo;
  _clockPaint();
  card.hidden = false;   // only once painted: a CHECK IN on screen always works
}

// Paints _clockView with the tap state (busy, confirm). No data read: a tap and the 30 s break
// counter repaint without asking anything.
function _clockPaint() {
  const v = _clockView;
  if (!v) return;
  const $ = id => document.getElementById(id);
  const tc = window.Ordinex.timeClock;
  const open = v.open, onBreak = !!open?.on_break_since;
  $('clockState').className = 'fx-clock-state'
    + (!v.linked ? '' : onBreak ? ' fx-clock-state--break' : open ? ' fx-clock-state--in' : ' fx-clock-state--out');
  // #clockStateTxt is the live region: written only when the state changes (a repaint of the same text
  // would be read out again). The break's minutes tick every 30 s beside it, outside it.
  const state = !v.linked ? 'Ask your admin to link your account'
    : onBreak ? `On break since ${tc.fmtTime(open.on_break_since)}`
    : open ? `On site since ${tc.fmtTime(open.since)}` : 'Not checked in';
  if ($('clockStateTxt').textContent !== state) $('clockStateTxt').textContent = state;
  $('clockStateMin').textContent = onBreak ? ` · ${Math.floor((Date.now() - Date.parse(open.on_break_since)) / 60000)} min` : '';
  const job = $('clockJob');
  job.textContent = open?.project_name || '';
  job.hidden = !job.textContent;
  $('clockOpenNotice').hidden = !v.older;
  if (v.older) {
    // the day it started, named as the admin's On site now names it: "yesterday", "Fri", "Wed Oct 14"
    const since = v.older.shifts[v.older.shifts.length - 1].startAt;
    $('clockOpenNoticeTxt').textContent = `You're still checked in since ${window.Ordinex.period.dayName(since, Date.now())} ${tc.fmtTime(since)}`
      + ' — tell your foreman, they can set the end time.';
  }
  const btn = $('clockBtn'), out = $('clockOutBtn'), brk = $('clockBreakBtn'), undo = $('clockUndo'), last = $('clockLast');
  btn.hidden = !v.linked || !!open || !window.fieldProject?.id;
  btn.dataset.state = _clockBusy === 'clockBtn' ? 'busy' : 'out';
  btn.textContent = _clockBusy === 'clockBtn' ? 'Checking in…' : 'CHECK IN';
  out.hidden = out.parentElement.hidden = !open;   // off site the row is empty: no gap for it
  out.dataset.state = _clockBusy === 'clockOutBtn' ? 'busy' : _clockConfirm ? 'confirm' : 'out';
  out.textContent = _clockBusy === 'clockOutBtn' ? 'Checking out…' : _clockConfirm ? 'Confirm check out' : 'Check out';
  // Start break only where the company records breaks by hand; an open break can always be ended
  brk.hidden = !open || (!onBreak && _clockSettings().breakMode !== 'manual');
  brk.textContent = onBreak ? 'Resume work' : 'Start break';
  undo.hidden = !v.undo;
  undo.textContent = v.undo === 'in' ? 'Undo check-in' : v.undo === 'out' ? 'Undo check-out' : '';
  for (const b of [btn, out, brk, undo]) b.disabled = !!_clockBusy;
  last.hidden = !v.last;
  if (v.last) last.textContent = `Yesterday ${v.last.start} – ${v.last.end} · ${v.last.hours.toFixed(1)} h`;
  const tick = onBreak && document.visibilityState === 'visible';
  if (tick && !_clockTick) _clockTick = setInterval(_clockPaint, 30000);
  if (!tick && _clockTick) { clearInterval(_clockTick); _clockTick = null; }
}

// One punch per tap: the card's buttons stay disabled from the tap until the punch has landed or
// been queued (spec 8), so a double tap, or a second tap while the GPS answers, sends nothing twice.
// _clockRunning is that punch on its way: a Move tapped meanwhile waits for it.
let _clockRunning = null;
async function _clockRun(id, run) {
  if (_clockBusy) return;
  _clockBusy = id;
  _clockPaint();
  const running = (async () => {
    try { await run(); } catch (e) {
      console.warn('[clock] the punch was not saved:', e);
      showToast('Could not save the punch: ' + (e?.message || e), 'error');
    } finally {
      _clockBusy = null;
      _clockPaint();
    }
  })();
  _clockRunning = running;
  await running;
  if (_clockRunning === running) _clockRunning = null;
}
function _clockTapIn() {
  _clockRun('clockBtn', () => punch('in', {
    workerId: window.clockCtx.worker_id, projectId: window.fieldProject.id, projectName: window.fieldProject.name }));
}
// Two taps: the first asks "Confirm check out" for 5 s (a pocket or a glove taps once).
function _clockTapOut() {
  if (_clockBusy) return;
  clearTimeout(_clockConfirmTimer);
  if (!_clockConfirm) {
    _clockConfirm = true;
    _clockPaint();
    _clockConfirmTimer = setTimeout(() => { _clockConfirm = false; _clockPaint(); }, 5000);
    return;
  }
  _clockConfirm = false;
  const { open, openWorkDate } = _clockView;   // on break too: only the out is written, the day closes the break at it
  _clockRun('clockOutBtn', () => punch('out', {
    workerId: window.clockCtx.worker_id, projectId: open.project_id, projectName: open.project_name,
    workDate: openWorkDate ?? undefined }));
}
// A break reads no location: the PIPA notice promises it only when checking in or out.
function _clockTapBreak() {
  const { open, openWorkDate } = _clockView;
  _clockRun('clockBreakBtn', () => punch(open.on_break_since ? 'break_end' : 'break_start', {
    workerId: window.clockCtx.worker_id, projectId: open.project_id, projectName: open.project_name,
    workDate: openWorkDate ?? undefined, gps: false }));
}
function _clockTapUndo() {
  const target = _clockView.target;
  _clockRun('clockUndo', () => punchMany(Ordinex.clockCorrection.undoPunch(target), { gps: false }));
}

// ── The foreman's crew card ──────────────────────────────────────────────────

// The punches of one job (projectId null: every job the reader's RLS lets him read) of work_date
// fromDate..toDate, of one worker when given, the author's name as text (D9). Paged: PostgREST stops
// a select at 1000 rows without a word, and the cut drops the NEWEST rows, so a day whose check-out
// was punched lately would read as still open (G2). Offline, or when the read fails or outlasts the
// network deadline: [] marked `unread` (the cards then stand on the clock context and the outbox).
async function _clockProjectPunches(projectId, fromDate, toDate, workerId = null) {
  const unread = () => Object.assign([], { unread: true });
  if (!navigator.onLine) return unread();
  const rows = [];
  try {
    for (let from = 0; ; from += 1000) {
      let q = sbClient.from('time_punches').select('*, punched_by_name:profiles!punched_by(full_name)')
        .gte('work_date', fromDate).lte('work_date', toDate);
      if (projectId) q = q.eq('project_id', projectId);
      if (workerId) q = q.eq('worker_id', workerId);
      const { data, error } = await _withNetTimeout(q.order('punched_at').order('id').range(from, from + 999), 'job punches');
      if (error) throw error;
      rows.push(...data);
      if (data.length < 1000) break;
    }
  } catch (e) {
    console.warn('[clock] the job punches are unread, the clock context stands in:', e?.message ?? e);
    return unread();
  }
  return rows.map(r => ({ ...r, punched_by_name: r.punched_by_name?.full_name ?? null }));
}

// The open shifts the clock context knows (the crew's and the user's own) as the punch rows they
// stand for: an in, and a break_start while on break. The server's word on who is in when the phone
// cannot read the punches (offline). Only a CURRENT shift: an older open day is for the read punches
// to show (14 days), and a stale one must not come back. A shift whose in was read (`read`) is left
// to the read rows.
function _clockCtxRows(read = new Set()) {
  const ctx = window.clockCtx, rows = [];
  const add = (workerId, o) => {
    if (!o || read.has(o.punch_id) || !_clockIsCurrent(o)) return;
    const base = { worker_id: workerId, project_id: o.project_id, punched_by_name: o.by_name ?? null,
      work_date: window.Ordinex.clockCorrection.localDate(o.since) };
    rows.push({ ...base, id: o.punch_id ?? null, kind: 'in', punched_at: o.since });
    if (o.on_break_since) rows.push({ ...base, id: null, kind: 'break_start', punched_at: o.on_break_since });
  };
  for (const c of ctx?.crew ?? []) add(c.worker_id, c.open);
  if (ctx?.worker_id) add(ctx.worker_id, ctx.open);
  return rows;
}

// Every day still open on this job within the last 14 days (W3 open point 1), per worker, oldest
// first: { [worker_id]: DayResult[] }, an array on purpose ("yesterday open + today in" is two open
// days of one worker). Derived, never laid over by hand (B4-2): the job's rows, the current shifts
// of the clock context, then what this phone still holds (landed since the last context read, or
// queued) — server rows first, so the server's copy of a punch is the one that counts (E2-a). The
// job's rows: read now (network), else the last good read of this page (_crewRead).
async function _clockOpenOnProject(projectId, { network = navigator.onLine } = {}) {
  const OR = window.Ordinex, today = _todayLocal();
  let from = today;
  for (let i = 0; i < 14; i++) from = OR.period.dayBefore(from);
  const ticket = ++_crewTicket;
  let read = network ? await _clockProjectPunches(projectId, from, OR.period.dayAfter(today)) : null;
  if (read && !read.unread && ticket > (_crewReadAt.get(projectId) ?? 0)) {
    _crewRead.set(projectId, read);
    _crewReadAt.set(projectId, ticket);
  } else read = _crewRead.get(projectId) ?? [];   // no read, or one older than what the kept rows hold
  const queued = [..._clockLanded, ...(await _outboxPendingClock()).punches];
  const rows = _clockWithQueued([...read, ..._clockCtxRows(new Set(read.map(r => r.id)))], queued)
    .filter(p => p.project_id === projectId);
  return _clockOpenDaysOf(rows);
}

// A worker's name: from the crew, the worker list, or the user's own.
function _clockWorkerName(id) {
  const ctx = window.clockCtx;
  return ctx?.crew?.find(c => c.worker_id === id)?.name
    || (window.workersFull ?? []).find(w => String(w.id) === String(id))?.name
    || (id === ctx?.worker_id ? window.currentProfile?.full_name : '') || '';
}

let _crewView = null;          // what the crew card shows: the last render's data, painted by _crewPaint
let _crewRenderSeq = 0;        // only the latest render paints
let _crewConfirm = null;       // { id, kind } of a row tapped once: a second tap within 5 s does it
let _crewConfirmTimer = null;

// The foreman's crew card (spec 6.2): who is on this job now. Idempotent; draws the ONE
// #crewClockCard node wherever it sits, or first moves it into `container` (the Crew clock page's
// slot; Today takes it back: never a second copy, its ids stay unique — A24). Hidden unless the user
// is a foreman or admin, the time clock is on and a job is picked. Its first paint comes from what
// the phone holds, then the job's punches repaint it (a line that hangs never keeps it off screen).
// network false: the phone's copy only — punchMany paints so, then asks the server without waiting,
// so a tap's buttons are free again before the read of the job comes back.
async function renderCrewClockCard(container = null, { network = navigator.onLine } = {}) {
  const seq = ++_crewRenderSeq;
  try {
    const card = document.getElementById('crewClockCard');
    if (!card) return;
    if (container && card.parentElement !== container) container.appendChild(card);
    // Today shows the one-line summary; the Crew clock page the whole list (css .fx-clock-crew--compact).
    card.classList.toggle('fx-clock-crew--compact', !!document.getElementById('homeScreen')?.contains(card));
    const role = window.currentProfile?.role;
    if (!((role === 'foreman' || role === 'admin') && _clockEnabled() && window.fieldProject?.id)) { card.hidden = true; return; }
    if (_crewView && _crewView.projectId !== window.fieldProject.id) {   // another job: its rows, never the last job's buttons
      _crewView = _crewConfirm = null;
      card.hidden = true;
    }
    if (network && card.hidden) await _crewDraw(card, seq, false);
    await _crewDraw(card, seq, network);
  } catch (e) { console.warn('[clock] the crew card could not be drawn:', e); }
}
// One paint of the card from the data (network: the job's punches read too); the latest render only.
async function _crewDraw(card, seq, network) {
  const view = await _crewData(window.fieldProject, network);
  if (seq !== _crewRenderSeq) return;
  _crewView = view;
  const $ = id => document.getElementById(id);
  $('crewClockRows').onclick = _crewRowClick;
  $('crewCheckInAll').onclick = crewCheckInAll;
  $('crewCheckOutAll').onclick = crewCheckOutAll;
  $('crewAddSomeone').onclick = _crewAddOpen;
  $('crewAddSelect').onchange = _crewAddPicked;
  _crewPaint();
  card.hidden = false;
}

// The rows: his crew (clock_context), anyone else open on this job (a worker borrowed from another
// crew, so somebody can close his day) and the user himself when he has a workers row "(you)". A
// row's dot and words come from his CURRENT open shift (_clockIsCurrent, never a calendar test);
// every older day still open on this job adds "open since {Day}" to the same row (Review Focus 1).
// Open on another job now: that job's name, and "Move here" when it is one of the user's jobs.
async function _crewData(job, network) {
  const ctx = window.clockCtx, tc = window.Ordinex.timeClock, settings = _clockSettings();
  const here = await _clockOpenOnProject(job.id, { network });
  const queued = [..._clockLanded, ...(await _outboxPendingClock()).punches];
  const elsewhere = _clockOpenDaysOf(_clockWithQueued(_clockCtxRows(), queued));
  const jobs = ctx.projects ?? [];
  const byId = Object.fromEntries(jobs.map(j => [j.id, j]));
  const site = tc.siteOf(byId[job.id], byId);
  const current = d => _clockIsCurrent({ since: d.shifts[d.shifts.length - 1].startAt });
  const people = (ctx.crew ?? []).map(c => ({ id: c.worker_id, name: c.name, crew: true, open: c.open }));
  const listed = new Set(people.map(p => p.id));
  people.push(...Object.keys(here).filter(id => !listed.has(id) && id !== ctx.worker_id)
    .map(id => ({ id, name: _clockWorkerName(id) || 'Worker', crew: false, open: null }))
    .sort((a, b) => a.name.localeCompare(b.name)));
  if (ctx.worker_id) {
    const me = people.find(p => p.id === ctx.worker_id);
    if (me) me.you = true;
    else people.push({ id: ctx.worker_id, name: _clockWorkerName(ctx.worker_id), crew: true, open: ctx.open, you: true });
  }
  const rows = people.map(p => {
    const days = here[p.id] ?? [];
    const cur = days.filter(current).pop() ?? null;
    const older = days.filter(d => !current(d));
    let away = null;
    const there = cur ? null : (elsewhere[p.id] ?? []).filter(d => d.projectId !== job.id && current(d)).pop();
    if (there) {
      away = {
        projectId: there.projectId, workDate: there.date, since: there.shifts[there.shifts.length - 1].startAt,
        name: jobs.find(j => j.id === there.projectId)?.name ?? (p.open?.project_id === there.projectId ? p.open.project_name : ''),
        mine: jobs.some(j => j.id === there.projectId),
      };
    }
    return { ...p, cur, older, away, state: cur ? (cur.onBreakSince ? 'break' : 'in') : away ? 'away' : 'out',
      far: !!cur && tc.isFar(cur.openIn, site, settings.gpsFlagM) };   // the in of the shift he is on now
  });
  return { projectId: job.id, projectName: job.name, rows, onSite: rows.filter(r => r.cur).length };
}

// Who the two buttons take: "Check in all" the crew (and the user) not on the clock now; "Check out
// all" everyone open or on break on THIS job — exactly the rows with the in / break dot. A row that is
// only an older open day is never in it: an out now would be glued to that day's in (the stamp trigger
// gives an out the work_date of the open in), closing Monday with a 50 h shift; that day is closed
// through its sheet with a chosen time (spec 6.2 "Add missing check-out").
function _crewTargets() {
  const rows = _crewView?.rows ?? [];
  return { checkIn: rows.filter(r => r.crew && r.state === 'out'), checkOut: rows.filter(r => r.cur) };
}

// Paints _crewView with the tap state (a row asking to confirm, a punch on its way). No data read. A
// row is a box of sibling buttons: its main part (check in / out, or the older day), then "open since
// {Ddd}" and "Move here" when they apply; a row on another job has no main action. While a punch is
// on its way they are aria-disabled, not disabled, so the keyboard focus survives the repaint and is
// given back to the same button (a second Enter confirms).
function _crewPaint() {
  const v = _crewView;
  if (!v) return;
  const tc = window.Ordinex.timeClock, esc = _fieldEsc, busy = !!_clockBusy, off = busy ? ' aria-disabled="true"' : '';
  const weekday = ymd => { const [y, m, d] = ymd.split('-').map(Number); return new Date(y, m - 1, d).toLocaleDateString('en-CA', { weekday: 'short' }); };
  const rowsEl = document.getElementById('crewClockRows');
  const had = rowsEl.contains(document.activeElement) ? document.activeElement : null;
  const focus = had && { id: had.closest('[data-worker-id]')?.dataset.workerId, go: had.dataset.go };
  document.getElementById('crewClockCount').textContent = `${v.onSite}/${v.rows.length}`;
  rowsEl.innerHTML = v.rows.map(r => {
    const ask = _crewConfirm?.id === r.id ? _crewConfirm.kind : null;
    const dot = r.cur ? r.state : r.older.length ? 'open' : 'out';
    let said = '';
    if (ask) said = `${ask === 'in' ? 'Check in' : 'Check out'} ${r.name}?`;
    else if (r.state === 'in') said = `in ${tc.fmtTime(r.cur.shifts[r.cur.shifts.length - 1].startAt)}`;
    else if (r.state === 'break') said = `break ${tc.fmtTime(r.cur.onBreakSince)}`;
    else if (r.state === 'away') said = `in · ${r.away.name} since ${tc.fmtTime(r.away.since)}${r.away.mine ? '' : ' — ask their foreman'}`;
    else if (!r.older.length) said = 'not in';
    const name = `<span class="fx-row-name">${esc(r.name)}${r.you ? ' (you)' : ''}</span>`;
    const inner = `<span class="fx-row-dot fx-row-dot--${dot}${r.far ? ' fx-row-dot--far' : ''}"></span>`
      + (r.state === 'away'   // on another job: the name, and under it where he is (the long line never squeezes the name)
        ? `<span class="fx-row-txt">${name}<span class="fx-row-sub">${esc(said)}</span></span>`
        : name + (said ? `<span class="fx-row-mut${ask ? ' fx-clock-wrap' : ''}">${esc(said)}</span>` : ''))
      + (r.far && !ask ? '<span class="fx-clock-far">⚠ GPS</span>' : '');
    const main = r.state === 'away'
      ? `<div class="fx-clock-main">${inner}</div>`
      : `<button type="button" class="fx-clock-main" data-go="main"${off}>${inner}</button>`;
    let sides = '';
    if (!ask && r.older.length) {
      sides += (said ? '<span class="fx-row-mut fx-clock-sep" aria-hidden="true"> · </span>' : '')
        + `<button type="button" class="fx-row-side" data-go="day"${off}>open since ${esc(weekday(r.older[0].date))}</button>`;
    }
    if (!ask && r.away?.mine) sides += `<button type="button" class="fx-row-side" data-go="move"${off}>Move here</button>`;
    return `<div class="fx-row fx-clock-row${r.state === 'away' ? ' fx-row--static' : ''}" data-worker-id="${esc(r.id)}"`
      + `${ask ? ' data-confirm' : ''}>${main}${sides}</div>`;
  }).join('');
  if (focus?.id) {
    const row = [...rowsEl.querySelectorAll('[data-worker-id]')].find(x => x.dataset.workerId === focus.id);
    (row?.querySelector(`[data-go="${focus.go}"]`) ?? row?.querySelector('[data-go]'))?.focus({ preventScroll: true });
  }
  const { checkIn, checkOut } = _crewTargets();
  document.getElementById('crewCheckInAll').disabled = busy || !checkIn.length;
  document.getElementById('crewCheckOutAll').disabled = busy || !checkOut.length;
  // Today's one-line card offers only the bulk buttons that have someone to take
  const compact = document.getElementById('crewClockCard').classList.contains('fx-clock-crew--compact');
  document.getElementById('crewCheckInAll').hidden = compact && !checkIn.length;
  document.getElementById('crewCheckOutAll').hidden = compact && !checkOut.length;
  document.getElementById('crewAddSomeone').disabled = busy;
}

function _crewRowClick(e) {
  const btn = e.target.closest('[data-go]');
  const id = btn?.closest('[data-worker-id]')?.dataset.workerId;
  if (!id || _clockBusy) return;
  if (btn.dataset.go === 'move') _crewRun(() => crewMoveHere(id));
  else if (btn.dataset.go === 'day') _crewOpenDay(id);
  else _crewTap(id);
}

// A row tap: a row that is only an older day opens its sheet; otherwise the first tap asks
// "Check in {name}?" / "Check out {name}?" on the row itself and a second tap within 5 s does it
// (spec 6.2: inline, never a dialog). A row on another job has no tap of its own.
function _crewTap(id) {
  const r = _crewView?.rows.find(x => x.id === id);
  if (!r || r.state === 'away' || _clockBusy) return;
  if (r.state === 'out' && r.older.length) { _crewOpenDay(id); return; }
  const kind = r.state === 'out' ? 'in' : 'out';
  clearTimeout(_crewConfirmTimer);
  if (_crewConfirm?.id !== id || _crewConfirm.kind !== kind) {   // asked again when the row changed under the finger
    _crewConfirm = { id, kind };
    _crewPaint();
    _crewConfirmTimer = setTimeout(() => { _crewConfirm = null; _crewPaint(); }, 5000);
    return;
  }
  _crewConfirm = null;
  _crewRun(() => crewPunch(id, kind));
}
// The sheet of the oldest day this worker left open on this job.
function _crewOpenDay(id) {
  const r = _crewView?.rows.find(x => x.id === id);
  if (r?.older.length) openClockDaySheet(id, _crewView.projectId, r.older[0].date, { onSaved: renderCrewClockCard });
}

// One crew action at a time: the card's rows and buttons wait from the tap until its punches have
// landed or been queued — the worker card's own guard (_clockRun: one busy flag for every clock tap).
async function _crewRun(run) {
  await _clockRun('crew', async () => { _crewPaint(); await run(); });
  _crewPaint();
}

// One crew punch (spec D3): source 'crew', the foreman's phone's GPS. An out or a break takes the
// day of the shift it closes, as the database stamps it (a night shift stays one day on the phone).
async function crewPunch(workerId, kind, { projectId = window.fieldProject.id } = {}) {
  const r = _crewView?.rows.find(x => x.id === workerId);
  return punch(kind, { workerId, projectId, projectName: window.fieldProject?.name, source: 'crew',
    workDate: kind === 'in' ? undefined : r?.cur?.date });
}

// One batch for the two buttons: one instant, one GPS read spread into every punch, one INSERT each in
// a fixed worker order (contract W1 #15); a worker refused never stops the others (punchMany). Then one
// toast that counts what landed and what waits on this phone — only when every punch did one or the
// other: a refusal ("could not be saved", "already checked … in") keeps its own toast on screen.
async function _crewBatch(payloads, verb) {
  payloads.sort((a, b) => a.worker_id.localeCompare(b.worker_id));
  await _crewRun(async () => {
    const { landed, queued, stuck } = await punchMany(payloads, { projectName: _crewView?.projectName, gps: true });
    if (stuck || landed + queued !== payloads.length) return;
    showToast([landed && `${landed} ${verb}`, queued && `${queued} saved on this phone`].filter(Boolean).join(' · '), 'success');
  });
}
async function crewCheckInAll() {
  const v = _crewView, rows = _crewTargets().checkIn;
  if (!v || !rows.length || _clockBusy || !confirm('Check in all?')) return;
  const punchedAt = new Date().toISOString();
  await _crewBatch(rows.map(r => _punchPayload('in', { workerId: r.id, projectId: v.projectId, source: 'crew', punchedAt })),
    'checked in');
}
async function crewCheckOutAll() {
  const v = _crewView, rows = _crewTargets().checkOut;
  if (!v || !rows.length || _clockBusy || !confirm('Check out all?')) return;
  const punchedAt = new Date().toISOString();
  await _crewBatch(rows.map(r => _punchPayload('out',
    { workerId: r.id, projectId: v.projectId, source: 'crew', punchedAt, workDate: r.cur.date })), 'checked out');
}

// "Move here": out of the other job of the user's he is on and in on this one, one instant, each half
// named after its own job (B4-7); the out half takes the day of the shift it closes.
async function crewMoveHere(workerId) {
  const away = _crewView?.rows.find(x => x.id === workerId)?.away;
  if (!away?.mine) return;
  return punchMany(window.Ordinex.clockCorrection.movePair({ workerId, projectId: away.projectId, workDate: away.workDate },
    window.fieldProject.id, new Date().toISOString(), 'crew'), { projectName: window.fieldProject.name, pair: 'move', gps: true });
}

// "+ Someone else": every worker (the timesheet's picker, "My crew" / "Other workers"), for one
// borrowed from another crew; picked and confirmed, he is checked in here and joins the rows.
function _crewAddOpen() {
  const sel = document.getElementById('crewAddSelect');
  if (!sel.hidden) { sel.hidden = true; return; }
  sel.innerHTML = '<option value="">Choose a worker</option>';
  _appendGroupedWorkerOptions(sel, w => {
    const o = document.createElement('option');
    o.value = String(w.id);
    o.textContent = w.name;
    return o;
  });
  sel.hidden = false;
  sel.focus();
}
function _crewAddPicked() {
  const sel = document.getElementById('crewAddSelect');
  const id = sel.value, name = sel.selectedOptions[0]?.textContent;
  sel.hidden = true;
  if (!id || _clockBusy || !confirm(`Check in ${name}?`)) return;
  _crewRun(() => crewPunch(id, 'in'));
}

// ── The day sheet: one worker's punches of one job-day, fixed by new rows ──────────────────

let _clockDayCtx = null;   // the open sheet's day (the correction ctx) and what the save needs
let _clockDaySeq = 0;      // only the latest open fills the sheet; a close drops a read still on its way
const _CLOCK_KIND = { in: 'Check in', out: 'Check out', break_start: 'Start break', break_end: 'Resume work' };

// "Mon Oct 12" of a 'YYYY-MM-DD' (Ordinex.period.dayLabel: the admin's sheet title is the same formatter)
function _clockDayLabel(ymd) {
  return window.Ordinex.period.dayLabel(ymd);
}

// The day sheet of one worker on one job and work_date (spec 6.2): its punches as radio rows, the
// date and time boxes, the reason and Break (min). Break (min) is the minutes the WEEK takes off this
// job-day — weekFromPunches over his punches on every job the user can read, the day before to the
// day after, the server's rows first and this phone's after (W3 Task 0b review): on a Move day the
// break of a block lands on the job where it fell due, so this job-day alone could read 0 where the
// week deducts 30, and "no lunch" (0) could never be saved. A day still open, or no derived day:
// empty — its break is still to come (an open shift has 0 h), and whatever is typed, 0 included, is
// written with the check-out that closes it; nothing is written for the break until a value is
// typed. The list and the corrections keep this job-day's rows. With the server's rows unread
// (offline, or the read failed) the sheet stands on what this phone last saw — the crew card's kept
// read of this job, and his punches still on the phone — says so, and saves: the corrections queue
// and replay like any punch, and one whose target changed meanwhile answers then, on the pill or a
// toast, never in silence. Nothing at all to stand on: it says so and saves nothing.
async function openClockDaySheet(workerId, projectId, workDate, { onSaved } = {}) {
  const OR = window.Ordinex, tc = OR.timeClock, esc = _fieldEsc, $ = id => document.getElementById(id);
  const seq = ++_clockDaySeq;
  _clockDayCtx = null;
  $('clockDayTitle').textContent = `${_clockDayLabel(workDate)} · ${_clockWorkerName(workerId)}`;
  $('clockDayList').innerHTML = '<div class="fx-row-mut">Loading…</div>';
  Object.assign($('clockFixDate'), { value: workDate, min: OR.period.dayBefore(workDate), max: OR.period.dayAfter(workDate) });
  for (const id of ['clockFixTime', 'clockFixReason', 'clockBreakMin']) $(id).value = '';
  $('clockDayError').textContent = '';
  $('clockAddOut').parentElement.hidden = true;
  $('clockAddOut').disabled = $('clockFixSave').disabled = true;
  $('clockDaySheet').style.display = 'flex';
  try {
    const from = OR.period.dayBefore(workDate), to = OR.period.dayAfter(workDate);
    let read = await _clockProjectPunches(null, from, to, workerId);
    const offline = !!read.unread;
    const his = p => p.worker_id === workerId && p.work_date >= from && p.work_date <= to;   // his, the days the read covers
    if (offline) read = (_crewRead.get(projectId) ?? []).filter(his);
    const me = { punched_by: window.currentUser?.id, punched_by_name: window.currentProfile?.full_name ?? null };   // this phone's copies are his
    const queued = [..._clockLanded, ...(await _outboxPendingClock()).punches].filter(his).map(p => ({ ...p, ...me }));
    if (seq !== _clockDaySeq) return;
    if (offline && !read.length && !queued.length) {   // nothing to stand on: the buttons stay disabled
      $('clockDayList').innerHTML = `<div class="fx-row-mut">Couldn't read this day — check your signal and open it again.</div>`;
      return;
    }
    const all = _clockWithQueued(read, queued), settings = _clockSettings();
    const day = tc.weekFromPunches(all, settings).byWorker[workerId]?.[workDate]?.[projectId] ?? null;
    const closed = !!day && !day.open;
    const punches = tc.effectivePunches(all).filter(p => p.project_id === projectId && p.work_date === workDate);
    _clockDayCtx = { workerId, projectId, workDate, punches, settings, onSaved, breakMinWas: closed ? day.breakMin : '',
      projectName: window.clockCtx?.projects?.find(p => p.id === projectId)?.name ?? null };
    $('clockDayList').innerHTML = (offline ? '<div class="fx-clock-note">Offline — showing what this phone last saw.</div>' : '')
      + (OR.clockCorrection.daySheetLines(punches).map(l => '<label class="fx-row fx-clock-line">'
      + `<input type="radio" name="clockFixTarget" value="${esc(l.id)}">`
      + `<span class="fx-row-txt"><span class="fx-row-name">${esc(_CLOCK_KIND[l.kind] ?? l.kind)} ${esc(l.time)}`
      + (l.corrected || !l.by ? '' : ` by ${esc(l.by)}`) + '</span>'
      + (l.corrected ? `<span class="fx-row-sub">Corrected by ${esc(l.by ?? '')}: ${esc(l.reason ?? '')}</span>` : '')
      + '</span></label>').join('') || '<div class="fx-row-mut">No punches on this day.</div>');
    $('clockBreakMin').value = closed ? String(day.breakMin) : '';
    $('clockAddOut').parentElement.hidden = !day?.open;   // a closed day has no check-out to add
    $('clockAddOut').disabled = $('clockFixSave').disabled = false;
  } catch (e) {
    console.warn('[clock] the day could not be read:', e);
    if (seq === _clockDaySeq) $('clockDayError').textContent = "Couldn't load this day. Try again.";
  }
}

// The day sheet builder's codes as the sheets word them (contract 4.6): the one table both day sheets read
// (Ordinex.clockCorrection.fixErrorKey -> an i18n key); anything else is said plainly and logged.
function _clockFixError(e) {
  const key = window.Ordinex.clockCorrection.fixErrorKey(e);
  if (key) return _tF(key);
  console.warn('[clock] the day sheet could not build the change:', e);
  return "Couldn't save this change. Try again.";
}

// The sheet's two buttons: 'addOut' (Add missing check-out: the date and time boxes; the reason may
// stay empty, "forgot to check out") and 'fix' (Save: the picked punch moved to the typed time and/or
// the break when Break (min) changed; a reason for both). ONE builder for the field and the admin
// sheet (Ordinex.clockCorrection.daySheetPayloads, F2); nothing is sent when it says no. Then
// punchMany like every punch (no signal: queued) and the sheet closes.
async function _clockDaySheetSave(mode) {
  const c = _clockDayCtx, $ = id => document.getElementById(id);
  if (!c) return;
  $('clockDayError').textContent = '';
  let payloads;
  try {
    payloads = window.Ordinex.clockCorrection.daySheetPayloads(c, {
      mode, targetId: document.querySelector('#clockDayList input[name="clockFixTarget"]:checked')?.value ?? null,
      date: $('clockFixDate').value, time: $('clockFixTime').value,
      breakMin: $('clockBreakMin').value, breakMinWas: c.breakMinWas,
      reason: $('clockFixReason').value, reasonRequired: false,
    });
  } catch (e) { $('clockDayError').textContent = _clockFixError(e); return; }
  if (!payloads.length) { $('clockDayError').textContent = 'Nothing changed.'; return; }
  $('clockAddOut').disabled = $('clockFixSave').disabled = true;   // one save per tap
  try {
    await punchMany(payloads, { projectName: c.projectName, gps: false });
  } catch (e) {
    console.warn('[clock] the day sheet change was not saved:', e);
    if (_clockDayCtx === c) {
      $('clockDayError').textContent = "Couldn't save this change. Try again.";
      $('clockAddOut').disabled = $('clockFixSave').disabled = false;
      $(mode === 'addOut' ? 'clockAddOut' : 'clockFixSave').focus();   // a disabled button lets its focus go to <body>: back to the one he pressed
    }
    return;
  }
  if (_clockDayCtx === c) closeClockDaySheet();   // only the sheet this save was made from: another may be open by now
  c.onSaved?.();
}

function closeClockDaySheet() {
  document.getElementById('clockDaySheet').style.display = 'none';
  _clockDayCtx = null;
  _clockDaySeq++;
}

// ── My time (the worker's own days) and the Crew clock page ──────────────────

// Last week's days and this week's that have a check-in, oldest first, as the lines My time prints. The
// hours come from ONE weekFromPunches over his own punches of both weeks (every job and day, so a block of
// work that crosses a job move or midnight is one block for the break rule — never a bare dayFromPunches of
// a job-day): "Tue Oct 20 · 6:55 – 15:30 · break 30m · 8.1 h" (first in – last out; the break only when
// minutes were taken off, as the timesheet hint; the hours of every job that day, one decimal) and
// "Mon Oct 19 · 8:00 – OPEN" while a shift is open. `by`: who checked him in, when a foreman did; `fix`:
// the day's last correction. No overtime, no GPS flag, nothing about the pay week: he reads his hours (6.1).
function _myTimeDays(own, settings, workerId, monday) {
  const tc = window.Ordinex.timeClock, wk = tc.weekFromPunches(own, settings).byWorker[workerId] ?? {};
  const eff = tc.effectivePunches(own), ms = Date.parse;
  const out = [];
  for (const date of [...tc.weekDates(window.Ordinex.period.dayBefore(monday)), ...tc.weekDates(monday)]) {
    const jobs = Object.values(wk[date] ?? {}).filter(d => d.startAt);   // a day with no check-in has no line
    if (!jobs.length) continue;
    const start = jobs.reduce((a, d) => (ms(d.startAt) < ms(a) ? d.startAt : a), jobs[0].startAt);
    const end = jobs.reduce((a, d) => (d.endAt && (!a || ms(d.endAt) > ms(a)) ? d.endAt : a), null);
    const brk = jobs.reduce((m, d) => m + d.breakMin, 0);
    const hours = tc.round1(jobs.reduce((h, d) => h + d.worked, 0));
    const day = eff.filter(p => p.work_date === date);
    const crewIn = day.find(p => p.kind === 'in' && p.source === 'crew');
    const fix = day.filter(p => p.source === 'correction').pop();
    out.push({
      date, thisWeek: date >= monday,
      text: `${_clockDayLabel(date)} · ${tc.fmtTime(start)} – `
        + (jobs.some(d => d.open) ? 'OPEN' : `${tc.fmtTime(end)}${brk ? ` · break ${brk}m` : ''} · ${hours.toFixed(1)} h`),
      by: crewIn ? crewIn.punched_by_name || 'your foreman' : null,
      fix: fix ? `Corrected by ${fix.punched_by_name || 'your foreman'}${fix.reason ? ': ' + fix.reason : ''}` : null,
    });
  }
  return out;
}

// The list My time shows: this week's days, then last week folded under "Last week" (`keepOpen`: the
// second paint leaves it as he left it). `unread`: the page read nothing (no signal and nothing kept for
// this Monday), which is not a week with no check-ins: it says so instead.
const _MYTIME_UNREAD = "Couldn't load your time — check your signal and open it again.";
function _myTimeHtml(days, unread, keepOpen) {
  const block = (ds) => '<div class="fx-rows">' + ds.map(d => '<div class="fx-row fx-row--static"><span class="fx-row-txt">'
    + `<span class="fx-row-name">${_fieldEsc(d.text)}</span>`
    + (d.by ? `<span class="fx-row-sub">checked in by ${_fieldEsc(d.by)}</span>` : '')
    + (d.fix ? `<span class="fx-chip fx-chip--mut">${_fieldEsc(d.fix)}</span>` : '')
    + '</span></div>').join('') + '</div>';
  if (unread && !days.length) return `<div class="fx-empty"><div class="fx-empty-t">${_MYTIME_UNREAD}</div></div>`;
  const now = days.filter(d => d.thisWeek), before = days.filter(d => !d.thisWeek);
  return (now.length ? block(now) : '<div class="fx-empty"><div class="fx-empty-t">No check-ins yet this week.</div></div>')
    + (before.length ? `<details class="fx-more"${keepOpen ? ' open' : ''}><summary>Last week</summary><div class="fx-more-body">${block(before)}</div></details>` : '')
    + (unread ? `<div class="fx-empty"><div class="fx-empty-s">${_MYTIME_UNREAD}</div></div>` : '');   // some days are queued here, the rest could not be read
}

// The worker's own days (spec 6.1), read-only: this week Mon → today, last week folded under it. It paints at
// once from what the phone holds (the kept rows and the outbox), then again from the server's rows: a line
// that hangs never keeps the page empty, and with no signal the first paint is the page.
let _myTimeSeq = 0;   // only the latest open paints
async function openMyTimePage() {
  showInnerPage('myTimePage');
  const seq = ++_myTimeSeq, list = document.getElementById('myTimeList');
  const wid = window.clockCtx?.worker_id, monday = window.Ordinex.timeClock.weekDates(_todayLocal())[0];
  if (!wid) { list.innerHTML = '<div class="fx-empty"><div class="fx-empty-t">Ask your admin to link your account</div></div>'; return; }
  // asking: the server is about to be read, so "nothing was read" is not an answer yet and carries no note
  const paint = (rows, asking = false) => {
    list.innerHTML = _myTimeHtml(_myTimeDays(rows, _clockSettings(), wid, monday), !!rows.unread && !asking, !!list.querySelector('details')?.open);
  };
  list.innerHTML = '<div class="docs-loading">Loading…</div>';
  try {
    const kept = await _clockOwnPunches(monday, { network: false });
    if (seq !== _myTimeSeq) return;
    const online = navigator.onLine;
    if (kept.length || !online) paint(kept, online);   // online with nothing on the phone: Loading… until the server answers
    if (!online) return;
    const rows = await _clockOwnPunches(monday);
    if (seq === _myTimeSeq) paint(rows);
  } catch (e) {
    console.warn('[clock] My time could not be drawn:', e);
    if (seq === _myTimeSeq) list.innerHTML = `<div class="fx-empty"><div class="fx-empty-t">${_MYTIME_UNREAD}</div></div>`;
  }
}

// The Crew clock page (the foreman's rail and Go-to door): the crew card of Today, on a page of its own.
// The card is ONE node: renderCrewClockCard moves it into the page's empty slot and showHomeDashboard takes
// it back, so its ids stay unique. It is a job's crew: with no job picked the list opens instead.
async function openCrewClockPage() {
  if (!window.fieldProject?.id) { showToast('Pick a project first.', 'warning'); _fieldHome(); return; }
  showInnerPage('crewClockPage');
  const job = document.getElementById('crewClockPageJob');
  job.textContent = window.fieldProject.name || '';
  job.hidden = !job.textContent;
  await renderCrewClockCard(document.getElementById('crewClockPageCard'));
  // spec 6.2: the desktop Crew clock page carries the time-off requests too (the same row as Today)
  renderTimeOffPendingRow(document.getElementById('crewClockTimeOffRow')).catch(err => console.warn('time-off row failed:', err));
}

// ── Time off (spec 8): the requests a worker (or his foreman for him) made, and the sheet to ask ──
// One path online and offline: a request is an outbox record (syncKind 'timeoff', its id made here) and
// the replay is the insert (js/offline.js _replayTimeOff). The page paints at once from the last list the
// server gave (ordinexTimeOff:<uid>) with the queued requests on top, then again from the server.
function openTimeOffPage() {
  showInnerPage('timeOffPage');
  renderTimeOffPage();
}

let _timeOffSeq = 0;   // only the latest render paints
async function renderTimeOffPage() {
  const page = document.getElementById('timeOffPage');
  if (!page || page.style.display === 'none') return;   // flushOutbox calls it after a replay: only while open
  const seq = ++_timeOffSeq, list = document.getElementById('timeOffList');
  const uid = window.currentUser?.id, key = 'ordinexTimeOff:' + uid, ctx = window.clockCtx ?? {};
  const canAsk = !!ctx.worker_id || !!ctx.crew?.length;   // staff with a crew ask for it, linked or not
  document.getElementById('timeOffRequestBtn').hidden = !canAsk;
  if (!canAsk) { list.innerHTML = '<div class="fx-empty"><div class="fx-empty-t">Ask your admin to link your account</div></div>'; return; }
  const paint = async (rows, unread) => {
    const clock = await _outboxPendingClock();
    const queued = clock.timeoff.map(r => ({ ...r, requested_by: uid, queued: true }));
    if (seq !== _timeOffSeq) return;
    const ids = new Set(queued.map(r => r.id));
    // a decision still on this phone: "Approved · sending…", no second Approve
    const decided = new Map(clock.decisions.map(d => [d.requestId, d.decision.status]));
    list.innerHTML = _timeOffHtml([...queued, ...rows.filter(r => !ids.has(r.id))], unread, decided);
  };
  let kept = null;
  try { kept = JSON.parse(localStorage.getItem(key)); } catch { /* nothing kept */ }
  if (Array.isArray(kept) || !navigator.onLine) await paint(Array.isArray(kept) ? kept : [], !Array.isArray(kept));
  else list.innerHTML = '<div class="docs-loading">Loading…</div>';
  if (!navigator.onLine) return;
  try {
    await paint(await _timeOffFetch(key), false);
  } catch (e) {
    console.warn('[timeoff] the list could not be read:', e);
    if (!Array.isArray(kept)) await paint([], true);
  }
}

// The server's list, kept for the next offline open (ordinexTimeOff:<uid>). RLS scopes it: a worker his own, a foreman
// his crew's, an admin the org's. The page and the pending row read it the same way.
async function _timeOffFetch(key) {
  const { data, error } = await _withNetTimeout(sbClient.from('time_off_requests')
    .select('*, workers(name)').order('created_at', { ascending: false }), 'time off');
  if (error) throw error;
  try { localStorage.setItem(key, JSON.stringify(data || [])); } catch { /* full storage: the next open reads the server again */ }
  return data || [];
}

function _timeOffHtml(rows, unread, decided = new Map()) {
  const T = window.Ordinex.timeOff, esc = _fieldEsc, role = window.currentProfile?.role, ctx = window.clockCtx ?? {};
  const unreadNote = "Couldn't load your requests — check your signal and open it again.";
  if (!rows.length) return `<div class="fx-empty"><div class="fx-empty-t">${unread ? unreadNote : 'No time off requested yet.'}</div></div>`;
  const nameOf = r => (r.worker_id === ctx.worker_id ? '' : r.workers?.name || ctx.crew?.find(c => c.worker_id === r.worker_id)?.name || '');
  return '<div class="fx-rows">' + rows.map(r => {
    const n = r.days.length, who = nameOf(r);
    const status = r.queued ? `<span class="fx-row-mut">${r.refused ? 'Not sent' : 'sending…'}</span>`
      : decided.has(r.id) ? `<span class="fx-row-mut">${esc(T.statusLabel(decided.get(r.id)))} · sending…</span>`
      : r.status === 'approved' ? '<span class="fx-row-ok">Approved</span>'
      : `<span class="fx-row-mut">${esc(T.statusLabel(r.status))}</span>`;
    // staff decide right here (a request just made for a crewman too, once it is sent)
    const decide = !r.queued && !decided.has(r.id) && _timeOffDecidable(r)
      ? `<button type="button" class="fx-timeoff-cancel fx-timeoff-approve" onclick="decideTimeOffField('${esc(r.id)}','approved')">Approve</button>`
        + `<button type="button" class="fx-timeoff-cancel" onclick="decideTimeOffField('${esc(r.id)}','denied')">Deny</button>` : '';
    // only the author. A foreman cancels what is still on his phone (a local delete: the only way out of a refused one)
    // and his own pending request (RLS "worker delete pending own" has no role test); a crewman's sent one he Denies
    const cancel = !decided.has(r.id) && r.status === 'pending' && r.requested_by === currentUser.id
      && (r.queued || role !== 'foreman' || r.worker_id === ctx.worker_id)
      ? `<button type="button" class="fx-timeoff-cancel" onclick="cancelTimeOff('${esc(r.id)}')">Cancel request</button>` : '';
    const sub = [who, r.refused, r.note].filter(Boolean).map(esc).join(' · ');
    return '<div class="fx-row fx-row--static fx-timeoff-row">'
      + (r.status === 'pending' ? '<span class="fx-row-dot" aria-hidden="true"></span>' : '')
      + `<span class="fx-row-txt"><span class="fx-row-name">${esc(T.kindLabel(r.kind))} · ${esc(T.fmtDays(r.days))} · ${n} day${n === 1 ? '' : 's'}</span>`
      + (sub ? `<span class="fx-row-sub">${sub}</span>` : '') + '</span>'
      + `<span class="fx-row-side">${status}${decide}${cancel}</span></div>`;
  }).join('') + '</div>' + (unread ? `<div class="fx-empty"><div class="fx-empty-s">${unreadNote}</div></div>` : '');
}

// The request sheet: who (staff only: the crew, own worker first), Sick / Vacation, From → To, one
// checkbox per day (Sat/Sun born unticked), a note. Created `pending` for every role (spec 8).
function openTimeOffSheet() {
  const $ = id => document.getElementById(id), ctx = window.clockCtx ?? {}, esc = _fieldEsc;
  const staff = window.currentProfile?.role !== 'worker';
  const people = [
    ...(ctx.worker_id ? [{ worker_id: ctx.worker_id, name: `${window.currentProfile?.full_name || 'Me'} (you)` }] : []),
    ...(staff ? (ctx.crew ?? []).filter(c => c.worker_id !== ctx.worker_id) : []),
  ];
  $('timeOffWorker').innerHTML = people.map(p => `<option value="${esc(p.worker_id)}">${esc(p.name)}</option>`).join('');
  $('timeOffWorkerWrap').hidden = people.length < 2;
  const today = _todayLocal();
  $('timeOffKind').value = 'sick';
  $('timeOffFrom').value = $('timeOffTo').value = today;
  $('timeOffTo').min = today;
  $('timeOffNote').value = '';
  $('timeOffError').textContent = '';
  _timeOffDays();
  $('timeOffSheet').style.display = 'flex';
}

function closeTimeOffSheet() {
  document.getElementById('timeOffSheet').style.display = 'none';
}

// From changed: To follows when it is empty or before it. Either changed: the day list again.
function _timeOffDates(which) {
  const from = document.getElementById('timeOffFrom'), to = document.getElementById('timeOffTo');
  if (which === 'from') { to.min = from.value; if (!to.value || to.value < from.value) to.value = from.value; }
  _timeOffDays();
}

function _timeOffDays() {
  const $ = id => document.getElementById(id);
  $('timeOffDays').innerHTML = window.Ordinex.timeOff.dayList($('timeOffFrom').value, $('timeOffTo').value).map(d =>
    `<label class="fx-row fx-row--static"><input type="checkbox" value="${d.date}"${d.checked ? ' checked' : ''} onchange="_timeOffCount()">`
    + `<span class="fx-row-name">${_fieldEsc(_clockDayLabel(d.date))}</span></label>`).join('');
  _timeOffCount();
}

function _timeOffCount() {
  const n = document.querySelectorAll('#timeOffDays input:checked').length, btn = document.getElementById('timeOffSend');
  btn.textContent = window.Ordinex.timeOff.sendLabel(n);
  btn.disabled = n === 0;
}

async function requestTimeOff() {
  const $ = id => document.getElementById(id);
  let request;
  try {
    request = window.Ordinex.timeOff.requestPayload({
      id: crypto.randomUUID(), worker_id: $('timeOffWorker').value || window.clockCtx?.worker_id, kind: $('timeOffKind').value,
      days: [...document.querySelectorAll('#timeOffDays input:checked')].map(i => i.value), note: $('timeOffNote').value });
  } catch { $('timeOffError').textContent = 'Tick at least one day.'; return; }
  $('timeOffSend').disabled = true;   // one request per tap
  try {
    // a NEW record: add (the outbox makes its key), never _outboxPut
    await _outboxAdd(window.Ordinex.offlineRecord.buildClock({ syncKind: 'timeoff', request,
      orgId: window.currentOrg?.id, userId: window.currentUser?.id, userName: window.currentProfile?.full_name || '' }));
  } catch (e) {
    console.warn('[timeoff] the request could not be saved:', e);
    $('timeOffError').textContent = "Couldn't save the request. Try again.";
    $('timeOffSend').disabled = false;
    return;
  }
  closeTimeOffSheet();
  await renderTimeOffPage();   // the row at once, "sending…"
  _renderOfflineUI();
  if (navigator.onLine) flushOutbox({ toast: false });   // the replay is the insert; the page repaints after it
}

// Cancel a pending request: still on this phone -> out of the outbox (works offline); already sent ->
// DELETE (worker "delete pending own" / admin all). A 0-row delete means RLS filtered it out (decided
// meanwhile): PostgREST says nothing, so we do.
async function cancelTimeOff(id) {
  if (!confirm('Cancel this request?')) return;
  let rec = null;
  try { rec = (await _outboxAll()).filter(_isMine).find(r => r.syncKind === 'timeoff' && r.request?.id === id); } catch { /* unreadable: the server's copy */ }
  if (rec) {
    await _outboxDelete(rec.id);
    _renderOfflineUI();
    return renderTimeOffPage();
  }
  if (!navigator.onLine) { showToast('Connect to cancel a request that was already sent', 'warning'); return; }
  const { error, count } = await sbClient.from('time_off_requests').delete({ count: 'exact' }).eq('id', id);
  if (error) showToast(error.message, 'error');
  else if (count === 0) showToast('Could not cancel — the request is no longer pending', 'warning');
  return renderTimeOffPage();
}

// ── Time off: the foreman's (or admin's) decision (spec 8) ──
// Staff decide, and never their own request: time_off_guard refuses a non-admin's own (42501), and only the
// office decides a foreman's own (task-5 R5). The row, the sheet and the page's buttons all ask this.
function _timeOffDecidable(r) {
  return r.status === 'pending' && window.currentProfile?.role !== 'worker'
    && r.worker_id !== window.clockCtx?.worker_id;
}

let _timeOffPending = [];   // what the row counts and the sheet walks: decidable, not decided on this phone, oldest first
// "{n} time-off requests" (Today, and the Crew clock page: container), a tap opens the sheet. The queued decisions are
// read BEFORE the list: a decision that lands meanwhile is then either still queued or already decided on the server.
async function renderTimeOffPendingRow(container = document.getElementById('timeOffPendingRow')) {
  if (!_clockEnabled()) { if (container) container.hidden = true; return; }
  if (!container) return;
  const queued = new Set((await _outboxPendingClock()).decisions.map(d => d.requestId));
  const key = 'ordinexTimeOff:' + window.currentUser?.id;
  let rows = null;
  try { rows = JSON.parse(localStorage.getItem(key)); } catch { /* nothing kept */ }
  if (navigator.onLine) {
    try { rows = await _timeOffFetch(key); } catch (e) { console.warn('[timeoff] the pending row reads the kept list:', e); }
  }
  _timeOffPending = (Array.isArray(rows) ? rows : []).filter(r => _timeOffDecidable(r) && !queued.has(r.id))
    .sort((a, b) => String(a.created_at).localeCompare(String(b.created_at)));
  const n = _timeOffPending.length;
  container.innerHTML = `<span class="fx-row-dot" aria-hidden="true"></span><span class="fx-row-name">${n} time-off request${n === 1 ? '' : 's'}</span><span class="fx-row-go">Review</span>`;
  container.hidden = !n;
}

// The sheet walks the pending list one request at a time ("2 of 3"); after a decision the next one loads, and it
// closes when none is left. `request` (optional) starts it on that one.
let _timeOffSheetList = [], _timeOffSheetAt = 0;
function openTimeOffDecideSheet(request) {
  _timeOffSheetList = [..._timeOffPending];
  _timeOffSheetAt = Math.max(0, request ? _timeOffSheetList.findIndex(r => r.id === request.id) : 0);
  if (!_timeOffSheetList.length) return;
  _timeOffDecideSheetLoad();
  document.getElementById('timeOffDecideSheet').style.display = 'flex';
}

function closeTimeOffDecideSheet() {
  document.getElementById('timeOffDecideSheet').style.display = 'none';
}

function _timeOffDecideSheetLoad() {
  const $ = id => document.getElementById(id), T = window.Ordinex.timeOff, ctx = window.clockCtx ?? {};
  const r = _timeOffSheetList[_timeOffSheetAt], n = r.days.length, total = _timeOffSheetList.length;
  const name = r.workers?.name || ctx.crew?.find(c => c.worker_id === r.worker_id)?.name || '';
  $('timeOffDecideSheet').dataset.id = r.id;
  $('timeOffDecideCount').textContent = `${_timeOffSheetAt + 1} of ${total}`;
  $('timeOffDecideCount').hidden = total < 2;
  $('timeOffDecideWhat').textContent = [name, T.kindLabel(r.kind), `${T.fmtDays(r.days)} (${n} day${n === 1 ? '' : 's'})`].filter(Boolean).join(' · ');
  $('timeOffDecideAsked').textContent = r.note ? `'${r.note}'` : '';
  $('timeOffDecideAsked').hidden = !r.note;
  $('timeOffDecideNote').value = '';
}

// Approve / Deny: the request the sheet holds (dataset.id), with the note typed in the sheet.
async function _timeOffDecideSheetGo(status) {
  const sheet = document.getElementById('timeOffDecideSheet');
  if (!await decideTimeOffField(sheet.dataset.id, status, document.getElementById('timeOffDecideNote').value)) return;
  if (++_timeOffSheetAt < _timeOffSheetList.length) _timeOffDecideSheetLoad();
  else closeTimeOffDecideSheet();
}

// One decision = one outbox record (syncKind 'timeoff_decision'), online or not; the replay is the UPDATE
// (js/offline.js _replayTimeOffDecision). The kept list learns the new status at once, so a repaint before the
// server is read again never offers the same Approve twice.
async function decideTimeOffField(requestId, status, note = '') {
  try {
    // a NEW record: add (the outbox makes its key), never _outboxPut
    await _outboxAdd(window.Ordinex.offlineRecord.buildClock({ syncKind: 'timeoff_decision',
      ...window.Ordinex.timeOff.decisionRecord(requestId, status, note),
      orgId: window.currentOrg?.id, userId: window.currentUser?.id, userName: window.currentProfile?.full_name || '' }));
  } catch (e) {
    console.warn('[timeoff] the decision could not be saved:', e);
    showToast("Couldn't save the decision. Try again.", 'error');
    return false;
  }
  const key = 'ordinexTimeOff:' + window.currentUser?.id;
  try {
    const kept = JSON.parse(localStorage.getItem(key));
    if (Array.isArray(kept)) localStorage.setItem(key, JSON.stringify(kept.map(r => (r.id === requestId ? { ...r, status } : r))));
  } catch { /* the server's list on the next read */ }
  _renderOfflineUI();
  renderTimeOffPendingRow().catch(err => console.warn('time-off row failed:', err));
  renderTimeOffPendingRow(document.getElementById('crewClockTimeOffRow')).catch(err => console.warn('time-off row failed:', err));
  renderTimeOffPage();   // its row: "Approved · sending…" (paints only while it is open)
  if (navigator.onLine) flushOutbox({ toast: false });
  return true;
}

// Back to the app: the clock context again (once a minute at most; the worker's job-list
// revalidate in auth.js asks on the same wake and shares the request), then the surfaces.
// Hidden: the break counter stops.
let _clockWokeAt = 0;
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState !== 'visible') { clearInterval(_clockTick); _clockTick = null; return; }
  _clockPaint();
  if (!window.currentUser || Date.now() - _clockWokeAt < 60000) return;
  _clockWokeAt = Date.now();
  _loadClockContext().then(() => _clockRefreshSurfaces());
});

// Foremen open on THEIR crew (the admin's "Assign Foremen" checkboxes); the
// scope bar swaps to the whole roster and back. Admins — and any account with
// zero assignments — see everyone, no bar: an unconfigured org keeps the old
// behaviour instead of a confusing empty page.
function _applyCertScope() {
  const ids     = window.myCrewIds instanceof Set ? window.myCrewIds : new Set();
  const isAdmin = window.currentProfile?.role === 'admin';
  const mine    = (!isAdmin && ids.size)
    ? _certAllWorkers.filter(w => ids.has(String(w.id)))
    : [];
  const scoping = mine.length > 0 && mine.length < _certAllWorkers.length;

  _certWorkers = (scoping && !_certShowAll) ? mine : _certAllWorkers;

  const bar = document.getElementById('certScopeBar');
  if (bar) {
    bar.style.display = scoping ? 'block' : 'none';
    if (scoping) {
      bar.innerHTML = _certShowAll
        ? `Showing all ${_certAllWorkers.length} workers · <button type="button" class="cert-scope-btn" onclick="certToggleScope()">Show only my crew (${mine.length})</button>`
        : `Showing your crew (${mine.length}) · <button type="button" class="cert-scope-btn" onclick="certToggleScope()">Show all ${_certAllWorkers.length} workers</button>`;
    }
  }
}

function certToggleScope() {
  _certShowAll = !_certShowAll;
  _applyCertScope();
  renderCertWorkersList(document.getElementById('certSearch')?.value || '');
}

// ── Status helpers ────────────────────────────────────────────────────────────
function _certStatus(expiresIso) {
  if (!expiresIso) return 'unknown';
  const diff = Math.ceil((new Date(expiresIso) - new Date()) / 86400000);
  if (diff < 0)  return 'expired';
  if (diff < 30) return 'expiring';
  return 'valid';
}

function _workerWorstStatus(workerId) {
  const certs = _certRecords.filter(c => c.worker_id === workerId);
  if (!certs.length) return 'none';
  if (certs.some(c => _certStatus(c.expires_date) === 'expired'))  return 'expired';
  if (certs.some(c => _certStatus(c.expires_date) === 'expiring')) return 'expiring';
  return 'valid';
}

// ── Render workers list ───────────────────────────────────────────────────────
function renderCertWorkersList(filter) {
  const listEl = document.getElementById('certPageList');
  if (!listEl) return;

  const q = (filter || '').toLowerCase().trim();
  const list = q
    ? _certWorkers.filter(w =>
        w.name?.toLowerCase().includes(q) || w.trade?.toLowerCase().includes(q))
    : _certWorkers;

  if (!list.length) {
    if (!_certWorkers.length && !q) {
      // Same empty-state pattern as My Actions (icon + title + hint), and the
      // hint speaks to the role reading it — admins assign workers themselves.
      const hint = window.currentProfile?.role === 'admin'
        ? 'Assign workers to foremen in Admin Panel → Crew.'
        : 'Ask your admin to assign workers to your account.';
      listEl.innerHTML = `
        <div class="docs-empty" style="text-align:center;padding:24px 16px">
          <div style="margin-bottom:8px;color:var(--text-dim)"><i data-lucide="users" style="width:28px;height:28px"></i></div>
          <div style="font-weight:700;color:var(--text);margin-bottom:4px">No workers assigned to you yet</div>
          <div style="font-size:13px;color:var(--text-muted)">${hint}</div>
        </div>`;
      window.lucide && lucide.createIcons();
    } else {
      listEl.innerHTML = '<div class="docs-empty">No workers match your search.</div>';
    }
    return;
  }

  const s = _fieldEsc;

  // Redesign v3: the status was a coloured emoji circle — it rendered as a
  // glossy 3D sphere on Android, and colour alone is not a state. It says the
  // word now, in a chip tinted the same semantic colour.
  const CERT_STATE = {
    expired:  ['Expired',  'err'],
    expiring: ['Expiring', 'warn'],
    valid:    ['Valid',    'ok'],
  };
  listEl.innerHTML = `<div class="fx-rows">` + list.map(w => {
    const [label, tone] = CERT_STATE[_workerWorstStatus(w.id)] || ['No certs', 'mut'];
    const n = _certRecords.filter(c => c.worker_id === w.id).length;
    return `
      <button class="fx-row cert-worker-row" type="button"
        onclick="openWorkerCertDetail('${w.id}')">
        <span class="fx-row-txt">
          <span class="fx-row-name">${s(w.name)}</span>
          <span class="fx-row-sub">${s(w.trade || 'No trade set')} · ${n} cert${n !== 1 ? 's' : ''}</span>
        </span>
        <span class="fx-chip fx-chip--${tone}">${label}</span>
      </button>`;
  }).join('') + `</div>`;
}

// ── Open worker detail modal ──────────────────────────────────────────────────
function openWorkerCertDetail(workerId) {
  const worker = _certWorkers.find(w => w.id === workerId);
  if (!worker) return;
  _certCurrentWId = workerId;
  const _issuedEl = document.getElementById('foremanCertIssued');
  if (_issuedEl) _issuedEl.max = _todayLocal();

  const nameEl  = document.getElementById('certDetailWorkerName');
  const tradeEl = document.getElementById('certDetailWorkerTrade');
  if (nameEl)  nameEl.textContent  = worker.name  || '';
  if (tradeEl) tradeEl.textContent = worker.trade || '';

  _resetAddCertForm();
  _renderWorkerCerts(workerId);
  // View-only worker: the RLS policy ('worker_certifications: staff write') is
  // admin/foreman only, so the Add form and Save are not his — pressing Save
  // used to answer with the raw policy text (stranger sweep 2026-10-17).
  const canWrite = window.currentProfile?.role !== 'worker';
  const addSec = document.getElementById('foremanAddCertSec');
  if (addSec) addSec.hidden = !canWrite;
  const saveBtn = document.getElementById('foremanCertSaveBtn');
  if (saveBtn) saveBtn.hidden = !canWrite;

  const modal = document.getElementById('foremanCertModal');
  if (modal) { modal.style.display = 'flex'; document.body.style.overflow = 'hidden'; }
}

function closeWorkerCertModal() {
  const modal = document.getElementById('foremanCertModal');
  if (modal) modal.style.display = 'none';
  document.body.style.overflow = '';
  _certCurrentWId = null;
}

// ── Render cert list inside modal ─────────────────────────────────────────────
function _renderWorkerCerts(workerId) {
  const listEl = document.getElementById('workerCertDetailList');
  if (!listEl) return;

  const certs = _certRecords.filter(c => c.worker_id === workerId);
  if (!certs.length) {
    listEl.innerHTML = '<p class="cert-item-none">No certifications on file.</p>';
    return;
  }

  const s = _fieldEsc;

  listEl.innerHTML = certs.map(c => {
    const status = _certStatus(c.expires_date);
    const badge  = `<span class="cert-badge ${status}">${
      status === 'expired'  ? 'Expired'      :
      status === 'expiring' ? 'Expiring Soon':
      status === 'valid'    ? 'Valid'         : 'No Expiry'}</span>`;
    const typeName = c.certification_types?.name || 'Unknown';
    const issued   = c.issued_date  ? new Date(c.issued_date  + 'T12:00:00').toLocaleDateString('en-CA') : '—';
    const expires  = c.expires_date ? new Date(c.expires_date + 'T12:00:00').toLocaleDateString('en-CA') : '—';
    return `
      <div class="cert-item">
        <div class="cert-item-header">
          <span class="cert-item-type">${s(typeName)}</span>
          ${badge}
        </div>
        <div class="cert-item-meta">Issued: ${issued} · Expires: ${expires}</div>
        ${c.notes ? `<div class="cert-item-notes">${s(c.notes)}</div>` : ''}
        ${c.document_path ? `
          <button class="cert-item-doc" onclick="viewForemanCertDoc(${s(JSON.stringify(String(c.document_path)))})">
            <i data-lucide="file-text"></i>View document</button>` : ''}
      </div>`;
  }).join('');
  // The rows carry lucide placeholders now; without this the document button
  // renders as an empty <i>.
  window.lucide && lucide.createIcons();
}

async function viewForemanCertDoc(path) {
  try {
    const { data, error } = await sbClient.storage.from('certifications').createSignedUrl(path, 3600);
    if (error) throw error;
    window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
  } catch (e) {
    showToast('Could not open document: ' + e.message, 'error');
  }
}

// ── Add cert form ─────────────────────────────────────────────────────────────
function _resetAddCertForm() {
  const form = document.getElementById('foremanAddCertForm');
  if (form) form.reset();
  const errEl = document.getElementById('foremanCertError');
  if (errEl) errEl.textContent = '';

  // Reset drag-drop zone
  resetCertDropZone('foremanCertFile', 'foremanCertFileName', 'foremanCertDropZone');

  // Populate type dropdown from cached cert types
  const sel = document.getElementById('foremanCertTypeSelect');
  if (sel) {
    // t.name is admin-authored (certification_types, managed in the admin
    // Crew/Workers tab) — the admin's own matrix already escapes it
    // (70-workers-certs.js's esc(ct.name)); this foreman-facing dropdown had
    // been left out of that discipline.
    sel.innerHTML = '<option value="">Select type…</option>' +
      _certTypes.map(t =>
        `<option value="${t.id}" data-months="${t.validity_months || ''}">${_fEsc(t.name)}</option>`
      ).join('');
  }
}

// ── Drag-and-drop cert file helpers (also defined in admin.js; duplicated for foreman standalone use) ──
function certDropOver(e, zoneId) {
  e.preventDefault();
  document.getElementById(zoneId)?.classList.add('cert-drop-zone--over');
}
function certDropLeave(zoneId) {
  document.getElementById(zoneId)?.classList.remove('cert-drop-zone--over');
}
function certDropDrop(e, inputId, zoneId, labelId) {
  e.preventDefault();
  certDropLeave(zoneId);
  const file = e.dataTransfer?.files?.[0];
  if (!file) return;
  const input = document.getElementById(inputId);
  if (!input) return;
  const dt = new DataTransfer();
  dt.items.add(file);
  input.files = dt.files;
  certFileChosen(input, labelId, zoneId);
}
function certFileChosen(input, labelId, zoneId) {
  const file  = input?.files?.[0];
  const label = document.getElementById(labelId);
  const zone  = document.getElementById(zoneId);
  if (file && label) {
    label.textContent = file.name;
    zone?.classList.add('cert-drop-zone--chosen');
  } else if (label) {
    label.textContent = 'Drop file or click to browse';
    zone?.classList.remove('cert-drop-zone--chosen');
  }
}
function resetCertDropZone(inputId, labelId, zoneId) {
  const input = document.getElementById(inputId);
  if (input) input.value = '';
  const label = document.getElementById(labelId);
  if (label) label.textContent = 'Drop file or click to browse';
  const zone = document.getElementById(zoneId);
  zone?.classList.remove('cert-drop-zone--chosen', 'cert-drop-zone--over');
}

function autoFillForemanCertExpiry() {
  const sel      = document.getElementById('foremanCertTypeSelect');
  const issuedEl = document.getElementById('foremanCertIssued');
  const expEl    = document.getElementById('foremanCertExpires');
  if (!sel || !issuedEl || !expEl) return;

  const months = parseInt(sel.options[sel.selectedIndex]?.dataset?.months || '0');
  const issued  = issuedEl.value;
  if (!months || !issued) return;

  expEl.value = window.Ordinex.guards.addMonthsIso(issued, months);
}

// The refusal line sits under Notes, below the modal body's scroll fold on
// both widths — after Save nothing visibly changed. Say it AND show it.
function _certSay(errEl, msg) {
  if (!errEl) return;
  errEl.textContent = msg;
  try { errEl.scrollIntoView({ block: 'nearest' }); } catch (_) {}
}

async function saveForemanCert() {
  const errEl = document.getElementById('foremanCertError');
  if (errEl) errEl.textContent = '';
  if (window.currentProfile?.role === 'worker') { _certSay(errEl, _tF('certs.staffOnly')); return; }

  const typeId  = document.getElementById('foremanCertTypeSelect')?.value;
  const issued  = document.getElementById('foremanCertIssued')?.value;
  const expires = document.getElementById('foremanCertExpires')?.value;
  const notes   = document.getElementById('foremanCertNotes')?.value.trim();
  const fileEl  = document.getElementById('foremanCertFile');
  const file    = fileEl?.files?.[0] || null;

  if (!typeId) { _certSay(errEl, 'Please select a certification type.'); return; }
  // Same rule as the admin forms (js/core/field-guards.js): issued not in the
  // future, expiry required — worker_certifications.expires_date is NOT NULL,
  // so a blank one failed AFTER the file upload with a raw constraint message
  // and orphaned the file — and on or after the issued date.
  const _certMsg = window.Ordinex.guards.certDateError(issued, expires, _todayLocal());
  if (_certMsg) { _certSay(errEl, _certMsg); return; }
  if (!_certCurrentWId) return;

  const btn = document.getElementById('foremanCertSaveBtn');
  if (btn) { btn.disabled = true; btn.textContent = 'Saving…'; }

  try {
    let docPath = null, docUrl = null;

    if (file) {
      const ext  = file.name.split('.').pop().toLowerCase();
      // Private + org-scoped bucket: path must start with {org_id}/
      const path = `${window.currentOrg?.id}/worker_${_certCurrentWId}/cert_${typeId}_${Date.now()}.${ext}`;
      const { error: upErr } = await sbClient.storage
        .from('certifications').upload(path, file, { contentType: file.type, upsert: false });
      if (upErr) throw new Error('File upload failed: ' + upErr.message);

      const { data: urlData } = await sbClient.storage
        .from('certifications').createSignedUrl(path, 3600);
      docPath = path;
      docUrl  = urlData?.signedUrl || null;
    }

    const { data: newRec, error: insErr } = await sbClient
      .from('worker_certifications')
      .insert({
        worker_id:     _certCurrentWId,
        cert_type_id:  typeId,
        issued_date:   issued,
        expires_date:  expires,
        document_url:  docUrl,
        document_path: docPath,
        notes:         notes || null
      })
      .select('id, worker_id, cert_type_id, issued_date, expires_date, document_path, notes, certification_types(name, validity_months)')
      .single();

    if (insErr) throw new Error(insErr.message);

    _certRecords.push(newRec);
    showToast('Certification saved!', 'success');
    _resetAddCertForm();
    _renderWorkerCerts(_certCurrentWId);
    renderCertWorkersList(document.getElementById('certSearch')?.value || '');

  } catch (e) {
    // Online-only screen: in a dead zone the typed form stays, and says why.
    // A policy refusal is a sentence, never the Postgres text.
    const raw = e.message || '';
    _certSay(errEl, window.Ordinex.guards.networkErrorMessage(e)
      || (/row-level security|42501|permission denied/i.test(raw) ? _tF('certs.staffOnly') : raw));
  } finally {
    if (btn) { btn.disabled = false; btn.textContent = 'Save Certification'; }
  }
}

// ── User Avatar (deterministic image per user) ───────────────────────────────
const TOTAL_AVATARS = 18;

function avatarNumberForUser(userId) {
  if (!userId) return 1;
  let sum = 0;
  for (let i = 0; i < userId.length; i++) sum += userId.charCodeAt(i);
  return (sum % TOTAL_AVATARS) + 1;        // 1..18
}

function avatarUrl(userId) {
  const n = avatarNumberForUser(userId);
  return `assets/avatars/avatar-${String(n).padStart(2, '0')}.png`;
}

// Apply user avatar to an element. Keeps the initial letter as a fallback.
// Only swaps to image once it has loaded successfully.
function applyUserAvatar(el) {
  if (!el) return;
  const userId = window.currentUser?.id;
  if (!userId) return;
  const url = avatarUrl(userId);
  const test = new Image();
  test.onload = () => {
    el.style.backgroundImage = `url('${url}')`;
    el.classList.add('has-avatar');
  };
  // If the image fails (e.g. 404), do nothing — the initial letter remains visible.
  test.src = url;
}

// ── User Menu Dropdown ───────────────────────────────────────────────────────
function toggleUserMenu(ev) {
  if (ev) ev.stopPropagation();
  const menu = document.getElementById('userMenu');
  const btn  = document.getElementById('dashAvatarBtn');
  if (!menu) return;

  const isOpen = menu.classList.contains('open');
  if (isOpen) {
    closeUserMenu();
    return;
  }

  // Populate menu header from current profile
  const name   = window.currentProfile?.full_name || 'User';
  const email  = window.currentUser?.email || '';
  const initial = _initials2(name);
  document.getElementById('userMenuName').textContent  = name;
  document.getElementById('userMenuEmail').textContent = email;
  const menuAv = document.getElementById('userMenuAvatar');
  if (menuAv) {
    menuAv.textContent = initial;
    applyUserAvatar(menuAv);
  }

  // Show Admin Panel item only for admins
  const adminItem = document.getElementById('userMenuAdminBtn');
  if (adminItem) {
    adminItem.style.display = (window.currentProfile?.role === 'admin') ? '' : 'none';
  }

  menu.classList.add('open');
  menu.setAttribute('aria-hidden', 'false');
  btn?.setAttribute('aria-expanded', 'true');

  // Close on outside click / Escape
  setTimeout(() => {
    document.addEventListener('click', _userMenuOutsideHandler);
    document.addEventListener('keydown', _userMenuEscHandler);
  }, 0);
}

function closeUserMenu() {
  const menu = document.getElementById('userMenu');
  const btn  = document.getElementById('dashAvatarBtn');
  if (!menu) return;
  menu.classList.remove('open');
  menu.setAttribute('aria-hidden', 'true');
  btn?.setAttribute('aria-expanded', 'false');
  document.removeEventListener('click', _userMenuOutsideHandler);
  document.removeEventListener('keydown', _userMenuEscHandler);
}

function _userMenuOutsideHandler(ev) {
  const menu = document.getElementById('userMenu');
  if (menu && !menu.contains(ev.target) && !document.getElementById('dashAvatarBtn').contains(ev.target)) {
    closeUserMenu();
  }
}

function _userMenuEscHandler(ev) {
  if (ev.key === 'Escape') closeUserMenu();
}

// ── Change Password Modal ─────────────────────────────────────────────────────
function openChangePasswordModal() {
  document.getElementById('cpNewPassword').value  = '';
  document.getElementById('cpNewPassword2').value = '';
  document.getElementById('cpError').textContent   = '';
  document.getElementById('cpSuccess').textContent = '';
  document.getElementById('changePasswordModal').style.display = 'flex';
  document.body.style.overflow = 'hidden';
  setTimeout(() => document.getElementById('cpNewPassword')?.focus(), 50);
}

function closeChangePasswordModal() {
  document.getElementById('changePasswordModal').style.display = 'none';
  document.body.style.overflow = '';
}

async function handleChangePassword() {
  const errEl  = document.getElementById('cpError');
  const succEl = document.getElementById('cpSuccess');
  const btn    = document.getElementById('cpSaveBtn');
  errEl.textContent  = '';
  succEl.textContent = '';

  const p1 = document.getElementById('cpNewPassword').value;
  const p2 = document.getElementById('cpNewPassword2').value;

  if (p1.length < 8) { errEl.textContent = 'Password must be at least 8 characters.'; return; }
  if (p1 !== p2)     { errEl.textContent = 'The two passwords do not match.'; return; }

  btn.disabled = true; btn.textContent = 'Saving…';

  const { error } = await sbClient.auth.updateUser({ password: p1 });

  if (error) {
    errEl.textContent = /different from the old|same_password/i.test(`${error.message} ${error.code}`)
      ? 'Your new password must be different from the current one.'
      : error.message || 'Could not update password.';
    btn.disabled = false; btn.textContent = 'Save new password';
    return;
  }

  succEl.textContent = 'Password updated!';
  setTimeout(() => {
    closeChangePasswordModal();
    btn.disabled = false; btn.textContent = 'Save new password';
  }, 1200);
}

// ── Production / Time-sheet dashboard sync ────────────────────────────────────
// Both Production Report and Weekly Timesheet are submitted as a weekly batch
// (Mon-Sun in the form). The admin dashboard reads from per-day rows, so we
// expand the form into one row per day here, called right after submissions
// insert. These run best-effort — a failure here doesn't block PDF/email.

// Returns a Date set to the Monday of the week containing dateIso (YYYY-MM-DD).
function _weekStartFor(dateIso) {
  // Parse as local-time midnight to avoid TZ off-by-one
  const [y, m, d] = (dateIso || _todayLocal()).split('-').map(Number);
  const date = new Date(y, (m || 1) - 1, d || 1);
  const dow  = date.getDay();           // 0=Sun, 1=Mon, ..., 6=Sat
  const offset = dow === 0 ? -6 : 1 - dow;
  date.setDate(date.getDate() + offset);
  return date;
}

// dayIndex: 0=Mon, 1=Tue, ..., 6=Sun → YYYY-MM-DD
function _dateFromWeekStart(weekStart, dayIndex) {
  const d = new Date(weekStart);
  d.setDate(d.getDate() + dayIndex);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// ── Which rows does this filing own? ─────────────────────────────────────────
// Both weekly writers used to replace the ENTIRE (project, foreman, week) on
// every submission. That killed real double-count bugs, but it had a blind
// side: filing a second report onto an already-filed week silently swallowed
// the first one — the older submission and its PDF kept existing while its
// numbers vanished from every dashboard (Langley, 2026-08-15).
//
// Now the DB offers two scopes (see migration 20260816010000_week_add_mode):
//   'mine' — replace only what this same submission wrote before. Two filings
//            in one week SUM, and deleting one subtracts exactly that one.
//   'week' — the old semantics, kept for corrections: the person explicitly
//            chooses to replace the week they already filed.
//
// The question is asked ONCE, before anything is generated — the three-button
// sheet in _confirmWeekModeOrAbort (Add / Replace / Cancel; the first cut used
// window.confirm, whose two buttons forced Cancel to mean Replace). By the
// time a sync runs, the decision arrives as an argument or there was none to
// make — and a replay on a phone in a truck must never ask again.
// Records queued before this feature replay as 'mine': additive can only ever
// show too much (visible, fixable) — 'week' would destroy someone else's
// filing (not fixable from a dashboard).
function _resolveWeekMode(mode) {
  return mode === 'week' ? 'week' : 'mine';
}

// ── The 3-way week sheet: Add / Replace / walk away ──────────────────────────
// Runs at the top of handleSubmit for the two weekly forms, online only.
//
// The answer lands on `state._weekMode` — deliberately NOT on state.fields.
// The first cut put it in the payload, and the payload is persisted: through
// _buildSubmissionData into submissions.data, and through saveDraft into the
// drafts row. That turned a one-tap answer into a durable one, with two ways
// to destroy work:
//   · the self-heal watchdog rebuilt a repair from submissions.data days
//     later, read 'week' back out, and re-ran a whole-week delete that wiped a
//     DIFFERENT filing which had landed in the meantime;
//   · a draft resumed hours later skipped the question entirely and replaced
//     the week silently, because the answer was still sitting in the draft.
// A stale destructive answer is worse than no answer. It lives in memory, for
// this attempt, and is handed to the sync as an argument.
//
// Offline, no project, or a failed probe: no question and no mode — the sync
// falls back to 'mine', the choice that cannot destroy another filing.
async function _confirmWeekModeOrAbort() {
  if (!WEEKLY_DRAFT_FORMS.includes(state.submissionType)) return true;
  const isProd = state.submissionType === 'Production Report';
  const weekStart = _weekStartFor(state.date);
  // The answer was given about ONE week: this form, this project, this crew,
  // this Monday. Change any of them between two attempts (the crew picker, the
  // date, the project) and it is a different week that nobody was asked about
  // — "Replace the week" reused there deleted another crew's filed rows.
  // Checked before the offline return: the outbox record carries _weekMode too.
  const _for = [state.submissionType, state.project,
    isProd ? (state.productionCrewId || _submitterId()) : _submitterId(),
    _dateFromWeekStart(weekStart, 0)].join('|');
  if (state._weekModeFor !== _for) { state._weekMode = null; state._weekModeFor = _for; }
  // A sheet that carries my earlier filing of this week (FILED cells, the clock prefill) IS the whole
  // week: Replace, with no question — "Add" would count those days twice (spec §9.3).
  if (state.submissionType === 'Weekly Timesheet' && _tsFiledCells()) {
    state._weekMode = 'week';
    showToast('Replacing this week\'s filing — ' + _tsFiledRange() + ' from your earlier sheet are included', 'info');
    return true;
  }
  if (state._weekMode === 'mine' || state._weekMode === 'week') {
    return true;   // answered on an earlier attempt of this same filing
  }
  if (!navigator.onLine) {
    // No server to ask — but the outbox on this phone can be: a filing of
    // this same week queued here means a re-file is being typed, and
    // replaying both as 'mine' doubled the week with no question. (A week
    // filed ONLINE earlier cannot be seen offline; that replay stays
    // additive — the choice that cannot destroy a filing.)
    let queued = false;
    try {
      const pending = typeof outboxPendingSync === 'function' ? await outboxPendingSync() : null;
      queued = !!(pending && pending.byFiling && pending.byFiling.has(_for));
    } catch (_) { /* unreadable store — stay additive */ }
    if (!queued) return true;
    const pick = await _weekModeSheet();
    if (!pick) return false;
    state._weekMode = pick;
    return true;
  }
  let taken = false;
  try {
    // Deadline-raced like every other network step in handleSubmit: on a
    // jobsite the browser reports onLine while requests hang, and this one
    // runs BEFORE the offline fallback exists, so a hang here used to strand
    // the foreman on a dead button with no outbox save.
    taken = await _withNetTimeout((async () => {
      const { data: project } = await sbClient.from('projects')
        .select('id').eq('name', state.project).maybeSingle();
      if (!project) return false;          // sync will surface the real problem
      const foreman = isProd ? (state.productionCrewId || _submitterId()) : _submitterId();
      if (!isProd) {
        // The days filed on this job-week by anyone (the same read the prefill makes); mine = taken.
        const { data, error } = await sbClient.rpc('timesheet_filed_days',
          { p_project_id: project.id, p_week_of: _dateFromWeekStart(weekStart, 0) });
        if (error) return false;           // cannot tell → do not ask, stay additive
        return (data || []).some(r => r.foreman_id === foreman);
      }
      const { count, error } = await sbClient.from('production_reports')
        .select('id', { count: 'exact', head: true })
        .eq('project_id', project.id).eq('foreman_id', foreman)
        .gte('report_date', _dateFromWeekStart(weekStart, 0))
        .lte('report_date', _dateFromWeekStart(weekStart, 6));
      if (error) return false;             // cannot tell → do not ask, stay additive
      return (count || 0) > 0;
    })(), 'week check');
  } catch (_) { return true; }
  if (!taken) {
    state._weekMode = 'mine';
    return true;
  }
  const pick = await _weekModeSheet();
  if (!pick) return false;                       // walked away — nothing filed
  state._weekMode = pick;
  return true;
}

// The sheet itself: three buttons, none of them doing double duty. Escape and
// the backdrop both mean "go back", same as the Cancel button — backing out of
// a question must never be the destructive answer.
function _weekModeSheet() {
  return new Promise((resolve) => {
    const isProd = state.submissionType === 'Production Report';
    const what = isProd ? 'production report' : 'timesheet';
    const wrap = document.createElement('div');
    wrap.className = 'delivery-modal-overlay';
    wrap.style.cssText = 'display:flex;z-index:10002';
    wrap.innerHTML = `
      <div class="delivery-modal" style="max-width:440px;width:calc(100vw - 24px)" role="dialog" aria-modal="true" aria-labelledby="weekModeTitle">
        <div class="delivery-modal-header">
          <h3 id="weekModeTitle" style="margin:0">This week already has a ${what}</h3>
        </div>
        <div style="padding:16px 20px 4px;line-height:1.5">
          <p style="margin:0 0 14px">There is already a ${what} for <strong></strong> this week. What should happen?</p>
        </div>
        <div style="display:flex;flex-direction:column;gap:10px;padding:0 20px 20px">
          <button type="button" class="btn btn-next" data-pick="mine" style="width:100%">
            Add to the week — both count, totals add up
          </button>
          <button type="button" class="btn" data-pick="week" style="width:100%;color:var(--danger, #b3261e);border-color:currentColor">
            Replace the week — the earlier numbers are removed
          </button>
          <button type="button" class="btn" data-pick="" style="width:100%">
            Cancel — don't file anything
          </button>
        </div>
      </div>`;
    wrap.querySelector('strong').textContent = state.project || 'this project';
    const done = (val) => {
      document.removeEventListener('keydown', onKey, true);
      wrap.remove();
      resolve(val);
    };
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); done(null); } };
    document.addEventListener('keydown', onKey, true);
    wrap.addEventListener('click', (e) => {
      if (e.target === wrap) { done(null); return; }         // backdrop = go back
      const b = e.target.closest('[data-pick]');
      if (b) done(b.dataset.pick || null);
    });
    document.body.appendChild(wrap);
    wrap.querySelector('[data-pick="mine"]').focus();
  });
}

// ── The watchdog: no filed week may quietly count for nothing ────────────────
// Scans MY recent Production Reports / Weekly Timesheets, asks the database
// which of them actually produced per-day rows, and rebuilds the ones whose
// numbers never landed — from the payload the submission already carries.
//
// Three things it must never do, each of which it once did:
//   · run only when the outbox happens to be non-empty. The failure it exists
//     for is an ONLINE filing whose second write failed, which queues nothing,
//     so the check now runs on its own trigger (see _startSyncWatchdog).
//   · repair a week the person deliberately REPLACED. A superseded filing has
//     the same shape as a broken one — payload with work, zero rows — so when
//     another submission owns rows in that week, this one is left alone and
//     the admin is told, instead of the numbers being silently doubled.
//   · trust a failed read. supabase-js returns { data: null, error } without
//     throwing; treating null as "no rows" flagged every recent filing at once.
//
// It also never carries a stored week mode: a rebuild days later replaces only
// its own rows ('mine'), never a whole week.
const _WATCHDOG_WINDOW_DAYS = 14;
const _WATCHDOG_MIN_GAP_MS  = 5 * 60 * 1000;
let _selfCheckAt = 0;
async function _syncSelfCheck(opts = {}) {
  if (!opts.force && Date.now() - _selfCheckAt < _WATCHDOG_MIN_GAP_MS) return;
  if (!navigator.onLine || !window.currentUser || !window.Ordinex?.syncRepair) return;
  _selfCheckAt = Date.now();
  try {
    const SR = window.Ordinex.syncRepair;
    const since = new Date(Date.now() - _WATCHDOG_WINDOW_DAYS * 86400000).toISOString();
    const { data: subs, error: subErr } = await sbClient.from('submissions')
      .select('id, submission_type, project_name, week_of, submitted_at, foreman_id, foreman_name, data')
      .eq('foreman_id', window.currentUser.id)
      .in('submission_type', WEEKLY_DRAFT_FORMS)
      .gte('submitted_at', since);
    if (subErr || !subs || !subs.length) return;

    // Both probes must SUCCEED. A failed read returns data:null with no throw,
    // and reading that as "zero rows" would queue a repair for every filing.
    const ids = subs.map(x => x.id);
    const [pr, te, sd] = await Promise.all([
      sbClient.from('production_reports')
        .select('submission_id, project_id, report_date').in('submission_id', ids),
      sbClient.from('time_entries')
        .select('submission_id, project_id, work_date').in('submission_id', ids),
      // a sick day the database left unpaid (hours on another sheet) is not a missing row
      sbClient.from('sick_days').select('submission_id, note').in('submission_id', ids),
    ]);
    if (pr.error || te.error || sd.error) {
      console.warn('sync self-check: probe failed, skipping', pr.error || te.error || sd.error);
      return;
    }
    const prHas = new Set((pr.data || []).map(r => r.submission_id));
    const teHas = new Set((te.data || []).map(r => r.submission_id));
    const unpaidElsewhere = {};
    (sd.data || []).filter(SR.isUnpaidElsewhere).forEach(r => { unpaidElsewhere[r.submission_id] = (unpaidElsewhere[r.submission_id] || 0) + 1; });

    // Which (project, week) pairs already hold rows from SOME filing. A
    // submission with no rows whose week is otherwise covered was replaced on
    // purpose — repairing it would double the week.
    //
    // The pair must be keyed by PROJECT AND WEEK, and the project half has to
    // survive the comparison: the rows carry project_id, the submission carries
    // project_name, so the ids are translated to names here. Matching on the
    // week alone (what the first cut did) let any other project's covered week
    // veto this one's repair — in a two-project org that is most of them, and
    // the silent self-heal turned back into an admin chore.
    const projectIds = [...new Set([...(pr.data || []), ...(te.data || [])]
      .map(r => r.project_id).filter(Boolean))];
    const nameById = new Map();
    if (projectIds.length) {
      const { data: projRows, error: projErr } = await sbClient.from('projects')
        .select('id, name').in('id', projectIds);
      // A failed read must not turn into "nothing is covered" — that would
      // repair a week the person deliberately replaced. Stop instead.
      if (projErr) {
        console.warn('sync self-check: project lookup failed, skipping', projErr);
        return;
      }
      (projRows || []).forEach(p => nameById.set(p.id, p.name));
    }
    const coveredWeeks = new Set();
    const cover = (rows, dateKey) => (rows || []).forEach(r => {
      const d = r[dateKey];
      const name = nameById.get(r.project_id);
      if (name && d) coveredWeeks.add(name + '|' + _dateFromWeekStart(_weekStartFor(d), 0));
    });
    cover(pr.data, 'report_date');
    cover(te.data, 'work_date');

    // One read of the outbox for the whole scan, not one per submission: the
    // old shape re-deserialised every queued PDF to answer a question about
    // two scalars. Also matches FULL queued records (a submission that landed
    // but whose sync threw), which the sync-only lookup could not see.
    const pending = (await (window.outboxPendingSync?.() ?? Promise.resolve(null))) || {
      bySubmission: new Set(), byWeek: new Set(),
    };

    const isAdmin = window.currentProfile?.role === 'admin';
    let orgHasSubs = null, subsUnknown = false;
    let queued = 0, superseded = 0;
    for (const sub of subs) {
      const isProd = sub.submission_type === 'Production Report';
      const rows = (isProd ? prHas : teHas).has(sub.id) ? 1 : 0;
      if (!SR.needsRepair(sub.submission_type, sub.data, rows, unpaidElsewhere[sub.id] || 0)) continue;

      // week_of is the filing's own week; submitted_at is when it reached the
      // server, which on a replay is the reconnect, not the work. Without a
      // week we cannot place the rows at all, so we ask rather than guess.
      const weekDate = sub.week_of || null;
      const projectKey = sub.project_name || '';
      if (!weekDate) {
        superseded++;   // counted with the "needs a person" bucket
        console.warn('sync self-check: submission has no week_of, not repairing', sub.id);
        continue;
      }
      const weekKey = _dateFromWeekStart(_weekStartFor(weekDate), 0);
      if (pending.bySubmission.has(sub.id) || pending.byWeek.has(projectKey + '|' + weekKey)) continue;

      if (orgHasSubs === null && !subsUnknown && isAdmin && isProd) {
        if (_knownSubCrews().length > 0) orgHasSubs = true;     // this device already knows
        else {
          const { count, error: crewErr } = await sbClient.from('profiles')
            .select('id', { count: 'exact', head: true }).eq('is_subcontractor', true);
          // A failed read must not turn into "this org has no sub crews": that
          // rebuilt an admin-typed week as the admin's own production, with no
          // labour cost, and left the watchdog satisfied. Leave it for the
          // next scan instead.
          if (crewErr || count == null) {
            subsUnknown = true;
            console.warn('sync self-check: sub-crew count failed, leaving admin production reports for the next scan', crewErr);
          } else orgHasSubs = count > 0;
        }
      }
      if (subsUnknown && isAdmin && isProd) continue;
      const decision = SR.repairDecision({
        submissionType: sub.submission_type,
        alreadyQueued: false,                       // handled by `pending` above
        authorIsAdmin: isAdmin,
        orgHasSubcontractors: !!orgHasSubs,
        weekCoveredByAnother: coveredWeeks.has(projectKey + '|' + weekKey),
        attempts: (window.repairAttemptsFor?.(sub.id) ?? 0),
      });
      if (decision === 'skip') continue;
      if (decision === 'notify') {
        superseded++;
        // Admin-directed (sync-repair.js: 'notify' = tell the admin): the fix
        // lives in the admin Submissions tab, which a foreman has no door to.
        // Once per page life — it used to re-toast on every boot, reconnect
        // and tab return, over whatever form was open (stranger sweep).
        if (isAdmin && !_watchdogNotified) {
          _watchdogNotified = true;
          showToast(`A ${sub.submission_type} for ${sub.project_name} has no numbers on the dashboard. Open Submissions and re-file or delete it.`, 'warning');
        }
        continue;
      }
      await saveSyncRepairOffline({
        submissionType: sub.submission_type,
        project: sub.project_name,
        date: weekDate,
        fields: sub.data || {},
        _effForemanId: sub.foreman_id, _effForemanName: sub.foreman_name,
        _crewForemanId: null, _crewForemanName: '',
        weekMode: null,        // a rebuild replaces its own rows and nothing else
      }, sub.id);
      queued++;
    }
    if (queued) {
      showToast(`Recovering ${queued} report${queued > 1 ? 's' : ''} — the numbers will sync automatically.`, 'info');
      if (typeof flushOutbox === 'function') setTimeout(() => flushOutbox({ toast: false }), 500);
    }
  } catch (e) {
    console.warn('sync self-check failed:', e);
  }
}

// The watchdog's own trigger. flushOutbox is the wrong one on its own: it
// returns early when the outbox is empty, which is precisely the state a
// healthy device is in when an online filing's second write failed.
let _watchdogNotified = false;
function _startSyncWatchdog() {
  const kick = () => { _syncSelfCheck().catch(() => {}); };
  kick();                                               // app open / login
  window.addEventListener('online', kick);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') kick();
  });
}
window._startSyncWatchdog = _startSyncWatchdog;
window._syncSelfCheck = _syncSelfCheck;

async function syncProductionReportToDb(s, submissionId, weekMode) {
  // Author of the report: the queued record supplies it on offline replay (a
  // later session), else the live globals (online). Lazy `??` so the replay path
  // never touches the globals (state is gone by then).
  const authorId   = s._effForemanId   ?? _submitterId();
  const authorName = s._effForemanName ?? _submitterName();
  // Whose production it is. Only differs from the author when an admin entered a
  // subcontract crew's week (see renderProductionCrewPicker) — and that is the
  // whole point: Job Costs prices sub labour off production_reports.foreman_id,
  // so the units have to sit on the sub's row to be costed at all. The DB
  // enforces the same rule: writing another foreman_id is accepted only for an
  // admin, and only when that profile is flagged is_subcontractor.
  // PRESENCE, not `??`: a replay/repair spells the crew out as null on
  // purpose, and `null ?? x` falls through to x — so a queued repair used to
  // pick up whatever crew the admin happened to have selected in an open form
  // and file another crew's production under it. Job Costs prices sub labour
  // off production_reports.foreman_id, so that is money on the wrong row.
  const fromRecord = ('_crewForemanId' in s);
  const crewId   = (fromRecord ? s._crewForemanId   : state.productionCrewId)   || null;
  const crewName = (fromRecord ? s._crewForemanName : state.productionCrewName) || '';
  const effForemanId   = crewId   || authorId;
  const effForemanName = crewId ? (crewName || authorName) : authorName;
  // Resolve project name → id
  const { data: project } = await sbClient.from('projects')
    .select('id').eq('name', s.project).maybeSingle();
  if (!project) {
    // THROW, do not return. On an offline replay this silent return let
    // flushOutbox delete the record — the only copy of the week — while the
    // foreman was told it had been sent. A project that cannot be resolved by
    // name (renamed, archived, or the foreman unassigned) has to keep the
    // record queued and visible.
    const msg = 'syncProductionReport: project not found: ' + s.project;
    console.warn(msg);
    _syncFailed('production', { message: msg });
    throw new Error(msg);
  }

  const weekStart = _weekStartFor(s.date);
  const DAY_KEYS  = ['mon','tue','wed','thu','fri','sat','sun'];
  const items = Array.isArray(s.fields?.production?.items) ? s.fields.production.items : [];
  // NO early return on an empty week. A foreman who strips every item out and
  // resubmits is CORRECTING the week, and that correction has to reach the
  // database — the old `if (!items.length) return;` here (and its twin on
  // dayRows below) meant the previous filing's numbers stood forever while he
  // got a PDF and a success screen. An empty payload now replaces the week
  // with nothing, which is exactly what he just said it should be.

  // Load catalog → name → { id, category }
  const { data: mtypes } = await sbClient.from('material_types').select('id, name, category');
  const typeByName = {};
  (mtypes || []).forEach(t => { typeByName[t.name] = t; });

  // Per-day totals (blocks_installed / bricks_installed kept for back-compat
  // with the current dashboard; per-type detail goes into production_line_items).
  const dayBlocks = {}, dayBricks = {}, dayFirestop = {}, dayHas = {};
  DAY_KEYS.forEach(d => { dayBlocks[d] = 0; dayBricks[d] = 0; dayFirestop[d] = 0; dayHas[d] = false; });

  items.forEach(item => {
    const cat = typeByName[item.type]?.category;
    DAY_KEYS.forEach(day => {
      const q = parseFloat(item.qty?.[day]) || 0;
      if (q > 0) {
        dayHas[day] = true;
        if (cat === 'block') dayBlocks[day] += q;
        else if (cat === 'brick') dayBricks[day] += q;
        else if (cat === 'firestop') dayFirestop[day] += q;   // linear feet
      }
    });
  });

  const dayRows = DAY_KEYS
    .map((day, idx) => ({ day, idx }))
    .filter(d => dayHas[d.day])
    .map(d => ({
      date: _dateFromWeekStart(weekStart, d.idx),
      dayKey: d.day,
      // project/foreman/submission are the RPC's arguments now — repeating
      // them per row would let a payload disagree with the week being replaced.
      payload: {
        report_date:      _dateFromWeekStart(weekStart, d.idx),
        bricks_installed: Math.round(dayBricks[d.day]),
        blocks_installed: Math.round(dayBlocks[d.day]),
        firestop_lf:      Math.round(dayFirestop[d.day] * 10) / 10,
        notes:            (s.fields?.delays || '').trim() || null,
      },
    }));

  // (No early return here either — see the note on `items` above.)

  // One line item per material per day. bricklayers/helpers columns still
  // exist in the DB for legacy data but are no longer collected in the form
  // (labor cost comes from time_entries / sub rates, headcount was redundant).
  const lineItems = [];
  dayRows.forEach(r => {
    items.forEach(item => {
      const mt = typeByName[item.type];
      if (!mt) return;
      const qty = parseFloat(item.qty?.[r.dayKey]) || 0;
      if (qty <= 0) return;
      lineItems.push({
        report_date:      r.date,
        material_type_id: mt.id,
        quantity:         qty,
      });
    });
  });

  // ONE transaction, the whole week, exactly like the timesheet's
  // replace_timesheet_week. It replaces a three-step client-side dance
  // (upsert the days it happened to touch, delete the line items of those
  // days, re-insert) that could not express four things it needed to:
  //   · a day the foreman BLANKED never got deleted — it kept the previous
  //     filing's quantities and kept earning;
  //   · a week emptied completely never reached the database at all;
  //   · one week could end up owned by two or three submissions, so deleting
  //     "the report" erased a fragment;
  //   · two queued replays wiped each other — that one already cost 556 sf of
  //     Veneer Stone at Langet Stadium on 2026-07-27.
  // See supabase/migrations/20260814230000_replace_production_week.sql.
  const mode = _resolveWeekMode(weekMode);

  const { error } = await sbClient.rpc('replace_production_week', {
    p_project_id:    project.id,
    p_week_start:    _dateFromWeekStart(weekStart, 0),
    p_foreman_id:    effForemanId,
    p_foreman_name:  effForemanName,
    p_submission_id: submissionId || null,
    p_days:          dayRows.map(r => r.payload),
    p_items:         lineItems,
    p_mode:          mode,
  });
  if (error) {
    // The office already filed this week for the crew: only an admin can
    // change it (audit 2026-10-29). Not a hiccup — retrying would only end as
    // a "stuck" record. Say so once and stop; the numbers are in the PDF.
    if (/office_filed_production/.test(error.message || '')) {
      showToast('Filed — the office already entered production for this week, so these quantities were not added. Your admin can update them.', 'warning');
      return;
    }
    console.warn('production week write failed (nothing changed — atomic):', error.message);
    _syncFailed('production', error);
    // THROW so the caller can queue an automatic repair. Returning quietly
    // here is how a filed report with an unmoved P&L went unnoticed until a
    // support session — twice in one day (2026-08-16).
    throw error;
  }
}

// ── Sick-day yearly allowance ─────────────────────────────────────────────────
// Each in-house worker gets 5 PAID sick days per calendar year. A sick day
// beyond that is still recorded in sick_days (absence tracking — the admin tab
// shows it in red) but pays nothing: no 8h time_entries row, 0 hrs on the PDF.
// This runs at submit time and stamps `cell.sick_unpaid = true` on the
// over-allowance cells, so the PDF generator and syncTimesheetToDb both see it.
// The number itself is the ORG's (Settings), not this file's — it is a
// multi-tenant product and a company on a different allowance would otherwise
// be silently held to five. Falls back to five, which is BC's.
const _sickAllowance = () =>
  Number(window.currentOrg?.sickDaysPerYear) ||
  window.Ordinex.sickAllowance.DEFAULT_SICK_DAYS_PER_YEAR;
// The clock's stat list: a sick day on one of these pays the holiday only and
// uses no paid sick day. None when the clock module is off (unchanged there).
const _sickStatDays = () =>
  window.Ordinex.timeClock.enabled(window.currentOrg) ? (window.currentOrg?.statHolidays || []) : [];

async function _annotateUnpaidSickDays(s) {
  if (s.submissionType !== 'Weekly Timesheet') return;
  const employees = Array.isArray(s.fields?.timesheet) ? s.fields.timesheet : [];
  const DAY_KEYS  = ['mon','tue','wed','thu','fri','sat','sun'];
  const weekStart = _weekStartFor(s.date);

  const entries = [];   // { emp, cell, date }
  employees.forEach(emp => {
    DAY_KEYS.forEach((day, idx) => {
      const cell = emp?.days?.[day];
      if (!cell) return;
      delete cell.sick_unpaid;   // clear stale flags from drafts / resubmits
      delete cell.sick_hours;
      if (cell.sick) entries.push({ emp, cell, date: _dateFromWeekStart(weekStart, idx) });
    });
  });
  if (!entries.length) return;

  // Stamp what a paid sick day is worth HERE, before anything that can fail.
  // The org's number comes from the cached org context, so this works with no
  // signal — and it must, because everything below needs the network. A phone
  // filing from a basement still writes the company's own day length; only the
  // "has this worker used up the year?" question falls back to paid.
  //
  // Stamped, not looked up later: the timesheet keeps the number it was signed
  // with, so changing the rule never rewrites a record that is already filed.
  const paidHours = window.Ordinex.timesheetActivity.orgPaidSickHours(window.currentOrg);
  entries.forEach(e => { e.cell.sick_hours = paidHours; });

  // Resolve worker ids the same way the sync does (dropdown id, else name).
  const names = [...new Set(entries
    .filter(e => !e.emp.worker_id && (e.emp.name || '').trim())
    .map(e => e.emp.name.trim()))];
  const idByName = {};
  if (names.length) {
    const { data: ws } = await sbClient.from('workers').select('id, name').in('name', names);
    (ws || []).forEach(w => { idByName[w.name.toLowerCase()] = w.id; });
  }
  const workerIdOf = emp =>
    (emp.worker_id || '').trim() || idByName[(emp.name || '').trim().toLowerCase()] || null;

  const ids = [...new Set(entries.map(e => workerIdOf(e.emp)).filter(Boolean))];
  if (!ids.length) return;

  const year = weekStart.getFullYear();
  const { data: rows, error } = await sbClient.from('sick_days')
    .select('worker_id, sick_date, portion, note')
    .in('worker_id', ids)
    .gte('sick_date', year + '-01-01')
    .lte('sick_date', year + '-12-31');
  if (error) throw error;

  // Exclude the dates in THIS submission from the "used" tally — a resubmitted
  // week would otherwise count itself and flip its own days to unpaid.
  const submittedDates = {};   // worker_id → Set of ISO dates
  entries.forEach(e => {
    const wid = workerIdOf(e.emp); if (!wid) return;
    (submittedDates[wid] = submittedDates[wid] || new Set()).add(e.date);
  });
  const SA = window.Ordinex.sickAllowance;
  const used = SA.usedByWorker(rows, submittedDates, _sickStatDays());

  // Chronological within the week; each paid day consumes one allowance unit.
  // The decision itself is js/core/sick-allowance.js — see that file for why it
  // is not inline any more (it was unreachable code for months and nobody could
  // tell, because an RLS-filtered SELECT returns fewer rows, not an error).
  const cellByKey = new Map();
  entries.forEach(e => {
    const wid = workerIdOf(e.emp); if (!wid) return;
    cellByKey.set(wid + '|' + e.date, e.cell);
  });
  SA.markSickDays(
    entries.map(e => ({ workerId: workerIdOf(e.emp), date: e.date })),
    used,
    _sickAllowance(),
    _sickStatDays(),
  ).forEach(d => {
    if (d.unpaid) {
      const cell = cellByKey.get(d.workerId + '|' + d.date);
      if (cell) cell.sick_unpaid = true;
    }
  });
}

async function syncTimesheetToDb(s, submissionId, weekMode) {
  // Effective foreman name: from the queued record on offline replay, else the
  // live global (online). Lazy `??` so replay never touches the (gone) global.
  // (The rows carry no foreman id — replace_timesheet_week stamps auth.uid().)
  const effForemanName = s._effForemanName ?? _submitterName();
  const { data: project } = await sbClient.from('projects')
    .select('id').eq('name', s.project).maybeSingle();
  if (!project) {
    // THROW, do not return. On an offline replay this silent return let
    // flushOutbox delete the record — the only copy of the week — while the
    // foreman was told it had been sent. A project that cannot be resolved by
    // name (renamed, archived, or the foreman unassigned) has to keep the
    // record queued and visible.
    const msg = 'syncTimesheet: project not found: ' + s.project;
    console.warn(msg);
    _syncFailed('timesheet', { message: msg });
    throw new Error(msg);
  }

  const weekStart = _weekStartFor(s.date);
  const employees = Array.isArray(s.fields?.timesheet) ? s.fields.timesheet : [];

  // Prefer the worker_id snapshotted at form-fill time (the new dropdown sets it
  // directly). Fall back to a name lookup for older drafts that still carry only
  // a name — case-insensitive against the workers table.
  const idsSeen = [...new Set(employees.map(e => (e?.worker_id || '').trim()).filter(Boolean))];
  const namesForLookup = [...new Set(employees
    .filter(e => !e?.worker_id && (e?.name || '').trim())
    .map(e => e.name.trim()))];
  const workerByName = {};
  if (namesForLookup.length) {
    const { data: workers } = await sbClient.from('workers').select('id, name').in('name', namesForLookup);
    (workers || []).forEach(w => { workerByName[w.name.toLowerCase()] = w.id; });
  }
  if (!idsSeen.length && !Object.keys(workerByName).length) {
    // THROW, do not return. A silent return read as success: flushOutbox
    // deleted the queued record and toasted "sent", the watchdog re-detected
    // the same submission five minutes later, and the pair looped forever
    // while the hours never landed. Throwing lets attempts/stuck do its job.
    const msg = 'syncTimesheet: none of this week\'s workers exist any more';
    _syncFailed('timesheet', { message: msg });
    throw new Error(msg);
  }

  // Per-task activity split (Phase 4). Old drafts carry no `fields.activity`
  // at all → {} → every day inherits week: '' → task_id null on every row,
  // which is exactly what today's (pre-split) rows already looked like.
  const activity = s.fields?.activity || {};
  const TA = window.Ordinex.timesheetActivity;

  // The signed sheet → rows: ONE walk, shared with the admin's "Re-file
  // hours" (js/core/timesheet-activity.js sheetRows), so the office can never
  // derive a different week than this phone did. A sick day goes to the
  // sick_days ledger (the admin Sick Days tab reads it, so it counts
  // immediately); a PAID one is also a task-less row of regular hours in
  // `rows`, so the dashboard / project P&L labour cost includes the sick pay.
  const sheet = TA.sheetRows(employees, activity, _dateFromWeekStart(weekStart, 0),
    emp => (emp?.worker_id || '').trim() || workerByName[(emp?.name || '').trim().toLowerCase()]);
  const rows = sheet.rows.map(r => ({ ...r, submission_id: submissionId || null }));
  const sickRows = sheet.sick.map(sd => ({
    worker_id:   sd.worker_id,
    worker_name: sd.name || 'Unknown',
    sick_date:   sd.work_date,
    portion:     1,
    // The timesheet that recorded it. sick_days.submission_id has ON
    // DELETE CASCADE, so deleting the timesheet now takes its sick days
    // with it — which is what the admin's confirm dialog has always
    // promised and never did. A day logged by hand on the Sick Days tab
    // keeps a NULL here and survives on its own.
    submission_id: submissionId || null,
    note:        'From timesheet — ' + effForemanName +
                 (sd.cell.sick_unpaid ? ' (UNPAID — 5-day allowance used)' : ''),
  }));

  // Idempotency by replacement: this foreman's week on this project. The old
  // upsert keyed on UNIQUE (worker_id, work_date) — gone now, a split day is
  // two rows — and it silently clobbered a worker filed by two foremen on the
  // same date. A plain client-side delete-then-insert left a window where a
  // dropped connection between the two halves could double hours (delete
  // lost) or wipe the week (insert lost) — so the whole replace runs as ONE
  // transaction inside the DB via replace_timesheet_week (SECURITY INVOKER:
  // RLS still judges every row, foreman_id is forced to auth.uid() inside).
  // Empty `rows` still calls the RPC — a resubmitted week with everything
  // removed must clear the old rows, not leave them stranded.
  const mode = _resolveWeekMode(weekMode);

  const { error } = await sbClient.rpc('replace_timesheet_week', {
    p_project_id: project.id,
    p_week_start: _dateFromWeekStart(weekStart, 0),
    p_foreman_name: effForemanName,
    // A clock day's punches' times ride along (sheetRows); a typed day sends none.
    p_rows: rows.map(({ worker_id, work_date, regular_hours, overtime_hours, hours_worked, task_id, submission_id,
      start_time = null, end_time = null, break_minutes = 0 }) =>
      ({ worker_id, work_date, regular_hours, overtime_hours, hours_worked, task_id, submission_id, start_time, end_time, break_minutes })),
    p_submission_id: submissionId || null,
    p_mode: mode,
  });
  if (error) {
    console.warn('timesheet week write failed (nothing changed — atomic):', error.message);
    // A week the office closed is said once, by the caller (_tsWeekClosedMsg), not twice.
    if (!/week_closed/.test(error.message || '')) _syncFailed('hours', error);
    throw error;   // same contract as production: the caller queues a repair
  }
  if (sickRows.length) {
    // Upsert on (worker_id, sick_date) so re-submitting the same week never
    // duplicates a sick day (unique index sick_days_worker_date_uniq).
    const { error } = await sbClient.from('sick_days')
      .upsert(sickRows, { onConflict: 'worker_id,sick_date' });
    if (error) {
      console.warn('sick_days upsert failed:', error.message);
      _syncFailed('sick days', error);
    }
  }
}

// A structured write that fails after the form is already filed used to end at
// console.warn — a line nobody on a jobsite will ever open. sick_days had been
// admin-only in RLS since the port while the field is what writes it, so every
// sick day a foreman ticked was refused, for months, in total silence. The
// submission is safe either way (the PDF and the record are already stored);
// what is lost is the structured half that Job Costs and the Sick Days tab
// read. That is worth a line on screen.
function _syncFailed(what, error) {
  const msg = (error && error.message) || '';
  // "permission denied" / "violates row-level security" is not a hiccup to
  // retry — it is a rule that has to be changed in the database, and saying
  // "try again" would send a foreman in circles.
  const isPermission = /permission denied|row-level security|policy/i.test(msg);
  showToast(
    isPermission
      ? `Filed — but the ${what} did not save. Ask your admin to check permissions.`
      : `Filed — but the ${what} did not save. Show this to your admin.`,
    'warning',
  );
}

// Exposed for the offline outbox replay (js/offline.js is a separate classic
// script): on reconnect it runs the SAME idempotent structured sync.
window.syncProductionReportToDb = syncProductionReportToDb;
window.syncTimesheetToDb = syncTimesheetToDb;
// offline.js stamps week_of on queued records and needs the same Monday the
// syncs use. Explicit window export, not the bare global: build.ps1 minifies
// each classic script on its own and a top-level name is not a contract.
window._weekStartFor = _weekStartFor;
// Exposed so the outbox stamps weekOf in the SAME 'YYYY-MM-DD' shape the
// watchdog looks it up by. It used to store the raw Date, which stringified as
// 'Mon Aug 11 2025 00:00:00 GMT-0700 (…)' and never matched anything.
window._dateFromWeekStart = _dateFromWeekStart;
window.syncExtraWorkOrderToDb = syncExtraWorkOrderToDb;
// (Photos are compressed at add-time via window.Ordinex.image — Onda 2a — so
//  the old _compressPhotoForUpload helper here was retired in Onda 2d.)

// ══════════════════════════════════════════════════════════════════════════════
//  Extra Work Order — write the parent + labour + materials rows
//  Foreman submits WHO + HOURS + WHAT-MATERIAL + QTY. All rates / amounts /
//  burden / overhead / fee start at 0 — admin fills them during finalisation.
// ══════════════════════════════════════════════════════════════════════════════
async function syncExtraWorkOrderToDb(s, submissionId) {
  const effForemanId   = s._effForemanId   ?? _submitterId();
  const effForemanName = s._effForemanName ?? _submitterName();
  const { data: project } = await sbClient.from('projects')
    .select('id, burden_pct').eq('name', s.project).maybeSingle();
  if (!project) { console.warn('syncEwo: project not found', s.project); return; }

  // Idempotent get-or-create by submission_id (Onda 2d): a re-replay reuses the
  // SAME EWO + number instead of creating a duplicate. Children are re-synced
  // (delete+insert) below, photos use deterministic paths + upsert.
  let ewoId = null, ewoNumber = 0, isNew = false;
  if (submissionId) {
    const { data: existing } = await sbClient.from('extra_work_orders')
      .select('id, ewo_number').eq('submission_id', submissionId).maybeSingle();
    if (existing) { ewoId = existing.id; ewoNumber = existing.ewo_number; }
  }

  // Photos → Storage. Already compressed at add-time (Onda 2a). Deterministic
  // path ({submissionId}/photo-{i}) + upsert = a re-replay overwrites, never
  // duplicates. Offline records store {blob,...}; online stores File objects.
  const photoPaths = [];
  const photoFiles = Array.isArray(s.photos) ? s.photos : [];
  for (let i = 0; i < Math.min(photoFiles.length, 9); i++) {
    try {
      const p = photoFiles[i];
      const file = (p && p.blob) || p;
      if (!file) continue;
      const ext = (file.type === 'image/png') ? 'png' : 'jpg';
      // The tenant prefix is not decoration — it is the whole reason the upload
      // is allowed. The bucket's only INSERT policy ("fs: own-folder insert")
      // requires foldername[1] = org id AND foldername[2] = auth.uid(). On
      // 2026-07-23 this path was rewritten to `ewo-photos/<sub>/photo-N.ext`,
      // which puts the literal word "ewo-photos" where the org id has to be, so
      // it could never pass — for anyone, admin included. Every EWO photo since
      // then was refused and dropped by the console.warn below: 0 objects in
      // the bucket carry that shape, while EWOs from before it carry 1, 1 and 9
      // photos. The deterministic tail stays, so a replay still overwrites
      // nothing new.
      const orgId = window.currentOrg?.id, uid = window.currentUser?.id;
      if (!orgId || !uid) {
        console.warn('EWO photo upload skipped: no org/user context');
        _syncFailed('EWO photos', { message: 'not signed in' });
        break;
      }
      const path = `${orgId}/${uid}/ewo-photos/${submissionId || 'nosub'}/photo-${i}.${ext}`;
      // upsert:false on purpose. This bucket has NO update policy at all, so an
      // upsert over an existing object is refused outright; a replay that finds
      // its own file already there is a success, not a failure — same bytes,
      // same deterministic path.
      const { error: upErr } = await sbClient.storage.from('form-submissions')
        .upload(path, file, { contentType: file.type || 'image/jpeg', upsert: false });
      const alreadyThere = upErr && (String(upErr.statusCode) === '409'
        || /already exists|duplicate/i.test(upErr.message || ''));
      if (upErr && !alreadyThere) {
        console.warn('EWO photo upload failed:', upErr.message);
        _syncFailed('EWO photos', upErr);
        continue;
      }
      photoPaths.push(path);
    } catch (phErr) { console.warn('EWO photo skipped:', phErr); }
  }

  if (!ewoId) {
    // Resolve EWO# — the peeked number (online) or, when it's absent (offline
    // replay), the RPC assigns the authoritative number here at sync time.
    ewoNumber = Number(s._ewoNextNumber) || 0;
    if (!ewoNumber) {
      const { data: n } = await sbClient.rpc('next_ewo_number', { p_project_id: project.id });
      ewoNumber = Number(n) || 0;
    }
    if (!ewoNumber) { console.warn('syncEwo: could not resolve EWO number'); return; }

    const parent = {
      ewo_number:      ewoNumber,
      project_id:      project.id,
      foreman_id:      effForemanId,
      foreman_name:    effForemanName,
      submission_id:   submissionId || null,
      week_ending:     s.fields?.week_ending || null,
      work_description: (s.fields?.work_description || '').trim() || null,
      status:          'submitted',
      burden_pct:      Number(project.burden_pct) || 0,
      overhead_pct:    10,
      fee_pct:         5,
      small_tool_pct:  18,
      total_labour:    0,
      total_burden:    0,
      total_material:  0,
      subtotal:        0,
      overhead_amount: 0,
      fee_amount:      0,
      total_amount:    0,
      foreman_signature_b64: s.signature   || null,
      gc_signature_b64:      s.gcSignature || null,
      gc_name:               (s.fields?.gc_name || '').trim() || null,
      photo_urls:            photoPaths,
    };
    const { data: inserted, error: insErr } = await sbClient
      .from('extra_work_orders').insert(parent).select('id, ewo_number').single();
    if (insErr) { console.error('extra_work_orders insert failed:', insErr); return; }
    ewoId = inserted.id; ewoNumber = inserted.ewo_number; isNew = true;
  } else if (photoPaths.length) {
    // Existing EWO (re-replay): refresh the (idempotent) photo paths.
    await sbClient.from('extra_work_orders').update({ photo_urls: photoPaths }).eq('id', ewoId);
  }

  // Children — clean re-sync (delete then insert) so a re-replay never duplicates.
  await sbClient.from('extra_work_order_labour').delete().eq('ewo_id', ewoId);
  const labourRows = (s.fields?.ewo_labour || [])
    .filter(r => r.worker_id || r.worker_name)
    .map((r, i) => ({
      ewo_id:       ewoId,
      worker_id:    r.worker_id || null,
      worker_name:  r.worker_name || '(unnamed)',
      worker_trade: r.worker_trade || null,
      hourly_rate:  0,          // admin sets at finalisation
      reg_hours:    Number(r.reg_hours) || 0,
      ot_hours:     Number(r.ot_hours)  || 0,
      td_hours:     Number(r.td_hours)  || 0,
      amount:       0,
      line_order:   i,
    }))
    .filter(r => r.reg_hours || r.ot_hours || r.td_hours);
  if (labourRows.length) {
    const { error } = await sbClient.from('extra_work_order_labour').insert(labourRows);
    if (error) console.warn('ewo labour insert failed:', error.message);
  }

  // Material rows — only those the foreman actually filled (qty > 0, or a custom
  // item with a description even if qty wasn't entered). Clean re-sync as well.
  await sbClient.from('extra_work_order_materials').delete().eq('ewo_id', ewoId);
  const materialRows = (s.fields?.ewo_materials || [])
    .filter(m => (Number(m.qty) || 0) > 0 || (!m.from_list && (m.description || '').trim()))
    .map((m, i) => ({
      ewo_id:      ewoId,
      po_number:   null,                     // admin fills
      vendor:      null,                     // admin fills
      description: m.description || '(no description)',
      qty:         Number(m.qty) || 0,
      unit:        m.unit || null,
      rate:        0,                        // admin sets at finalisation
      amount:      0,
      is_auto:     false,
      line_order:  i,
    }));
  if (materialRows.length) {
    const { error } = await sbClient.from('extra_work_order_materials').insert(materialRows);
    if (error) console.warn('ewo materials insert failed:', error.message);
  }

  // Photos already uploaded above (deterministic path + upsert) and their paths
  // set on photo_urls — the admin Final PDF fetches them from there.

  // ── Fire notification email to admin — only on first creation, so a re-replay
  // never re-notifies. Best-effort, non-blocking. ───────────────────────────
  if (isNew) {
    try {
      await sbClient.functions.invoke('notify-extra-work', {
        body: {
          ewo_id:       ewoId,
          ewo_number:   ewoNumber,
          project_name: s.project,
          foreman_name: effForemanName,
          week_ending:  s.fields?.week_ending || null,
          labour_count: labourRows.length,
          material_count: materialRows.length,
        }
      });
    } catch (e) { console.warn('notify-extra-work invoke failed:', e); }
  }
}
